// Dictionnaire de l'interface. Les trois langues sont maintenues ensemble, dans un seul
// fichier tant qu'il tient : une clé ajoutée d'un côté et oubliée de l'autre se voit
// immédiatement, et TypeScript refuse une langue incomplète (voir le type Messages).

export const LANGUAGES = ['fr', 'ar', 'en'] as const

export type Language = (typeof LANGUAGES)[number]

export const RTL_LANGUAGES: readonly Language[] = ['ar']

const fr = {
  'nav.audit': "Journal d'audit",
  'nav.sales': 'Ventes',
  'audit.title': "Journal d'audit",
  'audit.description': 'Qui a créé, modifié ou supprimé quoi. Conservé trois ans.',
  'audit.when': 'Quand',
  'audit.who': 'Auteur',
  'audit.what': 'Fiche',
  'audit.action': 'Action',
  'audit.action.insert': 'Création',
  'audit.action.update': 'Modification',
  'audit.action.delete': 'Suppression',
  'audit.changed': 'Ce qui a changé',
  'audit.from': 'Depuis le',
  'audit.to': "Jusqu'au",
  'audit.entity': 'Table',
  'audit.empty': 'Aucune écriture sur cette période.',
  'audit.window': 'Sans dates, la fenêtre est le mois écoulé.',
  'audit.system': 'Écriture technique',
  'audit.admin_only': "Le journal d'audit est réservé aux administrateurs.",
  'season.title': 'Campagnes',
  'season.description': 'Ouverture, clôture contrôlée et réouverture tracée (§10.5).',
  'season.open': 'Ouvrir une campagne',
  'season.opened': 'Campagne ouverte.',
  'season.close': 'Clôturer',
  'season.closed': 'Campagne close.',
  'season.reopen': 'Rouvrir',
  'season.reopened': 'Campagne rouverte.',
  'season.reopenReason': 'Motif de la réouverture',
  'season.reopenHint': 'Rouvrir remet en mouvement des chiffres déjà lus : le motif est conservé.',
  'season.reopenings': 'Réouvertures',
  'season.blockers': 'Ce qui empêche la clôture',
  'season.canClose': 'Aucun obstacle : la clôture passera.',
  'season.state.open': 'Ouverte',
  'season.state.closed': 'Close',
  'season.period': 'Période',
  'agro.season.close.pending_operations': 'Interventions non validées',
  'agro.season.close.harvest_without_destination': 'Récoltes sans destination',
  'agro.season.reopen_reason_required': 'Le motif de réouverture est obligatoire.',
  'agro.season.already_closed': 'Cette campagne est déjà close.',
  'agro.season.not_closed': "Cette campagne n'est pas close : il n'y a rien à rouvrir.",
  'agro.season.manager_only': 'Seuls un responsable ou un administrateur closent une campagne.',
  'stock.alerts': 'Alertes',
  'stock.alerts.empty': 'Aucune alerte de stock.',
  'stock.alerts.window': 'Fenêtre de péremption (jours)',
  'stock.alert.below_reorder_point': 'Sous le seuil de réapprovisionnement',
  'stock.alert.expiring_soon': 'Péremption proche',
  'stock.alert.expired': 'Déjà périmé',
  'stock.alert.negative_balance': 'Solde négatif',
  'stock.movements': 'Journal des mouvements',
  'stock.movements.empty': 'Aucun mouvement.',
  'stock.movements.direction': 'Sens',
  'stock.movements.reason': 'Nature',
  'stock.movements.source': 'Document d’origine',
  'stock.movements.value': 'Valeur',
  'stock.lots': 'Lots et péremption',
  'stock.lots.empty': 'Aucun lot suivi.',
  'stock.lots.code': 'Lot',
  'stock.lots.expiry': 'Péremption',
  'stock.lots.remaining': 'Reste',
  'stock.count': "Feuille d'inventaire",
  'stock.count.theoretical': 'Théorique',
  'stock.count.counted': 'Compté',
  'stock.count.gap': 'Écart',
  'stock.count.export': "Exporter la feuille",
  'ops.list': 'Interventions',
  'ops.list.empty': 'Aucune intervention.',
  'ops.filter.season': 'Campagne',
  'ops.filter.farm': 'Exploitation',
  'ops.filter.from': 'Depuis le',
  'ops.filter.to': "Jusqu'au",
  'fieldbook.byPlot': 'Cahier par parcelle',
  'fieldbook.export': 'Exporter en Excel',
  'contracts.title': 'Contrats de vente',
  'contracts.description': 'Un contrat se suit jusqu’à sa dernière livraison.',
  'contracts.empty': 'Aucun contrat.',
  'contracts.customer': 'Client',
  'contracts.crop': 'Culture',
  'contracts.quantity': 'Quantité contractée',
  'contracts.delivered': 'Déjà livré',
  'contracts.remaining': 'Reste à livrer',
  'contracts.price': 'Prix convenu',
  'contracts.dueDate': 'Échéance',
  'contracts.deliveries': 'Bons de livraison',
  'contracts.newDelivery': 'Nouveau bon de livraison',
  'contracts.deliveryHint': 'Sans quantité, le bon livre ce qui reste.',
  'contracts.complete': 'Contrat soldé',
  'customers.outstanding': 'Encours client',
  'customers.outstanding.empty': 'Aucun encours.',
  'app.name': 'AgriculteurApp',
  'app.tagline': 'Gestion des coûts agricoles',

  'nav.farms': 'Exploitations',
  'nav.plots': 'Parcelles',
  'nav.warehouses': 'Dépôts',
  'nav.campaigns': 'Campagnes',
  'nav.campaignGroups': 'Groupes de campagnes',
  'nav.purchases': 'Achats',
  'nav.dashboard': 'Tableau de bord',
  'nav.exports': 'Exports',
  'nav.products': 'Articles',

  'nav.assets': 'Actifs',
  'assets.title': 'Immobilisations',
  'assets.description': 'Registre des actifs, transferts et sorties (lot A : socle).',
  'assets.empty': 'Aucun actif.',
  'assets.code': 'Code',
  'assets.designation': 'Désignation',
  'assets.entryValue': "Valeur d'entrée",
  'assets.status': 'Statut',
  'assets.status.draft': 'Brouillon',
  'assets.status.in_progress': 'En cours de construction',
  'assets.status.in_service': 'En service',
  'assets.status.inactive': 'Inactif',
  'assets.status.disposed': 'Cédé',
  'assets.create': 'Nouvel actif',
  'assets.created': 'Actif créé.',
  'assets.transfer': 'Transférer',
  'assets.transfer.done': 'Transfert enregistré.',
  'assets.dispose': 'Céder',
  'assets.dispose.buyer': 'Acquéreur',
  'assets.dispose.capitalGain': 'Résultat de cession',
  'assets.dispose.date': 'Date de cession',
  'assets.dispose.method': 'Mode de sortie',
  'assets.dispose.method.destruction': 'Destruction',
  'assets.dispose.method.reform': 'Réforme',
  'assets.dispose.method.sale': 'Vente',
  'assets.dispose.method.theft': 'Vol',
  'assets.dispose.nbv': 'Valeur nette comptable',
  'assets.dispose.noPrice': 'Sans prix de cession',
  'assets.dispose.notValued': "Cession non valorisée : l'actif ne portait pas de plan d'amortissement.",
  'assets.dispose.price': 'Prix de cession',
  'assets.dispose.reason': 'Motif de la sortie',
  'assets.inProgress': 'Dépenses de construction',
  'assets.inProgress.add': 'Ajouter la dépense',
  'assets.inProgress.amount': 'Montant',
  'assets.inProgress.date': 'Date',
  'assets.inProgress.done': 'Dépense imputée.',
  'assets.inProgress.label': 'Libellé',
  'assets.inProgress.none': 'Aucune dépense imputée pour le moment.',
  'assets.inProgress.supplier': 'Fournisseur',
  'assets.inProgress.total': "Total, qui deviendra la valeur d'entrée",
  'assets.lifecycle.putInProgress': 'Déclarer en construction',
  'assets.dispose.done': 'Sortie enregistrée.',
  'assets.categories': 'Catégories',

  'assets.dashboard.assets': 'Actifs',
  'assets.dashboard.nbv': 'VNC totale',
  'assets.dashboard.hours': 'Heures productives',
  'assets.dashboard.alerts': 'Alertes',
  'assets.meters': 'Compteurs',
  'assets.meters.kind': 'Nature du compteur',
  'assets.meters.hours': 'Heures',
  'assets.meters.km': 'Kilomètres',
  'assets.meters.value': 'Valeur du relevé',
  'assets.meters.record': 'Relever',
  'assets.meters.done': 'Relevé enregistré.',
  'assets.meters.empty': 'Aucun relevé.',
  'assets.depreciation': 'Amortissement',
  'assets.depreciation.year': 'Année',
  'assets.depreciation.item': 'Élément',
  'assets.depreciation.charge': 'Dotation',
  'assets.depreciation.cumulative': 'Cumul',
  'assets.depreciation.nbv': 'VNC',
  'assets.depreciation.recompute': 'Régénérer le tableau',
  'assets.depreciation.recomputed': 'Tableau régénéré sur la base actuelle.',
  'assets.depreciation.duration': "Durée (ans)",
  'assets.depreciation.startHint': 'Le plan démarre à la date de mise en service, à défaut à la date d’acquisition :',
  'assets.depreciation.startMissing': 'aucune des deux n’est renseignée.',
  'assets.acquisition': 'Acquisition et mise en service',
  'assets.acquisitionDate': 'Date d’acquisition',
  'assets.commissioningDate': 'Date de mise en service',
  'assets.acquisition.hint': 'La date de mise en service ouvre l’amortissement : c’est elle qui en fixe la première année.',
  'assets.acquisition.save': 'Enregistrer les dates',
  'assets.acquisition.done': 'Dates enregistrées.',
  'assets.depreciation.create': 'Créer le plan',
  'assets.depreciation.done': 'Tableau généré.',
  'assets.hourly': 'Coût horaire',
  'assets.hourly.unavailable': 'Pas encore d heure productive : le coût refuse de répondre.',
  'assets.maintenance': 'Maintenance',
  'assets.maintenance.empty': 'Aucune intervention.',
  'assets.maintenance.title': "Titre de l'intervention",
  'assets.maintenance.cost': 'Coût des pièces',
  'assets.maintenance.add': 'Ajouter',
  'assets.maintenance.done': 'Intervention enregistrée.',
  'assets.tyres': 'Pneus',
  'assets.tyres.empty': 'Aucun pneu suivi.',
  'assets.tyres.removed': 'démonté',
  'assets.insurance': 'Assurance',
  'assets.insurance.empty': 'Aucune police.',
  'assets.stocktake': 'Inventaire',
  'assets.stocktake.empty': 'Aucun constat.',
  'assets.stocktake.record': 'Constater',
  'assets.stocktake.done': 'Constat enregistré.',
  'assets.lifecycle': 'Cycle de vie',
  'assets.lifecycle.commission': 'Mettre en service',
  'assets.lifecycle.done': 'Statut mis à jour.',

  'nav.assetsDashboard': 'Tableau actifs',
  'assets.usages': 'Utilisations',
  'assets.usages.empty': 'Aucune heure rattachée.',
  'assets.usages.operation': "N° d'intervention",
  'assets.usages.line': 'N° de ligne matériel',
  'assets.usages.attach': 'Rattacher',
  'assets.usages.done': 'Heures rattachées à l’actif.',
  'assets.components': 'Composants',
  'assets.components.empty': 'Aucun composant.',
  'assets.components.add': 'Ajouter',
  'assets.components.done': 'Composant ajouté : régénérez le tableau.',
  'assets.revaluation.reason': 'Motif de la réévaluation',
  'assets.revaluation.apply': 'Réévaluer',
  'assets.revaluation.done': 'Valeur réécrite et tableau régénéré.',
  'assets.hourly.fuel': 'Carburant',
  'assets.fuel.theoretical': 'Conso théorique (L/h)',
  'assets.fuel.set': 'Enregistrer',
  'assets.fuel.done': 'Consommation théorique enregistrée.',
  'assets.maintenance.kind': 'Nature',
  'assets.maintenance.corrective': 'Corrective',
  'assets.maintenance.preventive': 'Préventive',
  'assets.maintenance.meter': 'Compteur relevé',
  'assets.maintenance.nextMeter': 'Prochaine échéance (compteur)',
  'assets.tyres.add': 'Monter un pneu',
  'assets.tyres.done': 'Pneu enregistré.',
  'assets.tyres.position': 'Position (ex. AVG)',
  'assets.insurance.add': 'Ajouter une police',
  'assets.insurance.done': 'Police enregistrée.',
  'assets.insurance.insurer': 'Assureur',
  'assets.insurance.policy': 'N° de police',
  'assets.insurance.start': 'Début',
  'assets.insurance.end': 'Fin',
  'assets.lease': 'Crédit-bail',
  'assets.lease.empty': 'Aucun contrat.',
  'assets.lease.add': 'Rattacher un contrat',
  'assets.lease.done': 'Contrat enregistré.',
  'assets.lease.lessor': 'Organisme financier',
  'assets.lease.ref': 'Référence contrat',
  'assets.lease.start': 'Début du contrat',
  'assets.lease.end': 'Fin du contrat',
  'assets.lease.assetValue': 'Valeur du bien',
  'assets.lease.downPayment': 'Apport initial',
  'assets.lease.count': 'Nombre d’échéances prévues',
  'assets.lease.amount': 'Loyer par échéance',
  'assets.lease.installments.entered': 'échéances saisies, sur',
  'assets.lease.installments.planned': 'prévues au contrat.',
  'assets.lease.installments.empty': 'Aucune échéance saisie : ni l’échéancier de trésorerie ni le coût horaire ne voient ce contrat.',
  'assets.lease.installment.new': 'Ajouter une échéance',
  'assets.lease.installment.dueDate': 'Échéance',
  'assets.lease.installment.principal': 'Capital',
  'assets.lease.installment.interest': 'Intérêts',
  'assets.lease.installment.vat': 'Taxe',
  'assets.lease.installment.paid.amount': 'Payé',
  'assets.lease.installment.balance': 'Reste dû',
  'assets.lease.installment.add': 'Ajouter l’échéance',
  'assets.lease.installment.added': 'Échéance ajoutée.',
  'assets.lease.installment.payment': 'Montant payé',
  'assets.lease.installment.paidOn': 'Jour du paiement',
  'assets.lease.installment.pay': 'Payer',
  'assets.lease.installment.paid': 'Paiement enregistré.',
  'assets.lease.installment.settled': 'Soldée',
  // Lot 8d (migration 0103) : un paiement d'échéance est une pièce, qui s'annule.
  'assets.lease.payments': 'Paiements',
  'assets.lease.payment.date': 'Payé le',
  'assets.lease.payment.installment': 'Échéance du',
  'assets.lease.payment.status.posted': 'Enregistré',
  'assets.lease.payment.status.cancelled': 'Annulé',
  'assets.lease.payment.cancel': 'Annuler',
  'assets.lease.payment.cancelled': 'Paiement annulé : l’échéance redevient due.',
  'assets.lease.payment.reason': 'Motif d’annulation',
  'assets.lease.payment.reason.hint': 'Obligatoire pour annuler un paiement. Un paiement ne se corrige pas : il s’annule.',
  'assets.fixedCosts': 'Coûts fixes annuels',
  'assets.fixedCosts.label': 'Libellé (ex. taxe)',
  'assets.fixedCosts.amount': 'Montant',
  'assets.fixedCosts.add': 'Ajouter',
  'assets.fixedCosts.done': 'Coût fixe enregistré.',
  'assets.documents': 'Dossier numérique',
  'assets.documents.empty': 'Aucune pièce classée.',
  'assets.documents.type': 'Nature',
  'assets.documents.name': 'Nom du fichier',
  'assets.documents.add': 'Classer',
  'assets.documents.done': 'Pièce classée (métadonnées).',
  'assets.transfer.to': 'Destination (ex. Hangar 01)',
  'assets.dashboard.title': 'Tableau de bord des actifs',
  'assets.dashboard.fleet': 'Parc',
  'assets.dashboard.inService': 'En service',
  'assets.dashboard.gross': 'Valeur brute',
  'assets.dashboard.accumulated': 'Amortissement cumulé',
  'assets.dashboard.depreciation.none': "Aucun actif du parc n'a de plan d'amortissement : le cumul et la valeur nette comptable ne se calculent pas.",
  'assets.dashboard.work': 'Exploitation',
  'assets.dashboard.maintenance': 'Maintenance',
  'assets.dashboard.fuel': 'Carburant',
  'assets.dashboard.missing': 'Sans inventaire',
  'assets.dashboard.noAlerts': 'Aucune alerte.',
  'operations.fixedAsset': 'Actif suivi',
  'asset.usage.asset_required': 'Le rattachement désigne un actif suivi.',
  'asset.component.duplicate_code': 'Un composant porte déjà ce code.',
  'asset.meter_on_disposed': 'Un actif cédé ne reçoit plus de relevé.',
  'asset.fuel.anomaly': 'Consommation anormale à contrôler.',
  'asset.maintenance.due_meter': 'Maintenance due au compteur.',
  'asset.document.type_required': 'La nature du document est obligatoire.',
  'asset.meter.decrease_refused': 'Le compteur affiche déjà davantage : ce relevé est un recul.',
  'asset.depreciation.plan_exists': 'Cet actif porte déjà un plan.',
  'asset.depreciation.start_required': 'Le plan demande une date de départ.',
  'asset.hourly_cost.unavailable': 'Aucune heure productive : le coût refuse de répondre.',
  'asset.maintenance.negative_cost': 'Les coûts ne peuvent pas être négatifs.',
  'asset.lease.already_exists': 'Cet actif porte déjà un crédit-bail.',
  'asset.fixed_asset.duplicate_code': 'Un actif porte déjà ce code.',
  'asset.fixed_asset.not_found': "L'actif demandé est introuvable.",
  'asset.fixed_asset.commissioning_before_acquisition': 'La mise en service ne peut pas précéder acquisition.',
  'asset.transfer_on_disposed': "L'actif est cédé, il ne peut plus changer d'affectation.",
  'asset.disposal.already_disposed': 'Cet actif est déjà cédé.',
  'asset.disposal.reason_required': 'Le motif de la sortie est obligatoire.',
  'asset.category.duplicate_code': 'Une catégorie porte déjà ce code.',
  // Lot G — l'écran du coût horaire (§15, §16). Libellés des trois blocs de la fiche.
  'assets.referenceRate': 'Tarif de référence',
  'assets.referenceRate.none': "Aucun tarif de référence : une ligne sans tarif saisi s'appliquera à zéro.",
  'assets.referenceRate.observed': 'Coût horaire constaté',
  'assets.referenceRate.drift': "L'écart entre ce tarif et le coût constaté se juge au tableau de bord des actifs.",
  'assets.referenceRate.sourceLabel': 'Origine du tarif',
  'assets.referenceRate.source.budget': 'Budgété',
  'assets.referenceRate.source.fiscalYear': "Repris d'un exercice",
  'assets.referenceRate.year': "Exercice d'origine",
  'assets.referenceRate.takenFromYear': "Le tarif est recalculé sur l'exercice choisi : rien à saisir.",
  'assets.referenceRate.rate': 'Tarif (DT/h)',
  'assets.referenceRate.set': 'Poser le tarif',
  'assets.referenceRate.done': 'Tarif de référence enregistré.',
  'assets.hours': "Heures de l'exercice",
  'assets.hours.empty': 'Les heures de cet exercice ne se lisent pas.',
  'assets.hours.noCapacity': 'Capacité annuelle non déclarée.',
  'assets.hours.theoretical': 'Capacité annuelle',
  'assets.hours.unavailable': 'Indisponibles',
  'assets.hours.available': 'Disponibles',
  'assets.hours.productive': 'Productives',
  'assets.hours.utilization': "Taux d'emploi",
  'assets.hours.availability': 'Taux de disponibilité',
  'assets.downtimes': "Registre d'indisponibilité",
  'assets.downtimes.empty': 'Aucun arrêt sur cet exercice.',
  'assets.downtimes.add': 'Inscrire un arrêt',
  'assets.downtimes.done': 'Arrêt inscrit.',
  'assets.downtimes.from': 'Début',
  'assets.downtimes.to': 'Fin',
  'assets.downtimes.hours': 'Heures',
  'assets.downtimes.reason': 'Motif',
  'assets.downtimes.notes': 'Note',
  'assets.downtimes.deduced': 'Période déduite',
  'assets.downtimes.deducedHint': "Période déduite du carnet d'entretien : la fin est calculée à vingt-quatre heures par jour civil, elle n'a pas été saisie.",
  'assets.downtimes.reason.breakdown': 'Panne',
  'assets.downtimes.reason.maintenance': 'Entretien',
  'assets.downtimes.reason.weather': 'Intempérie',
  'assets.downtimes.reason.idle': 'Inaction',
  'assets.hourlyCost.tyres': 'Pneumatiques',
  'assets.hourlyCost.tyresIncomplete': "Au moins un pneu consommé n'a pas de prix : la part pneumatique est minorée.",
  'assets.hourlyCost.maintenancePerHour': "Entretien à l'heure",
  'assets.hourlyCost.maintenancePerHectare': "Entretien à l'hectare",
  'assets.hourlyCost.noArea': "Aucune superficie travaillée : le coût à l'hectare ne se calcule pas.",
  'assets.dashboard.fleetHourlyCost': 'Coût horaire du parc',
  'assets.dashboard.fleetHourlyCost.none': "Aucun actif du parc n'a de coût horaire calculable.",
  'assets.dashboard.alertsCapped': "Chaque famille d'alertes est coupée à vingt lignes, et rien ne dit combien ont été écartées.",
  'operations.referenceRate.hint': "Tarif de référence de l'actif, appliqué si ce champ reste vide :",
  'operations.referenceRate.none': "Cet actif n'a pas de tarif de référence : un champ vide s'appliquera à zéro.",
  // Codes d'erreur du module Actifs (lots A à G). Un code sans traduction ne casse rien
  // — translateError se replie sur le message français du serveur — mais un arabophone
  // lirait alors du français dans une application dont le trilinguisme est la promesse.
  'asset.category.code_required': 'Le code de la catégorie est obligatoire (30 caractères au plus).',
  'asset.category.invalid_depreciation_years': "La durée d'amortissement par défaut doit être comprise entre 1 et 100 ans.",
  'asset.category.name_required': 'Le nom de la catégorie est obligatoire (100 caractères au plus).',
  'asset.category.organization_required': "La catégorie d'actif doit être rattachée à une organisation.",
  'asset.category.parent_not_found': 'La catégorie parente est introuvable.',
  'asset.category.parent_required': 'La catégorie parente est invalide.',
  'asset.component.asset_required': 'Le composant doit désigner un actif.',
  'asset.component.code_required': 'Le code du composant est obligatoire (30 caractères au plus).',
  'asset.component.designation_required': 'La désignation du composant est obligatoire (150 caractères au plus).',
  'asset.component.invalid_duration': 'La durée du composant doit être comprise entre 1 et 100 ans.',
  'asset.component.negative_value': 'La valeur du composant ne peut pas être négative.',
  'asset.component.organization_required': 'Le composant doit être rattaché à une organisation.',
  'asset.depreciation.asset_required': 'Le plan doit désigner un actif.',
  'asset.depreciation.empty_schedule': 'Le tableau demande au moins un élément à amortir.',
  'asset.depreciation.invalid_duration': 'La durée doit être comprise entre 1 et 100 ans.',
  'asset.depreciation.invalid_month': 'Le mois de la dotation doit être compris entre 1 et 12.',
  'asset.depreciation.method_unsupported': "Seul l'amortissement linéaire est calculé pour l'instant.",
  'asset.depreciation.month_required': 'Un plan mensuel ne porte que des lignes mensuelles : le mois est obligatoire.',
  'asset.depreciation.month_unexpected': 'Un plan annuel ne porte que des lignes annuelles : le mois est de trop.',
  'asset.depreciation.negative_base': 'La base amortissable ne peut pas être négative.',
  'asset.depreciation.organization_required': 'Le plan doit être rattaché à une organisation.',
  'asset.depreciation.period_unsupported': "La dotation est annuelle ou mensuelle, rien d'autre.",
  'asset.depreciation.plan_not_found': "Cet actif ne porte aucun plan d'amortissement.",
  'asset.disposal.asset_required': 'La sortie doit désigner un actif.',
  'asset.disposal.before_commissioning': "La date de cession ne peut pas précéder la mise en service de l'actif.",
  'asset.disposal.future_date': 'Une cession ne peut pas être enregistrée à une date future.',
  'asset.disposal.method_required': 'Le mode de sortie est obligatoire : sale, destruction, theft ou reform.',
  'asset.disposal.negative_net_book_value': 'La valeur nette comptable ne peut pas être négative.',
  'asset.disposal.negative_price': 'Le prix de cession ne peut pas être négatif.',
  'asset.disposal.organization_required': 'La sortie doit être rattachée à une organisation.',
  'asset.document.asset_required': 'Le document doit désigner un actif.',
  'asset.document.file_required': 'Le nom du fichier est obligatoire (200 caractères au plus).',
  'asset.document.organization_required': 'Le document doit être rattaché à une organisation.',
  'asset.document.path_required': 'Le chemin du fichier est obligatoire.',
  'asset.downtime.asset_required': "L'arrêt doit désigner un actif.",
  'asset.downtime.hours_exceed_period': "Ces heures d'arrêt dépassent ce qu'une période peut couvrir.",
  'asset.downtime.invalid_hours': "Un arrêt dure un nombre d'heures strictement positif.",
  'asset.downtime.invalid_period': 'Un arrêt se termine après son début.',
  'asset.downtime.invalid_reason': 'Le motif est une panne, un entretien, une intempérie ou une inaction.',
  'asset.downtime.organization_required': "L'arrêt doit être rattaché à une entreprise.",
  'asset.downtime.overlap': 'Un arrêt couvre déjà tout ou partie de cette période.',
  'asset.fixed_asset.code_required': "Le code de l'actif est obligatoire (30 caractères au plus).",
  'asset.fixed_asset.designation_required': "La désignation de l'actif est obligatoire (150 caractères au plus).",
  'asset.fixed_asset.disposed_locked': 'Un actif cédé ne se modifie plus.',
  'asset.fixed_asset.entry_value_blocks_in_progress': "Ramenez la valeur d'entrée à zéro avant de repasser l'actif en cours : elle sera recalculée depuis ses dépenses à la mise en service.",
  'asset.fixed_asset.invalid_entry_value': "La valeur d'entrée doit être positive et rester vraisemblable.",
  'asset.fixed_asset.invalid_responsibility_center': 'Le centre de responsabilité tient en 100 caractères au plus.',
  'asset.fixed_asset.invalid_theoretical_hours': 'La capacité annuelle doit être strictement positive, ou absente.',
  'asset.fixed_asset.invalid_year': "L'année de fabrication doit être comprise entre 1900 et 2100.",
  'asset.fixed_asset.negative_amount': "Les montants d'acquisition ne peuvent pas être négatifs.",
  'asset.fixed_asset.negative_theoretical_fuel': 'La consommation théorique ne peut pas être négative.',
  'asset.fixed_asset.organization_required': "L'actif doit être rattaché à une organisation.",
  'asset.fixed_cost.asset_required': 'Le coût fixe doit désigner un actif.',
  'asset.fixed_cost.invalid_year': "L'année doit être comprise entre 1900 et 2100.",
  'asset.fixed_cost.label_required': 'Le libellé est obligatoire (100 caractères au plus).',
  'asset.fixed_cost.negative_amount': 'Le montant ne peut pas être négatif.',
  'asset.fixed_cost.organization_required': 'Le coût fixe doit être rattaché à une organisation.',
  'asset.hourly_cost.drift': "Le tarif appliqué s'écarte du coût constaté : c'est le tarif qu'il faut revoir.",
  'asset.hourly_cost.unrecorded': "La machine a tourné et aucun coût n'a été saisi sur l'exercice : ce sont les charges qu'il faut saisir.",
  'asset.hourly_cost.invalid_year': "L'année doit être comprise entre 1900 et 2100.",
  'asset.hourly_cost.negative_input': 'Les composantes du coût horaire ne peuvent pas être négatives.',
  'asset.hours.negative': 'Aucune de ces heures ne peut être négative.',
  'asset.in_progress.amount_below_millime': "Une dépense inférieure au millime n'est pas représentable.",
  'asset.in_progress.asset_required': 'La dépense doit désigner un actif.',
  'asset.in_progress.commissioning_date_required': "La mise en service d'une immobilisation en cours exige sa date.",
  'asset.in_progress.entry_value_not_editable':
    "La valeur d'entrée d'une immobilisation en cours est la somme de ses dépenses, elle ne se saisit pas.",
  'asset.in_progress.expense_after_commissioning': 'Une dépense ne peut pas être postérieure à la mise en service.',
  'asset.in_progress.invalid_amount': 'Le montant de la dépense doit être strictement positif.',
  'asset.in_progress.label_required': 'Le libellé de la dépense est obligatoire (150 caractères au plus).',
  'asset.in_progress.no_expense': 'Une immobilisation en cours sans aucune dépense ne se met pas en service.',
  'asset.in_progress.not_in_progress': "L'actif n'est pas une immobilisation en cours.",
  'asset.in_progress.organization_required': 'La dépense doit être rattachée à une organisation.',
  'asset.in_progress.supplier_not_found': 'Le fournisseur de la dépense est introuvable.',
  'asset.insurance.asset_required': "L'assurance doit désigner un actif.",
  'asset.insurance.incoherent_dates': 'La fin de la police doit suivre son début.',
  'asset.insurance.insurer_required': "L'assureur est obligatoire (150 caractères au plus).",
  'asset.insurance.negative_premium': 'La prime ne peut pas être négative.',
  'asset.insurance.organization_required': "L'assurance doit être rattachée à une organisation.",
  'asset.lease.asset_required': 'Le crédit-bail doit désigner un actif.',
  'asset.lease.incoherent_dates': 'La fin du contrat doit suivre son début.',
  'asset.lease.installment_not_found': "L'échéance est introuvable.",
  'asset.lease.empty_installment': 'Une échéance porte au moins un montant.',
  'asset.lease.installment_outside_contract': 'L’échéance tombe hors de la période du contrat.',
  'asset.lease.invalid_count': "Le nombre d'échéances doit être compris entre 1 et 600.",
  'asset.lease.invalid_payment': 'Le paiement doit être positif et ne pas dépasser le solde dû.',
  'asset.lease.payment_in_future': 'Un paiement d’échéance ne se date pas dans le futur.',
  'asset.lease.payment_immutable': 'Un paiement d’échéance ne se réécrit ni ne se supprime : il s’annule, et cela ne se défait pas.',
  'asset.lease.paid_amount_is_derived': 'Ce qui est payé d’une échéance est la somme de ses paiements ; cela ne s’écrit pas à la main.',
  'asset.lease.payment_not_found': 'Paiement d’échéance introuvable.',
  'asset.lease.payment_already_cancelled': 'Ce paiement d’échéance est déjà annulé.',
  'asset.lease.cancellation_reason_required': 'L’annulation d’un paiement d’échéance se motive.',
  'asset.lease.text_too_long': 'La note d’un paiement d’échéance, ou son motif d’annulation, tient en 300 caractères.',
  'asset.lease.invalid_terms': 'Les conditions financières sont incohérentes.',
  'asset.lease.lease_required': "L'échéance doit désigner un contrat.",
  'asset.lease.lessor_required': 'Le bailleur est obligatoire.',
  'asset.lease.negative_installment': 'Principal et intérêts ne peuvent pas être négatifs.',
  'asset.lease.not_found': 'Le contrat de crédit-bail est introuvable.',
  'asset.lease.organization_required': 'Le crédit-bail doit être rattaché à une organisation.',
  'asset.maintenance.asset_required': 'La maintenance doit désigner un actif.',
  'asset.maintenance.kind_required': 'La nature est obligatoire : preventive ou corrective.',
  'asset.maintenance.negative_meter': "Compteurs et heures d'arrêt ne peuvent pas être négatifs.",
  'asset.maintenance.organization_required': 'La maintenance doit être rattachée à une organisation.',
  'asset.maintenance.title_required': 'Le titre est obligatoire (150 caractères au plus).',
  'asset.meter.asset_required': 'Le relevé doit désigner un actif.',
  'asset.meter.future_date': 'La date du relevé ne peut pas être dans le futur.',
  'asset.meter.kind_required': 'La nature du compteur est obligatoire : hours ou km.',
  'asset.meter.negative_value': 'La valeur du compteur ne peut pas être négative.',
  'asset.meter.organization_required': 'Le relevé doit être rattaché à une organisation.',
  'asset.reference_rate.excessive': 'Le tarif de référence dépasse le plafond plausible.',
  'asset.reference_rate.invalid_source': 'La source du tarif de référence doit être budget ou fiscal_year.',
  'asset.reference_rate.invalid_year': "L'exercice d'origine du tarif doit être compris entre 1900 et 2100.",
  'asset.reference_rate.negative': 'Le tarif de référence ne peut pas être négatif.',
  'asset.reference_rate.rate_required': 'Le tarif de référence est obligatoire quand une source est posée.',
  'asset.reference_rate.year_required': "Un tarif repris d'un exercice doit nommer cet exercice.",
  'asset.revaluation.asset_required': 'La réévaluation doit désigner un actif.',
  'asset.revaluation.negative_value': 'Les valeurs réévaluées ne peuvent pas être négatives.',
  'asset.revaluation.organization_required': 'La réévaluation doit être rattachée à une organisation.',
  'asset.revaluation.reason_required': 'Le motif de la réévaluation est obligatoire (300 caractères au plus).',
  'asset.stocktake.asset_required': 'Le constat doit désigner un actif.',
  'asset.stocktake.condition_required': "L'état constaté est obligatoire : good, worn, damaged ou out_of_service.",
  'asset.stocktake.organization_required': 'Le constat doit être rattaché à une organisation.',
  'asset.transfer.asset_required': 'Le transfert doit désigner un actif.',
  'asset.transfer.destination_required': 'La destination du transfert est obligatoire (200 caractères au plus).',
  'asset.transfer.future_date': 'La date de transfert ne peut pas être dans le futur.',
  'asset.transfer.organization_required': 'Le transfert doit être rattaché à une organisation.',
  'asset.tyre.asset_required': 'Le pneu doit désigner un actif.',
  'asset.tyre.incoherent_dates': 'Le démontage ne peut pas précéder le montage.',
  'asset.tyre.incoherent_meter': 'Le compteur de démontage ne peut pas précéder celui du montage.',
  'asset.tyre.invalid_life': 'La durée de vie de référence doit être strictement positive, ou absente.',
  'asset.tyre.invalid_meter_kind': 'La nature du compteur est « hours » ou « km ».',
  'asset.tyre.label_too_long': 'La marque et la référence du pneu font 100 caractères au plus.',
  'asset.tyre.negative_meter': 'Le compteur de montage ne peut pas être négatif.',
  'asset.tyre.negative_price': 'Le prix du pneu ne peut pas être négatif.',
  'asset.tyre.not_found': 'Le pneu est introuvable.',
  'asset.tyre.organization_required': 'Le pneu doit être rattaché à une organisation.',
  'asset.tyre.position_required': 'La position du pneu est obligatoire (30 caractères au plus).',
  'nav.signOut': 'Se déconnecter',
  'nav.group.reporting': 'Reporting',
  'nav.group.structure': 'Structure',
  'nav.group.structure_params': 'Paramètres généraux',
  'nav.group.structure_norms': 'Standards et normes',
  'nav.group.purchasing': 'Achats',
  'nav.group.sales': 'Ventes',
  'nav.group.stock': 'Dépôts et stock',
  'nav.group.editions': 'États et éditions',
  'nav.group.admin': 'Administration',
  'nav.qualityControls': 'Contrôle qualité',
  'nav.productStandards': 'Normes produit',
  'qc.title': 'Contrôle qualité',
  'qc.subtitle': 'On saisit des mesures ; le système les compare à la norme de l’article et libère ou bloque le lot. Un lot en contrôle, bloqué ou rejeté ne sort pas.',
  'qc.new': 'Nouveau contrôle',
  'qc.history': 'Historique',
  'qc.lot': 'Lot',
  'qc.lotHint': 'Choisir parmi les lots en stock.',
  'qc.product': 'Article',
  'qc.warehouse': 'Dépôt',
  'qc.date': 'Date',
  'qc.type': 'Type',
  'qc.result': 'Résultat',
  'qc.decision': 'Décision',
  'qc.notes': 'Notes',
  'qc.submit': 'Enregistrer le contrôle',
  'qc.created': 'Contrôle qualité enregistré.',
  'qc.lotRequired': 'Choisissez un lot.',
  'qc.empty': 'Aucun contrôle qualité.',
  'qc.guard': "Le refus est posé en base : consommation, transfert et vente d'un lot bloqué sont rejetés, quel que soit l'écran.",
  'qc.type.organoleptic': 'Organoleptique',
  'qc.type.physico_chemical': 'Physico-chimique',
  'qc.type.microbiological': 'Microbiologique',
  'qc.type.lait': 'Lait',
  'qc.type.other': 'Autre',
  // Lot Q1a (migration 0085) : le contrôle calculé et les normes de produit.
  'qc.result.compliant': 'Conforme',
  'qc.result.non_compliant': 'Non conforme',
  'qc.result.incomplete': 'Incomplet',
  'qc.result.no_standard': 'Sans norme',
  'qc.result.waived': 'Dérogation',
  'qc.decision.in_control': 'En contrôle',
  'qc.decision.released': 'Libéré',
  'qc.decision.blocked': 'Bloqué',
  'qc.decision.rejected': 'Rejeté',
  'qc.decision.conditional': 'Libération conditionnelle',
  'qc.decision.downgraded': 'Déclassé',
  'qc.basis': 'Fondement',
  'qc.basis.measured': 'Mesuré',
  'qc.basis.no_standard': 'Sans norme',
  'qc.basis.override': 'Dérogation',
  'qc.basis.legacy': 'Ancien contrôle',
  'qc.verdict': 'Verdict',
  'qc.verdict.compliant': 'Conforme',
  'qc.verdict.non_compliant': 'Non conforme',
  'qc.verdict.not_measured': 'Non mesuré',
  'qc.standard': 'Norme',
  'qc.parameter': 'Paramètre',
  'qc.threshold': 'Seuil',
  'qc.measured': 'Valeur mesurée',
  'qc.mandatory': 'Obligatoire',
  'qc.optional': 'Facultatif',
  'qc.judgement.none': 'Non jugé',
  'qc.reason': 'Motif',
  'qc.last': 'Dernier contrôle enregistré',
  'qc.sheetHint': 'Saisissez les valeurs mesurées : le système les compare à la norme et décide.',
  'qc.invalidNumber': 'Une valeur mesurée n’est pas un nombre.',
  'qc.measure.submit': 'Enregistrer les mesures',
  'qc.outcome.released': 'Lot libéré : toutes les mesures obligatoires sont conformes.',
  'qc.outcome.blocked': 'Lot bloqué : une mesure obligatoire est non conforme.',
  'qc.outcome.in_control': 'Contrôle incomplet : une mesure obligatoire manque, le lot reste en contrôle.',
  'qc.rejectedFinal': 'Ce lot est rejeté : un rejet est définitif. Il ne quitte le stock que par une perte.',
  'qc.noStandard.title': 'Cet article n’a pas de norme en vigueur',
  'qc.noStandard.body': 'Il n’y a rien à mesurer : la décision vous revient, et la fiche dira « sans norme », jamais « conforme ».',
  'qc.noStandard.forbidden': 'Il n’y a rien à mesurer. Seul un responsable peut décider du sort de ce lot.',
  'qc.manual.submit': 'Enregistrer la décision',
  'qc.manual.decisionRequired': 'Choisissez une décision.',
  'qc.override.title': 'Dérogation',
  'qc.override.body': 'Un lot bloqué peut être libéré sous condition, ou rejeté définitivement. Le motif est conservé avec votre nom.',
  'qc.override.reason': 'Motif',
  'qc.override.reasonRequired': 'Le motif est obligatoire.',
  'qc.override.conditional': 'Libération conditionnelle',
  'qc.override.reject': 'Rejeter le lot',
  'stock.lots.filterQuality': 'Statut qualité',
  'stock.lots.filterQuality.all': 'Tous',
  // Lot Q2 (migration 0087) : la scission de lot et le stock par statut.
  'stock.lots.origin': 'Lot d’origine',
  'stock.lots.grade': 'Grade',
  'stock.split': 'Scinder',
  'stock.split.title': 'Scinder le lot',
  'stock.split.submit': 'Détacher ces parts',
  'stock.split.hint': 'Détachez une ou plusieurs parts : chacune devient un lot, qui hérite du statut de son lot d’origine et se juge ensuite par son propre contrôle. Le lot d’origine garde le reste.',
  'stock.split.quantity': 'Quantité de la part',
  'stock.split.grade': 'Grade de la part',
  'stock.split.addPart': 'Ajouter une part',
  'stock.split.removePart': 'Retirer la part',
  'stock.split.reason': 'Motif de la scission',
  'stock.split.done': 'Lot scindé : les parts héritent de son statut.',
  'stock.split.partsRequired': 'Saisissez au moins une part, avec une quantité positive.',
  'stock.split.exceeds': 'Les parts dépassent ce que le lot porte dans ce dépôt.',
  'stock.summary.title': 'Stock par statut qualité',
  'stock.summary.available': 'Disponible',
  'stock.summary.inControl': 'En contrôle',
  'stock.summary.quarantine': 'Quarantaine',
  'stock.summary.rejected': 'Rejeté',
  'qc.basis.inherited': 'Hérité du lot d’origine',
  // Lot Q3a — l'achat crée son lot (migration 0088).
  'qc.basis.takeover': 'Reprise — stock antérieur au suivi par lot',
  // Lot Q4 — contrat puis livraison (migration 0090).
  'sales.delivery_note.stock_exit_required': 'Ce bon dit avoir livré un article géré en stock, et n’a rien sorti du stock.',
  'sales.delivery_note.lot_mismatch': 'Le lot désigné n’est pas un lot de l’article de cette ligne.',
  'contracts.deliver': 'Livrer',
  'contracts.deliver.warehouse': 'Dépôt de départ',
  'contracts.deliver.date': 'Date de livraison',
  'contracts.deliver.carrier': 'Transporteur',
  'contracts.deliver.plate': 'Immatriculation',
  'contracts.deliver.quantity': 'Quantité livrée',
  'contracts.deliver.lot': 'Lot livré',
  'contracts.deliver.lot.chooseWarehouse': 'Choisissez d’abord le dépôt : on ne livre que les lots qui s’y trouvent.',
  'contracts.deliver.lot.none': 'Aucun lot libéré de cet article dans ce dépôt.',
  'contracts.deliver.submit': 'Émettre le bon de livraison',
  'contracts.deliver.close': 'Fermer',
  'contracts.deliver.incomplete': 'Choisissez un dépôt et une quantité à livrer sur au moins une ligne.',
  'contracts.deliver.done': 'Bon de livraison émis :',
  'contracts.deliver.done.hint': 'La marchandise est sortie du stock du dépôt. La traçabilité du lot montre cette livraison.',
  'contracts.new.title': 'Nouveau contrat de vente',
  'contracts.new.number': 'N° de contrat',
  'contracts.new.number.hint': 'Facultatif : laissé vide, le numéro est attribué automatiquement.',
  'contracts.new.product': 'Article',
  'contracts.new.addLine': 'Ajouter une ligne',
  'contracts.new.submit': 'Créer le contrat',
  'contracts.new.incomplete': 'Choisissez un client et saisissez au moins une ligne : article, quantité et prix.',
  'contracts.new.created': 'Contrat créé :',
  // Lot Q3b — la traçabilité se lit (migration 0089).
  'nav.lotTrace': 'Traçabilité',
  'stock.lot_trace.term_too_short': 'Saisissez au moins 2 caractères du code du lot ou du n° de lot du fournisseur.',
  'trace.open': 'Tracer ce lot',
  'trace.title': 'Traçabilité des lots',
  'trace.description': 'Un lot, dans les deux sens : d’où il vient, et où il est allé.',
  'trace.search.title': 'Chercher un lot',
  'trace.search.term': 'Code du lot ou n° de lot du fournisseur',
  'trace.search.hint': 'Le n° de lot du fournisseur est celui qu’il cite quand il rappelle une fabrication.',
  'trace.search.submit': 'Chercher',
  'trace.search.none': 'Aucun lot ne porte ce code.',
  'trace.loading': 'Lecture de la traçabilité…',
  'trace.lot': 'Lot',
  'trace.product': 'Article',
  'trace.supplierLotCode': 'N° de lot fournisseur',
  'trace.receivedAt': 'Reçu le',
  'trace.expiryDate': 'Péremption',
  'trace.initialQuantity': 'Quantité à l’origine',
  'trace.grade': 'Grade',
  'trace.status': 'Statut qualité',
  'trace.quantity': 'Quantité',
  'trace.warehouse': 'Dépôt',
  'trace.upstream': 'Amont — d’où vient ce lot',
  'trace.downstream': 'Aval — où ce lot est allé',
  'trace.origin': 'Origine',
  'trace.origin.purchase': 'Achat',
  'trace.origin.harvest': 'Récolte',
  'trace.origin.takeover': 'Reprise',
  'trace.origin.direct': 'Entrée sans commande',
  'trace.origin.split': 'Part d’un lot',
  'trace.origin.unknown': 'Origine inconnue',
  'trace.origin.unknown.hint': 'Rien n’écrit d’où vient ce lot : ni réception, ni récolte, ni lot d’origine.',
  'trace.origin.ofAncestor': 'Origine du lot dont celui-ci a été détaché :',
  'trace.origin.takeover.hint': 'Stock antérieur au suivi par lot : son origine n’est pas connue.',
  'trace.origin.direct.hint': 'Entré en stock sans commande d’achat : aucun fournisseur n’y est attaché.',
  'trace.supplier': 'Fournisseur',
  'trace.receipt': 'Réception',
  'trace.receiptDate': 'Date de réception',
  'trace.supplierDocument': 'Bon du fournisseur',
  'trace.order': 'Commande',
  'trace.plot': 'Parcelle',
  'trace.plots': 'Parcelles',
  'trace.season': 'Campagne',
  'trace.crop': 'Culture',
  'trace.harvestDate': 'Date de récolte',
  'trace.takeoverDate': 'Repris le',
  'trace.directEntryDate': 'Entré le',
  'trace.ancestors': 'Lots d’origine',
  'trace.controls': 'Contrôles qualité',
  'trace.controlDate': 'Date du contrôle',
  'trace.decision': 'Décision',
  'trace.controls.none': 'Aucun contrôle : ce lot attend sa décision qualité.',
  'trace.fieldInputs': 'Intrants appliqués à la parcelle',
  'trace.fieldInputs.hint': 'Ce qui a été appliqué à la parcelle avant la récolte, interventions annulées exclues. Le lien passe par la parcelle ; la quantité est celle de l’intervention.',
  'trace.fieldInputs.noLot': 'Sans lot',
  'trace.fieldInputs.none': 'Aucun intrant enregistré sur cette parcelle avant la récolte.',
  'trace.operation': 'Intervention',
  'trace.operationDate': 'Date de l’intervention',
  'trace.operationState': 'État',
  'trace.operation.cancelled': 'Intervention annulée',
  'trace.parts': 'Parts détachées',
  'trace.balances': 'Où il se trouve',
  'trace.balances.none': 'Il n’en reste dans aucun dépôt.',
  'trace.consumptions': 'Interventions qui l’ont consommé',
  'trace.consumptions.none': 'Aucune intervention ne l’a consommé.',
  'trace.harvests': 'Récoltes des parcelles traitées',
  'trace.harvests.hint': 'Récoltes des parcelles où ce lot a été appliqué avant elles. Le lien passe par la parcelle, pas par la matière.',
  'trace.harvests.notStocked': 'Pas encore en stock',
  'trace.harvests.none': 'Aucune récolte sur une parcelle où ce lot a été appliqué.',
  'trace.deliveries': 'Livraisons',
  'trace.deliveryDate': 'Date de livraison',
  'trace.deliveryNote': 'Bon de livraison',
  'trace.customer': 'Client',
  'trace.deliveries.none': 'Aucune livraison.',
  'trace.movements': 'Tous ses mouvements',
  'trace.movementDate': 'Date',
  'trace.reason': 'Nature',
  'trace.direction': 'Sens',
  'trace.direction.in': 'Entrée',
  'trace.direction.out': 'Sortie',
  'trace.movements.none': 'Aucun mouvement ventilé sur ce lot.',
  'trace.reason.reception': 'Réception',
  'trace.reason.consumption': 'Consommation',
  'trace.reason.sale': 'Vente',
  'trace.reason.loss': 'Perte',
  'trace.reason.return_out': 'Retour fournisseur',
  'trace.reason.transfer_in': 'Transfert reçu',
  'trace.reason.transfer_out': 'Transfert envoyé',
  'trace.reason.adjustment_in': 'Régularisation à la hausse',
  'trace.reason.adjustment_out': 'Régularisation à la baisse',
  'trace.reason.production_in': 'Entrée de récolte',
  'trace.reason.split_in': 'Part reçue d’une scission',
  'trace.reason.split_out': 'Détaché par scission',
  'stock.lot.required': 'Cet article est suivi par lot : désignez le lot.',
  'stock.balance.lot_required': 'Cet article est suivi par lot : son stock se tient lot par lot.',
  'stock.movement.lot_ventilation_required': 'Cet article est suivi par lot : son mouvement doit désigner ses lots, en totalité.',
  'stock.lot.already_decided': 'Ce lot est déjà jugé : une marchandise neuve reçoit son propre lot, qui sera contrôlé.',
  'stock.lot.supplier_code_too_long': 'Le n° de lot du fournisseur ne dépasse pas 50 caractères.',
  'stock.quality_control.takeover_is_automatic': 'La libération d’un lot de reprise est posée par le système, une fois ; elle ne s’écrit pas.',
  'operations.consumption.movement_not_recorded': 'La sortie de stock n’a pas rendu son mouvement : l’intrant n’a pas été rattaché, rien n’a été consommé.',
  'purchase.landed_cost.no_balance': 'Aucun solde de stock où poser ces frais d’approche.',
  'purchases.receive.lot': 'Lot reçu',
  'purchases.receive.supplierLotCode': 'N° de lot fournisseur',
  'purchases.receive.expiryDate': 'Péremption du lot',
  'purchases.receive.lotsCreated': 'Lots créés, en contrôle :',
  'purchases.receive.lotsCreated.hint': 'Ils se consomment une fois libérés par un contrôle qualité.',
  'warehouses.transferLot': 'Lot',
  'warehouses.transferLot.held': 'Les lots en contrôle, bloqués ou rejetés ne sont pas proposés : ils attendent une décision qualité.',
  'stock.lot_split.parts_required': 'Une scission détache au moins une part.',
  'stock.lot_split.invalid_quantity': 'La quantité d’une part doit être strictement positive.',
  'stock.lot_split.text_too_long': 'Le motif tient en 300 caractères, un grade en 30.',
  'stock.lot_split.not_neutral': 'Une scission ne crée ni quantité ni valeur : ses entrées doivent égaler sa sortie.',
  'stock.lot.parent_is_final': 'Le lot d’origine d’un lot se pose à sa naissance et ne change plus.',
  'stock.lot.parent_mismatch': 'Une part est du même article que son lot d’origine, et vient d’une scission de ce lot.',
  'stock.quality_control.inherited_is_automatic': 'L’héritage du statut est posé à la naissance d’une part ; il ne s’écrit pas.',
  'ps.title': 'Normes produit',
  'ps.subtitle': 'Pour chaque article suivi par lot : les paramètres à mesurer à la réception, et leurs seuils. Une norme active juge les lots ; pour la réviser, créez une version.',
  'ps.new': 'Nouvelle norme produit',
  'ps.edit': 'Modifier le brouillon',
  'ps.product': 'Article',
  'ps.productHint': 'Seuls les articles suivis par lot.',
  'ps.code': 'Code',
  'ps.name': 'Désignation',
  'ps.version': 'Version',
  'ps.effectiveFrom': 'Date d’application',
  'ps.reference': 'Référence',
  'ps.status.draft': 'Brouillon',
  'ps.status.active': 'Active',
  'ps.status.retired': 'Retirée',
  'ps.parameters': 'Paramètres',
  'ps.mandatoryCount': 'Obligatoires',
  'ps.norm': 'Comparaison',
  'ps.controlType': 'Type de contrôle',
  'ps.addParameter': 'Ajouter un paramètre',
  'ps.remove': 'Retirer',
  'ps.save': 'Enregistrer le brouillon',
  'ps.saved': 'Norme produit enregistrée.',
  'ps.activate': 'Activer',
  'ps.activated': 'Norme activée : elle juge désormais les lots de cet article.',
  'ps.retire': 'Retirer la norme',
  'ps.retiredDone': 'Norme retirée.',
  'ps.empty': 'Aucune norme produit.',
  'ps.noParameters': 'Aucun paramètre : ajoutez-en au moins un obligatoire avant d’activer.',
  'ps.lockedHint': 'Cette norme n’est plus un brouillon : elle ne se modifie pas. Pour réviser ses seuils, créez une version.',
  'ps.required': 'Article, code, désignation, version et date d’application sont obligatoires.',
  'stock.lots.quality': 'Qualité',
  'farms.codeGenerated': 'Attribué à la création (EXP01, EXP02…).',
  'farms.locality': 'Localité',
  'farms.gps': 'Position GPS',
  'products.lotTrackedHint': 'Obligatoire en FIFO.',
  'stock.lot.blocked_by_quality': 'Lot bloqué par la qualité : une décision est requise avant toute sortie.',
  'stock.lot.insufficient_usable_quantity': 'Stock utilisable insuffisant.',
  'stock.lot.not_found': 'Lot introuvable.',
  'stock.lot.duplicate_code': 'Ce code de lot existe déjà pour cet article.',
  'stock.quality_control.invalid_decision': 'Décision inconnue.',
  'stock.quality_control.invalid_sample': 'La quantité échantillonnée doit être positive.',
  // Codes frappés par le lot Q1a, côté gestionnaires comme côté déclencheurs.
  'stock.quality_control.not_found': 'Contrôle qualité introuvable.',
  'stock.quality_control.not_readable': 'Le contrôle a été écrit mais ne se relit pas.',
  'stock.quality_control.references_required': 'Un contrôle qualité désigne une entreprise, un lot et un article.',
  'stock.quality_control.standard_in_force': 'Cet article a une norme en vigueur : son lot se juge par des mesures, pas par une décision.',
  'stock.quality_control.no_standard_in_force': 'Cet article n’a pas de norme en vigueur à cette date : il n’y a rien à mesurer.',
  'stock.quality_control.unknown_parameter': 'Un paramètre mesuré n’appartient pas à la norme en vigueur.',
  'stock.quality_control.duplicate_parameter': 'Un même paramètre est mesuré deux fois dans ce contrôle.',
  'stock.quality_control.invalid_verdict': 'Jugement inconnu.',
  'stock.quality_control.comment_too_long': 'Le commentaire d’une mesure dépasse 300 caractères.',
  'stock.quality_control.override_reason_required': 'Une dérogation se motive.',
  'stock.quality_control.override_reason_too_long': 'Le motif dépasse 500 caractères.',
  'stock.quality_control.lot_not_blocked': 'Une libération conditionnelle porte sur un lot bloqué ; un rejet, sur un lot bloqué ou en contrôle.',
  'stock.quality_control.invalid_basis': 'Un contrôle est mesuré, sans norme, ou une dérogation.',
  'stock.quality_control.not_measurable': 'Seul un contrôle mesuré porte des mesures.',
  'stock.lot.rejected_is_final': 'Ce lot est rejeté, et un rejet est définitif.',
  'stock.lot.status_is_decided': 'Le statut qualité d’un lot ne se modifie pas directement : il suit la décision d’un contrôle.',
  'agro.norm.not_found': 'Norme introuvable.',
  'agro.product_standard.not_found': 'Norme de produit introuvable.',
  'agro.product_standard.not_readable': 'La norme de produit a été écrite mais ne se relit pas.',
  'agro.product_standard.duplicate_version': 'Cette norme porte déjà cette version.',
  'agro.product_standard.no_mandatory_parameter': 'Une norme ne s’active qu’avec au moins un paramètre obligatoire.',
  'agro.product_standard.product_not_lot_tracked': 'Seul un article suivi par lot reçoit une norme de produit.',
  'agro.product_standard.product_not_found': 'Article introuvable.',
  'agro.product_standard.not_draft': 'Seule une norme en brouillon se modifie ; pour réviser, créez une version.',
  'agro.product_standard.invalid_transition': 'Une norme passe de brouillon à active, puis à retirée.',
  'agro.product_standard.unknown_norm': 'La norme désignée par ce paramètre est introuvable.',
  'agro.product_standard.code_required': 'Le code est obligatoire.',
  'agro.product_standard.name_required': 'La désignation est obligatoire.',
  'agro.product_standard.version_required': 'La version est obligatoire.',
  'agro.product_standard.field_too_long': 'Code : 30 caractères au plus ; désignation : 150 ; version : 20 ; référence : 150.',
  'agro.product_standard.invalid_control_type': 'Type de contrôle inconnu.',
  'agro.product_standard.duplicate_parameter': 'Une même comparaison figure deux fois parmi les paramètres.',
  'agro.product_standard.invalid_status': 'Statut inconnu.',
  'agro.product.fifo_requires_lots': 'La méthode FIFO exige le suivi par lot.',
  'agro.product.lot_requires_stock': 'Le suivi par lot exige un article géré en stock.',
  'farm.farm.code_generation_failed': "Aucun code d'exploitation libre : saisissez-en un.",
  'nav.comparatifs': 'Comparatifs',
  'nav.alertes': 'Alertes',
  'nav.partners': 'Clients',
  'nav.units': 'Unités',
  'nav.normes': 'Normes',
  'nav.normCategories': 'Catégories de normes',
  'nav.besoins': 'Expression des besoins',
  'nav.devis': 'Devis',
  'nav.bonCommande': 'Bon de commande',
  'nav.bonLivraison': 'Bon de livraison',
  'nav.avoirs': 'Avoirs',
  'nav.offrePrix': 'Offre de prix',
  'nav.vergers': 'Vergers',
  'nav.cheptel': 'Cheptel',
  'nav.supplierInvoices': 'Factures fournisseur',
  'nav.subscription': 'Abonnement',
  'topbar.allActivities': 'Toutes activités',
  'topbar.season': 'Campagne',
  'common.theme': 'Thème',
  'theme.light': 'Passer au thème clair',
  'theme.dark': 'Passer au thème sombre',

  'module.crop': 'Grandes cultures',
  'module.orchard': 'Arboriculture',
  'module.livestock': 'Élevage',

  'login.title': 'Connexion',
  'login.email': 'Adresse électronique',
  'login.password': 'Mot de passe',
  'login.submit': 'Se connecter',
  'login.pending': 'Connexion en cours…',
  'login.tagline': 'Gestion technico-économique des exploitations agricoles',
  'login.remember': 'Rester connecté',
  'login.forgot': 'Mot de passe oublié ?',
  'login.welcome': 'Bon retour',
  'login.subtitle': 'Connectez-vous à l’espace de votre entreprise.',
  'login.notice': 'L’accès est réservé aux utilisateurs autorisés. Les tentatives peuvent être enregistrées à des fins de sécurité.',
  'login.hero.title': 'Chaque dinar de la campagne, rattaché à sa parcelle.',
  'login.hero.point1': 'Coûts à l’hectare et marges par campagne',
  'login.hero.point2': 'Stock, achats et ventes reliés aux interventions',
  'login.hero.point3': 'Saisie hors ligne sur mobile, au champ',
  'login.hero.activities': 'Grandes cultures · Arboriculture · Élevage',
  'login.showPassword': 'Afficher le mot de passe',
  'login.hidePassword': 'Masquer le mot de passe',
  'login.capsLock': 'Le verrouillage des majuscules est activé.',
  'login.rememberHint': 'Décoché, la session se ferme avec l’onglet — pour un poste partagé.',
  'mdpo.title': 'Mot de passe oublié',
  'mdpo.subtitle': 'Indiquez votre adresse : si elle correspond à un compte, vous recevrez un lien pour choisir un nouveau mot de passe.',
  'mdpo.email': 'Courriel',
  'mdpo.submit': 'Envoyer le lien',
  'mdpo.pending': 'Envoi en cours…',
  'mdpo.sent': 'Si cette adresse correspond à un compte, un courriel vient de partir. Pensez à vérifier vos courriers indésirables.',
  'mdpo.back': 'Retour à la connexion',
  'mdpn.title': 'Nouveau mot de passe',
  'mdpn.subtitle': 'Choisissez le nouveau mot de passe de votre compte.',
  'mdpn.password': 'Nouveau mot de passe',
  'mdpn.confirm': 'Confirmer le mot de passe',
  'mdpn.submit': 'Changer le mot de passe',
  'mdpn.pending': 'Changement en cours…',
  'mdpn.mismatch': 'Les deux mots de passe ne sont pas identiques.',
  'mdpn.missingToken': 'Ce lien est incomplet : il ne contient pas le jeton de réinitialisation. Ouvrez le lien tel qu’il figure dans le courriel, ou demandez-en un nouveau.',
  'mdpn.requestNew': 'Demander un nouveau lien',
  'mdpn.done': 'Votre mot de passe est changé. Vous pouvez vous connecter.',
  'mdpn.toLogin': 'Se connecter',

  'farms.title': 'Exploitations',
  'farms.search': 'Rechercher une exploitation',
  'farms.code': 'Code',
  'farms.name': 'Nom',
  'farms.governorate': 'Gouvernorat',
  'farms.manager': 'Responsable',
  'farms.area': 'Superficie (ha)',
  'farms.empty': 'Aucune exploitation enregistrée.',

  'plots.title': 'Parcelles',
  'plots.search': 'Rechercher une parcelle',
  'plots.code': 'Code',
  'plots.name': 'Nom',
  'plots.area': 'Superficie (ha)',
  'plots.irrigable': 'Irrigable',
  'plots.empty': 'Aucune parcelle enregistrée.',
  'plots.moduleRequired.title': 'Module Grandes cultures non souscrit.',
  'plots.moduleRequired.description': "Votre abonnement n'inclut pas le module Grandes cultures. Contactez votre administrateur pour y accéder.",
  'farms.quotaExceeded': "Votre plan autorise un nombre limité d'exploitations. Changez de plan pour en ajouter une.",
  'plots.quotaExceeded': 'Votre plan autorise un nombre limité de parcelles ou une surface maximale. Changez de plan pour continuer.',

  'orchard.plots.title': 'Vergers',
  'orchard.plots.search': 'Rechercher un verger',
  'orchard.plots.empty': 'Aucun verger enregistré.',
  'orchard.plots.farm': 'Exploitation',
  'orchard.plots.parcel': 'Parcelle foncière',
  'orchard.plots.species': 'Espèce',
  'orchard.plots.variety': 'Variété',
  'orchard.plots.plantingYear': 'Année de plantation',
  'orchard.plots.density': 'Densité (pieds/ha)',
  'orchard.plots.status': 'Statut',
  'orchard.plots.status.young': 'Jeune',
  'orchard.plots.status.production': 'En production',
  'orchard.plots.status.decline': 'En déclin',
  'orchard.plots.status.removed': 'Arraché',
  'orchard.interventions.status.planned': 'Planifiée',
  'orchard.interventions.status.validated': 'Validée',
  'orchard.interventions.status.reversed': 'Contre-passée',
  'orchard.interventions.status.cancelled': 'Annulée',
  'orchard.plots.create': 'Enregistrer le verger',
  'orchard.plots.plantationCost': 'Coût de plantation',
  'orchard.plots.usefulLife': 'Durée productive (ans)',
  'orchard.plot.incomplete_amortization': "Le coût de plantation et sa durée d'amortissement se déclarent ensemble.",
  'orchard.plot.invalid_plantation_cost': 'Le coût de plantation ne peut pas être négatif.',
  'orchard.plot.invalid_useful_life': 'La durée productive est comprise entre 1 et 100 ans.',
  'orchard.plots.created': 'Verger enregistré.',
  'orchard.plots.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.plots.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour y accéder.",
  'orchard.plot.duplicate_plot': 'Cette parcelle porte déjà un verger.',
  'orchard.plot.planting_year_in_future': "L'année de plantation ne peut pas être dans le futur.",
  'orchard.plot.invalid_density': 'La densité doit être strictement positive, en pieds par hectare.',
  'orchard.plot.plot_out_of_scope': "La parcelle foncière désignée n'appartient pas à cette exploitation.",

  'orchard.interventions.title': 'Interventions du verger',
  'orchard.interventions.search': 'Rechercher une intervention',
  'orchard.interventions.empty': 'Aucune intervention enregistrée.',
  'orchard.interventions.plot': 'Verger',
  'orchard.interventions.type': "Type de travaux",
  'orchard.interventions.date': "Date d'intervention",
  'orchard.interventions.product': 'Produit',
  'orchard.interventions.quantity': 'Quantité sortie',
  'orchard.interventions.create': "Enregistrer l'intervention",
  'orchard.interventions.created': 'Intervention enregistrée.',
  'orchard.interventions.validate': "Valider",
  'orchard.interventions.validated': 'Intervention validée : elle est verrouillée.',
  'orchard.interventions.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.interventions.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour y accéder.",
  'orchard.intervention.warehouse_required': "La sortie de stock exige un dépôt : désignez celui d'où sort le produit.",
  'orchard.intervention.invalid_quantity': 'La quantité sortie doit être strictement positive.',
  'orchard.intervention.movement_required': "L'intervention consomme un produit : sa sortie de stock est obligatoire.",
  'orchard.intervention.dar_required': "Le produit n'a pas de délai avant récolte renseigné : un traitement phytosanitaire sans DAR est refusé.",
  'orchard.intervention.plot_out_of_scope': "Le verger désigné n'appartient pas à cette exploitation.",
  'orchard.intervention.not_found': 'Intervention introuvable.',
  'orchard.harvest_pass.not_found': 'Passe de récolte introuvable.',
  'orchard.harvest_pass.window_closed': 'Cette récolte tombe hors de la fenêtre de récolte de la région.',
  'orchard.intervention.farm_mismatch': 'L’intervention et le verger ne désignent pas la même exploitation.',
  'orchard.intervention.farm_required': 'L’intervention doit être rattachée à une exploitation.',
  'orchard.intervention.invalid_area': 'La superficie travaillée doit être strictement positive, en hectares.',
  'orchard.intervention.invalid_dose': 'La dose à l’hectare ne peut pas être négative.',
  'orchard.intervention.invalid_labor_cost': 'Le coût de main-d’œuvre ne peut pas être négatif.',
  'orchard.intervention.invalid_labor_hours': 'Les heures de main-d’œuvre ne peuvent pas être négatives.',
  'orchard.intervention.invalid_reversal': 'Seule une intervention validée se contre-passe.',
  'orchard.intervention.invalid_status_transition': 'Ce changement de statut n’est pas permis pour cette intervention.',
  'orchard.intervention.movement_mismatch': 'La sortie de stock ne correspond pas au produit de l’intervention.',
  'orchard.intervention.movement_not_recorded': 'La sortie de stock n’a pas été enregistrée : l’intervention ne peut pas y être rattachée.',
  'orchard.intervention.movement_out_of_scope': 'Cette sortie de stock n’appartient pas à votre entreprise.',
  'orchard.intervention.organization_required': 'L’intervention doit être rattachée à une entreprise.',
  'orchard.intervention.plot_required': 'L’intervention doit désigner un verger.',
  'orchard.intervention.product_out_of_scope': 'Ce produit n’appartient pas à votre entreprise, ou a été retiré.',
  'orchard.intervention.product_required': 'Le produit désigné n’est pas valide.',
  'orchard.intervention.reversal_out_of_scope': 'L’intervention à contre-passer n’appartient pas à votre entreprise.',
  'orchard.intervention.season_out_of_scope': 'Cette campagne n’appartient pas à votre entreprise, ou a été retirée.',
  'orchard.intervention.type_out_of_scope': 'Ce type de travaux n’est ni au catalogue commun ni à votre entreprise.',
  'orchard.intervention.type_required': 'L’intervention doit désigner un type de travaux.',
  'orchard.irrigation.not_found': 'Arrosage introuvable.',
  'orchard.phenology.farm_mismatch': 'L’observation et le verger ne désignent pas la même exploitation.',
  'orchard.phenology.not_found': 'Observation de phénologie introuvable.',
  'orchard.plot.crop_not_orchard': 'Cette espèce ne relève pas de l’arboriculture.',
  'orchard.plot.crop_out_of_scope': 'Cette espèce n’est ni au catalogue commun ni à votre entreprise.',
  'orchard.plot.crop_required': 'Le verger doit désigner une espèce arboricole.',
  'orchard.plot.farm_mismatch': 'Le verger et la parcelle foncière ne désignent pas la même exploitation.',
  'orchard.plot.farm_required': 'Le verger doit être rattaché à une exploitation.',
  'orchard.plot.invalid_certification': 'La certification est conventionnelle, biologique ou en conversion.',
  'orchard.plot.invalid_planting_month': 'Le mois de plantation est compris entre 1 et 12.',
  'orchard.plot.invalid_planting_year': 'L’année de plantation doit être comprise entre 1900 et 2100.',
  'orchard.plot.invalid_status': 'Le statut du verger est inconnu.',
  'orchard.plot.invalid_status_transition': 'Le verger ne revient pas en arrière dans son cycle de vie.',
  'orchard.plot.irrigation_required': 'Le mode d’irrigation est obligatoire : en sec, choisissez « pluvial ».',
  'orchard.plot.not_found': 'Verger introuvable.',
  'orchard.plot.organization_required': 'Le verger doit être rattaché à une entreprise.',
  'orchard.plot.plot_required': 'Le verger doit être rattaché à une parcelle foncière.',
  'orchard.plot.variety_crop_mismatch': 'Cette variété n’appartient pas à l’espèce du verger.',
  'orchard.plot.variety_not_orchard': 'Cette variété ne relève pas de l’arboriculture.',
  'orchard.plot.variety_out_of_scope': 'Cette variété n’est ni au catalogue commun ni à votre entreprise.',
  'orchard.plot.variety_required': 'La variété désignée n’est pas valide.',
  'orchard.transformation.not_found': 'Transformation introuvable.',
  'orchard.intervention.immutable': 'Une intervention validée est immuable : corrigez-la par une contre-passation.',

  'orchard.harvest.title': 'Récolte du verger',
  'orchard.harvest.search': 'Rechercher une passe de récolte',
  'orchard.harvest.empty': 'Aucune passe de récolte enregistrée.',
  'orchard.harvest.plot': 'Verger',
  'orchard.harvest.season': 'Campagne',
  'orchard.harvest.passNumber': 'Numéro de passe',
  'orchard.harvest.date': 'Date de récolte',
  'orchard.harvest.gross': 'Poids brut (kg)',
  'orchard.harvest.tare': 'Tare (kg)',
  'orchard.harvest.net': 'Poids net (kg)',
  'orchard.harvest.workers': 'Cueilleurs',
  'orchard.harvest.create': 'Enregistrer la passe',
  'orchard.harvest.created': 'Passe de récolte enregistrée.',
  'orchard.harvest.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.harvest.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour y accéder.",
  'orchard.harvest_pass.organization_required': 'La passe de récolte doit être rattachée à une organisation.',
  'orchard.harvest_pass.farm_required': 'La passe de récolte doit être rattachée à une exploitation.',
  'orchard.harvest_pass.plot_required': 'La passe de récolte doit désigner un verger.',
  'orchard.harvest_pass.season_required': 'La passe de récolte doit désigner une campagne.',
  'orchard.harvest_pass.invalid_pass_number': 'Le numéro de passe doit être strictement positif.',
  'orchard.harvest_pass.invalid_weights': 'Le poids brut doit être positif et la tare strictement inférieure au brut.',
  'orchard.harvest_pass.invalid_worker_count': 'Le nombre de cueilleurs ne peut pas être négatif.',
  'orchard.harvest_pass.invalid_labor_cost': "Le coût de main-d'œuvre ne peut pas être négatif.",
  'orchard.harvest_pass.plot_out_of_scope': "Le verger désigné n'appartient pas à cette exploitation.",
  'orchard.harvest_pass.farm_mismatch': "La passe et le verger ne désignent pas la même exploitation.",
  'orchard.harvest_pass.season_out_of_scope': "La campagne désignée n'appartient pas à cette exploitation.",
  'orchard.harvest_pass.dar_active': 'La parcelle est encore sous délai avant récolte : la récolte est refusée avant échéance.',
  'orchard.quality.title': 'Contrôle qualité',
  'orchard.quality.search': 'Rechercher un contrôle qualité',
  'orchard.quality.empty': 'Aucun contrôle qualité enregistré.',
  'orchard.quality.pass': 'Passe de récolte',
  'orchard.quality.date': 'Date du contrôle',
  'orchard.quality.acidity': 'Acidité (%)',
  'orchard.quality.peroxide': 'Indice de peroxyde',
  'orchard.quality.brix': 'Brix (%)',
  'orchard.quality.caliber': 'Calibre',
  'orchard.quality.defect': 'Défauts (%)',
  'orchard.quality.oilYield': 'Rendement en huile (%)',
  'orchard.quality.classification': 'Classification',
  'orchard.quality.create': 'Enregistrer le contrôle',
  'orchard.quality.created': 'Contrôle qualité enregistré.',
  'orchard.quality.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.quality.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour y accéder.",
  'orchard.quality_control.organization_required': 'Le contrôle qualité doit être rattaché à une organisation.',
  'orchard.quality_control.farm_required': 'Le contrôle qualité doit être rattaché à une exploitation.',
  'orchard.quality_control.pass_required': 'Le contrôle qualité doit désigner une passe de récolte.',
  'orchard.quality_control.invalid_acidity': "L'acidité ne peut pas être négative.",
  'orchard.quality_control.invalid_peroxide': "L'indice de peroxyde ne peut pas être négatif.",
  'orchard.quality_control.invalid_brix': 'Le Brix est un pourcentage, compris entre 0 et 100.',
  'orchard.quality_control.invalid_defect': 'Le taux de défauts est un pourcentage, compris entre 0 et 100.',
  'orchard.quality_control.invalid_oil_yield': "Le rendement en huile est un pourcentage, compris entre 0 et 100.",
  'orchard.quality_control.pass_out_of_scope': "La passe de récolte désignée n'appartient pas à cette exploitation.",
  'orchard.quality_control.farm_mismatch': 'Le contrôle et la passe ne désignent pas la même exploitation.',
  'orchard.irrigation.title': 'Irrigation du verger',
  'orchard.irrigation.search': 'Rechercher un arrosage',
  'orchard.irrigation.empty': 'Aucun arrosage enregistré.',
  'orchard.irrigation.plot': 'Verger',
  'orchard.irrigation.date': "Date d'arrosage",
  'orchard.irrigation.volume': 'Volume (m³)',
  'orchard.irrigation.waterSource': "Source d'eau",
  'orchard.irrigation.costPerM3': 'Prix du m³',
  'orchard.irrigation.costTotal': 'Coût total',
  'orchard.irrigation.create': "Enregistrer l'arrosage",
  'orchard.irrigation.created': 'Arrosage enregistré.',
  'orchard.irrigation.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.irrigation.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour l'ouvrir.",
  'orchard.irrigation.organization_required': "L'arrosage doit être rattaché à une organisation.",
  'orchard.irrigation.farm_required': "L'arrosage doit être rattaché à une exploitation.",
  'orchard.irrigation.plot_required': "L'arrosage doit désigner un verger.",
  'orchard.irrigation.invalid_volume': "Le volume d'eau doit être strictement positif.",
  'orchard.irrigation.water_source_required': "L'arrosage doit désigner sa source d'eau.",
  'orchard.irrigation.invalid_cost': 'Le prix du m³ ne peut pas être négatif.',
  'orchard.irrigation.plot_out_of_scope': "Le verger désigné n'appartient pas à cette exploitation.",
  'orchard.irrigation.farm_mismatch': "L'arrosage et le verger ne désignent pas la même exploitation.",
  'orchard.irrigation.movement_out_of_scope': "Le mouvement de stock désigné n'appartient pas à cette exploitation.",
  'orchard.transformation.title': "Transformation de l'olive",
  'orchard.transformation.search': 'Rechercher une transformation',
  'orchard.transformation.empty': 'Aucune transformation enregistrée.',
  'orchard.transformation.pass': 'Passe de récolte',
  'orchard.transformation.date': 'Date de transformation',
  'orchard.transformation.mill': 'Huilerie',
  'orchard.transformation.input': "Entrée d'olives (kg)",
  'orchard.transformation.output': "Sortie d'huile (kg)",
  'orchard.transformation.yield': 'Rendement (%)',
  'orchard.transformation.acidity': "Acidité (%)",
  'orchard.transformation.classification': "Classe de l'huile",
  'orchard.transformation.batch': 'Lot',
  'orchard.transformation.create': 'Enregistrer la transformation',
  'orchard.transformation.created': 'Transformation enregistrée.',
  'orchard.transformation.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.transformation.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour l'ouvrir.",
  'orchard.transformation.classification.extra_virgin': "Huile d'olive extra-vierge",
  'orchard.transformation.classification.virgin': "Huile d'olive vierge",
  'orchard.transformation.classification.lampante': "Huile d'olive lampante",
  'orchard.transformation.organization_required': 'La transformation doit être rattachée à une organisation.',
  'orchard.transformation.farm_required': 'La transformation doit être rattachée à une exploitation.',
  'orchard.transformation.pass_required': 'La transformation doit désigner sa passe de récolte.',
  'orchard.transformation.invalid_input': "La quantité d'olives entrée doit être strictement positive.",
  'orchard.transformation.invalid_output': "La quantité d'huile sortie doit être strictement positive.",
  'orchard.transformation.output_exceeds_input': "La sortie d'huile ne peut pas dépasser l'entrée d'olives.",
  'orchard.transformation.invalid_acidity': "L'acidité de l'huile ne peut pas être négative.",
  'orchard.transformation.pass_out_of_scope': "La passe de récolte désignée n'appartient pas à cette exploitation.",
  'orchard.transformation.farm_mismatch': "La transformation et la passe de récolte ne désignent pas la même exploitation.",

  'orchard.phenology.title': 'Phénologie du verger',
  'orchard.phenology.search': 'Rechercher une observation',
  'orchard.phenology.empty': 'Aucune observation enregistrée.',
  'orchard.phenology.plot': 'Verger',
  'orchard.phenology.date': "Date d'observation",
  'orchard.phenology.stage': 'Stade BBCH',
  'orchard.phenology.stage_label': 'Libellé du stade',
  'orchard.phenology.notes': 'Notes',
  'orchard.phenology.create': "Enregistrer l'observation",
  'orchard.phenology.created': 'Observation enregistrée.',
  'orchard.phenology.moduleRequired.title': 'Module Arboriculture non souscrit.',
  'orchard.phenology.moduleRequired.description': "Votre abonnement n'inclut pas le module Arboriculture. Contactez votre administrateur pour l'ouvrir.",
  'orchard.phenology.organization_required': "L'observation doit être rattachée à une organisation.",
  'orchard.phenology.farm_required': "L'observation doit être rattachée à une exploitation.",
  'orchard.phenology.plot_required': "L'observation doit désigner un verger.",
  'orchard.phenology.stage_required': 'Le stade BBCH observé est obligatoire.',
  'orchard.phenology.plot_out_of_scope': "Le verger désigné n'appartient pas à cette exploitation.",

  'orchard.reports.title': 'Coûts et rentabilité du verger',
  'orchard.reports.search': 'Coût par verger et par campagne',
  'orchard.reports.empty': 'Aucun coût de verger pour cette campagne.',
  'orchard.reports.season': 'Campagne',
  'orchard.reports.campaign': 'Campagne',
  'orchard.reports.plot': 'Verger',
  'orchard.reports.area': 'Surface',
  'orchard.reports.interventions': 'Travaux',
  'orchard.reports.irrigation': 'Eau',
  'orchard.reports.harvest': 'Récolte',
  'orchard.reports.amortized': 'Part amortie',
  'orchard.reports.total': 'Coût total',
  'orchard.reports.yield': 'Récolte (kg)',
  'orchard.reports.costPerHa': 'Coût / ha',
  'orchard.reports.costPerKg': 'Coût / kg',

  'orchard.region': 'Région',
  'orchard.region.none': 'Non déclarée',
  'orchard.region.nord': 'Nord',
  'orchard.region.nord_ouest': 'Nord-Ouest',
  'orchard.region.centre': 'Centre',
  'orchard.region.centre_sud': 'Centre-Sud',
  'orchard.region.cap_bon': 'Cap Bon',
  'orchard.region.sahel': 'Sahel',
  'orchard.region.djerid_nefzaoua': 'Djerid & Nefzaoua',
  'orchard.region.mornag': 'Mornag',
  'orchard.plot.invalid_region': 'La région du verger est inconnue du terroir.',

  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.loading': 'Chargement…',
  'common.retry': 'Réessayer',
  'common.language': 'Langue',
  'common.page': 'Page',
  'common.previous': 'Précédent',
  'common.next': 'Suivant',

  'error.network': "Le serveur n'a pas répondu. Vérifiez votre connexion.",
  'error.unexpected': "Une erreur inattendue s'est produite.",
  'request.invalid_body': "La saisie n'a pas pu être lue : vérifiez les nombres (point décimal) et les dates.",
  'db.duplicate': 'Cette valeur existe déjà : le code ou le courriel doit être unique.',
  'db.reference_missing': "Un élément désigné n'existe pas (article, unité, tiers ou lot).",
  'db.check_violation': 'Une valeur est hors des bornes admises.',
  'db.value_required': 'Une valeur obligatoire manque.',
  'saas.no_active_subscription': "Aucun abonnement actif : contactez votre administrateur avant de créer des exploitations ou des parcelles.",
  'saas.quota.farms_exceeded': "Votre plan n'autorise pas d'exploitation supplémentaire.",
  'saas.quota.plots_exceeded': "Votre plan n'autorise pas de parcelle supplémentaire.",
  'saas.quota.users_exceeded': "Votre plan n'autorise pas d'utilisateur supplémentaire.",
  'saas.quota.area_exceeded': 'La superficie totale dépasserait le plafond de votre plan.',
  'purchase.order.no_lines': 'Ajoutez au moins une ligne : une commande sans ligne ne commande rien.',
  'purchases.receive.empty': 'Indiquez une quantité arrivée sur au moins une ligne.',
  'purchases.cancel.confirm': "Annuler cette commande ? L'annulation est tracée et définitive.",
  'stock.warehouse.capacity_unit_required': 'Une capacité doit être exprimée dans une unité de mesure.',
  'stock.transfer.incomplete': 'Désignez le dépôt d’origine, le dépôt de destination, l’article et la quantité.',
  'operations.header_required': "Choisissez l'exploitation et la nature des travaux avant d'enregistrer.",
  'operations.consumption_incomplete': 'Choisissez un article et une quantité strictement positive.',
  'users.admin_only_page': 'La gestion des utilisateurs est réservée aux administrateurs.',
  'subscription.none': "Aucun abonnement actif n'est enregistré pour votre entreprise.",
  'subscription.plan': 'Plan',
  'subscription.status': 'Statut',
  'subscription.period': 'Période en cours',
  'subscription.unlimited': 'Sans plafond',
  'subscription.module': 'Module',
  'subscription.noModule': 'Aucun module d’activité souscrit.',
  'subscription.usage': 'Consommation',
  'subscription.unpaidTitle': 'En cas d’impayé',
  'subscription.unpaid1': 'Après la période de grâce, l’organisation passe en lecture seule.',
  'subscription.unpaid2': 'La consultation et l’export restent ouverts ; seule la saisie est fermée.',
  'subscription.unpaid3': 'Aucune donnée n’est supprimée.',
  'common.active': 'Actif',
  'auth.invalid_credentials': 'Identifiants invalides.',
  'auth.account_locked': 'Compte temporairement verrouillé. Réessayez dans quelques minutes.',
  'auth.account_disabled': "Compte désactivé. Contactez l'administrateur de votre organisation.",
  'auth.password_too_short': 'Le mot de passe doit compter au moins dix caractères.',
  'auth.invalid_reset_token': 'Ce lien n’est plus valable : il a expiré ou il a déjà servi. Demandez-en un nouveau.',
  'growth.dash.title': 'Tableau de bord',
  'growth.dash.description': 'Revenu récurrent, clients, impayés et encaissements, calculés au jour même.',
  'growth.dash.mrr': 'Revenu récurrent mensuel (HT)',
  'growth.dash.arr': 'Annuel',
  'growth.dash.clients': 'Clients',
  'growth.dash.churn30': 'Départs sur 30 jours',
  'growth.dash.outstanding': 'Reste à encaisser (TTC)',
  'growth.dash.overdue': 'dont échu',
  'growth.dash.collectedMonth': 'Encaissé ce mois',
  'growth.dash.collectedYear': 'cette année',
  'growth.dash.renewals30': 'Renouvellements à 30 jours',
  'growth.dash.openRequests': 'Demandes ouvertes',
  'growth.dash.byStatus': 'Abonnements par état',
  'growth.dash.byPlan': 'Par formule',
  'growth.dash.plan': 'Formule',
  'growth.dash.trialsEnding': 'Essais qui finissent sous 14 jours',
  'growth.dash.trialEnds': 'Fin de l’essai',
  'growth.dash.noTrials': 'Aucun essai ne finit bientôt.',
  'growth.addons.title': 'Options et remise',
  'growth.addons.kind': 'Option',
  'growth.addons.unitPrice': 'Prix par période (HT)',
  'growth.addons.since': 'Depuis',
  'growth.addons.empty': 'Aucune option en cours.',
  'growth.addons.add': 'Ajouter une option',
  'growth.addons.added': 'Option ajoutée ; le plafond est relevé.',
  'growth.addons.end': 'Retirer',
  'growth.addons.endTitle': 'Retirer cette option',
  'growth.addons.ended': 'Option retirée.',
  'growth.addons.priceHint': 'Vide pour des exploitations : le prix de la formule.',
  'growth.addonKind.farms': 'Exploitations en plus',
  'growth.addonKind.users': 'Utilisateurs en plus',
  'growth.addonKind.plots': 'Parcelles en plus',
  'growth.addonKind.area': 'Hectares en plus',
  'growth.discount.title': 'Remise',
  'growth.discount.apply': 'Appliquer le code',
  'growth.discount.applied': 'Remise appliquée aux prochaines factures.',
  'growth.discount.remove': 'Retirer la remise',
  'growth.discount.removed': 'Remise retirée.',
  'growth.discount.remaining': 'périodes restantes :',
  'growth.codes.title': 'Codes de remise',
  'growth.codes.description': 'Un pourcentage de 1 à 99, pour un nombre de périodes ou sans fin. Ce qui est déjà accordé ne change pas quand un code est modifié.',
  'growth.codes.list': 'Codes',
  'growth.codes.form': 'Créer ou modifier un code',
  'growth.codes.code': 'Code',
  'growth.codes.percent': 'Remise (%)',
  'growth.codes.percentHint': 'De 1 à 99.',
  'growth.codes.duration': 'Durée (périodes)',
  'growth.codes.durationHint': 'Vide : tant que l’abonnement vit.',
  'growth.codes.forever': 'Sans fin',
  'growth.codes.validUntil': 'Applicable jusqu’au',
  'growth.codes.max': 'Utilisations au plus',
  'growth.codes.uses': 'Utilisations',
  'growth.codes.descriptionLabel': 'Description',
  'growth.codes.inactive': 'Désactivé',
  'growth.codes.edit': 'Modifier',
  'growth.codes.empty': 'Aucun code pour l’instant.',
  'growth.codes.saved': 'Code enregistré.',
  'growth.requests.title': 'Demandes des clients',
  'growth.requests.description': 'Ce que les clients demandent : changer de formule, résilier, ajouter une option. Clore une demande ne fait pas le geste : il se fait sur la fiche du client.',
  'growth.requests.list': 'Demandes',
  'growth.requests.empty': 'Aucune demande.',
  'growth.requests.date': 'Date',
  'growth.requests.kind': 'Demande',
  'growth.requests.message': 'Message',
  'growth.requests.response': 'Réponse',
  'growth.requests.handle': 'Traiter',
  'growth.requests.handleHint': 'La réponse est montrée au client. Elle est obligatoire pour un refus.',
  'growth.requests.done': 'Marquer comme fait',
  'growth.requests.decline': 'Refuser',
  'growth.requests.handled': 'Demande close.',
  'growth.requestKind.plan_change': 'Changement de formule',
  'growth.requestKind.cancel': 'Résiliation',
  'growth.requestKind.addon': 'Option',
  'growth.requestKind.other': 'Autre',
  'growth.requestStatus.open': 'En attente',
  'growth.requestStatus.done': 'Traitée',
  'growth.requestStatus.declined': 'Refusée',
  'growth.leads.title': 'Prospects du site',
  'growth.leads.description': "Les demandes de contact venues du site vitrine, de la plus récente à la plus ancienne. Personne n'est prévenu à leur arrivée : c'est ici qu'on les lit et qu'on les classe.",
  'growth.leads.list': 'Prospects',
  'growth.leads.empty': 'Aucun prospect pour l’instant.',
  'growth.leads.date': 'Date',
  'growth.leads.name': 'Nom',
  'growth.leads.company': 'Entreprise',
  'growth.leads.email': 'Courriel',
  'growth.leads.phone': 'Téléphone',
  'growth.leads.governorate': 'Gouvernorat',
  'growth.leads.source': 'Source',
  'growth.leads.status': 'Statut',
  'growth.leads.message': 'Message',
  'growth.leadStatus.new': 'Nouveau',
  'growth.leadStatus.handled': 'Traité',
  'growth.leadStatus.spam': 'Indésirable',
  'growth.leads.action': 'Classement',
  'growth.leads.markHandled': 'Marquer traité',
  'growth.leads.markSpam': 'Indésirable',
  'growth.leads.reopen': 'Rouvrir',
  'growth.lead_status_invalid': 'Un prospect est nouveau, traité ou indésirable.',
  'growth.lead_not_found': 'Ce prospect est introuvable.',
  'portal.invoices.title': 'Mes factures',
  'portal.plans.title': 'Changer de formule',
  'portal.plans.hint': 'Prix hors taxes. Une demande de changement est étudiée par la plateforme ; rien ne change avant sa réponse.',
  'portal.plans.current': 'actuelle',
  'portal.plans.price': 'Prix',
  'portal.plans.month': 'mois',
  'portal.plans.year': 'an',
  'portal.plans.period': 'période',
  'portal.plans.features': 'Fonctionnalités',
  'portal.plans.ask': 'Demander',
  'portal.requests.title': 'Mes demandes',
  'portal.requests.send': 'Envoyer la demande',
  'portal.requests.sent': 'Demande envoyée à la plateforme.',
  'growth.addon_kind_unknown': 'Option inconnue.',
  'growth.addon_quantity_invalid': 'La quantité est positive, entière sauf pour des hectares.',
  'growth.addon_price_required': 'Indiquez le prix par période de l’option : la formule n’en fixe pas.',
  'growth.addon_not_found': 'Cette option est introuvable ou déjà retirée.',
  'growth.code_invalid': 'Un code tient de 2 à 30 lettres, chiffres ou tirets.',
  'growth.percent_invalid': 'Une remise va de 1 à 99 %.',
  'growth.cycles_invalid': 'Une remise dure de 1 à 120 périodes, ou sans fin.',
  'growth.max_invalid': 'Le nombre maximal d’utilisations est positif.',
  'growth.code_unavailable': 'Ce code n’existe pas, est désactivé ou a expiré.',
  'growth.code_exhausted': 'Ce code a atteint son nombre maximal d’utilisations.',
  'growth.discount_exists': 'Une remise est déjà en cours : retirez-la d’abord.',
  'growth.request_status_invalid': 'Une demande se clôt comme traitée ou refusée.',
  'growth.request_not_found': 'Cette demande est introuvable ou déjà close.',
  'subscription.request_kind_unknown': 'Sorte de demande inconnue.',
  'subscription.request_message_required': 'Dites ce que vous demandez.',
  'subscription.request_open': 'Une demande de cette sorte est déjà en cours de traitement.',
  'platform.nav.invoices': 'Factures',
  'billing.invoices.title': 'Factures et avoirs',
  'billing.invoices.description': 'Ce que la plateforme a facturé à ses clients. Une facture émise ne se modifie pas : elle se corrige par un avoir.',
  'billing.invoices.list': 'Pièces émises',
  'billing.invoices.empty': 'Aucune facture pour l’instant.',
  'billing.col.number': 'Numéro',
  'billing.col.client': 'Client',
  'billing.col.issuedOn': 'Émise le',
  'billing.col.dueOn': 'Échéance',
  'billing.col.total': 'Total TTC',
  'billing.col.balance': 'Reste à payer',
  'billing.col.status': 'État',
  'billing.status.all': 'Tous les états',
  'billing.status.unpaid': 'À payer',
  'billing.status.partial': 'Payée en partie',
  'billing.status.paid': 'Payée',
  'billing.status.overdue': 'Échue',
  'billing.status.cancelled': 'Annulée par avoir',
  'billing.status.credit_note': 'Avoir',
  'billing.kind.invoice': 'Facture',
  'billing.kind.credit_note': 'Avoir',
  'billing.seller.title': 'Profil de facturation',
  'billing.seller.description': 'L’identité de la plateforme sur ses factures. La modifier ne réécrit aucune facture déjà émise.',
  'billing.seller.incompleteTitle': 'Aucune facture ne part',
  'billing.seller.incomplete': 'Tant que la raison sociale, le matricule fiscal et l’adresse de la plateforme manquent, aucune facture n’est émise, ni à la main ni au renouvellement.',
  'billing.seller.complete': 'Compléter le profil',
  'billing.seller.ready': 'Prêt à facturer',
  'billing.seller.identity': 'Identité',
  'billing.seller.terms': 'Conditions',
  'billing.seller.legalName': 'Raison sociale',
  'billing.seller.taxId': 'Matricule fiscal',
  'billing.seller.taxIdHint': 'Tel qu’il figure sur la carte d’identification fiscale.',
  'billing.seller.address': 'Adresse',
  'billing.seller.city': 'Ville',
  'billing.seller.postalCode': 'Code postal',
  'billing.seller.phone': 'Téléphone',
  'billing.seller.email': 'Courriel',
  'billing.seller.bankName': 'Banque',
  'billing.seller.bankAccount': 'RIB',
  'billing.seller.vatRate': 'Taux de TVA (%)',
  'billing.seller.stampDuty': 'Droit de timbre (TND)',
  'billing.seller.paymentTermsDays': 'Échéance de paiement (jours)',
  'billing.seller.footer': 'Mentions de pied de page',
  'billing.seller.footerHint': 'Forme juridique, capital, registre de commerce…',
  'billing.seller.autoInvoice': 'Facturer automatiquement au renouvellement',
  'billing.seller.autoInvoiceHint': 'À chaque fin d’essai ou échéance, la tâche du cycle de vie émet la facture de la nouvelle période.',
  'billing.seller.saved': 'Profil de facturation enregistré.',
  'billing.detail.back': 'Toutes les factures',
  'billing.detail.pdf': 'Télécharger le PDF',
  'billing.detail.downloaded': 'PDF téléchargé.',
  'billing.detail.summary': 'Pièce',
  'billing.detail.period': 'Période',
  'billing.detail.credited': 'Avoir sur la facture',
  'billing.detail.lines': 'Lignes et totaux',
  'billing.detail.description': 'Désignation',
  'billing.detail.quantity': 'Quantité',
  'billing.detail.unitPrice': 'Prix unitaire HT',
  'billing.detail.amountHt': 'Montant HT',
  'billing.detail.totalHt': 'Total HT',
  'billing.detail.vat': 'TVA',
  'billing.detail.stamp': 'Droit de timbre',
  'billing.detail.totalTtc': 'Total TTC',
  'billing.detail.payments': 'Paiements',
  'billing.detail.noPayments': 'Aucun paiement enregistré.',
  'billing.detail.credits': 'Avoirs',
  'billing.detail.noCredits': 'Aucun avoir sur cette facture.',
  'billing.payment.title': 'Enregistrer un paiement',
  'billing.payment.amount': 'Montant (TND)',
  'billing.payment.date': 'Date du paiement',
  'billing.payment.dateHint': 'Vide : aujourd’hui.',
  'billing.payment.method': 'Moyen de paiement',
  'billing.payment.reference': 'Référence',
  'billing.payment.submit': 'Enregistrer le paiement',
  'billing.payment.recorded': 'Paiement enregistré.',
  'billing.payment.reactivated': 'Paiement enregistré : la facture est soldée et l’abonnement du client a repris.',
  'billing.payment.void': 'Annuler',
  'billing.payment.voidTitle': 'Annuler ce paiement',
  'billing.payment.voidReason': 'Motif de l’annulation',
  'billing.payment.voided': 'Paiement annulé ; il reste visible, barré.',
  'billing.payment.voidedBadge': 'Annulé',
  'billing.method.transfer': 'Virement',
  'billing.method.cheque': 'Chèque',
  'billing.method.cash': 'Espèces',
  'billing.method.card': 'Carte',
  'billing.method.other': 'Autre',
  'billing.credit.title': 'Émettre un avoir',
  'billing.credit.full': 'Annuler toute la facture (droit de timbre compris)',
  'billing.credit.amountHt': 'Montant HT à créditer',
  'billing.credit.reason': 'Motif',
  'billing.credit.submit': 'Émettre l’avoir',
  'billing.credit.created': 'Avoir émis',
  'billing.new.note': 'Note',
  'billing.client.title': 'Factures',
  'billing.client.currentPeriod': 'Facturer la période en cours',
  'billing.client.newInvoice': 'Nouvelle facture',
  'billing.client.addLine': 'Ajouter une ligne',
  'billing.client.issue': 'Émettre la facture',
  'billing.client.issued': 'Facture émise.',
  'billing.client.priceHint': 'Hors taxes, au millime près.',
  'billing.seller_incomplete': 'Renseignez la raison sociale, le matricule fiscal et l’adresse de la plateforme avant d’émettre une facture.',
  'billing.lines_required': 'Une facture porte au moins une ligne.',
  'billing.line_invalid': 'Chaque ligne a un libellé, une quantité positive et un prix au millime près.',
  'billing.total_zero': 'Une facture à zéro ne s’émet pas.',
  'billing.date_in_future': 'Une facture ne se date pas dans le futur.',
  'billing.date_before_last': 'La numérotation suit la chronologie : la date ne peut précéder celle de la dernière facture.',
  'billing.legal_name_required': 'La raison sociale de la plateforme est obligatoire.',
  'billing.vat_invalid': 'Le taux de TVA va de 0 à 100 %.',
  'billing.stamp_invalid': 'Le droit de timbre va de 0 à 100 TND.',
  'billing.terms_invalid': 'L’échéance de paiement va de 0 à 120 jours.',
  'billing.no_billable_subscription': 'Ce client n’a pas d’abonnement en cours à facturer.',
  'billing.period_already_invoiced': 'Cette période d’abonnement est déjà facturée.',
  'billing.invoice_not_found': 'Cette facture est introuvable.',
  'billing.method_unknown': 'Moyen de paiement inconnu.',
  'billing.payment_date_invalid': 'La date de paiement est absente, future ou sans rapport avec la facture.',
  'billing.amount_invalid': 'Le montant est positif, au millime près.',
  'billing.payment_exceeds_balance': 'Le montant dépasse ce qui reste à payer sur la facture.',
  'billing.payment_not_found': 'Ce paiement est introuvable ou déjà annulé.',
  'billing.already_credited': 'Cette facture est déjà entièrement créditée.',
  'billing.credit_exceeds_invoice': 'L’avoir dépasse ce qui reste à créditer sur la facture.',
  'auth.use_admin_portal': "Ce compte administre la plateforme : connectez-vous par le portail d'administration.",
  'auth.use_client_portal': "Ce portail est réservé aux administrateurs de la plateforme. Connectez-vous par l'application.",
  'auth.challenge_invalid': 'Étape de connexion expirée. Recommencez avec votre mot de passe.',
  'auth.totp_invalid': 'Code incorrect ou déjà utilisé.',
  'auth.totp_unavailable': 'La double authentification de ce compte est illisible. Un opérateur doit la réinitialiser.',
  'auth.wrong_portal': "Cette session n'ouvre pas cet espace. Reconnectez-vous.",
  'admin.title': "Portail d'administration",
  'admin.login.subtitle': 'Accès réservé aux administrateurs de la plateforme, avec double authentification.',
  'admin.login.continue': 'Continuer',
  'admin.enroll.title': 'Activer la double authentification',
  'admin.enroll.intro': 'Scannez ce code avec votre application d’authentification (Google Authenticator, Microsoft Authenticator, Aegis…), puis saisissez le code à six chiffres qu’elle affiche.',
  'admin.enroll.qrAlt': 'Code QR de l’application d’authentification',
  'admin.enroll.manual': 'Saisie manuelle de la clé',
  'admin.code.label': 'Code à six chiffres',
  'admin.code.submit': 'Vérifier',
  'admin.verify.title': 'Vérification en deux étapes',
  'admin.verify.intro': 'Saisissez le code affiché par votre application d’authentification.',
  'admin.verify.useRecovery': 'Utiliser un code de secours',
  'admin.verify.useApp': 'Utiliser l’application',
  'admin.recovery.label': 'Code de secours',
  'admin.recovery.title': 'Vos codes de secours',
  'admin.recovery.intro': 'Chacun sert une seule fois, si vous perdez votre téléphone. Ils ne seront plus jamais affichés : conservez-les hors ligne, en lieu sûr.',
  'admin.recovery.copy': 'Copier les codes',
  'admin.recovery.copied': 'Codes copiés.',
  'admin.recovery.saved': 'Je les ai conservés',
  'admin.restart': 'Recommencer',
  'saas.module_not_subscribed': "Ce module n'est pas souscrit par votre organisation.",

  'nav.library': 'Bibliothèque',
  'library.title': 'Bibliothèque d’écrans',
  'library.description': 'Les composants partagés, montrés avec les données de démonstration. Basculez la langue et le thème : rien n’est écrit en dur.',
  'library.theme': 'Thème',
  'library.theme.clair': 'Clair',
  'library.theme.sombre': 'Sombre',
  'library.section.layout': 'Grille et cartes',
  'library.section.table': 'Tableau',
  'library.section.form': 'Formulaire',
  'library.section.feedback': 'Bandeaux et notifications',
  'library.warehouse': 'Dépôt',
  'library.warehouse.code': 'Code du dépôt',
  'library.warehouse.name': 'Nom',
  'library.warehouse.farm': 'Exploitation',
  'library.warehouse.type': 'Nature',
  'library.warehouse.capacity': 'Capacité',
  'library.warehouse.negative': 'Autoriser un stock négatif',
  'library.warehouse.negativeHint': 'À réserver aux dépôts dont les prélèvements sont relevés après coup.',
  'library.warehouse.codeHint': 'Trente caractères au plus, mis en majuscules automatiquement.',
  'library.warehouse.codeError': 'Un dépôt portant ce code existe déjà.',
  'library.stock.product': 'Article',
  'library.stock.quantity': 'Quantité',
  'library.stock.unitCost': 'Coût unitaire',
  'library.stock.value': 'Valeur',
  'library.stock.empty': 'Aucun stock dans ce dépôt.',
  'library.save': 'Enregistrer',
  'library.saving': 'Enregistrement…',
  'library.cancel': 'Annuler',
  'library.delete': 'Supprimer',
  'library.saved': 'Dépôt enregistré.',
  'library.notifyInfo': 'Notification d’information',
  'library.notifySuccess': 'Notification de succès',
  'library.notifyWarning': 'Notification d’avertissement',
  'library.notifyError': 'Notification d’erreur',
  'library.alert.info': 'Les montants sont exprimés en dinars tunisiens.',
  'library.alert.warning': 'Ce dépôt est désactivé : il ne peut plus recevoir de marchandise.',
  'library.alert.error': 'Stock insuffisant pour effectuer ce transfert.',
  'library.active': 'Actif',
  'library.inactive': 'Désactivé',

  'nav.suppliers': 'Fournisseurs',
  'suppliers.title': 'Fournisseurs',
  'suppliers.description': 'Un fournisseur est un tiers qualifié comme tel : la fiche s’ajoute au tiers existant, elle ne le remplace pas.',
  'suppliers.search': 'Rechercher un fournisseur',
  'suppliers.code': 'Code',
  'suppliers.name': 'Nom',
  'suppliers.city': 'Ville',
  'suppliers.phone': 'Téléphone',
  'suppliers.status': 'Agrément',
  'suppliers.approved': 'Agréé',
  'suppliers.notApproved': 'Non agréé',
  'suppliers.empty': 'Aucun fournisseur enregistré.',
  'suppliers.fiche': 'Fiche fournisseur',
  'suppliers.selectHint': 'Choisissez un fournisseur pour afficher sa fiche.',
  'suppliers.notSupplier': 'Ce tiers n’est pas déclaré fournisseur.',
  'suppliers.reference': 'Notre référence chez eux',
  'suppliers.creditLimit': 'Encours autorisé',
  'suppliers.outstanding': 'Encours constaté',
  'suppliers.creditAvailable': 'Marge disponible',
  'suppliers.noLimit': 'Sans plafond',
  'suppliers.leadDays': 'Délai de livraison (jours)',
  'suppliers.minimumOrder': 'Commande minimale',
  'suppliers.bankName': 'Banque',
  'suppliers.bankAccount': 'Numéro de compte',
  'suppliers.iban': 'IBAN',
  'suppliers.rating': 'Note qualité (1 à 5)',
  'suppliers.approve': 'Fournisseur agréé',
  'suppliers.approveHint': 'L’agrément conserve sa date et son auteur : c’est la première question d’un audit achat.',
  'suppliers.save': 'Enregistrer la fiche',
  'suppliers.saving': 'Enregistrement…',
  'suppliers.saved': 'Fiche fournisseur enregistrée.',

  'warehouses.title': 'Dépôts',
  'warehouses.description': 'Un dépôt est un lieu de stockage. Le transfert déplace de la valeur d’un dépôt à l’autre — il n’en crée jamais.',
  'warehouses.search': 'Rechercher un dépôt',
  'warehouses.code': 'Code',
  'warehouses.name': 'Nom',
  'warehouses.farm': 'Exploitation',
  'warehouses.unattached': 'Non rattaché',
  'warehouses.governorate': 'Gouvernorat',
  'warehouses.manager': 'Responsable',
  'warehouses.capacity': 'Capacité',
  'warehouses.status': 'État',
  'warehouses.active': 'Actif',
  'warehouses.inactive': 'Inactif',
  'warehouses.default': 'Par défaut',
  'warehouses.showInactive': 'Afficher les dépôts inactifs',
  'warehouses.empty': 'Aucun dépôt enregistré.',
  'warehouses.new': 'Nouveau dépôt',
  'warehouses.create': 'Créer le dépôt',
  'warehouses.creating': 'Création en cours…',
  'warehouses.created': 'Dépôt créé.',
  'warehouses.allowNegative': 'Autoriser le stock négatif',
  'warehouses.allowNegativeHint': 'À n’activer que sur un dépôt de champ, où la sortie précède souvent la saisie de l’entrée.',
  'warehouses.attach': 'Rattacher',
  'warehouses.attached': 'Rattachement enregistré.',
  'warehouses.detach': 'Détacher',
  'warehouses.transfer': 'Transférer du stock',
  'warehouses.transferFrom': 'Dépôt d’origine',
  'warehouses.transferTo': 'Dépôt de destination',
  'warehouses.transferProduct': 'Article',
  'warehouses.transferQuantity': 'Quantité',
  'warehouses.transferSubmit': 'Transférer',
  'warehouses.transferring': 'Transfert en cours…',
  'warehouses.unitCost': 'Valeur unitaire',
  'warehouses.transferredValue': 'Valeur déplacée',
  'warehouses.transferNoValue': 'La valeur unitaire est conservée : un transfert déplace du stock, il n’en valorise pas.',
  'warehouses.remainingSource': 'Reste à l’origine',
  'warehouses.remainingTarget': 'Reste à destination',

  'nav.operations': 'Interventions',
  'operations.title': 'Interventions',
  'operations.description': 'Un travail, des parcelles, un coût : la dépense rencontre ici la campagne qui la porte.',
  'operations.new': 'Nouvelle intervention',
  'operations.farm': 'Exploitation',
  'operations.workType': 'Nature de travaux',
  'operations.date': 'Date',
  'operations.notes': 'Notes',
  'operations.targets': 'Parcelles travaillées',
  'operations.area': 'Superficie (ha)',
  'operations.noPlots': 'Cette exploitation n’a aucune parcelle : créez-en une avant d’y intervenir.',
  'operations.machines': 'Matériel',
  'operations.addMachine': 'Ajouter du matériel',
  'operations.machine': 'Machine',
  'operations.hours': 'Heures',
  'operations.hourlyCost': 'Coût horaire',
  'operations.labor': 'Main-d’œuvre',
  'operations.addLabor': 'Ajouter de la main-d’œuvre',
  'operations.partner': 'Prestataire',
  'operations.workers': 'Effectif',
  'operations.quantity': 'Quantité',
  'operations.payBasis': 'Base de rémunération',
  'operations.payBasis.hourly': 'À l’heure',
  'operations.payBasis.daily': 'Au jour',
  'operations.payBasis.monthly': 'Au mois',
  'operations.payBasis.piece_rate': 'À la tâche',
  'operations.unitCost': 'Coût unitaire',
  'operations.responsible': 'Responsable',
  'operations.meterKind': 'Type de compteur',
  'operations.meterKind.none': 'Aucun relevé',
  'operations.meterKind.hours': 'Horamètre (heures)',
  'operations.meterKind.km': 'Compteur kilométrique',
  'operations.meterStart': 'Compteur au départ',
  'operations.meterEnd': 'Compteur au retour',
  'operations.distanceKm': 'Kilométrage',
  'operations.fromMeter': 'Déduit du compteur : ne se saisit pas à côté.',
  'operations.employee': 'Salarié',
  'operations.startTime': 'Heure de début',
  'operations.endTime': 'Heure de fin',
  'operations.fromTimes': 'Déduite de l’horaire : ne se saisit pas à côté.',
  'operations.unitCost.fromEmployee': 'Laissé vide, le coût de la fiche du salarié s’applique.',
  'operations.plannedInputs': 'Intrants prévus',
  'operations.addPlannedInput': 'Prévoir un intrant',
  'operations.plannedProduct': 'Article prévu',
  'operations.plannedQuantity': 'Quantité prévue',
  'operations.driver': 'Conducteur',
  'operations.plan.title': 'Prévu et réel',
  'operations.plan.planned': 'Prévu',
  'operations.plan.actual': 'Réel',
  'operations.plan.gap': 'Écart',
  'operations.charges': 'Prestations et autres charges',
  'operations.addCharge': 'Ajouter une charge',
  'operations.charge.kind': 'Type de charge',
  'operations.charge.kind.service': 'Prestation sous-traitée',
  'operations.charge.kind.other': 'Autre charge',
  'operations.charge.partner': 'Entreprise ou tiers',
  'operations.charge.description': 'Nature ou libellé',
  'operations.charge.quantity': 'Nombre d’unités',
  'operations.charge.unitPrice': 'Prix unitaire',
  'operations.charge.total': 'Montant :',
  'operations.breakdown.prestations': 'Prestations sous-traitées',
  'operations.breakdown.autres': 'Autres charges',
  'nav.employees': 'Salariés',
  'employees.title': 'Salariés',
  'employees.description': 'Le personnel de l’exploitation : qui répond d’une intervention, qui y travaille, et ce qu’il coûte.',
  'employees.new': 'Nouveau salarié',
  'employees.edit': 'Modifier le salarié',
  'employees.code': 'Matricule',
  'employees.fullName': 'Nom et prénom',
  'employees.jobTitle': 'Fonction',
  'employees.employmentType': 'Type de personnel',
  'employees.farm': 'Exploitation de rattachement',
  'employees.farm.all': 'Toutes les exploitations',
  'employees.hourlyCost': 'Coût horaire',
  'employees.dailyCost': 'Coût journalier',
  'employees.monthlyCost': 'Coût mensuel',
  'employees.active': 'En activité',
  'employees.active.hint': 'Un salarié inactif ne se désigne plus sur une intervention ; ses lignes passées restent.',
  'employees.empty': 'Aucun salarié enregistré.',
  'employees.saved': 'Salarié enregistré.',
  'employees.required': 'Un matricule, un nom et un type de personnel sont requis.',
  'employees.readOnly': 'Lecture seule : la fiche d’un salarié se tient avec le droit de gérer les référentiels.',
  'operations.saveDraft': 'Enregistrer le brouillon',
  'operations.saveValidate': 'Enregistrer et valider',
  'operations.saving': 'Enregistrement…',
  'operations.created': 'Intervention créée.',
  'operations.validated': 'Intervention validée : elle est désormais immuable.',
  'operations.validatedBadge': 'Validée — immuable',
  'operations.result': 'Résultat de l’intervention',
  'operations.number': 'Numéro',
  'operations.directCost': 'Coût direct',
  'operations.validateDraft': 'Valider l’intervention',
  'operations.consumption': 'Intrants sortis du dépôt',
  'operations.consumptionHint': 'La consommation sort l’article du dépôt AU PRIX DU STOCK et l’attache à l’intervention : le prix n’est jamais ressaisi. Validez ensuite.',
  'operations.warehouse': 'Dépôt',
  'operations.product': 'Article',
  'operations.consume': 'Consommer',
  'operations.consuming': 'Consommation…',
  'operations.consumed': 'Intrant rattaché à l’intervention.',
  'operations.breakdown': 'Coût direct, ligne à ligne',
  'operations.breakdown.intrants': 'Intrants',
  'operations.breakdown.machines': 'Machines',
  "operations.breakdown.main-d'œuvre": 'Main-d’œuvre',
  'operations.lineLabel': 'Ligne',
  'operations.unitPrice': 'Prix unitaire',
  'operations.total': 'Total',
  'operations.importTitle': 'Importer des interventions (CSV)',
  'operations.importHint': 'Une ligne du fichier = une intervention, validée à l’import. Téléchargez le gabarit : il porte les consignes dans ses premières lignes.',

  'references.importTitle': 'Importer en masse (CSV)',
  'references.importHint': 'Les lignes valides entrent, les fautives sont rapportées avec leur numéro. Le gabarit porte les colonnes attendues.',
  'references.importHintVariety': 'Pour les variétés, la colonne crop_code désigne la culture par son code exact — créez les cultures d’abord.',

  'import.template': 'Télécharger le gabarit',
  'import.pending': 'Import en cours…',
  'import.allDone': '{created} ligne(s) importée(s) sur {total}.',
  'import.partial': '{created} ligne(s) importée(s) sur {total} ; {failed} refusée(s) — corrigez-les et resoumettez-les seules.',
  'import.columnLine': 'Ligne',
  'import.columnError': 'Erreur',
  'import.columnDetail': 'Détail',

  'csv.empty': 'Le fichier est vide.',
  'csv.header_missing': 'La première ligne doit porter les noms de colonnes.',
  'csv.header_invalid': 'Colonnes du fichier différentes du gabarit attendu.',
  'csv.no_data_rows': 'Le fichier ne porte aucune donnée sous son en-tête.',
  'csv.column_count': 'Une ligne porte plus de valeurs que le gabarit n’admet de colonnes.',
  'csv.too_many_rows': 'Trop de lignes : découpez le fichier.',

  'import.crop_unknown': 'Culture inconnue : créez-la d’abord dans le référentiel des cultures.',
  'import.farm_unknown': 'Exploitation inconnue : vérifiez son code.',
  'import.work_type_unknown': 'Nature de travaux inconnue : vérifiez son code.',
  'import.plot_unknown': 'Parcelle inconnue dans cette exploitation.',
  'import.plot_duplicate': 'Une même parcelle figure deux fois dans la ligne.',
  'import.no_open_season': 'Aucune campagne ouverte sur cette parcelle.',
  'import.machine_unknown': 'Matériel inconnu : vérifiez son code.',
  'import.hours_required': 'Des heures positives sont requises quand un matériel est désigné.',
  'import.partner_unknown': 'Prestataire inconnu : vérifiez son code.',
  'import.pay_basis_invalid': 'Base de rémunération invalide (hourly, daily, monthly, piece_rate).',
  'import.number_invalid': 'Nombre illisible.',
  'import.date_invalid': 'Date illisible (AAAA-MM-JJ ou JJ/MM/AAAA).',
  'import.quantity_required': 'Une quantité positive est requise.',

  'farm.plot.no_open_season': 'Cette parcelle n’a aucune campagne ouverte : créez-en une avant d’y imputer des travaux.',
  'farm.plot.not_found': 'Parcelle introuvable.',
  'farm.plot.irrigation_requires_irrigable': 'Une parcelle en sec n’a ni mode d’irrigation ni source d’eau : dites-la irrigable, ou retirez-les.',
  'reference.not_found': 'L’entrée choisie n’existe pas, ou plus, dans le référentiel.',
  'farm.farm.not_found': 'Exploitation introuvable.',
  'purchase.requisition.organization_required': 'Une demande d’achat appartient à une entreprise.',
  'purchase.requisition.document_number_required': 'Le numéro de la demande est obligatoire (30 caractères au plus).',
  'purchase.requisition.already_numbered': 'Cette demande a déjà son numéro.',
  'purchase.requisition.needed_before_request': 'Un besoin ne peut pas être attendu avant d’avoir été demandé.',
  'purchase.requisition.text_too_long': 'Le motif d’une demande tient en 500 caractères, celui d’une ligne ou d’une décision en 300.',
  'purchase.requisition.no_lines': 'Une demande qui ne demande rien ne se crée ni ne s’approuve.',
  'purchase.requisition.rejection_reason_required': 'Un refus se motive.',
  'purchase.requisition.decided': 'Cette demande est déjà décidée : elle ne se modifie plus et ne se redécide pas.',
  'purchase.requisition.not_requester': 'Seul celui qui a fait la demande la retire. Les autres la refusent, avec un motif.',
  'purchase.requisition.not_found': 'Demande d’achat introuvable.',
  'purchase.requisition.unknown_status': 'Une demande est soumise, approuvée, refusée ou retirée.',
  'purchase.requisition.line_not_found': 'La ligne de demande désignée est introuvable.',
  'purchase.requisition.not_approved': 'Une commande ne répond qu’à une demande approuvée.',
  'purchase.requisition.product_mismatch': 'La ligne de commande ne porte pas l’article de la demande à laquelle elle répond.',
  'purchase.requisition.unit_mismatch': 'Une ligne qui répond à une demande se compte dans l’unité de l’article.',
  'purchase.requisition_line.invalid': 'Une ligne de demande désigne un article et une quantité strictement positive.',
  'purchase.requisition_line.duplicate_product': 'Cet article est déjà demandé : un article se demande une fois par demande.',
  'purchase.requisition_line.product_not_found': 'Un article demandé est introuvable.',
  'agro.budget.season_closed': 'Cette campagne est close : son budget ne se modifie plus.',
  'agro.budget.invalid_yield': 'Un rendement prévu n’est pas négatif.',
  'agro.budget.invalid_price': 'Un prix prévisionnel n’est pas négatif.',
  'agro.budget.unit_required': 'Un rendement ou un prix prévisionnel se donne avec son unité.',
  'agro.budget.text_too_long': 'La note d’un budget tient en 300 caractères, le libellé d’une ligne en 120.',
  'agro.budget.not_found': 'Cette culture n’a pas de budget dans cette campagne.',
  'agro.budget_line.invalid_nature': 'Une ligne de budget est un intrant, du matériel, de la main-d’œuvre ou une autre charge.',
  'agro.budget_line.label_required': 'Une ligne de budget dit ce qu’elle budgète : son libellé est obligatoire.',
  'agro.budget_line.invalid_quantity': 'La quantité à l’hectare est strictement positive.',
  'agro.budget_line.price_required': 'Le prix d’une ligne de budget est obligatoire : zéro est un prix, un champ vide n’en est pas un.',
  'agro.budget_line.invalid_price': 'Un prix n’est pas négatif.',
  'agro.budget_line.duplicate': 'Ce budget a déjà une ligne de ce libellé et de cette nature.',
  'agro.budget_line.not_found': 'Cette ligne n’est pas une ligne de ce budget.',
  'sales.invoice.not_found': 'Facture introuvable.',
  'sales.invoice.due_before_invoice': 'Une facture n’est pas due avant sa date.',
  'sales.invoice.date_in_future': 'Une facture ne se date pas dans le futur.',
  'sales.invoice.no_deliveries': 'Une facture reprend au moins un bon de livraison.',
  'sales.invoice.delivery_not_invoicable': 'Un bon est introuvable, non livré, d’un autre client ou déjà facturé : rien n’est facturé.',
  'sales.invoice.mixed_partners': 'Une facture ne mélange pas les clients.',
  'sales.invoice.tax_rate_invalid': 'Ce taux de taxe n’est pas en vigueur.',
  'sales.payment.amount_must_be_positive': 'Le montant d’un encaissement est strictement positif.',
  'sales.payment.date_in_future': 'Un encaissement ne se date pas dans le futur.',
  'sales.payment.text_too_long': 'La référence d’un encaissement tient en 60 caractères, sa note ou son motif d’annulation en 300.',
  'sales.payment.customer_not_found': 'Client introuvable.',
  'sales.payment.method_not_found': 'Mode de règlement introuvable.',
  'sales.payment.reference_required': 'Ce mode de règlement exige une référence.',
  'sales.payment.allocation_invalid': 'Une imputation est d’un montant strictement positif.',
  'sales.payment.allocation_duplicate': 'Une facture ne s’impute qu’une fois par encaissement.',
  'sales.payment.over_allocated': 'Les imputations dépassent ce que l’encaissement vaut encore.',
  'sales.payment.invoice_not_payable': 'Une facture désignée n’est pas une facture émise de ce client.',
  'sales.payment.exceeds_remaining': 'Une imputation dépasse ce que la facture doit encore, avoirs déduits.',
  'sales.payment.not_found': 'Encaissement introuvable.',
  'sales.payment.cancelled': 'Cet encaissement est annulé : il n’éteint plus rien.',
  'sales.payment.already_cancelled': 'Cet encaissement est déjà annulé.',
  'sales.payment.cancellation_reason_required': 'L’annulation d’un encaissement se motive.',
  'sales.payment.partner_mismatch': 'L’encaissement et la facture ne sont pas du même client.',
  'sales.payment.allocation_immutable': 'Une imputation ne se modifie ni ne se supprime : c’est l’encaissement qu’on annule.',
  'sales.payment.immutable': 'Un encaissement ne se réécrit pas : il s’annule.',
  'agro.plot_season.not_found': 'Campagne de parcelle introuvable.',
  'agro.plot_season.area_must_be_positive': 'La superficie semée doit être strictement positive.',
  'agro.plot_season.area_exceeds_plot': 'La superficie semée dépasse la superficie de la parcelle.',
  'agro.plot_season.area_unknown': 'Superficie inconnue pour cette parcelle : la préciser explicitement.',
  'agro.plot_season.already_assigned': 'Cette parcelle porte déjà une culture sur cette campagne.',
  'agro.plot_season.invalid_sequence': 'Le rang de culture vaut 1 (principale) ou 2 (dérobée).',
  'agro.plot_season.double_cropping_disabled': 'La double culture n’est pas activée : une parcelle ne porte qu’une culture par campagne.',
  'agro.plot_season.invalid_period': 'La fin de la campagne doit venir après son début.',
  'agro.plot_season.period_outside_season': 'Les dates de la campagne sortent de la période qui la porte.',
  'agro.season.not_found': 'Période introuvable.',
  'agro.plot_season.code_too_long': 'Le code de la campagne dépasse 30 caractères.',
  'agro.plot_season.duplicate_code': 'Une campagne porte déjà ce code.',
  'agro.plot_season.name_too_long': 'Le nom de la campagne dépasse 150 caractères.',
  'agro.plot_season.description_too_long': 'La description de la campagne dépasse 500 caractères.',
  'agro.cropping_plan.no_plot_selected': 'Aucune parcelle sélectionnée : il n’y a rien à affecter.',
  'agro.cropping_plan.too_many_plots': 'Trop de parcelles d’un coup (200 au maximum) : procéder par exploitation.',

  'stock.warehouse_required': 'Un dépôt doit être désigné.',

  'ops.operation.no_target': 'Désignez au moins une parcelle : une intervention s’impute à quelque chose.',
  'ops.operation.no_line': 'Ajoutez du matériel, de la main-d’œuvre ou une charge : une intervention sans coût ne se valide pas.',
  'ops.operation.already_validated': 'Intervention déjà validée.',
  'ops.operation.immutable': 'Intervention validée : elle est immuable, corrigez-la par contre-passation.',
  'ops.operation.cancelled': 'Une intervention annulée ne se valide pas.',
  'ops.operation.references_required': 'Choisissez une exploitation et une nature de travaux.',
  'ops.operation.invalid_area': 'La superficie couverte doit être strictement positive.',
  'ops.operation_target.cost_object_required': 'Une cible désigne un objet de coût.',
  'ops.operation_target.allocation_key_required': 'Chaque cible porte une superficie ou une quote-part.',
  'ops.operation_target.invalid_allocation': 'La superficie doit être positive et la quote-part entre 0 et 100.',
  'ops.operation_target.duplicate': 'Cette parcelle est déjà couverte par l’intervention.',
  'ops.operation_input.quantity_must_be_positive': 'La quantité consommée doit être strictement positive.',
  'ops.operation_input.movement_already_used': 'Ce mouvement de stock est déjà rattaché à une consommation.',
  'ops.operation_input.references_required': 'Un intrant désigne un article, son mouvement et une unité.',
  'ops.operation_machine.invalid': 'Une machine, des heures positives et un coût horaire sont requis.',
  'ops.operation_labor.who_required': 'Désignez un employé OU un prestataire, pas les deux.',
  'ops.operation_labor.invalid': 'L’effectif et la quantité doivent être positifs.',
  'ops.operation_machine.invalid_meter': 'Un compteur se relève au départ et au retour, le retour au-dessus du départ, avec son type.',
  'ops.operation_machine.hours_from_meter': 'Les heures se déduisent du compteur horaire : elles ne se saisissent pas à côté.',
  'ops.operation_machine.distance_from_meter': 'Le kilométrage se déduit du compteur kilométrique : il ne se saisit pas à côté.',
  'ops.operation_machine.invalid_distance': 'Un kilométrage est strictement positif.',
  'ops.operation_labor.times_together': 'L’heure de début et l’heure de fin vont ensemble.',
  'ops.operation_labor.times_order': 'L’heure de fin suit l’heure de début. Une ligne ne passe pas minuit : saisissez-en deux.',
  'ops.operation_labor.hours_from_times': 'Les heures se déduisent de l’horaire : elles ne se saisissent pas à côté.',
  'agro.employee.not_found': 'Salarié introuvable.',
  'agro.employee.code_required': 'Le matricule est requis.',
  'agro.employee.name_required': 'Le nom est requis.',
  'agro.employee.text_too_long': 'Un matricule tient en 30 caractères, un nom en 150, une fonction en 100.',
  'agro.employee.negative_cost': 'Un coût ne peut pas être négatif.',
  'agro.employee.employment_type_not_found': 'Le type de personnel désigné n’existe pas.',
  'agro.employee.duplicate_code': 'Ce matricule est déjà pris.',
  'livestock.animal.organization_required': 'Un animal appartient à une entreprise.',
  'livestock.animal.farm_required': 'Un animal vit sur une exploitation.',
  'livestock.animal.tag_required': 'Le trigramme (numéro de boucle) est obligatoire.',
  'livestock.animal.species_required': 'L’espèce est obligatoire.',
  'livestock.animal.breed_required': 'La race est obligatoire.',
  'livestock.animal.sex_invalid': 'Le sexe est « male » ou « female ».',
  'livestock.animal.entry_reason_required': 'Le motif d’entrée (naissance, achat…) est obligatoire.',
  'livestock.animal.entry_date_required': 'La date d’entrée est obligatoire.',
  'livestock.animal.text_too_long': 'Un texte dépasse sa longueur maximale.',
  'livestock.animal.species_not_found': 'Espèce inconnue.',
  'livestock.animal.breed_not_found': 'Race inconnue.',
  'livestock.animal.duplicate_tag': 'Ce trigramme est déjà pris sur cette exploitation.',
  'livestock.movement.animal_not_found': 'Animal introuvable.',
  'livestock.movement.direction_invalid': 'Un mouvement entre (« in ») ou sort (« out »).',
  'livestock.movement.date_required': 'La date du mouvement est obligatoire.',
  'livestock.movement.reason_required': 'Le motif du mouvement est obligatoire.',
  'livestock.movement.animal_exited': 'Cet animal est sorti du troupeau.',
  'livestock.movement.exit_without_entry': 'Impossible de sortir un animal qui n’est jamais entré.',
  'livestock.movement.already_inside': 'Cet animal est déjà entré.',
  'livestock.registry.title': 'Registre des animaux',
  'livestock.registry.description': 'Chaque tête identifiée par son trigramme : inscription, entrées et sorties.',
  'livestock.registry.new': 'Nouvel animal',
  'livestock.registry.tag': 'Trigramme (boucle)',
  'livestock.registry.species': 'Espèce',
  'livestock.registry.breed': 'Race',
  'livestock.registry.sex': 'Sexe',
  'livestock.registry.sex.male': 'Mâle',
  'livestock.registry.sex.female': 'Femelle',
  'livestock.registry.farm': 'Exploitation',
  'livestock.registry.entryDate': 'Date d’entrée',
  'livestock.registry.entryReason': 'Motif d’entrée',
  'livestock.registry.saved': 'Animal inscrit au registre.',
  'livestock.registry.required': 'Trigramme, espèce, race et motif d’entrée sont requis.',
  'livestock.registry.movement.title': 'Entrée / sortie',
  'livestock.registry.movement.animal': 'Animal',
  'livestock.registry.movement.direction': 'Sens',
  'livestock.registry.movement.in': 'Entrée',
  'livestock.registry.movement.out': 'Sortie',
  'livestock.registry.movement.date': 'Date',
  'livestock.registry.movement.reason': 'Motif',
  'livestock.registry.movement.saved': 'Mouvement constaté.',
  'livestock.registry.status': 'Statut',
  'livestock.registry.status.active': 'Présent',
  'livestock.registry.status.exited': 'Sorti',
  'livestock.inventory.title': 'Inventaire du troupeau',
  'livestock.inventory.description': 'Les bêtes présentes, comptées par espèce et catégorie.',
  'livestock.inventory.species': 'Espèce',
  'livestock.inventory.category': 'Catégorie',
  'livestock.inventory.count': 'Effectif',
  'livestock.farm.all': 'Toutes les exploitations',
  'nav.livestock.registry': 'Registre',
  'nav.livestock.inventory': 'Inventaire',
  'references.kind.species': 'Espèces',
  'references.kind.breed': 'Races',
  'references.kind.animal_category': 'Catégories d’animaux',
  'livestock.registry.empty': 'Aucun animal au registre.',
  'livestock.registry.register': 'Inscrire',
  'livestock.registry.movement.record': 'Constater',
  'livestock.registry.movement.required': 'Animal, sens et motif sont requis.',
  'livestock.registry.movement.price': 'Prix (DT)',
  'livestock.inventory.empty': 'Troupeau vide.',
  'livestock.movement.withdrawal_active': 'Cet animal est sous temps d’attente : sa sortie à l’abattoir est refusée.',
  'livestock.movement_immutable': 'Un mouvement de troupeau ne se modifie ni ne se supprime : il se corrige par un mouvement inverse.',
  'livestock.movement.price_invalid': 'Le prix d’un mouvement ne peut pas être négatif.',
  'livestock.health.organization_required': 'Un événement sanitaire appartient à une entreprise.',
  'livestock.health.animal_required': 'Un événement sanitaire concerne un animal.',
  'livestock.health.date_required': 'La date de l’intervention est obligatoire.',
  'livestock.health.type_required': 'Le genre d’intervention (traitement, vaccination…) est obligatoire.',
  'livestock.health.withdrawal_invalid': 'Le temps d’attente ne peut pas être négatif.',
  'livestock.health.dose_invalid': 'La dose, quand elle est saisie, est strictement positive.',
  'livestock.health.text_too_long': 'Un texte dépasse sa longueur maximale.',
  'livestock.health.animal_not_found': 'Animal introuvable.',
  'livestock.health.after_exit': 'Cet animal est sorti du troupeau : on ne le soigne plus après sa sortie.',
  'livestock.health.title': 'Suivi sanitaire',
  'livestock.health.description': 'Traitements, vaccinations, visites — et le temps d’attente avant l’abattoir.',
  'livestock.health.new': 'Nouvelle intervention',
  'livestock.health.animal': 'Animal',
  'livestock.health.type': 'Genre d’intervention',
  'livestock.health.type.treatment': 'Traitement',
  'livestock.health.type.vaccination': 'Vaccination',
  'livestock.health.type.visit': 'Visite',
  'livestock.health.type.deworming': 'Vermifugation',
  'livestock.health.date': 'Date d’intervention',
  'livestock.health.product': 'Produit',
  'livestock.health.dose': 'Dose',
  'livestock.health.withdrawal': 'Temps d’attente (jours)',
  'livestock.health.vet': 'Vétérinaire',
  'livestock.health.saved': 'Intervention constatée.',
  'livestock.health.required': 'Animal, date et genre sont requis.',
  'livestock.health.empty': 'Aucune intervention pour cette bête.',
  'livestock.health.record': 'Constater',
  'livestock.health.cost': 'Coût (DT)',
  'livestock.health.cost_invalid': 'Le coût d’une intervention ne peut pas être négatif.',
  'livestock.alerts.title': 'Vaccinations dues',
  'livestock.alerts.description': 'Les bêtes présentes sans vaccination récente.',
  'livestock.alerts.empty': 'Aucune vaccination due.',
  'livestock.alerts.animal': 'Animal',
  'livestock.alerts.last': 'Dernière vaccination',
  'livestock.alerts.never': 'Jamais',
  'nav.livestock.health': 'Santé',
  'nav.livestock.production': 'Production',
  'livestock.production.title': 'Production laitière',
  'livestock.production.description': 'Traites quotidiennes, matin et soir — et le total journalier.',
  'livestock.production.table': 'Traites constatées',
  'livestock.production.new': 'Nouvelle traite',
  'livestock.production.animal': 'Animal',
  'livestock.production.date': 'Date de traite',
  'livestock.production.quantity': 'Quantité (l)',
  'livestock.production.quality': 'Score de qualité (0-100)',
  'livestock.production.session': 'Session',
  'livestock.production.session.morning': 'Matin',
  'livestock.production.session.evening': 'Soir',
  'livestock.production.date_total': 'Jour totalisé',
  'livestock.production.total': 'Total journalier',
  'livestock.production.saved': 'Traite constatée.',
  'livestock.production.after_exit': 'Cet animal est sorti du troupeau : on ne le trait plus après sa sortie.',
  'livestock.production.animal_not_found': 'Animal introuvable.',
  'livestock.production.animal_required': 'Une traite concerne un animal.',
  'livestock.production.date_required': 'La date de la traite est obligatoire.',
  'livestock.production.organization_required': 'Une traite appartient à une entreprise.',
  'livestock.production.product_required': 'Le produit (lait, laine, œufs) est obligatoire.',
  'livestock.production.quality_invalid': 'Le score de qualité tient entre 0 et 100.',
  'livestock.production.quantity_invalid': 'La quantité traite est strictement positive.',
  'livestock.production.text_too_long': 'Un texte dépasse sa longueur maximale.',
  'livestock.production.withdrawal_active': 'Cet animal est sous temps d’attente : sa traite est exclue de la collecte.',
  'livestock.production.required': 'Animal, date et quantité sont requis.',
  'livestock.production.empty': 'Aucune traite pour cet animal.',
  'livestock.production.record': 'Constater',
  'livestock.production.daily_total': 'Total journalier',
  'nav.livestock.feeding': 'Alimentation',
  'livestock.feeding.title': 'Alimentation',
  'livestock.feeding.description': 'Ce qu\'on donne à chaque animal : produit, quantité, coût.',
  'livestock.feeding.table': 'Alimentations constatées',
  'livestock.feeding.new': 'Nouvelle ration',
  'livestock.feeding.animal': 'Animal',
  'livestock.feeding.date': 'Date',
  'livestock.feeding.product': 'Produit alimentaire',
  'livestock.feeding.quantity': 'Quantité (kg)',
  'livestock.feeding.cost_total': 'Coût total (DT)',
  'livestock.feeding.saved': 'Alimentation constatée.',
  'livestock.feeding.required': 'Animal, date et quantité sont requis.',
  'livestock.feeding.empty': 'Aucune alimentation saisie.',
  'livestock.feeding.record': 'Constater',
  'livestock.feeding.animal_not_found': 'Animal introuvable.',
  'livestock.feeding.organization_required': 'Une alimentation appartient à une entreprise.',
  'livestock.feeding.animal_required': 'Une alimentation concerne un animal.',
  'livestock.feeding.date_required': 'La date de l\'alimentation est obligatoire.',
  'livestock.feeding.product_required': 'Le produit alimentaire est obligatoire.',
  'livestock.feeding.quantity_invalid': 'La quantité en kg est strictement positive.',
  'livestock.feeding.after_exit': 'Cet animal est sorti du troupeau : on ne l\'alimente plus après sa sortie.',
  'livestock.feeding.insufficient_stock': 'Stock insuffisant pour cette alimentation.',
  'livestock.feeding.no_warehouse': 'L’exploitation de cet animal n’a pas de dépôt par défaut : la ration ne peut pas sortir du stock.',
  'livestock.feeding.text_too_long': 'La note tient en 500 caractères.',
  'nav.livestock.reproduction': 'Reproduction',
  'livestock.reproduction.title': 'Reproduction et généalogie',
  'livestock.reproduction.description': 'Saillies, date prévue — et la gestation par espèce (ovin ≈ 150 j, bovin ≈ 285 j).',
  'livestock.reproduction.table': 'Saillies constatées',
  'livestock.reproduction.new': 'Nouvelle saillie',
  'livestock.reproduction.animal': 'Femelle',
  'livestock.reproduction.sire': 'Père (bélier, taureau)',
  'livestock.reproduction.date': 'Date de saillie',
  'livestock.reproduction.type': 'Type',
  'livestock.reproduction.type.sai': 'Saillie naturelle',
  'livestock.reproduction.type.natural': 'Saillie naturelle',
  'livestock.reproduction.type.ivt': 'Insémination',
  'livestock.reproduction.expected_due': 'Date prévue',
  'livestock.reproduction.notes': 'Notes',
  'livestock.reproduction.saved': 'Saillie constatée.',
  'livestock.reproduction.required': 'Animal, date et père sont requis.',
  'livestock.reproduction.empty': 'Aucune saillie pour cet animal.',
  'livestock.reproduction.record': 'Constater',
  'livestock.reproduction.animal_not_found': 'Animal introuvable.',
  'livestock.reproduction.organization_required': "Un événement de reproduction appartient à une entreprise.",
  'livestock.reproduction.animal_required': "Un événement de reproduction concerne une femelle.",
  'livestock.reproduction.sire_required': 'Le père (bélier, taureau) est obligatoire.',
  'livestock.reproduction.date_required': 'La date de saillie est obligatoire.',
  'livestock.reproduction.type_required': "Le type d'événement (saillie, insémination…) est obligatoire.",
  'livestock.reproduction.same_animal': 'Le père et la mère ne peuvent pas être le même animal.',
  'livestock.reproduction.dam_sex_invalid': 'La mère d’une saillie est une femelle.',
  'livestock.reproduction.sire_sex_invalid': 'Le père d’une saillie est un mâle.',
  'livestock.reproduction.sire_not_found': 'Le père est introuvable.',
  'livestock.reproduction.text_too_long': 'Un texte dépasse sa longueur maximale.',
  'livestock.reproduction.after_exit': "Cet animal est sorti du troupeau : on ne le reproduit plus après sa sortie.",
  'nav.livestock.reports': 'Coûts et marges',
  'livestock.reports.title': 'Coûts et marges du troupeau',
  'livestock.reports.description': 'Le coût par tête et le coût par litre — deux chiffres, deux lectures.',
  'livestock.reports.head': 'Têtes',
  'livestock.reports.total_cost': 'Coût cumulé',
  'livestock.reports.liters': 'Lait produit (l)',
  'livestock.reports.per_head': 'Coût par tête',
  'livestock.reports.per_liter': 'Coût par litre',
  'livestock.reports.table': 'Coût par bête',
  'livestock.reports.animal': 'Bête',
  'livestock.reports.purchase': 'Achat',
  'livestock.reports.feeding': 'Alimentation',
  'livestock.reports.health': 'Santé',
  'livestock.reports.total': 'Coût total',
  'livestock.reports.sale': 'Prix de vente',
  'livestock.reports.margin': 'Marge',
  'livestock.reports.empty': 'Aucune bête au registre.',
  'livestock.reports.forbidden': 'Votre rôle ne permet pas de consulter les coûts.',
  'ops.operation_planned_input.invalid': 'Un intrant prévu désigne un article et une quantité strictement positive.',
  'ops.operation_planned_input.duplicate': 'Cet article est déjà prévu sur l’intervention : un article se prévoit une fois.',
  'ops.operation_planned_input.product_not_stocked': 'Un article prévu n’est pas géré en stock : il ne se consomme pas, donc ne se prévoit pas.',
  'ops.operation_charge.invalid_kind': 'Une charge est une prestation sous-traitée ou une autre charge.',
  'ops.operation_charge.description_required': 'Dites ce qu’on a payé : la nature de la prestation, ou le libellé de la charge.',
  'ops.operation_charge.description_too_long': 'Le libellé d’une charge tient en 200 caractères.',
  'ops.operation_charge.partner_required': 'Une prestation sous-traitée nomme son prestataire.',
  'ops.operation_charge.invalid_amount': 'Une charge porte une quantité strictement positive et un prix qui n’est pas négatif.',
  'ops.operation_charge.price_required': 'Saisissez le prix : zéro est un prix, un champ vide n’en est pas un.',
  'agro.partner.not_found': 'Tiers introuvable.',
  'operations.consumption.no_lines': 'Ajoutez au moins un article à consommer.',
  'operations.operation.not_found': 'Intervention introuvable.',

  'nav.references': 'Référentiels',
  'nav.stock': 'Stock',
  'ops.rule.outside_season': 'Cette date est hors de la campagne : l’intervention imputerait son coût à une récolte qui n’est pas la sienne.',
  'ops.rule.inactive_plot': 'Cette parcelle n’est plus active : elle ne se travaille plus.',
  'references.title': 'Référentiels agronomiques',
  'references.description': 'Le catalogue livré est lisible par tous et modifiable par personne : renommez-le par surcharge, ajoutez vos propres entrées à côté.',
  'references.kind': 'Référentiel',
  'references.kind.crop_family': 'Familles de cultures',
  'references.kind.crop': 'Cultures',
  'references.kind.variety': 'Variétés',
  'references.kind.work_category': 'Catégories de travaux',
  'references.kind.work_type': 'Natures de travaux',
  'references.kind.machine_category': 'Matériel',
  'references.kind.employment_type': 'Main-d’œuvre',
  'references.kind.farm_type': 'Types d’exploitation',
  'references.kind.soil_type': 'Types de sol',
  'references.kind.irrigation_mode': 'Modes d’irrigation',
  'references.kind.water_source': 'Sources d’eau',
  'common.notSpecified': 'Non renseigné',
  'farms.type': 'Type d’exploitation',
  'farms.type.choose': 'Choisir le type',
  'farms.type.saved': 'Type d’exploitation enregistré.',
  'plots.soilType': 'Type de sol',
  'plots.irrigation.title': 'Sol et irrigation',
  'plots.irrigation.irrigable': 'Parcelle irrigable',
  'plots.irrigation.mode': 'Mode d’irrigation',
  'plots.irrigation.source': 'Source d’eau',
  'plots.irrigation.chooseMode': 'Choisir le mode d’irrigation',
  'plots.irrigation.chooseSource': 'Choisir la source d’eau',
  'plots.irrigation.dryHint': 'Une parcelle en sec n’a ni mode d’irrigation ni source d’eau.',
  'plots.irrigation.saved': 'Irrigation enregistrée.',
  'requisitions.title': 'Demandes d’achat',
  'requisitions.description': 'Dire de quoi on a besoin, le faire approuver, puis le commander.',
  'requisitions.new': 'Nouvelle demande',
  'requisitions.list': 'Demandes',
  'requisitions.one': 'Demande',
  'requisitions.empty': 'Aucune demande d’achat.',
  'requisitions.number': 'Numéro',
  'requisitions.requestDate': 'Demandée le',
  'requisitions.neededBy': 'Pour le',
  'requisitions.farm': 'Exploitation',
  'requisitions.farm.central': 'Besoin central',
  'requisitions.warehouse': 'Dépôt de destination',
  'requisitions.notes': 'Motif de la demande',
  'requisitions.product': 'Article',
  'requisitions.quantity': 'Quantité demandée',
  'requisitions.lineNotes': 'Précision',
  'requisitions.addLine': 'Ajouter un article',
  'requisitions.submit': 'Envoyer la demande',
  'requisitions.created': 'Demande envoyée.',
  'requisitions.open': 'Ouvrir',
  'requisitions.status.submitted': 'Soumise',
  'requisitions.status.approved': 'Approuvée',
  'requisitions.status.rejected': 'Refusée',
  'requisitions.status.cancelled': 'Retirée',
  'requisitions.fulfilment': 'Commande',
  'requisitions.fulfilment.none': 'Rien de commandé',
  'requisitions.fulfilment.partial': 'En partie commandée',
  'requisitions.fulfilment.full': 'Entièrement commandée',
  'requisitions.requested': 'Demandé',
  'requisitions.ordered': 'Commandé',
  'requisitions.remaining': 'Reste à commander',
  'requisitions.decisionReason': 'Motif de la décision',
  'requisitions.rejectReason': 'Motif du refus',
  'requisitions.rejectReason.hint': 'Obligatoire pour refuser.',
  'requisitions.approve': 'Approuver',
  'requisitions.reject': 'Refuser',
  'requisitions.cancel': 'Retirer la demande',
  'requisitions.approved': 'Demande approuvée.',
  'requisitions.rejected': 'Demande refusée.',
  'requisitions.cancelled': 'Demande retirée.',
  'requisitions.order.title': 'Commander le reste',
  'requisitions.order.supplier': 'Fournisseur',
  'requisitions.order.quantity': 'Quantité à commander',
  'requisitions.order.unitPrice': 'Prix négocié',
  'requisitions.order.submit': 'Créer la commande',
  'requisitions.order.created': 'Commande créée.',
  'requisitions.order.complete': 'Tout ce qui était demandé est commandé.',
  'requisitions.order.supplierRequired': 'Choisissez le fournisseur.',
  'requisitions.order.nothing': 'Saisissez au moins une quantité à commander.',
  'requisitions.order.priceRequired': 'Saisissez le prix de chaque ligne commandée : zéro est un prix, un champ vide n’en est pas un.',
  'nav.budget': 'Budget de campagne',
  'budget.title': 'Budget de campagne',
  'budget.description': 'Ce qu’on prévoit de dépenser et de produire, culture par culture et à l’hectare — puis ce qu’on a réellement fait.',
  'budget.season': 'Campagne',
  'budget.closed': 'Cette campagne est close : son budget ne se modifie plus.',
  'budget.forbidden': 'Le budget porte des montants : il est réservé à ceux qui voient les coûts.',
  'budget.noSeason': 'Aucune campagne. Ouvrez-en une pour la budgéter.',
  'budget.noCrop': 'Aucune culture à l’assolement de cette campagne, et aucune budgétée.',
  'budget.area': 'Surface à l’assolement',
  'budget.plots': 'Parcelles',
  'budget.notBudgeted': 'Pas de budget pour cette culture. Ajouter une ligne ou des hypothèses la budgète.',
  'budget.notPlanted': 'Aucune parcelle de l’assolement ne porte cette culture : ce budget ne pèse rien tant qu’elle n’est pas assolée.',
  'budget.assumptions': 'Hypothèses de vente',
  'budget.expectedYield': 'Rendement prévu à l’hectare',
  'budget.yieldUnit': 'Unité de rendement',
  'budget.forecastPrice': 'Prix prévisionnel par unité',
  'budget.saveAssumptions': 'Enregistrer les hypothèses',
  'budget.assumptionsSaved': 'Budget enregistré.',
  'budget.lines': 'Coûts prévus à l’hectare',
  'budget.noLine': 'Aucune ligne de coût.',
  'budget.nature': 'Nature',
  'budget.nature.input': 'Intrants',
  'budget.nature.machine': 'Matériel',
  'budget.nature.labor': 'Main-d’œuvre',
  'budget.nature.other': 'Autres charges',
  'budget.label': 'Libellé',
  'budget.quantityPerHa': 'Quantité à l’hectare',
  'budget.unit': 'Unité',
  'budget.unitPrice': 'Prix unitaire',
  'budget.costPerHa': 'Coût à l’hectare',
  'budget.totalPerHa': 'Total à l’hectare',
  'budget.addLine': 'Ajouter la ligne',
  'budget.lineAdded': 'Ligne ajoutée.',
  'budget.removeLine': 'Retirer',
  'budget.lineRemoved': 'Ligne retirée.',
  'budget.remove': 'Retirer le budget',
  'budget.removed': 'Budget retiré.',
  'budget.otherCrop': 'Budgéter une autre culture',
  'budget.otherCrop.hint': 'Une culture se budgète avant même d’être assolée.',
  'budget.otherCrop.submit': 'Budgéter',
  'budget.crop': 'Culture',
  'budget.comparison': 'Prévu et réalisé',
  'budget.comparison.empty': 'Aucune culture budgétée : rien à comparer.',
  'budget.post': 'Poste',
  'budget.planned': 'Prévu',
  'budget.actual': 'Réalisé',
  'budget.gap': 'Écart',
  'budget.gapPercent': 'Écart relatif',
  'budget.post.input': 'Intrants',
  'budget.post.machine': 'Matériel',
  'budget.post.labor': 'Main-d’œuvre',
  'budget.post.other': 'Autres charges',
  'budget.post.direct_cost': 'Coût direct',
  'budget.post.yield': 'Production',
  'budget.post.yield_per_ha': 'Rendement à l’hectare',
  'budget.post.revenue': 'Chiffre d’affaires',
  'budget.post.gross_margin': 'Marge brute',
  'budget.total': 'Ensemble des cultures budgétées',
  'budget.unitMismatch': 'Une récolte de cette culture est pesée dans une autre unité que celle du budget : la production réalisée n’est pas affichée.',
  'budget.unbudgeted': 'Cultures sans budget',
  'budget.unbudgeted.hint': 'Leur réalisé n’entre pas dans le total : il n’a rien en regard.',
  'budget.actualDirectCost': 'Coût direct réalisé',
  'budget.actualRevenue': 'Chiffre d’affaires réalisé',
  // Lot 8b (migration 0098) : les dettes fournisseurs et leurs règlements.
  // Lot 8c (migration 0099) : l'échéancier.
  'nav.group.treasury': 'Trésorerie',
  'nav.schedule': 'Échéancier',
  'nav.treasuryAccounts': 'Banque et caisse',
  'nav.commitments': 'Engagements',
  'nav.reconciliation': 'Rapprochement bancaire',
  'nav.executive': 'Direction',
  'schedule.title': 'Échéancier',
  'schedule.description': 'Ce qui reste à recevoir et à payer, à sa date.',
  'schedule.forbidden': 'L’échéancier est réservé à ceux qui voient les montants.',
  'schedule.asOf': 'Situation au',
  'schedule.asOf.hint': 'Laissé vide : aujourd’hui.',
  'schedule.situation': 'Situation des échéances',
  'schedule.situation.asOf': 'Rangée au',
  'schedule.bucket': 'Tranche',
  'schedule.bucket.overdue': 'Échu',
  'schedule.bucket.0-7': 'Sous 7 jours',
  'schedule.bucket.8-30': 'De 8 à 30 jours',
  'schedule.bucket.31-60': 'De 31 à 60 jours',
  'schedule.bucket.61-90': 'De 61 à 90 jours',
  'schedule.bucket.90+': 'Au-delà de 90 jours',
  'schedule.bucket.no_due_date': 'Sans échéance',
  'schedule.bucket.total': 'Ensemble',
  'schedule.receivable': 'À recevoir',
  'schedule.payable': 'À payer',
  'schedule.net': 'Net',
  'schedule.treasury': 'Trésorerie aujourd’hui :',
  'schedule.projected': 'Solde prévu',
  'schedule.projected.hint': 'Le solde prévu est ce qu’on aurait à la fin de chaque tranche si chacun payait à sa date : la trésorerie d’aujourd’hui, plus ce qui est attendu, moins ce qui est dû. Ce qui est échu est compté tout de suite ; ce qui n’a pas d’échéance n’y entre pas.',
  'schedule.entries': 'Échéances',
  'schedule.entries.hint': 'Une facture y est pour ce qu’elle doit encore. Un chèque ou une traite remis y reste, à sa propre échéance, jusqu’à ce que son débit soit noté sur l’écran des règlements.',
  'schedule.empty': 'Aucune échéance : rien à recevoir, rien à payer.',
  // Lot 8d (migration 0102) : les comptes de banque et de caisse.
  'treasury.title': 'Banque et caisse',
  'treasury.description': 'Les comptes de banque et les caisses, leur solde, et le relevé de chacun.',
  'treasury.forbidden': 'La trésorerie est réservée à ceux qui voient les montants.',
  'treasury.accounts': 'Comptes',
  'treasury.accounts.empty': 'Aucun compte : ouvrez votre banque et votre caisse ci-dessous.',
  'treasury.account': 'Compte',
  'treasury.code': 'Code',
  'treasury.name': 'Nom du compte',
  'treasury.kind': 'Nature',
  'treasury.kind.bank': 'Banque',
  'treasury.kind.cash': 'Caisse',
  'treasury.bankReference': 'Banque et numéro de compte',
  'treasury.bankReference.hint': 'Tels qu’on les lit sur un relevé.',
  'treasury.openingDate': 'Date d’ouverture',
  'treasury.openingBalance': 'Solde d’ouverture',
  'treasury.openingBalance.hint': 'Ce que le compte contenait ce jour-là. Un compte vide s’ouvre à 0, écrit ; à découvert, en négatif.',
  'treasury.inflow': 'Entrées',
  'treasury.outflow': 'Sorties',
  'treasury.balance': 'Solde',
  'treasury.amount': 'Montant',
  'treasury.status.open': 'Ouvert',
  'treasury.status.closed': 'Fermé',
  'treasury.totals': 'Trésorerie',
  'treasury.totals.bank': 'En banque',
  'treasury.totals.cash': 'En caisse',
  'treasury.totals.total': 'Trésorerie totale',
  'treasury.account.new': 'Ouvrir un compte',
  'treasury.account.new.hint': 'L’ouverture — solde et date — ne se corrige pas : tous les soldes du compte en partent. Un paiement daté d’avant l’ouverture est déjà dans ce solde : il désigne le compte sans le faire bouger. Saisi de travers, un compte se supprime tant qu’aucun paiement ne le désigne.',
  'treasury.account.create': 'Ouvrir le compte',
  'treasury.account.created': 'Compte ouvert.',
  'treasury.account.rename': 'Enregistrer le nom',
  'treasury.account.renamed': 'Compte corrigé.',
  'treasury.account.close': 'Fermer',
  'treasury.account.closed': 'Compte fermé : il garde son solde et ne reçoit plus rien.',
  'treasury.account.reopen': 'Rouvrir',
  'treasury.account.reopened': 'Compte rouvert.',
  'treasury.account.delete': 'Supprimer',
  'treasury.account.deleted': 'Compte supprimé.',
  'treasury.statement': 'Relevé',
  'treasury.statement.open': 'Relevé',
  'treasury.statement.date': 'Date',
  'treasury.statement.counterparty': 'Tiers',
  'treasury.statement.reference': 'Pièce',
  'treasury.statement.opening': 'Solde au départ',
  'treasury.statement.closing': 'Solde à l’arrivée',
  'treasury.statement.from': 'Du',
  'treasury.statement.from.hint': 'Vide : depuis l’ouverture du compte.',
  'treasury.statement.to': 'Au',
  'treasury.statement.empty': 'Aucun mouvement sur cette période.',
  'treasury.direction': 'Sens',
  'treasury.direction.in': 'Entrée',
  'treasury.direction.out': 'Sortie',
  'treasury.unassigned': 'Paiements à affecter',
  'treasury.unassigned.hint': 'Saisis avant les comptes, ils ne comptent dans aucun solde. Une fois affecté, un paiement ne change plus de compte.',
  'treasury.unassigned.assign': 'Affecter',
  'treasury.unassigned.assigned': 'Paiement affecté.',
  'treasury.field.bank': 'Compte bancaire',
  'treasury.field.cash': 'Caisse',
  'treasury.field.hint': 'Le compte que ce paiement fait bouger.',
  'treasury.field.none.bank': 'Aucun compte bancaire ouvert : ce mode de règlement en exige un.',
  'treasury.field.none.cash': 'Aucune caisse ouverte : des espèces en exigent une.',
  'treasury.field.open': 'Ouvrir un compte',
  'treasury.field.any': 'Banque ou caisse',
  'treasury.field.none.any': 'Aucun compte ouvert : ce paiement en exige un.',
  // Lot 8d (migration 0104) : les mouvements saisis, et le virement d'un compte à l'autre.
  'treasury.entries': 'Mouvements saisis',
  'treasury.entries.hint': 'Ce qui fait bouger un compte sans être le paiement d’une facture : frais bancaires, salaire, apport, retrait. Et le virement d’un compte à l’autre — des espèces déposées en banque, par exemple.',
  'treasury.entry.category': 'Nature',
  'treasury.entry.category.transfer': 'Virement entre comptes',
  'treasury.entry.category.bank_charges': 'Frais bancaires',
  'treasury.entry.category.interest': 'Intérêts',
  'treasury.entry.category.owner_contribution': 'Apport de l’exploitant',
  'treasury.entry.category.owner_withdrawal': 'Retrait de l’exploitant',
  'treasury.entry.category.salary': 'Salaires',
  'treasury.entry.category.rent': 'Loyers',
  'treasury.entry.category.insurance': 'Assurances',
  'treasury.entry.category.tax': 'Impôts et taxes',
  'treasury.entry.category.other': 'Autre',
  'treasury.entry.target': 'Compte de destination',
  'treasury.entry.target.hint': 'L’argent sort du premier compte et entre dans celui-ci.',
  'treasury.entry.label': 'Libellé',
  'treasury.entry.reference': 'Référence',
  'treasury.entry.submit': 'Enregistrer le mouvement',
  'treasury.entry.recorded': 'Mouvement enregistré.',
  'treasury.entry.cancel': 'Annuler',
  'treasury.entry.cancelled': 'Mouvement annulé : le compte revient.',
  'treasury.entry.reason': 'Motif d’annulation',
  'treasury.entry.reason.hint': 'Obligatoire pour annuler un mouvement. Un mouvement ne se corrige pas : il s’annule.',
  'treasury.entry.status.posted': 'Enregistré',
  'treasury.entry.status.cancelled': 'Annulé',
  // Lot 8d (migration 0105) : les autres engagements — salaires, loyers, assurances, impôts.
  'commitments.title': 'Engagements',
  'commitments.description': 'Salaires, loyers, assurances, impôts et autres charges : ce qu’on doit, à sa date, et ce qu’on en a payé.',
  'commitments.forbidden': 'Les engagements sont réservés à ceux qui voient les montants.',
  'commitments.due': 'À payer',
  'commitments.due.empty': 'Aucun engagement à payer.',
  'commitments.totalDue': 'Reste à payer :',
  'commitments.dueDate': 'Échéance',
  'commitments.category': 'Nature',
  'commitments.category.salary': 'Salaires',
  'commitments.category.rent': 'Loyer',
  'commitments.category.insurance': 'Assurance',
  'commitments.category.tax': 'Impôts et taxes',
  'commitments.category.other': 'Autre charge',
  'commitments.label': 'Libellé',
  'commitments.counterparty': 'À qui',
  'commitments.counterparty.hint': 'Un bailleur, un assureur, la recette des finances. Vide pour un salaire.',
  'commitments.paidAmount': 'Payé',
  'commitments.remaining': 'Reste dû',
  'commitments.pay': 'Payer',
  'commitments.payment': 'Montant payé',
  'commitments.paid': 'Paiement enregistré.',
  'commitments.paidOn': 'Jour du paiement',
  'commitments.cancel': 'Annuler l’engagement',
  'commitments.cancelled': 'Engagement annulé : il ne doit plus rien.',
  'commitments.reason': 'Motif d’annulation',
  'commitments.reason.hint': 'Obligatoire pour annuler un engagement. Ce qui en a été payé reste payé.',
  'commitments.new': 'Saisir un engagement',
  'commitments.new.hint': 'Un montant de paiement laissé vide paie tout ce qui reste. Une série écrit autant d’engagements, chacun à sa date : douze loyers, chaque mois, sont douze lignes. Un engagement ne se corrige pas, il s’annule.',
  'commitments.firstDueDate': 'Échéance — la première, pour une série',
  'commitments.count': 'Nombre d’échéances',
  'commitments.count.hint': 'Vide : une seule.',
  'commitments.interval': 'Se répète',
  'commitments.interval.monthly': 'Chaque mois',
  'commitments.interval.quarterly': 'Chaque trimestre',
  'commitments.interval.yearly': 'Chaque année',
  'commitments.create': 'Enregistrer l’engagement',
  'commitments.created': 'Engagement enregistré.',
  'commitments.closed': 'Soldés et annulés',
  'commitments.settlement.settled': 'Soldé',
  'commitments.settlement.cancelled': 'Annulé',
  // Lot 8e (migration 0106) : le rapprochement bancaire — un relevé, et les mouvements qu'on y pointe.
  'reconciliation.title': 'Rapprochement bancaire',
  'reconciliation.description': 'Saisir le relevé de la banque, y pointer les mouvements du compte, et lire ce qui les sépare.',
  'reconciliation.forbidden': 'Le rapprochement bancaire est réservé à ceux qui voient les montants.',
  'reconciliation.noBank': 'Aucun compte de banque : ouvrez-en un pour le rapprocher.',
  'reconciliation.noBank.link': 'Ouvrir un compte',
  'reconciliation.account': 'Compte de banque',
  'reconciliation.statements': 'Relevés',
  'reconciliation.statements.empty': 'Aucun relevé saisi pour ce compte.',
  'reconciliation.statementDate': 'Arrêté au',
  'reconciliation.reference': 'Référence',
  'reconciliation.reference.hint': 'Le numéro du relevé, s’il en a un.',
  'reconciliation.openingBalance': 'Solde initial',
  'reconciliation.closingBalance': 'Solde final',
  'reconciliation.pointedCount': 'Pointés',
  'reconciliation.gap': 'Écart',
  'reconciliation.state.reconciled': 'Rapproché',
  'reconciliation.state.open': 'À rapprocher',
  'reconciliation.chain.broken': 'Ne suit pas le relevé précédent',
  'reconciliation.open': 'Ouvrir',
  'reconciliation.delete': 'Supprimer',
  'reconciliation.deleted': 'Relevé supprimé.',
  'reconciliation.new': 'Nouveau relevé',
  'reconciliation.new.hint': 'Recopiez les deux soldes imprimés sur le relevé, fût-ce zéro. Un découvert se saisit en négatif.',
  'reconciliation.create': 'Saisir le relevé',
  'reconciliation.created': 'Relevé saisi.',
  'reconciliation.detail': 'Rapprochement du relevé',
  'reconciliation.line.date': 'Arrêté au :',
  'reconciliation.line.opening': 'Solde initial :',
  'reconciliation.line.pointed': 'Mouvements pointés :',
  'reconciliation.line.pointedBalance': 'Solde initial plus mouvements pointés :',
  'reconciliation.line.closing': 'Solde final du relevé :',
  'reconciliation.line.gap': 'Écart :',
  'reconciliation.reconciled.hint': 'Le solde initial plus les mouvements pointés donne le solde final : le relevé est rapproché.',
  'reconciliation.open.hint': 'Pointez les mouvements qui figurent sur le relevé. Ce qui y figure et manque ici — frais, agios, intérêts — se saisit dans « Banque et caisse », puis se pointe.',
  'reconciliation.chain.alert': 'Le solde initial ne reprend pas le solde final du relevé précédent : un relevé manque, ou un solde est mal recopié. Écart :',
  'reconciliation.correct': 'Corriger le relevé',
  'reconciliation.correct.hint': 'La date, la référence et les soldes se corrigent sans rien dépointer. Le compte, non.',
  'reconciliation.corrected': 'Relevé corrigé.',
  'reconciliation.pointed': 'Mouvements pointés',
  'reconciliation.pointed.empty': 'Aucun mouvement pointé sur ce relevé.',
  'reconciliation.unpointed': 'Mouvements à pointer',
  'reconciliation.unpointed.hint': 'Tous les mouvements du compte qu’aucun relevé n’a encore confirmés, quelle que soit leur date.',
  'reconciliation.unpointed.empty': 'Tous les mouvements de ce compte sont pointés.',
  'reconciliation.source': 'Pièce',
  'reconciliation.source.customer_payment': 'Encaissement',
  'reconciliation.source.supplier_payment': 'Règlement fournisseur',
  'reconciliation.source.lease_payment': 'Échéance de crédit-bail',
  'reconciliation.source.treasury_entry': 'Mouvement saisi',
  'reconciliation.point': 'Pointer',
  'reconciliation.pointedDone': 'Mouvement pointé.',
  'reconciliation.unpoint': 'Dépointer',
  'reconciliation.unpointedDone': 'Pointage retiré.',
  // Lot 9 (migration 0107) : le centre d'alertes.
  'alerts.title': 'Alertes',
  'alerts.description': 'Ce qui demande qu’on s’en occupe, en un endroit : échéances dépassées, interventions non valorisées, alertes agronomiques, stock, production non contrôlée, lots sans origine.',
  'alerts.asOf': 'Au',
  'alerts.total': 'Alertes :',
  'alerts.none': 'Rien à signaler.',
  'alerts.family.count': 'Nombre :',
  'alerts.family.total': 'Total :',
  'alerts.shown': 'Affichées :',
  'alerts.shown.of': 'sur',
  'alerts.open': 'Ouvrir :',
  'alerts.col.dueDate': 'Échéance',
  'alerts.col.daysOverdue': 'Jours de retard',
  'alerts.col.kind': 'Nature',
  'alerts.col.counterparty': 'Tiers',
  'alerts.col.reference': 'Pièce',
  'alerts.col.amount': 'Montant',
  'alerts.col.date': 'Date',
  'alerts.col.workType': 'Travail',
  'alerts.col.rule': 'Règle dépassée',
  'alerts.col.subject': 'Sur',
  'alerts.col.observed': 'Mesuré',
  'alerts.col.threshold': 'Seuil',
  'alerts.col.product': 'Article',
  'alerts.col.warehouse': 'Dépôt',
  'alerts.col.lot': 'Lot',
  'alerts.col.quantity': 'Quantité',
  'alerts.col.expiryDate': 'Péremption',
  'alerts.col.receivedOn': 'Entré le',
  'alert.payable_overdue': 'Échéances à payer dépassées',
  'alert.payable_overdue.hint': 'Ce qu’on doit et dont l’échéance est passée : factures, effets remis, loyers de crédit-bail, engagements.',
  'alert.receivable_overdue': 'Clients en retard de paiement',
  'alert.receivable_overdue.hint': 'Ce que des clients doivent et dont l’échéance est passée.',
  'ops.operation_unvalued': 'Interventions non valorisées',
  'ops.operation_unvalued.hint': 'Faites ou validées, avec un intrant, une machine, de la main-d’œuvre ou une charge qui ne vaut rien : leur coût est incomplet.',
  'ops.warning': 'Alertes agronomiques',
  'ops.warning.hint': 'Levées à la saisie des interventions, tant que leur campagne est ouverte : ce qui a été mesuré, et le seuil dépassé.',
  'stock.lot_uncontrolled': 'Production non contrôlée',
  'stock.lot_uncontrolled.hint': 'Des récoltes en stock que personne n’a encore contrôlées.',
  'stock.lot_without_origin': 'Lots sans origine',
  'stock.lot_without_origin.hint': 'Des lots en stock que n’explique ni une réception, ni une récolte, ni un partage, ni une reprise, ni une entrée.',
  'orchard.window_open': 'Fenêtre de récolte ouverte',
  'orchard.window_open.hint': "La fenêtre régionale de l’espèce est ouverte et rien n’a encore été récolté sur le verger.",
  'orchard.dar_active': 'Délai avant récolte en cours',
  'orchard.dar_active.hint': 'Un traitement phytosanitaire validé sur le verger : la récolte y reste bloquée jusqu’à la fin du délai avant récolte.',
  // Lot 9 (migration 0108) : le tableau de bord de direction.
  'executive.title': 'Tableau de bord de direction',
  'executive.description': 'Les indicateurs de l’exploitation, pour une campagne : surfaces, coûts et marge à l’hectare, production, stock, créances, dettes, trésorerie.',
  'executive.forbidden': 'Le tableau de bord de direction est réservé à ceux qui voient les montants.',
  'executive.season': 'Campagne',
  'executive.season.current': 'Campagne en cours',
  'executive.season.none': 'Aucune campagne en cours : les indicateurs de campagne se taisent. Choisissez-en une.',
  'executive.asOf': 'Au',
  'executive.readSeason': 'Campagne lue :',
  'executive.areas': 'Surfaces',
  'executive.farmedArea': 'Surface exploitée (ha) :',
  'executive.cultivatedArea': 'Surface cultivée (ha) :',
  'executive.perHa': 'À l’hectare',
  'executive.perHa.hint': 'Rapportés à la surface cultivée de la campagne.',
  'executive.costPerHa': 'Coût moyen :',
  'executive.machineCostPerHa': 'Coût des machines :',
  'executive.fuelCostPerHa': 'Coût du carburant :',
  'executive.marginPerHa': 'Marge :',
  'executive.production': 'Production',
  'executive.production.empty': 'Aucune récolte sur cette campagne.',
  'executive.production.hint': 'Le rendement se rapporte à la surface des parcelles récoltées, dans l’unité de la récolte.',
  'executive.production.product': 'Article',
  'executive.production.quantity': 'Quantité',
  'executive.production.area': 'Surface récoltée (ha)',
  'executive.production.yield': 'Rendement à l’hectare',
  'executive.fuel': 'Carburant',
  'executive.fuelLiters': 'Carburant consommé (litres) :',
  'executive.stock': 'Stock',
  'executive.stockInput': 'Stock d’intrants :',
  'executive.stockProduct': 'Stock de produits :',
  'executive.money': 'Tiers et trésorerie',
  'executive.receivables': 'Créances clients :',
  'executive.payables': 'Dettes fournisseurs :',
  'executive.treasury': 'Trésorerie :',
  'executive.silent': 'Un tiret : l’indicateur n’a pas de quoi se calculer — pas de campagne, pas de surface cultivée, pas de carburant relevé, pas de compte ouvert. Ce n’est pas zéro.',
  'schedule.dueDate': 'Échéance',
  'schedule.noDueDate': 'Sans échéance',
  'schedule.overdueDays': 'Jours de retard',
  'schedule.direction': 'Sens',
  'schedule.direction.in': 'À recevoir',
  'schedule.direction.out': 'À payer',
  'schedule.kind': 'Nature',
  'schedule.kind.customer_invoice': 'Facture client',
  'schedule.kind.supplier_invoice': 'Facture fournisseur',
  'schedule.kind.lease_installment': 'Échéance de crédit-bail',
  'schedule.kind.supplier_instrument': 'Chèque ou traite remis',
  'schedule.kind.commitment_salary': 'Salaires',
  'schedule.kind.commitment_rent': 'Loyer',
  'schedule.kind.commitment_insurance': 'Assurance',
  'schedule.kind.commitment_tax': 'Impôts et taxes',
  'schedule.kind.commitment_other': 'Autre charge',
  'schedule.counterparty': 'Tiers',
  'schedule.reference': 'Pièce',
  'schedule.amount': 'Montant',
  'nav.payables': 'Dettes et règlements',
  'payables.title': 'Dettes et règlements',
  'payables.description': 'Ce qu’on doit aux fournisseurs, depuis quand — et ce qu’on règle.',
  'payables.forbidden': 'Les dettes et les règlements sont réservés à ceux qui tiennent les documents.',
  'payables.aging': 'Ce qu’on doit aux fournisseurs',
  'payables.empty': 'Aucune facture approuvée n’attend de règlement, et aucune avance n’attend d’être imputée.',
  'payables.supplier': 'Fournisseur',
  'payables.outstanding': 'Reste dû',
  'payables.unallocated': 'Avances à imputer',
  'payables.overpaid': 'Trop-versé',
  'payables.net': 'Net',
  'payables.total': 'Ensemble des fournisseurs',
  'payables.open': 'Ouvrir',
  'payables.openOther': 'Ouvrir un autre fournisseur',
  'payables.openOther.hint': 'Pour verser un acompte à un fournisseur qui n’a encore aucune facture approuvée.',
  'payables.invoices': 'Factures approuvées non soldées',
  'payables.invoices.empty': 'Ce fournisseur n’a aucune facture approuvée non soldée.',
  'payables.invoice': 'Facture',
  'payables.invoiceDate': 'Date de facture',
  'payables.dueDate': 'Échéance',
  'payables.noDueDate': 'Sans échéance',
  'payables.setDueDate': 'Fixer l’échéance',
  'payables.dueDate.saved': 'Échéance enregistrée.',
  'payables.invoiced': 'Facturé',
  'payables.credited': 'Avoirs',
  'payables.paid': 'Réglé',
  'payables.remaining': 'Reste dû',
  'payables.allocate': 'Imputer',
  'payables.advances': 'Avances à imputer',
  'payables.advances.hint': 'Saisissez les montants à imputer dans le tableau des factures, puis imputez l’avance.',
  'payables.advance.allocate': 'Imputer cette avance',
  'payables.advance.allocated': 'Avance imputée.',
  'payables.payment': 'Règlement',
  'payables.payment.new': 'Nouveau règlement',
  'payables.payment.hint': 'Les montants saisis dans la colonne « Imputer » disent quelles factures ce règlement éteint. Ce qui n’est pas imputé reste une avance — un acompte, par exemple.',
  'payables.payment.submit': 'Enregistrer le règlement',
  'payables.payment.recorded': 'Règlement enregistré.',
  'payables.payment.cancel': 'Annuler',
  'payables.payment.cancelled': 'Règlement annulé.',
  'payables.payment.clear': 'Débité',
  'payables.payment.cleared': 'Débit noté : le règlement sort de l’échéancier.',
  'payables.clearedOn': 'Jour du débit',
  'payables.clearedOn.hint': 'Pour noter qu’un chèque ou une traite a été débité par la banque. Laissé vide : aujourd’hui.',
  'payables.paymentDate': 'Date de règlement',
  'payables.method': 'Mode de règlement',
  'payables.method.cash': 'Espèces',
  'payables.method.cheque': 'Chèque',
  'payables.method.transfer': 'Virement',
  'payables.method.bill_of_exchange': 'Traite',
  'payables.method.card': 'Carte',
  'payables.method.compensation': 'Compensation',
  'payables.reference': 'Référence',
  'payables.instrumentDueDate': 'Échéance du chèque ou de la traite',
  'payables.instrumentDueDate.hint': 'Le jour où l’argent sortira : ce n’est pas la date du règlement.',
  'payables.amount': 'Montant',
  'payables.allocated': 'Imputé',
  'payables.notes': 'Note',
  'payables.payments': 'Règlements',
  'payables.payments.empty': 'Aucun règlement.',
  'payables.cancelReason': 'Motif d’annulation',
  'payables.cancelReason.hint': 'Obligatoire pour annuler : chèque revenu, erreur de saisie…',
  'payables.status.issued': 'Émis',
  'payables.status.presented': 'Présenté',
  'payables.status.cleared': 'Débité',
  'payables.status.bounced': 'Revenu impayé',
  'payables.status.cancelled': 'Annulé',
  'nav.receivables': 'Créances et encaissements',
  'receivables.title': 'Créances et encaissements',
  'receivables.description': 'Ce que les clients doivent, depuis quand — et ce qu’on encaisse.',
  'receivables.forbidden': 'Les créances et les encaissements sont réservés à ceux qui tiennent les documents.',
  'receivables.aging': 'Ce que les clients doivent',
  'receivables.empty': 'Aucun client ne doit rien, et aucune avance n’attend d’être imputée.',
  'receivables.customer': 'Client',
  'receivables.outstanding': 'Reste dû',
  'receivables.bucket.not_due': 'À échoir',
  'receivables.bucket.1-30': 'Échu de 1 à 30 jours',
  'receivables.bucket.31-60': 'Échu de 31 à 60 jours',
  'receivables.bucket.61-90': 'Échu de 61 à 90 jours',
  'receivables.bucket.90+': 'Échu de plus de 90 jours',
  'receivables.bucket.no_due_date': 'Sans échéance',
  'receivables.unallocated': 'Avances à imputer',
  'receivables.overpaid': 'Trop-perçu',
  'receivables.net': 'Net',
  'receivables.total': 'Ensemble des clients',
  'receivables.open': 'Ouvrir',
  'receivables.openOther': 'Ouvrir un autre client',
  'receivables.openOther.hint': 'Pour encaisser une avance d’un client qui n’a encore aucune facture émise.',
  'receivables.invoices': 'Factures non soldées',
  'receivables.invoices.empty': 'Ce client n’a aucune facture non soldée.',
  'receivables.invoice': 'Facture',
  'receivables.invoiceDate': 'Date de facture',
  'receivables.dueDate': 'Échéance',
  'receivables.noDueDate': 'Sans échéance',
  'receivables.setDueDate': 'Fixer l’échéance',
  'receivables.dueDate.saved': 'Échéance enregistrée.',
  'receivables.invoiced': 'Facturé',
  'receivables.credited': 'Avoirs',
  'receivables.paid': 'Encaissé',
  'receivables.remaining': 'Reste dû',
  'receivables.allocate': 'Imputer',
  'receivables.advances': 'Avances à imputer',
  'receivables.advances.hint': 'Saisissez les montants à imputer dans le tableau des factures, puis imputez l’avance.',
  'receivables.advance.allocate': 'Imputer cette avance',
  'receivables.advance.allocated': 'Avance imputée.',
  'receivables.payment': 'Encaissement',
  'receivables.payment.new': 'Nouvel encaissement',
  'receivables.payment.hint': 'Les montants saisis dans la colonne « Imputer » disent quelles factures cet encaissement éteint. Ce qui n’est pas imputé reste une avance.',
  'receivables.payment.submit': 'Enregistrer l’encaissement',
  'receivables.payment.recorded': 'Encaissement enregistré.',
  'receivables.payment.cancel': 'Annuler',
  'receivables.payment.cancelled': 'Encaissement annulé.',
  'receivables.paymentDate': 'Date d’encaissement',
  'receivables.method': 'Mode de règlement',
  'receivables.reference': 'Référence',
  'receivables.reference.required': 'Ce mode de règlement exige une référence.',
  'receivables.amount': 'Montant',
  'receivables.allocated': 'Imputé',
  'receivables.notes': 'Note',
  'receivables.payments': 'Encaissements',
  'receivables.payments.empty': 'Aucun encaissement.',
  'receivables.cancelReason': 'Motif d’annulation',
  'receivables.cancelReason.hint': 'Obligatoire pour annuler : chèque impayé, erreur de saisie…',
  'receivables.status.posted': 'Enregistré',
  'receivables.status.cancelled': 'Annulé',
  'references.origin': 'Origine',
  'references.origin.catalog': 'Catalogue',
  'references.origin.own': 'Personnel',
  'references.status.active': 'Actif',
  'references.status.inactive': 'Inactif',
  'references.status.hidden': 'Masqué',
  'references.renamed': 'Renommé',
  'references.search': 'Rechercher dans ce référentiel',
  'references.showInactive': 'Afficher les entrées inactives et masquées',
  'references.empty': 'Aucune entrée dans ce référentiel.',
  'references.fiche': 'Fiche',
  'references.baseName': 'Libellé livré',
  'references.effectiveName': 'Nom affiché',
  'references.arabicLabel': 'Libellé arabe',
  'references.englishLabel': 'Libellé anglais',
  'references.crop': 'Culture',
  'references.maturityDays': 'Précocité (jours)',
  'references.certified': 'Semence certifiée',
  'references.referencesOriginField': 'Provenance',
  'references.activate': 'Activer',
  'references.deactivate': 'Désactiver',
  'references.activated': 'Entrée activée.',
  'references.deactivated': 'Entrée désactivée.',
  'references.overrideTitle': 'Surcharge du catalogue',
  'references.overrideHint': 'La ligne livrée reste identique chez tous les clients : votre libellé s’affiche à la place, sans la remplacer.',
  'references.overrideSave': 'Enregistrer la surcharge',
  'references.overrideSaved': 'Surcharge enregistrée.',
  'references.newEntry': 'Ajouter une entrée',
  'references.create': 'Créer l’entrée',
  'references.creating': 'Création en cours…',
  'references.created': 'Entrée ajoutée à votre référentiel.',

  'stock.balance.insufficient_quantity': 'Stock insuffisant : la sortie dépasse ce que le dépôt porte.',
  'harvest.batch.net_exceeds_gross': 'Le net ne peut pas dépasser le brut : saisie inversée ?',
  'purchase.receipt.over_delivery': 'Sur-livraison au-delà de la tolérance de l’abonnement.',
  'purchase.receipt.order_required': 'Réception sans commande interdite (V16) : créez d’abord une commande d’achat.',
  'purchase.invoice.requires_receipt': 'Facture sans réception interdite : rapprochez d’abord une réception.',
  'purchase.invoice_match.no_counterpart': 'Ligne de facture sans commande ni réception : rien à rapprocher.',
  'purchase.invoice_match.variance_reason_required': 'Écart constaté : un motif est requis pour l’accepter.',
  // Codes frappés par le lot 8b, côté gestionnaires comme côté déclencheurs.
  'purchase.invoice_match.rejection_reason_required': 'Le refus d’une facture se motive.',
  'purchase.invoice_match.no_lines': 'Cette facture n’a aucune ligne à rapprocher.',
  'purchase.invoice_match.quantity_must_be_positive': 'Une ligne facture une quantité nulle ou négative.',
  'purchase.invoice_match.not_found': 'Aucun rapprochement pour cette facture.',
  'purchase.supplier_invoice.not_found': 'Facture fournisseur introuvable.',
  'purchase.supplier_invoice.not_an_invoice': 'Un avoir ne se rapproche pas, et n’a pas d’échéance.',
  'purchase.supplier_invoice.already_decided': 'Cette facture est déjà décidée : elle ne se redécide pas.',
  'purchase.supplier_invoice.due_before_invoice': 'Une facture n’est pas due avant sa date.',
  'purchase.supplier_invoice.text_too_long': 'Le motif d’une décision de facture tient en 300 caractères.',
  'purchase.supplier_invoice.immutable': 'Cette facture n’est plus un brouillon : elle ne se réécrit pas.',
  'purchase.supplier_invoice.match_undecided': 'Cette facture n’a pas de rapprochement décidé en sa faveur : elle ne s’approuve pas.',
  'purchase.supplier_invoice.no_lines': 'Une facture sans ligne ne facture rien.',
  'purchase.supplier_invoice.quantity_must_be_positive': 'Chaque ligne de facture porte une quantité strictement positive.',
  'purchase.supplier_invoice.negative_amount': 'Ni un prix ni une taxe ne sont négatifs : ce qui diminue une facture est un avoir.',
  'purchase.supplier_invoice.duplicate_product': 'Un article ne figure qu’une fois sur une facture : regroupez ses quantités.',
  'purchase.supplier_invoice.date_in_future': 'Une facture ne se date pas dans le futur.',
  'purchase.supplier_invoice.reference_too_long': 'Le numéro de facture du fournisseur tient en 50 caractères.',
  'purchase.supplier_invoice.order_not_found': 'Commande introuvable.',
  'purchase.supplier_invoice.order_of_another_supplier': 'Cette commande a été passée à un autre fournisseur.',
  'purchase.supplier_invoice.nothing_to_invoice': 'Un article facturé n’a rien de reçu sur cette commande qui ne soit déjà facturé.',
  'purchase.payment.amount_must_be_positive': 'Le montant d’un règlement est strictement positif.',
  'purchase.payment.unknown_method': 'Un règlement se fait en espèces, par chèque, virement, traite, carte ou compensation.',
  'purchase.payment.due_date_required': 'Un chèque ou une traite se donne avec son échéance.',
  'purchase.payment.date_in_future': 'Un règlement ne se date pas dans le futur.',
  'purchase.payment.text_too_long': 'La référence d’un règlement tient en 50 caractères, sa note ou son motif d’annulation en 300.',
  'purchase.payment.supplier_not_found': 'Fournisseur introuvable.',
  'purchase.payment.allocation_invalid': 'Une imputation est d’un montant strictement positif.',
  'purchase.payment.allocation_duplicate': 'Une facture ne s’impute qu’une fois par règlement.',
  'purchase.payment.over_allocated': 'Les imputations dépassent ce que le règlement vaut encore.',
  'purchase.payment.invoice_not_payable': 'Une facture désignée n’est pas une facture approuvée de ce fournisseur.',
  'purchase.payment.exceeds_remaining': 'Une imputation dépasse ce que la facture doit encore, avoirs déduits.',
  'purchase.payment.not_found': 'Règlement introuvable.',
  'purchase.payment.cancelled': 'Ce règlement est annulé : il n’éteint plus rien.',
  'purchase.payment.already_cancelled': 'Ce règlement est déjà annulé.',
  'purchase.payment.cancellation_reason_required': 'L’annulation d’un règlement se motive.',
  'purchase.payment.partner_mismatch': 'Le règlement et la facture ne sont pas du même fournisseur.',
  'purchase.payment.allocation_immutable': 'Une imputation ne se modifie ni ne se supprime : c’est le règlement qu’on annule.',
  'purchase.payment.immutable': 'Un règlement ne se réécrit pas : il s’annule.',
  'purchase.payment.already_cleared': 'Ce règlement est déjà débité.',
  'purchase.payment.not_an_instrument': 'Seul un chèque ou une traite se déclare débité.',
  'purchase.payment.cleared_before_payment': 'Un chèque n’est pas débité avant d’être remis.',
  // Codes frappés par le lot 8d, côté gestionnaires comme côté déclencheurs.
  'finance.treasury.account_required': 'Un paiement qui fait bouger de l’argent désigne son compte de banque ou sa caisse.',
  'finance.treasury.account_not_applicable': 'Ce mode de règlement ne fait bouger aucun compte ; il n’en désigne pas.',
  'finance.treasury.account_not_found': 'Compte de trésorerie introuvable.',
  'finance.treasury.account_kind_mismatch': 'Des espèces passent par une caisse ; un chèque, un virement, une traite ou une carte, par une banque.',
  'finance.treasury.account_inactive': 'Ce compte est fermé : il ne reçoit ni ne paie plus rien.',
  'finance.treasury.account_immutable': 'La nature et l’ouverture d’un compte ne se réécrivent pas, ni le compte d’un paiement.',
  'finance.treasury.account_in_use': 'Ce compte a servi : il se ferme, il ne se supprime pas.',
  'finance.treasury.code_required': 'Un compte de trésorerie a un code.',
  'finance.treasury.name_required': 'Un compte de trésorerie a un nom.',
  'finance.treasury.unknown_kind': 'Un compte de trésorerie est une banque ou une caisse.',
  'finance.treasury.opening_balance_required': 'Le solde d’ouverture se saisit, fût-il zéro : tous les soldes du compte en partent.',
  'finance.treasury.cash_cannot_open_negative': 'Une caisse ne s’ouvre pas en négatif.',
  'finance.treasury.opening_in_future': 'Un compte ne s’ouvre pas à une date future.',
  'finance.treasury.code_taken': 'Ce code désigne déjà un compte de trésorerie.',
  'finance.treasury.text_too_long': 'Le code d’un compte tient en 30 caractères, son nom en 120, sa référence bancaire en 60.',
  'finance.treasury.period_invalid': 'Une période ne finit pas avant de commencer.',
  'finance.treasury.nothing_to_assign': 'Ce paiement n’est pas à affecter : inconnu, annulé, ou déjà sur un compte.',
  'finance.treasury.unknown_source': 'Seul un encaissement, un règlement ou un paiement d’échéance s’affecte à un compte.',
  'finance.treasury.unknown_category': 'La nature d’un mouvement de trésorerie est de la liste.',
  'finance.treasury.unknown_direction': 'Un mouvement de trésorerie entre ou sort.',
  'finance.treasury.entry_amount_must_be_positive': 'Le montant d’un mouvement de trésorerie est strictement positif.',
  'finance.treasury.entry_in_future': 'Un mouvement de trésorerie ne se date pas dans le futur.',
  'finance.treasury.label_required': 'Un mouvement de trésorerie se nomme.',
  'finance.treasury.entry_text_too_long': 'Le libellé d’un mouvement tient en 200 caractères, sa référence en 60, son motif d’annulation en 300.',
  'finance.treasury.transfer_target_required': 'Un virement désigne le compte où l’argent entre.',
  'finance.treasury.transfer_same_account': 'Un compte ne se vire pas à lui-même.',
  'finance.treasury.cancellation_reason_required': 'L’annulation d’un mouvement de trésorerie se motive.',
  'finance.treasury.entry_not_found': 'Mouvement de trésorerie introuvable.',
  'finance.treasury.entry_already_cancelled': 'Ce mouvement de trésorerie est déjà annulé.',
  'finance.treasury.entry_immutable': 'Un mouvement de trésorerie ne se réécrit ni ne se supprime : il s’annule, et cela ne se défait pas.',
  'finance.commitment.unknown_category': 'Un engagement est un salaire, un loyer, une assurance, un impôt ou une autre charge.',
  'finance.commitment.label_required': 'Un engagement se nomme.',
  'finance.commitment.text_too_long': 'Le libellé d’un engagement et son tiers tiennent en 200 caractères, son motif d’annulation en 300.',
  'finance.commitment.amount_must_be_positive': 'Le montant d’un engagement est strictement positif.',
  'finance.commitment.due_date_required': 'Un engagement a une échéance.',
  'finance.commitment.invalid_count': 'Une série compte de 1 à 60 échéances.',
  'finance.commitment.unknown_interval': 'Une série se répète chaque mois, chaque trimestre ou chaque année.',
  'finance.commitment.not_found': 'Engagement introuvable.',
  'finance.commitment.not_payable': 'Cet engagement ne doit plus rien : il est soldé, ou annulé.',
  'finance.commitment.payment_must_be_positive': 'Le paiement d’un engagement est strictement positif.',
  'finance.commitment.exceeds_remaining': 'Ce paiement dépasse ce que l’engagement doit encore.',
  'finance.commitment.payment_mismatch': 'Le paiement d’un engagement sort d’un compte, et il est de sa nature.',
  'finance.commitment.cancellation_reason_required': 'L’annulation d’un engagement se motive.',
  'finance.commitment.already_cancelled': 'Cet engagement est déjà annulé.',
  'finance.commitment.immutable': 'Un engagement ne se réécrit ni ne se supprime : il s’annule, et cela ne se défait pas.',
  'finance.reconciliation.account_not_bank': 'Ce compte est une caisse : une caisse se compte, elle ne se rapproche pas.',
  'finance.reconciliation.statement_date_required': 'Un relevé bancaire a une date : le jour où il s’arrête.',
  'finance.reconciliation.statement_in_future': 'Un relevé bancaire ne se date pas dans le futur.',
  'finance.reconciliation.balance_required': 'Le solde initial et le solde final se saisissent, fussent-ils zéro : l’écart en dépend.',
  'finance.reconciliation.reference_too_long': 'La référence d’un relevé tient en 60 caractères.',
  'finance.reconciliation.statement_exists': 'Ce compte a déjà un relevé arrêté à cette date.',
  'finance.reconciliation.statement_not_found': 'Relevé bancaire introuvable.',
  'finance.reconciliation.statement_has_marks': 'Des mouvements sont pointés sur ce relevé : dépointez-les avant de le supprimer.',
  'finance.reconciliation.statement_immutable': 'Un relevé ne change pas de compte : il se supprime et se ressaisit.',
  'finance.reconciliation.movement_not_found': 'Cette pièce n’est pas un mouvement du compte de ce relevé.',
  'finance.reconciliation.already_pointed': 'Ce mouvement est déjà pointé sur un relevé.',
  'finance.reconciliation.not_pointed': 'Ce mouvement n’est pas pointé sur ce relevé.',
  'finance.reconciliation.movement_pointed': 'Cette pièce est pointée sur un relevé bancaire : dépointez-la avant de l’annuler.',
  'finance.reconciliation.mark_immutable': 'Un pointage ne se réécrit pas : il se retire et se refait.',
  'purchase.order.not_approved': 'Commande non validée : aucune réception n’est possible.',
  'core.capability_denied': 'Accès refusé : votre rôle ne permet pas cette opération.',
  'operations.operation.not_planned': 'Intervention déjà validée : elle se contre-passe, elle ne se consomme plus.',
  'users.create.admin_only': 'Seul un administrateur crée des utilisateurs.',

  'farms.detail.title': 'Fiche exploitation',
  'farms.detail.back': 'Retour à la liste',
  'farms.create.title': 'Nouvelle exploitation',
  'farms.create.submit': 'Créer',
  'farms.create.creating': 'Création…',
  'farms.firstPlot.title': 'Première parcelle',
  'farms.firstPlot.hint':
    "Une exploitation n'est active qu'avec au moins une parcelle. Laissée vide, elle est enregistrée comme brouillon et n'apparaît que si vous affichez les inactives.",
  'farms.firstPlot.code': 'Code de la parcelle',
  'farms.firstPlot.name': 'Nom de la parcelle',
  'farms.firstPlot.area': 'Superficie cadastrale (ha)',
  'farms.firstPlot.irrigable': 'Parcelle irrigable',
  'farms.firstPlot.incomplete': 'Pour créer la première parcelle, son code et sa superficie sont nécessaires.',
  'farms.description': 'Description',
  'farms.owner': 'Propriétaire du foncier',
  'farms.address': 'Adresse',
  'farms.draft': 'Brouillon',
  'farms.created': 'Exploitation créée.',
  'farms.location': 'Localisation',
  'farms.latitude': 'Latitude',
  'farms.longitude': 'Longitude',
  'farms.map.open': 'Ouvrir dans OpenStreetMap',
  'farms.fields.codeHint': 'Code unique, majuscules.',
  'common.save': 'Enregistrer',
  'common.create': 'Créer',
  'common.cancel': 'Annuler',
  'common.details': 'Détails',
  'common.actions': 'Actions',

  'plots.detail.title': 'Fiche parcelle',
  'plots.detail.back': 'Retour à la liste',
  'plots.create.title': 'Nouvelle parcelle',
  'plots.create.submit': 'Créer la parcelle',
  'plots.create.creating': 'Création…',
  'plots.created': 'Parcelle créée.',
  'plots.fields.farm': 'Exploitation',
  'plots.fields.soilDepth': 'Profondeur de sol (cm)',
  'plots.fields.slope': 'Pente moyenne (%)',
  'plots.fields.parent': 'Parcelle mère',
  'plots.yields.title': 'Rendements de la campagne',
  'plots.yields.brut': 'Rendement brut',
  'plots.yields.net': 'Rendement net',
  'plots.yields.normalized': 'Rendement normalisé (14%)',
  'plots.yields.perHa': 'Rendement à l’hectare',
  'plots.yields.comparison': 'Comparaison',
  'plots.yields.farmAvg': 'Moyenne exploitation',
  'plots.yields.seasonAvg': 'Moyenne campagne',
  'plots.yields.deviation': 'Écart',
  'plots.location.title': 'Localisation',
  'plots.map.view': 'Voir la parcelle sur la carte',
  'plots.mapLabel': 'Carte de localisation de la parcelle',
  'plots.mapHint': 'Cliquer sur la carte pour placer la parcelle ; les champs latitude et longitude restent modifiables au clavier pour un relevé de terrain précis.',
  'plots.pickOnMap': 'Choisir sur la carte',
  'plots.latitude': 'Latitude',
  'plots.longitude': 'Longitude',

  'campaigns.title': 'Campagnes',
  'campaigns.description': 'Chaque campagne porte un code, un nom, un groupe normalisé et ses propres dates ; elle se rattache toujours à une parcelle.',
  'campaigns.list': 'Liste des campagnes',
  'campaigns.code': 'Code',
  'campaigns.name': 'Nom',
  'campaigns.period': 'Période',
  'campaigns.plot': 'Parcelle',
  'campaigns.group': 'Groupe',
  'campaigns.invalidPeriod': 'La fin de la campagne doit venir après son début.',
  'campaigns.outsidePeriod': 'Les dates de la campagne doivent tenir dans celles de la période :',
  'campaigns.status': 'État',
  'campaigns.status.planned': 'Planifiée',
  'campaigns.status.sown': 'Semée',
  'campaigns.status.growing': 'En croissance',
  'campaigns.status.harvested': 'Récoltée',
  'campaigns.status.closed': 'Clôturée',
  'campaigns.status.abandoned': 'Abandonnée',
  'campaigns.current': 'En cours',
  'campaigns.closed': 'Clôturée',
  'campaigns.empty': 'Aucune campagne.',
  'campaigns.create.title': 'Nouvelle campagne',
  'campaigns.create.submit': 'Créer la campagne',
  'campaigns.created': 'Campagne créée.',
  'campaigns.plan.title': 'Plan d’assolement',
  'campaigns.plan.empty': 'Aucune parcelle-campagne pour ce filtre.',
  'campaigns.plan.assign': 'Affecter une culture',
  'campaigns.plan.crop': 'Culture',
  'campaigns.plan.sownArea': 'Surface semée (ha)',
  'campaigns.plan.assigned': 'Assolement enregistré.',

  'campaignGroups.title': 'Groupes de campagnes',
  'campaignGroups.description': 'Le nom normalisé qui évite que « Tomate », « tomates » et « TOMATE » ne se dispersent en trois groupes distincts.',
  'campaignGroups.empty': 'Aucun groupe de campagnes.',

  'normCategories.title': 'Catégories de normes',
  'normCategories.description': 'Le classement des normes — qualité, phytosanitaire, agriculture, environnement, sécurité, production — commun à toute l’entreprise.',
  'normCategories.empty': 'Aucune catégorie de normes.',

  'norms.title': 'Normes',
  'norms.description': 'Une norme est une comparaison évaluable — seuils, opérateurs, unité facultative — jamais une phrase : l’expression affichée est composée par la base, jamais saisie.',
  'norms.empty': 'Aucune norme.',
  'norms.name': 'Nom',
  'norms.category': 'Catégorie',
  'norms.parameter': 'Paramètre observé',
  'norms.kind': 'Forme',
  'norms.minValue': 'Seuil minimum',
  'norms.maxValue': 'Seuil maximum',
  'norms.exactValue': 'Valeur exacte',
  'norms.tolerance': 'Tolérance',
  'norms.inclusive': 'Bornes incluses',
  'norms.expression': 'Expression',
  'norms.expressionHint': 'Composée automatiquement à partir des seuils : elle ne se saisit pas.',
  'norms.create.title': 'Nouvelle norme',
  'norms.create.submit': 'Créer la norme',
  'norms.created': 'Norme créée.',

  'normKinds.minimum': 'Minimum',
  'normKinds.maximum': 'Maximum',
  'normKinds.range': 'Plage',
  'normKinds.exact': 'Valeur exacte',
  'normKinds.qualitative': 'Qualitative',

  'verdicts.compliant': 'Conforme',
  'verdicts.non_compliant': 'Non conforme',
  'verdicts.not_measured': 'Non mesuré',
  'verdicts.not_applicable': 'Sans objet',

  'campaignNorms.title': 'Normes appliquées',
  'campaignNorms.empty': 'Aucune norme appliquée à cette campagne.',
  'campaignNorms.norm': 'Norme',
  'campaignNorms.observedValue': 'Valeur observée',
  'campaignNorms.appliedOn': "Date d'application",
  'campaignNorms.comment': 'Commentaire',
  'campaignNorms.verdict': 'Verdict',
  'campaignNorms.apply.title': 'Appliquer une norme',
  'campaignNorms.apply.submit': 'Appliquer',
  'campaignNorms.applied': 'Norme appliquée.',
  'campaignNorms.remove': 'Retirer',
  'campaignNorms.removeTitle': 'Retirer cette norme',
  'campaignNorms.removeMessage': 'La norme ne sera plus appliquée à cette campagne. La mesure déjà enregistrée n’apparaîtra plus.',
  'campaignNorms.removed': 'Norme retirée.',

  'purchases.title': 'Commandes d’achat',
  'purchases.description': 'Commande → réception sans quitter l’écran : le reste à recevoir se met à jour à chaque ligne.',
  'purchases.list.empty': 'Aucune commande.',
  'purchases.search': 'Rechercher',
  'purchases.new': 'Nouvelle commande',
  'purchases.code': 'Numéro',
  'purchases.partner': 'Fournisseur',
  'purchases.status': 'Statut',
  'purchases.status.draft': 'Brouillon',
  'purchases.status.approved': 'Validée',
  'purchases.status.received': 'Réceptionnée',
  'purchases.status.cancelled': 'Annulée',
  'purchases.total': 'Total HT',
  'purchases.detail.title': 'Fiche commande',
  'purchases.detail.back': 'Retour',
  'purchases.lines': 'Lignes',
  'purchases.lines.product': 'Article',
  'purchases.lines.quantity': 'Quantité',
  'purchases.lines.unitPrice': 'Prix unitaire',
  'purchases.lines.received': 'Reçu',
  'purchases.lines.remaining': 'Reste à recevoir',
  'purchases.lines.total': 'Total ligne',
  'purchases.approve': 'Valider la commande',
  'purchases.approving': 'Validation…',
  'purchases.approved': 'Commande validée.',
  'purchases.cancel': 'Annuler la commande',
  'purchases.cancelReason': 'Motif d’annulation',
  'purchases.cancelled': 'Commande annulée.',
  'purchases.receive.title': 'Réception',
  'purchases.receive.warehouse': 'Dépôt de réception',
  'purchases.receive.quantityArrived': 'Quantité arrivée',
  'purchases.receive.rejected': 'Quantité refusée',
  'purchases.receive.submit': 'Enregistrer la réception',
  'purchases.receive.receiving': 'Réception…',
  'purchases.received': 'Réception enregistrée.',
  'purchases.update': 'Enregistrer l’en-tête',
  'purchases.updated': 'Commande mise à jour.',
  'purchases.create.title': 'Nouvelle commande',
  'purchases.create.submit': 'Créer la commande',
  'purchases.created': 'Commande créée.',

  'dashboard.title': 'Tableau de bord',
  'dashboard.description': 'Indicateurs de campagne, coûts par poste, marges et comparaison entre parcelles.',
  'dashboard.filters': 'Filtres',
  'dashboard.season': 'Campagne',
  'dashboard.farm': 'Exploitation',
  'dashboard.level': 'Niveau',
  'dashboard.level.plot': 'Parcelle',
  'dashboard.level.farm': 'Exploitation',
  'dashboard.level.crop': 'Culture',
  'dashboard.level.season': 'Campagne',
  'dashboard.loading': 'Chargement…',
  'dashboard.kpis.totalArea': 'Surface totale (ha)',
  'dashboard.kpis.directCost': 'Coût direct',
  'dashboard.kpis.netCost': 'Coût net',
  'dashboard.kpis.revenue': 'Produit',
  'dashboard.kpis.margin': 'Marge',
  'dashboard.kpis.marginRate': 'Taux de marge',
  'dashboard.kpis.avgCostPerHa': 'Coût moyen / ha',
  'dashboard.kpis.avgMarginPerHa': 'Marge / ha',
  'dashboard.kpis.yieldTotal': 'Récolte totale (q)',
  'dashboard.costByPost': 'Coûts par poste',
  'dashboard.costByPost.input': 'Intrants',
  'dashboard.costByPost.machine': 'Machines',
  'dashboard.costByPost.labor': 'Main-d’œuvre',
  'dashboard.costByPost.other': 'Autres',
  'dashboard.comparison': 'Comparaison entre parcelles',
  'dashboard.comparison.empty': 'Aucune donnée à comparer.',
  'dashboard.table.label': 'Libellé',
  'dashboard.table.area': 'Surface',
  'dashboard.table.cost': 'Coût net',
  'dashboard.table.margin': 'Marge',
  'dashboard.table.yield': 'Rendement',
  'dashboard.refresh': 'Actualiser',

  'exports.title': 'Exports',
  'exports.description': 'Export complet réversible (§16.5) : CSV UTF-8 + manifeste, rechargeable sur base vierge.',
  'exports.download': 'Télécharger l’export ZIP',
  'exports.downloading': 'Génération…',
  'exports.downloaded': 'Export téléchargé.',
  'exports.manifest': 'Aperçu du manifeste',


  // --- Plan d'assolement (§7.3) ---
  'assolement.title': "Plan d'assolement",
  'assolement.description': "Affectez une culture à plusieurs parcelles d'un seul geste.",
  'assolement.season': 'Campagne',
  'assolement.farm': 'Exploitation',
  'assolement.allFarms': 'Toutes les exploitations',
  'assolement.crop': 'Culture',
  'assolement.variety': 'Variété',
  'assolement.unassigned': 'Parcelles libres sur cette campagne',
  'assolement.empty': 'Aucune parcelle libre : tout est déjà affecté sur cette campagne.',
  'assolement.selectAll': 'Tout sélectionner',
  'assolement.clearAll': 'Tout désélectionner',
  'assolement.selected': 'sélectionnée(s)',
  'assolement.area': 'Superficie semée (ha)',
  'assolement.areaHint': 'Laissé vide : chaque parcelle est semée sur sa propre superficie.',
  'assolement.assign': 'Affecter la culture',
  'assolement.assigning': 'Affectation en cours…',
  'assolement.chooseSeason': "Choisir une campagne pour voir les parcelles libres.",
  'assolement.chooseCrop': 'Choisir une culture avant d’affecter.',
  'assolement.result': 'Résultat',
  'assolement.result.succeeded': 'affectée(s)',
  'assolement.result.failed': 'refusée(s)',
  'assolement.result.area': 'hectares affectés',
  'assolement.irrigable': 'Irrigable',

  // --- Reprise des données (L12-04) ---
  'reprise.title': 'Reprise des données',
  'reprise.description': 'Récupérer, nettoyer, charger, contrôler — le client retrouve ses données.',
  'reprise.kind': 'Nature',
  'reprise.kind.plot': 'Parcelles',
  'reprise.kind.product': 'Articles',
  'reprise.kind.partner': 'Tiers',
  'reprise.file': 'Fichier CSV',
  'reprise.template': 'Colonnes attendues',
  'reprise.analyse': '1. Analyser (sans écrire)',
  'reprise.analysing': 'Analyse en cours…',
  'reprise.load': '2. Charger les lignes retenues',
  'reprise.loading': 'Chargement en cours…',
  'reprise.rowCount': 'Lignes lues',
  'reprise.accepted': 'Lignes retenues',
  'reprise.loaded': 'Lignes chargées',
  'reprise.rejected': 'Lignes refusées',
  'reprise.duplicates': 'Doublons',
  'reprise.orphans': 'Orphelines',
  'reprise.issues': 'Ce qui a été refusé',
  'reprise.line': 'Ligne',
  'reprise.history': 'Reprises précédentes',
  'reprise.empty': 'Aucune reprise enregistrée.',
  'reprise.noFile': 'Choisir un fichier avant d’analyser.',
  'reprise.status.analysed': 'Analysé',
  'reprise.status.loaded': 'Chargé',
  'reprise.status.failed': 'Échec',

  // --- Utilisateurs et portées (§4.3) ---
  'users.title': 'Utilisateurs',
  'users.description': 'Comptes, rôles et portées. Seul un administrateur en crée.',
  'users.create': 'Créer un utilisateur',
  'users.email': 'Adresse électronique',
  'users.fullName': 'Nom complet',
  'users.role': 'Rôle',
  'users.password': 'Mot de passe',
  'users.active': 'Actif',
  'users.inactive': 'Désactivé',
  'users.list': 'Liste',
  'users.empty': 'Aucun utilisateur.',
  'users.created': 'Utilisateur créé.',
  'users.updated': 'Modifications enregistrées.',
  'users.detail': 'Fiche utilisateur',
  'users.lastSignIn': 'Dernière connexion',
  'users.never': 'Jamais',
  'users.locale': 'Langue',
  'users.scopes': 'Portées',
  'users.scopes.farms': 'Exploitations',
  'users.scopes.activities': 'Activités',
  'users.scopes.all': 'Aucune portée posée : accès à tout ce que l’entreprise possède.',
  'users.scopes.restricted': 'Accès restreint aux exploitations cochées.',
  'users.scopes.save': 'Enregistrer les portées',
  'users.scopes.saved': 'Portées enregistrées.',
  'users.capabilities': 'Ce que ce rôle permet',
  'users.role.admin': 'Administrateur',
  'users.role.manager': 'Responsable d’exploitation',
  'users.role.accountant': 'Comptable',
  'users.role.operator': 'Opérateur',
  'users.role.viewer': 'Lecteur',

  // --- Profil ---
  'profile.title': 'Mon profil',
  'profile.description': 'Vos informations, votre rôle et vos droits.',
  'profile.organization': 'Entreprise',
  'profile.capabilities': 'Vos droits',
  'profile.capabilities.hint': 'Appliqués par le serveur : une route interdite répond 403, jamais des montants tronqués.',
  'profile.granted': 'accordé',
  'profile.denied': 'refusé',
  'profile.password.title': 'Changer mon mot de passe',
  'profile.password.current': 'Mot de passe actuel',
  'profile.password.new': 'Nouveau mot de passe',
  'profile.password.confirm': 'Confirmer le nouveau mot de passe',
  'profile.password.rule': 'Au moins dix caractères.',
  'profile.password.submit': 'Changer le mot de passe',
  'profile.password.pending': 'Changement…',
  'profile.password.mismatch': 'Les deux saisies ne correspondent pas.',
  'profile.password.other_sessions': 'Vos autres sessions ne pourront plus se prolonger et devront se reconnecter ; un appareil déjà ouvert peut le rester jusqu’à l’expiration de sa session en cours. Celle-ci reste ouverte.',
  'profile.password.changed': 'Mot de passe changé. Vos autres sessions ne peuvent plus se prolonger ; un appareil déjà ouvert peut le rester jusqu’à l’expiration de sa session en cours.',
  'capability.view_costs': 'Voir les coûts, les prix et les marges',
  'capability.record_work': 'Saisir interventions, récoltes et mouvements de stock',
  'capability.manage_documents': 'Saisir et valider les documents d’achat et de vente',
  'capability.manage_master_data': 'Gérer les référentiels, exploitations et parcelles',
  'capability.administer': 'Gérer les utilisateurs, les rôles et l’abonnement',

  // --- Avoirs fournisseur (L4-11) ---
  'nav.invoices': 'Factures de vente',
  'invoices.description': 'Factures de vente et avoirs. Un avoir corrige par des lignes négatives, il ne réécrit jamais l’original.',
  // Migration 0101 : émettre une facture client depuis ce qui est livré et pas encore facturé.
  'invoicing.title': 'Émettre une facture',
  'invoicing.hint': 'Une facture reprend des bons de livraison livrés, que n’a repris aucune facture. Cochez ceux de ce client qu’elle reprend ; la taxe, les totaux et le numéro sont posés à l’émission.',
  'invoicing.customer': 'Client',
  'invoicing.nothing': 'Rien de livré à ce client qui ne soit déjà facturé.',
  'invoicing.pick': 'Facturer',
  'invoicing.deliveryDate': 'Date de livraison',
  'invoicing.lines': 'Articles livrés',
  'invoicing.total': 'Reste à facturer, hors taxes',
  'invoicing.date': 'Date de la facture',
  'invoicing.dueDate': 'Échéance convenue',
  'invoicing.dueDate.hint': 'Facultative : sans elle, la facture est « sans échéance ».',
  'invoicing.taxRate': 'Taux de taxe',
  'invoicing.taxRate.none': 'Celui des livraisons',
  'invoicing.notes': 'Note',
  'invoicing.submit': 'Émettre la facture',
  'invoicing.created': 'Facture émise :',
  'invoices.empty': 'Aucune facture de vente.',
  'invoices.lineId': 'Ligne facturée',
  'invoices.creditHint': 'L’avoir désigne la ligne d’origine et son motif : c’est ce qui rend le rapprochement possible six mois plus tard.',
  'credit.title': 'Avoir fournisseur',
  'credit.reason': 'Motif',
  'credit.reason.return': 'Retour de marchandise',
  'credit.reason.damaged': 'Marchandise abîmée',
  'credit.reason.billing_error': 'Erreur de facturation',
  'credit.reason.rebate': 'Remise accordée après coup',
  'credit.warehouse': 'Dépôt d’où la marchandise repart',
  'credit.warehouseHint': 'Exigé pour un retour ou une marchandise abîmée : eux seuls sortent du stock.',
  'credit.lines': 'Lignes de l’avoir',
  'credit.create': 'Émettre l’avoir',
  'credit.created': 'Avoir émis.',
  'credit.movesStock': 'La marchandise est sortie du stock.',
  'credit.valueOnly': 'Aucun mouvement de stock : seule la valeur change.',
  'invoices.kind': 'Nature',
  'invoices.kind.invoice': 'Facture',
  'invoices.kind.credit_note': 'Avoir',
  'invoices.paid': 'Réglé',
  'matching.title': 'Rapprochement à trois documents',
  'matching.product': 'Article',
  'matching.ordered': 'Commandé',
  'matching.received': 'Reçu',
  'matching.invoiced': 'Facturé',
  'matching.variance': 'Écart',
  'matching.empty': 'Aucun rapprochement pour cette facture.',
  // Lot 8b : la décision qui fait d'une facture fournisseur une dette, ou un litige.
  'matching.status.pending': 'En attente de décision',
  'matching.status.matched': 'Concordant',
  'matching.status.variance_accepted': 'Écart accepté',
  'matching.status.rejected': 'Refusé',
  'matching.quantityVariance': 'Écart de quantité',
  'matching.valueVariance': 'Écart de prix',
  'matching.reason': 'Motif',
  'invoices.status.draft': 'À décider',
  'invoices.status.approved': 'Approuvée',
  'invoices.status.disputed': 'En litige',
  'invoices.payablesHint': 'Ce qui est dû et ce qui se règle se tiennent sur l’écran :',
  'invoices.decision.title': 'Décider cette facture',
  'invoices.decision.clean': 'Tout concorde avec la réception : la facture peut être approuvée.',
  'invoices.decision.variance': 'Un écart est constaté avec la réception : l’approuver demande un motif écrit.',
  'invoices.decision.blocked': 'Cette facture ne peut pas être approuvée, seulement refusée :',
  'invoices.decision.dueDate': 'Échéance de la facture',
  'invoices.decision.dueDate.hint': 'Facultative : elle pourra être fixée ou déplacée plus tard.',
  'invoices.decision.varianceReason': 'Motif de l’écart accepté',
  'invoices.decision.approve': 'Approuver la facture',
  'invoices.decision.approved': 'Facture approuvée : c’est désormais une dette.',
  'invoices.decision.rejectReason': 'Motif du refus',
  'invoices.decision.rejectReason.hint': 'Obligatoire pour refuser : la facture partira en litige.',
  'invoices.decision.reject': 'Refuser la facture',
  'invoices.decision.rejected': 'Facture refusée : elle est en litige.',
  // Migration 0100 : saisir une facture fournisseur depuis ce qui est reçu et pas encore facturé.
  'invoices.entry.title': 'Saisir une facture fournisseur',
  'invoices.entry.hint': 'Une facture se saisit depuis ce qui est reçu et pas encore facturé. Les quantités et les prix proposés sont ceux de la réception : corrigez-les d’après la facture du fournisseur — l’écart, s’il y en a un, se constatera à l’approbation.',
  'invoices.entry.supplier': 'Fournisseur',
  'invoices.entry.order': 'Commande reçue',
  'invoices.entry.noOrder': 'Rien de reçu chez ce fournisseur qui ne soit déjà facturé.',
  'invoices.entry.product': 'Article',
  'invoices.entry.received': 'Reçu, pas encore facturé',
  'invoices.entry.receiptPrice': 'Prix de réception',
  'invoices.entry.quantity': 'Quantité facturée',
  'invoices.entry.unitPrice': 'Prix facturé',
  'invoices.entry.lineHint': 'Videz la quantité d’un article que cette facture ne porte pas.',
  'invoices.entry.reference': 'Numéro de facture du fournisseur',
  'invoices.entry.date': 'Date de la facture',
  'invoices.entry.dueDate': 'Échéance convenue',
  'invoices.entry.dueDate.hint': 'Facultative : elle peut aussi se donner à l’approbation.',
  'invoices.entry.tax': 'Montant de la taxe',
  'invoices.entry.submit': 'Enregistrer la facture',
  'invoices.entry.created': 'Facture enregistrée, en attente de décision :',

  // --- Articles ---
  'products.title': 'Articles',
  'products.description': 'Fiche article, méthode de valorisation et historique de prix.',
  'products.search': 'Rechercher un article',
  'products.code': 'Code',
  'products.name': 'Désignation',
  'products.type': 'Famille',
  'products.unit': 'Unité',
  'products.valuation': 'Méthode de valorisation',
  'products.valuation.weighted_average': 'Coût moyen pondéré',
  'products.valuation.fifo': 'FIFO par lot',
  'products.valuation.lifo': 'LIFO — dernier entré, premier sorti',
  'products.valuation.standard': 'Coût standard',
  'products.valuation.last_purchase': 'Dernier prix d’achat',
  'products.valuationHint': 'Posée à la création (V20 : moyen pondéré par défaut).',
  'products.standardCost': 'Coût standard',
  'products.stockManaged': 'Géré en stock',
  'products.lotTracked': 'Suivi en lot',
  'products.trackingFromFamily': 'Le suivi vient de la famille choisie.',
  'products.outOfStockFamily': 'Famille hors stock : ni valorisation, ni suivi en lot.',
  'products.create': 'Créer un article',
  'products.created': 'Article créé.',
  'products.empty': 'Aucun article.',
  'products.detail': 'Fiche article',
  'products.prices': 'Historique des prix',
  'products.prices.partner': 'Tiers',
  'products.prices.price': 'Prix unitaire',
  'products.prices.from': 'À partir du',
  'products.prices.to': 'Jusqu’au',
  'products.prices.add': 'Ajouter un prix',
  'products.prices.empty': 'Aucun prix enregistré.',

  // --- Récolte ---
  'harvests.title': 'Récolte',
  'harvests.description': 'Lots récoltés, poids brut et net, et valorisation de l’invendu.',
  'harvests.plotSeason': 'Parcelle-campagne',
  'harvests.product': 'Article récolté',
  'harvests.gross': 'Poids brut',
  'harvests.net': 'Poids net',
  'harvests.moisture': 'Humidité (%)',
  'harvests.date': 'Date de récolte',
  'harvests.normalized': 'Poids normalisé',
  'harvests.create': 'Enregistrer le lot',
  'harvests.created': 'Lot enregistré.',
  'harvests.list': 'Lots récoltés',
  'harvests.empty': 'Aucun lot de récolte.',
  // Lot Q1b (migration 0086) : la récolte entre en stock.
  'harvests.stock': 'Stock',
  'harvests.stock.warehouse': 'Dépôt de mise en stock',
  'harvests.stock.warehouseHint': 'Facultatif : sans dépôt, la récolte est pesée et attend sa mise en stock. Sert aussi au bouton « Mettre en stock » de la liste.',
  'harvests.stock.later': 'Plus tard',
  'harvests.stock.enter': 'Mettre en stock',
  'harvests.stock.exempt': 'Antérieure au stock',
  'harvests.stock.unvalued': 'Non valorisée',
  'harvests.stock.warehouseRequired': 'Choisissez d’abord un dépôt de mise en stock.',
  'harvests.stock.done': 'Récolte mise en stock : son lot attend le contrôle qualité.',
  'harvests.stock.doneUnvalued': 'Récolte mise en stock en quantité seule : l’article n’a pas de coût standard.',
  'harvest.batch.not_found': 'Récolte introuvable.',
  'harvest.batch.already_stocked': 'Cette récolte est déjà en stock.',
  'harvest.batch.stock_exempt': 'Cette récolte est antérieure à la mise en stock des récoltes : elle se livre sans stock.',
  'harvest.batch.product_not_stock_managed': 'Cet article n’est pas géré en stock : sa récolte n’a pas de stock où entrer.',
  'harvest.batch.unit_mismatch': 'L’unité ne se convertit pas dans l’unité de l’article.',
  'harvest.batch.stock_fields_are_final': 'Cette récolte est déjà en stock : son dépôt, son lot et sa valeur d’entrée ne se réécrivent pas.',
  'harvest.batch.stock_exempt_is_final': 'L’exemption de stock d’une récolte ne se pose ni ne se retire.',
  'harvest.batch.not_readable': 'La récolte a été mise en stock mais ne se relit pas.',
  'sales.delivery_note.harvest_not_in_stock': 'Cette récolte n’est pas encore entrée en stock : mettez-la en stock avant de la livrer.',
  'sales.delivery_note.harvest_not_released': 'Le lot de cette récolte n’est pas libéré : une décision qualité est requise avant toute livraison.',
  'stock.balance.not_found': 'Aucun stock de cet article dans ce dépôt.',
  'stock.movement.inactive_warehouse': 'Un dépôt désactivé ne peut pas recevoir de marchandise.',
  'stock.warehouse.not_found': 'Dépôt introuvable.',
  'harvests.valuation': 'Valorisation de l’invendu (V3)',
  'harvests.valuation.unsold': 'Quantité invendue',
  'harvests.valuation.avgPrice': 'Prix moyen de vente',
  'harvests.valuation.stockValue': 'Valeur du stock',
  'harvests.valuation.load': 'Calculer',
  'harvest.valuation.no_sale_price': 'Aucune vente sur la campagne : le prix moyen n’existe pas encore, et le stock ne peut pas être valorisé sans lui.',
  'harvest.valuation.no_harvest': 'Aucune récolte enregistrée : il n’y a pas de stock à valoriser.',

  // --- Subventions (V7) ---
  'subsidies.title': 'Subventions',
  'subsidies.description': 'Aides reçues, et leur répartition au prorata des surfaces éligibles.',
  'subsidies.code': 'Code',
  'subsidies.name': 'Intitulé',
  'subsidies.amount': 'Montant accordé',
  'subsidies.type': 'Nature',
  'subsidies.status': 'Statut',
  'subsidies.allocated': 'Déjà réparti',
  'subsidies.remaining': 'Reste à répartir',
  'subsidies.create': 'Saisir une subvention',
  'subsidies.created': 'Subvention enregistrée.',
  'subsidies.allocate': 'Répartir',
  'subsidies.allocate.basis': 'Base de répartition',
  'subsidies.allocate.area': 'Au prorata des surfaces éligibles',
  'subsidies.allocate.equal': 'À parts égales',
  'subsidies.allocate.run': 'Lancer la répartition',
  'subsidies.allocate.done': 'Répartition enregistrée.',
  'subsidies.allocate.hint': 'Relancer une répartition remplace la précédente ; elle ne s’y ajoute pas.',
  'subsidies.allocations': 'Parts réparties',
  'subsidies.allocations.plot': 'Parcelle',
  'subsidies.allocations.area': 'Surface éligible (ha)',
  'subsidies.allocations.amount': 'Part',
  'subsidies.allocations.empty': 'Aucune répartition enregistrée.',
  'subsidies.empty': 'Aucune subvention.',
  'finance.subsidy.zero_basis': 'Toutes les surfaces éligibles sont nulles : choisir « à parts égales » si c’est ce qui est voulu.',
  'finance.subsidy.no_cost_object': 'Aucune parcelle-campagne éligible pour cette subvention.',

  // --- Champs personnalisés ---
  'customFields.title': 'Champs personnalisés',
  'customFields.description': 'Ajoutez vos propres champs aux fiches, sans développeur.',
  'customFields.entity': 'Fiche',
  'customFields.key': 'Clé',
  'customFields.name': 'Libellé',
  'customFields.type': 'Type',
  'customFields.required': 'Obligatoire',
  'customFields.options': 'Choix possibles (séparés par des virgules)',
  'customFields.create': 'Définir le champ',
  'customFields.created': 'Champ défini.',
  'customFields.empty': 'Aucun champ personnalisé sur cette fiche.',
  'customFields.values': 'Champs personnalisés',
  'customFields.save': 'Enregistrer',
  'customFields.saved': 'Valeurs enregistrées.',
  'customFields.export': 'Exporter en CSV',
  'customFields.type.string': 'Texte',
  'customFields.type.integer': 'Nombre entier',
  'customFields.type.decimal': 'Nombre décimal',
  'customFields.type.boolean': 'Oui / non',
  'customFields.type.date': 'Date',
  'customFields.type.list': 'Liste de choix',
  'meta.custom_field.required': 'Ce champ est obligatoire.',
  'meta.custom_field.not_in_options': 'Cette valeur ne fait pas partie des choix proposés.',
  'meta.custom_field.invalid_value': 'La valeur ne correspond pas au type attendu.',
  'meta.custom_field.above_maximum': 'La valeur dépasse le maximum autorisé.',
  'meta.custom_field.below_minimum': 'La valeur est inférieure au minimum autorisé.',

  // --- Cahier de plaine et alertes (§10.3) ---
  'fieldbook.title': 'Cahier de plaine',
  'fieldbook.date': 'Date',
  'fieldbook.work': 'Travail',
  'fieldbook.products': 'Intrants',
  'fieldbook.area': 'Surface (ha)',
  'fieldbook.warnings': 'Alertes',
  'fieldbook.empty': 'Aucune intervention enregistrée sur cette parcelle-campagne.',
  'fieldbook.noWarning': 'Aucune alerte',
  'ops.warning.dose_exceeded': 'Dose supérieure à celle de la fiche produit.',
  'ops.warning.pre_harvest_interval': 'Délai avant récolte : la récolte prévue tombe trop tôt.',
  'ops.warning.nitrogen_cumulative': 'Azote cumulé au-delà du plafond de la campagne.',
  'ops.warning.cost_anomaly': 'Coût très au-dessus des interventions comparables.',

  // --- Rapports (§15.1) ---
  'reports.title': 'Rapports',
  'reports.description': 'Cinq classeurs Excel, aux chiffres identiques à ceux des écrans.',
  'reports.download': 'Télécharger',
  'reports.language': 'Langue du rapport',
  'reports.hint': 'Les en-têtes sont traduits et les nombres restent des nombres : une colonne se somme telle quelle.',
  'report.couts-detailles': 'Coûts détaillés',
  'report.stocks': 'Stocks',
  'report.achats': 'Achats',
  'report.ventes': 'Ventes',
  'report.marges': 'Marges',

  'nav.stockJournal': 'Journal du stock',
  'nav.stockBalances': 'Soldes de stock',
  'nav.seasons': 'Cycle des campagnes',

  'common.search': 'Rechercher',
  'common.searchPlaceholder': 'Tapez pour filtrer…',
  'common.showInactive': 'Afficher les inactifs',
  'common.confirm': 'Confirmer',
  'common.edit': 'Modifier',
  'common.deactivate': 'Désactiver',
  'common.activate': 'Activer',
  'common.inactive': 'Inactif',

  'auth.too_many_attempts': 'Trop de tentatives depuis cet appareil. Réessayez dans une minute.',
  'dash.cost.inputs': 'Intrants',
  'dash.cost.machinery': 'Mécanisation',
  'dash.cost.labor': 'Main-d’œuvre',
  'dash.cost.other': 'Autres',
  'dash.costBreakdown': 'Répartition des charges',
  'dash.realData': 'données réelles',
  'dash.byNature': 'Par nature',
  'dash.byItem': 'Par poste',
  'dash.noCharges': 'Aucune charge enregistrée',
  'dash.alerts': 'Alertes à traiter',
  'dash.alertCount': 'alerte(s)',
  'dash.noAlerts': 'Aucune alerte',
  'dash.recentOps': 'Dernières interventions',
  'dash.netMarginPerHa': 'Marge nette / ha',
  'dash.totalYield': 'Rendement total',
  'dash.noAggregated': 'Aucune donnée agrégée pour ces filtres. Créez des interventions et des récoltes pour voir les chiffres.',
  'common.noData': 'Aucune donnée',
  'common.noDataToShow': 'Aucune donnée à afficher',
  'common.comingSoon': 'Bientôt disponible',
  'common.comingSoonHint': 'Cette rubrique sera disponible prochainement.',
  'common.total': 'Total',
  'common.date': 'Date',
  'common.work': 'Travail',
  'common.number': 'Numéro',
  'common.cost': 'Coût',
  'common.status': 'Statut',
  'ops.checkAlerts': 'Vérifier alertes §10.3',
  'ops.alertHint': 'L’alerte informe sans empêcher la saisie, et reste visible dans le cahier de plaine',
  'plots.titleFull': 'Parcelles et assolements',
  'plots.countLabel': 'parcelles',
  'plots.subtitle': 'La parcelle est permanente ; l’assolement — parcelle × campagne — est l’objet de coût.',
  'subscription.title': 'Abonnement en cours',
  'subscription.description': 'Plan, quotas et modules souscrits',
  'subscription.modules': 'Modules',
  'subscription.area': 'Surface',
  'subscription.support': 'Au-delà d’un plafond, la création est refusée. Pour changer de formule ou ajouter une option, l’administrateur de l’entreprise envoie une demande depuis cette page.',

  'common.inTotal': 'au total',
  'common.area': 'Superficie',
  'common.crop': 'Culture',
  'common.variety': 'Variété',
  'common.yield': 'Rendement',
  'common.costPerHa': 'Coût/ha',
  'common.marginPerHa': 'Marge/ha',
  'common.action': 'Action',
  'common.label': 'Libellé',
  'common.description': 'Description',
  'common.start': 'Début',
  'common.end': 'Fin',
  'common.filter': 'Filtrer',
  'common.open': 'Ouvrir',
  'common.toCheck': 'À vérifier',
  'common.inProgress': 'En cours',
  'common.closed': 'Clôturé',
  'common.toReview': 'à voir',
  'farms.countLabel': 'exploitations',
  'farms.subtitle': 'Suivi parcellaire et ateliers par exploitation.',
  'farms.new': 'Nouvelle exploitation',
  'farms.list': 'Liste des exploitations',
  'farms.delegation': 'Délégation',
  'farms.workshop': 'Atelier',
  'farms.phone': 'Téléphone',
  'farms.sheet': 'Fiche',
  'farms.codeHint': 'Code unique, majuscules.',
  'farms.areaBreakdown': 'Répartition des surfaces',
  'farms.hectaresPerFarm': 'hectares par exploitation',
  'plots.allFarms': 'Toutes exploitations',
  'plots.allCrops': 'Toutes cultures',
  'plots.allStatuses': 'Tous statuts',
  'plots.previousCrop': 'Précédent',
  'plots.cropBreakdown': 'Répartition par culture',
  'plots.consistency': 'Contrôle de cohérence',
  'plots.rule1': 'La somme des surfaces emblavées d’une parcelle ne peut dépasser sa surface cadastrale.',
  'plots.rule2': 'Le précédent cultural est renseigné automatiquement depuis la campagne antérieure.',
  'plots.rule3': 'Une parcelle inactive ne se travaille plus — alerte RA-02.',
  'season.countLabel': 'campagnes',
  'season.costObjects': 'Objets de coût',
  'season.checks': 'Contrôles avant clôture',
  'season.selectHint': 'Sélectionnez une campagne dans la table pour voir ses contrôles.',
  'season.check.operations': 'Interventions validées',
  'season.check.harvests': 'Récoltes rattachées',
  'season.check.costs': 'Charges réparties',
  'season.check.stock': 'Stocks inventoriés',
  'campaigns.doubleCrop': 'Double culture (V5)',
  'campaigns.doubleCrop.on': 'activée — séquences 1 et 2 autorisées',
  'campaigns.doubleCrop.off': 'désactivée — une seule culture par campagne (séquence 1)',

  'partners.counter': 'Compteur',
  'partners.code': 'Code',
  'partners.name': 'Nom',
  'partners.legalName': 'Raison sociale',
  'partners.taxId': 'Matricule fiscal',
  'partners.secondaryTaxId': 'Identifiant fiscal complémentaire',
  'partners.category': 'Nature',
  'partners.category.company': 'Société',
  'partners.category.individual': 'Personne physique',
  'partners.category.other': 'Autre',
  'partners.firstName': 'Prénom',
  'partners.lastName': 'Nom de famille',
  'partners.address': 'Adresse',
  'partners.city': 'Ville',
  'partners.governorate': 'Gouvernorat',
  'partners.postalCode': 'Code postal',
  'partners.country': 'Pays',
  'partners.phone': 'Téléphone',
  'partners.phoneSecondary': 'Téléphone secondaire',
  'partners.email': 'Courriel',
  'partners.website': 'Site web',
  'partners.contact': 'Contact principal',
  'partners.bankName': 'Banque',
  'partners.bankAccount': 'Compte bancaire',
  'partners.iban': 'IBAN',
  'partners.rib': 'RIB',
  'partners.paymentTermsDays': 'Délai de paiement (jours)',
  'partners.creditLimit': 'Plafond de crédit',
  'partners.notes': 'Notes',
  'partners.empty': 'Aucun tiers ne correspond.',
  'partners.searchHint': 'Compteur, matricule fiscal, code, raison sociale, téléphone ou courriel.',
  'partners.counterHint': 'Généré à la création, sur dix caractères. Il ne se modifie pas.',
  // Partagée entre tiers (fournisseurs, clients) et articles : le code proposé n'est
  // jamais réservé, seul l'index unique en base tranche entre deux saisies concurrentes.
  'referentiels.codeSuggere': 'Proposé à partir du dernier code. Modifiable.',
  'partners.deactivateTitle': 'Désactiver ce tiers',
  'partners.deactivateMessage': "{nom} n'apparaîtra plus dans les listes de saisie. Ses documents passés restent intacts.",
  'customers.title': 'Clients',
  'customers.description': 'Les tiers à qui vous vendez. Le compteur commence par 411.',
  'customers.empty': 'Aucun client ne correspond.',
  'partners.saved': 'Tiers enregistré.',
  'agro.partner.code_required': 'Le code du tiers est obligatoire (30 caractères au plus).',
  'agro.partner.name_required': 'Le nom du tiers est obligatoire (200 caractères au plus).',
  'agro.partner.legal_name_required': 'La raison sociale est obligatoire (200 caractères au plus).',
  'agro.partner.tax_id_required': 'Le matricule fiscal est obligatoire, sauf pour une personne physique.',
  'agro.partner.duplicate_code': 'Un tiers porte déjà ce code.',
  'agro.partner.duplicate_tax_id': 'Un tiers porte déjà ce matricule fiscal.',
  'agro.partner.negative_payment_terms': 'Le délai de paiement ne peut pas être négatif.',
  'agro.partner.negative_credit_limit': 'Le plafond de crédit ne peut pas être négatif.',

  // Référentiels articles (lot 2) : unités, familles, suivi de stock, valorisation.
  // Chaque comportement DIT ce qu'il calcule, pas seulement son sigle — la liste est
  // fermée, elle ne nomme que ce que le stock sait réellement faire (migration 0049).
  'behaviors.none': 'Hors stock',
  'behaviors.quantity': 'Quantité seule',
  'behaviors.lot': 'Lot par lot',
  'behaviors.weighted_average': 'Coût moyen pondéré',
  'behaviors.fifo': 'Premier entré, premier sorti',
  'behaviors.lifo': 'Dernier entré, premier sorti',
  'behaviors.last_purchase': 'Dernier prix d\'achat',
  'behaviors.standard': 'Coût standard',
  'behaviors.hint': 'Ce que la méthode calcule réellement. La liste est fermée.',
  'units.title': 'Unités de mesure',
  'units.description': 'Les unités dans lesquelles un article se compte, s’achète ou se vend.',
  'units.empty': 'Aucune unité ne correspond.',
  'units.symbol': 'Symbole',
  'units.dimension': 'Dimension',
  'units.factorToBase': 'Facteur vers l’unité de base',
  'units.isBase': 'Unité de base',
  // Dimension d'une unité : liste fermée (ck_unit_of_measure_dimension, migration 0049).
  'dimensions.mass': 'Masse',
  'dimensions.volume': 'Volume',
  'dimensions.area': 'Surface',
  'dimensions.length': 'Longueur',
  'dimensions.count': 'Dénombrement',
  'dimensions.time': 'Temps',
  'families.title': 'Familles d’articles',
  'families.description': 'Le regroupement qui porte le mode de suivi de stock et les taux de TVA par défaut d’un article.',
  'families.empty': 'Aucune famille ne correspond.',
  'families.counter': 'Compteur',
  // Nature d'une famille : liste fermée (ck_product_type_family, migration 0049).
  'families.nature': 'Nature',
  'families.nature.input': 'Intrant',
  'families.nature.harvest': 'Récolte',
  'families.nature.service': 'Service',
  'families.nature.asset': 'Immobilisation',
  'families.trackingMode': 'Mode de suivi',
  'families.purchaseTax': 'TVA achat',
  'families.salesTax': 'TVA vente',
  'trackingModes.title': 'Modes de suivi de stock',
  'trackingModes.description': 'Ce que le stock sait réellement faire d’un article : hors stock, en quantité, ou lot par lot.',
  'trackingModes.empty': 'Aucun mode de suivi ne correspond.',
  'trackingModes.behavior': 'Comportement',
  'valuationMethods.title': 'Méthodes de valorisation',
  'valuationMethods.description': 'Le calcul retenu pour évaluer un stock : CUMP, FIFO, LIFO, dernier prix d’achat ou coût standard.',
  'valuationMethods.empty': 'Aucune méthode ne correspond.',
  'valuationMethods.behavior': 'Comportement',
  'references.deactivateTitle': 'Désactiver cette entrée',
  'references.deactivateMessage': '{nom} n’apparaîtra plus dans les listes de saisie. Ce qui l’utilise déjà reste inchangé.',
  'nav.families': 'Familles',
  'nav.trackingModes': 'Suivi de stock',
  'nav.valuationMethods': 'Valorisation',
  'platform.title': 'Panneau plateforme',
  'platform.nav.clients': 'Clients',
  'platform.forbidden': 'Ce panneau est réservé aux administrateurs de la plateforme.',
  'platform.legal_name_required': 'La raison sociale du client est obligatoire.',
  'platform.modules_required': "Choisissez au moins une activité : un client sans activité ouverte ne peut rien faire.",
  'platform.module_unknown': "Cette activité n'existe pas au catalogue.",
  'platform.plan_unknown': "Cette formule n'existe pas, ou n'est plus proposée.",
  'platform.email_taken': "Ce courriel est déjà celui d'un compte. Il est unique pour tout le produit, tous clients confondus.",
  'platform.client_not_found': "Ce client n'existe plus, ou n'est pas administrable.",
  'platform.clients.title': 'Clients',
  'platform.clients.description': "Les entreprises qui utilisent le produit, leur formule et ce qu'elles en font.",
  'platform.clients.list': 'Les clients',
  'platform.clients.legalName': 'Raison sociale',
  'platform.clients.governorate': 'Gouvernorat',
  'platform.clients.plan': 'Formule',
  'platform.clients.farms': 'Exploitations',
  'platform.clients.users': 'Utilisateurs',
  'platform.clients.status': 'Statut',
  'platform.clients.createdAt': 'Créé le',
  'platform.clients.noPlan': 'Aucun abonnement',
  'platform.clients.empty': 'Aucun client pour le moment.',
  'platform.clients.new': 'Nouveau client',
  'platform.clients.new.description': 'Entreprise, premier administrateur et abonnement partent ensemble : les quatre existent, ou aucun.',
  'platform.clients.group.company': "L'entreprise",
  'platform.clients.group.admin': 'Son premier administrateur',
  'platform.clients.group.plan': "Ce qu'elle achète",
  'platform.clients.email': "Courriel de l'entreprise",
  'platform.clients.adminName': "Nom de l'administrateur",
  'platform.clients.adminEmail': "Courriel de l'administrateur",
  'platform.clients.adminPassword': 'Mot de passe initial',
  'platform.clients.passwordWarning': "Ce mot de passe se transmet hors du logiciel, de vive voix. Aucun écran ne permet aujourd'hui de le changer, et aucun courriel de réinitialisation ne part : notez-le avant de valider.",
  'platform.clients.passwordHint': 'Dix caractères au moins.',
  'platform.clients.modules': 'Activités ouvertes',
  'platform.clients.modulesHint': 'Au moins une. Une activité fermée pourra être ouverte plus tard.',
  'platform.clients.created': 'Client créé. Son administrateur peut ouvrir une session immédiatement.',
  'platform.clients.activated': 'Client activé : ses utilisateurs peuvent de nouveau ouvrir une session.',
  'platform.clients.deactivated': 'Client désactivé : plus personne ne peut y ouvrir de session.',
  'platform.clients.activate.title': 'Réactiver ce client ?',
  'platform.clients.activate.message': "Ses utilisateurs pourront de nouveau ouvrir une session. Aucune donnée n'a été perdue pendant la fermeture.",
  'platform.clients.deactivate.title': 'Désactiver ce client ?',
  'platform.clients.deactivate.message': "Personne ne pourra plus ouvrir de session chez ce client, ni son administrateur ni ses ouvriers, tant qu'il reste désactivé. Aucune donnée n'est supprimée.",
  'platform.plan.essentiel': 'Essentiel',
  'platform.plan.exploitation': 'Exploitation',
  'platform.plan.domaine': 'Domaine',
  'platform.plan.conseil': 'Cabinet de conseil',
  'platform.plan.sur_mesure': 'Sur mesure',
  'platform.subscription.trial': "Période d'essai",
  'platform.subscription.active': 'Actif',
  'platform.subscription.past_due': 'Impayé',
  'platform.subscription.suspended': 'Suspendu',
  'platform.subscription.cancelled': 'Résilié',
  'platform.subscription.expired': 'Expiré',
  'platform.client.back': 'Retour aux clients',
  'platform.client.levers.title': 'Deux leviers, et ils ne font pas la même chose',
  'platform.client.levers.organization': "Désactiver l'entreprise ferme la porte : plus personne n'ouvre de session chez ce client, ni son administrateur ni ses ouvriers. Aucune donnée n'est supprimée.",
  'platform.client.levers.subscription': "Suspendre l'abonnement ne ferme aucune session : le client se connecte, lit toutes ses données, et ne peut simplement plus rien créer — ni exploitation, ni parcelle. C'est le levier de l'impayé : on coupe l'ajout, pas l'accès.",
  'platform.client.company': "L'entreprise",
  'platform.client.orgType': 'Nature',
  'platform.client.taxId': 'Matricule fiscal',
  'platform.client.city': 'Ville',
  'platform.client.phone': 'Téléphone',
  'platform.client.subscription': "L'abonnement",
  'platform.client.startedOn': 'Début',
  'platform.client.periodEnd': 'Échéance',
  'platform.client.endedOn': 'Fin',
  'platform.client.price': 'Prix',
  'platform.client.autoRenew': 'Reconduction automatique',
  'platform.client.cancellationReason': 'Motif de résiliation',
  'platform.client.noSubscription': "Ce client n'a aucun abonnement. Aucun plafond ne s'applique, et il ne peut rien créer tant qu'il n'en a pas un actif.",
  'platform.client.noExpiry': 'Les abonnements avancent seuls : à la fin de l’essai ou à l’échéance, le paiement est attendu ; après la période de grâce, le client passe en lecture seule. Réactiver l’abonnement, c’est enregistrer le paiement.',
  'platform.client.suspend': 'Suspendre',
  'platform.client.resume': 'Réactiver',
  'platform.client.terminate': 'Résilier',
  'platform.client.changePlan': 'Changer de formule',
  'platform.client.extend': 'Prolonger',
  'platform.client.months': 'Nombre de mois',
  'platform.client.reason': 'Motif',
  'platform.client.reasonRequired': "Obligatoire : la base l'exige, et c'est ce qu'on devra expliquer au client qui rappelle.",
  'platform.client.suspend.title': 'Suspendre cet abonnement ?',
  'platform.client.suspend.message': "Le client continuera d'ouvrir sa session et de lire toutes ses données. Il ne pourra plus créer d'exploitation ni de parcelle. Personne n'est déconnecté : pour fermer la porte, il faut désactiver l'entreprise.",
  'platform.client.resume.title': 'Réactiver cet abonnement ?',
  'platform.client.resume.message': 'Le client pourra de nouveau créer des exploitations et des parcelles, dans les plafonds de sa formule.',
  'platform.client.terminate.title': 'Résilier cet abonnement ?',
  'platform.client.terminate.message': "La date de fin est posée à aujourd'hui et le client ne crée plus rien. Il garde ses données et continue de les lire tant que l'entreprise reste active.",
  'platform.client.changePlan.title': 'Changer la formule de ce client ?',
  'platform.client.extend.title': "Repousser l'échéance ?",
  'platform.client.extend.message': "L'échéance recule depuis sa valeur actuelle, et non depuis aujourd'hui : prolonger un abonnement en cours ne le raccourcit jamais.",
  'platform.client.extended': 'Échéance repoussée.',
  'platform.client.suspended': 'Abonnement suspendu : le client lit encore, il ne crée plus.',
  'platform.client.resumed': 'Abonnement réactivé.',
  'platform.client.terminated': 'Abonnement résilié.',
  'platform.client.planChanged': 'Formule changée.',
  'platform.client.grant': 'Attribuer un abonnement',
  'platform.client.grant.title': 'Attribuer un abonnement à ce client ?',
  'platform.client.grant.message': "L'abonnement démarre aujourd'hui, actif, pour une période de la formule choisie, et se renouvelle automatiquement. Prix, périodicité et devise sont ceux de la formule.",
  'platform.client.granted': 'Abonnement attribué.',
  'platform.client.modules': 'Les activités',
  'platform.client.modules.description': "Ce que le client a le droit d'exploiter. Une activité fermée disparaît de son menu.",
  'platform.client.module.guards': 'Fermer cette activité refuse réellement ses écrans.',
  'platform.client.module.cosmetic': "Fermer cette activité ne change aujourd'hui que le menu : elle ne garde aucun écran, parce qu'elle n'existe pas encore dans le produit.",
  'platform.client.module.open': 'Ouvrir',
  'platform.client.module.close': 'Fermer',
  'platform.client.module.openedAt': 'Ouverte le',
  'platform.client.module.closedAt': 'Fermée le',
  'platform.client.module.open.title': 'Ouvrir cette activité ?',
  'platform.client.module.open.message': "Le client verra cette activité dans son menu et pourra utiliser ses écrans, s'il en existe.",
  'platform.client.module.close.title': 'Fermer cette activité ?',
  'platform.client.module.opened': 'Activité ouverte.',
  'platform.client.module.closed': 'Activité fermée.',
  'platform.client.module.unchanged': "Cette activité était déjà dans cet état : rien n'a changé.",
  'platform.client.usage': "L'usage face aux plafonds",
  'platform.client.usage.description': "Compté en direct sur les exploitations, les parcelles et les comptes — jamais lu d'un compteur entretenu à la main, qui dériverait.",
  'platform.client.usage.limit': 'Plafond',
  'platform.client.usage.used': 'Consommé',
  'platform.client.usage.unlimited': 'Sans limite',
  'platform.client.usage.noPlan': "Sans abonnement, il n'y a aucun plafond à afficher.",
  'platform.client.usage.usersNotEnforced': "Ce plafond n'est PAS appliqué : la création d'un utilisateur ne passe par aucune garde. Un client limité à trois utilisateurs peut en créer trois cents. Le chiffre est indicatif, ce n'est pas une limite.",
  'platform.client.limit.farms': 'Exploitations',
  'platform.client.limit.plots': 'Parcelles',
  'platform.client.limit.area': 'Surface',
  'platform.client.limit.users': 'Utilisateurs',
  'platform.client.downgrade.title': 'Ce que cette formule retire à ce client',
  'platform.client.downgrade.intro': "Il garde toutes ses données et continue de les lire. Ce qu'il ne pourra plus faire, c'est en créer de nouvelles au-delà de ces plafonds.",
  'platform.client.downgrade.has': 'il en a',
  'platform.client.downgrade.allows': 'la formule visée en autorise',
  'platform.client.downgrade.none': 'Cette formule ne retire rien à ce client : son usage tient dans tous ses plafonds.',
  'platform.client.downgrade.notEnforced': "sans effet réel : ce plafond n'est pas appliqué",
  'platform.months_invalid': 'Prolonger demande un nombre de mois strictement positif.',
  'platform.no_subscription': "Ce client n'a aucun abonnement à modifier.",
  'platform.subscription_exists': 'Ce client a déjà un abonnement : réactivez-le ou changez sa formule.',
  'platform.status_unknown': "Un abonnement se pose à la main en actif, suspendu ou résilié, et rien d'autre.",
  'platform.reason_required': "Le motif est obligatoire : c'est ce qu'on devra expliquer au client.",
  'platform.last_module': "C'est la dernière activité ouverte de ce client : la fermer le laisserait sans rien pouvoir faire. Résiliez l'abonnement si c'est l'intention.",
  'notice.region': 'Avis sur votre abonnement',
  'notice.renewal_reminder': 'Votre abonnement arrive à échéance le',
  'notice.trial_ending': 'Votre période d’essai se termine le',
  'notice.past_due': 'Paiement attendu : sans règlement, votre compte passera en lecture seule le',
  'notice.suspended': 'Votre abonnement est suspendu : vous pouvez consulter vos données, pas en créer. Depuis le',
  'notice.expired': 'Votre abonnement a expiré le',
  'notice.cancelled': 'Votre abonnement a été résilié le',
  'notice.inDays': 'dans',
  'notice.see': 'Voir l’abonnement',
  'notice.dismiss': 'Masquer',
  'platform.plan_unchanged': 'Le client est déjà sur cette formule.',
  'platform.no_period_end': 'Cet abonnement n’a pas d’échéance : changez de formule immédiatement.',
  'platform.grace_invalid': 'La période de grâce dure de 0 à 90 jours.',
  'platform.reminders_invalid': 'Dix rappels au plus, chacun de 1 à 365 jours avant l’échéance.',
  'subscription.notice_not_found': 'Cet avis est introuvable ou déjà lu.',
  'platform.client.lifecycle': 'Cycle de vie',
  'platform.client.lifecycle.none': 'Aucun abonnement : rien à programmer.',
  'platform.client.lifecycle.pastDue': 'Paiement attendu depuis le',
  'platform.client.lifecycle.readOnlyOn': 'Lecture seule à partir du',
  'platform.client.lifecycle.reactivateHint': 'Réactivez l’abonnement dès réception du paiement.',
  'platform.client.lifecycle.trialUntil': 'Essai jusqu’au',
  'platform.client.lifecycle.nextEnd': 'Prochaine échéance le',
  'platform.client.lifecycle.willRenew': 'renouvellement automatique, paiement attendu ensuite.',
  'platform.client.lifecycle.willEnd': 'l’abonnement s’arrêtera à cette date.',
  'platform.client.lifecycle.scheduled': 'Changement de formule à l’échéance',
  'platform.client.lifecycle.switchTo': 'Passage à',
  'platform.client.lifecycle.on': 'le',
  'platform.client.lifecycle.cancelChange': 'Annuler ce changement',
  'platform.client.lifecycle.targetPlan': 'Formule visée',
  'platform.client.lifecycle.choosePlan': 'Choisir une formule…',
  'platform.client.lifecycle.scheduleChange': 'Programmer à l’échéance',
  'platform.client.lifecycle.changeScheduled': 'Changement programmé.',
  'platform.client.lifecycle.changeCancelled': 'Changement programmé annulé.',
  'platform.client.lifecycle.cancellation': 'Résiliation à l’échéance',
  'platform.client.lifecycle.cancelScheduled': 'Résiliation programmée à l’échéance. Motif :',
  'platform.client.lifecycle.withdrawCancellation': 'Retirer la résiliation',
  'platform.client.lifecycle.cancelAtEnd': 'Résilier à l’échéance',
  'platform.client.lifecycle.cancellationScheduled': 'Résiliation programmée.',
  'platform.client.lifecycle.cancellationWithdrawn': 'Résiliation retirée.',
  'platform.client.lifecycle.history': 'Historique',
  'platform.client.lifecycle.noHistory': 'Aucun événement enregistré.',
  'platform.client.lifecycle.when': 'Quand',
  'platform.client.lifecycle.what': 'Quoi',
  'platform.client.lifecycle.who': 'Par',
  'platform.client.lifecycle.byJob': 'Automatique',
  'platform.client.lifecycle.byPlatform': 'Panneau',
  'platform.client.lifecycle.event.created': 'Abonnement créé',
  'platform.client.lifecycle.event.status_changed': 'Statut changé',
  'platform.client.lifecycle.event.plan_changed': 'Formule changée',
  'platform.client.lifecycle.event.period_changed': 'Échéance déplacée',
  'platform.client.lifecycle.event.plan_change_scheduled': 'Changement de formule programmé',
  'platform.client.lifecycle.event.plan_change_cancelled': 'Changement programmé annulé',
  'platform.client.lifecycle.event.cancellation_scheduled': 'Résiliation programmée',
  'platform.client.lifecycle.event.cancellation_withdrawn': 'Résiliation retirée',
  'platform.policy.title': 'Échéances, grâce et rappels',
  'platform.policy.description': 'Toutes les six heures, la tâche du cycle de vie fait avancer les abonnements : fin d’essai et échéance → paiement attendu, puis lecture seule après la grâce ; rappels avant échéance. Aucun courriel ne part : les avis s’affichent dans l’application du client.',
  'platform.policy.graceDays': 'Jours de grâce',
  'platform.policy.graceHint': 'Jours de paiement attendu avant la lecture seule (0 à 90).',
  'platform.policy.reminderDays': 'Rappels (jours avant l’échéance)',
  'platform.policy.reminderHint': 'Séparés par des virgules, par exemple 30, 7, 1.',
  'platform.policy.saved': 'Politique enregistrée.',
  'platform.policy.runNow': 'Exécuter maintenant',
  'platform.policy.ran': 'Tâche exécutée. Actes :',
  'feature.stock': 'Stock et dépôts',
  'feature.purchasing': 'Achats',
  'feature.sales': 'Ventes et facturation',
  'feature.assets': 'Immobilisations',
  'feature.subsidies': 'Subventions',
  'feature.audit': 'Journal d’audit',
  'feature.mobile_sync': 'Application mobile hors ligne',
  'saas.feature_not_included': 'Cette fonctionnalité n’est pas comprise dans votre formule. Contactez votre administrateur pour changer de formule.',
  'platform.plan_code_invalid': 'Le code commence par une lettre et ne contient que des minuscules, des chiffres et des soulignés (2 à 30 caractères).',
  'platform.plan_code_taken': 'Une formule porte déjà ce code, même archivée. Choisissez-en un autre.',
  'platform.plan_name_required': 'Une formule sans nom ne se vend pas : donnez-lui un nom.',
  'platform.billing_period_invalid': 'La périodicité est mensuelle ou annuelle.',
  'platform.plan_price_invalid': 'Un prix est obligatoire et ne peut pas être négatif.',
  'platform.plan_quota_invalid': 'Un plafond est vide (sans limite) ou strictement positif : zéro n’autoriserait rien.',
  'platform.plan_trial_invalid': 'L’essai dure de 0 à 365 jours.',
  'platform.plan_module_count_invalid': 'Le nombre d’activités incluses ne peut pas dépasser le nombre d’activités proposées.',
  'platform.feature_unknown': 'Cette fonctionnalité n’existe pas au catalogue.',
  'platform.plan_not_found': 'Cette formule est introuvable.',
  'platform.plan_stale': 'La formule a été modifiée entre-temps par quelqu’un d’autre. Rechargez la page avant d’enregistrer.',
  'platform.expiry_invalid': 'Une exception ne peut pas expirer avant aujourd’hui.',
  'platform.nav.plans': 'Formules',
  'platform.plans.title': 'Formules',
  'platform.plans.description': 'Ce qui se vend : prix, plafonds, activités et fonctionnalités. Modifier une formule ne change ni le prix ni les droits d’un client déjà abonné.',
  'platform.plans.list': 'Catalogue des formules',
  'platform.plans.empty': 'Aucune formule.',
  'platform.plans.new': 'Nouvelle formule',
  'platform.plans.name': 'Formule',
  'platform.plans.price': 'Prix',
  'platform.plans.perMonth': 'mois',
  'platform.plans.perYear': 'an',
  'platform.plans.content': 'Contenu',
  'platform.plans.subscribers': 'Abonnés',
  'platform.plans.status': 'État',
  'platform.plans.state.public': 'Publique',
  'platform.plans.state.hidden': 'Masquée',
  'platform.plans.state.archived': 'Archivée',
  'platform.plans.archive': 'Archiver',
  'platform.plans.restore': 'Rétablir',
  'platform.plans.archive.title': 'Archiver cette formule ?',
  'platform.plans.archive.message': 'Elle ne sera plus proposée à la création de client ni au changement de formule. Ses abonnés en place gardent leur formule, leur prix et leurs fonctionnalités.',
  'platform.plans.archive.subscribers': 'Abonnés en place :',
  'platform.plans.restore.title': 'Rétablir cette formule ?',
  'platform.plans.restore.message': 'Elle sera de nouveau proposée à la création de client et au changement de formule.',
  'platform.plans.archived': 'Formule archivée.',
  'platform.plans.restored': 'Formule rétablie.',
  'platform.plans.created': 'Formule créée.',
  'platform.plans.saved': 'Formule enregistrée.',
  'platform.plans.editor.description': 'Prix, plafonds, activités et fonctionnalités d’une formule.',
  'platform.plans.editor.grandfathered': 'Les clients déjà abonnés gardent leur prix : un changement ici ne s’applique qu’aux nouvelles souscriptions. Abonnés en place :',
  'platform.plans.group.identity': 'Identité',
  'platform.plans.group.price': 'Prix et essai',
  'platform.plans.group.limits': 'Plafonds',
  'platform.plans.group.content': 'Contenu',
  'platform.plans.group.visibility': 'Visibilité',
  'platform.plans.code': 'Code',
  'platform.plans.codeHint': 'Minuscules, chiffres et soulignés. Il ne pourra plus changer.',
  'platform.plans.codeFrozen': 'Le code est figé après création.',
  'platform.plans.nameFr': 'Nom (français)',
  'platform.plans.nameAr': 'Nom (arabe)',
  'platform.plans.nameEn': 'Nom (anglais)',
  'platform.plans.descriptionField': 'Description',
  'platform.plans.period': 'Périodicité',
  'platform.plans.yearly': 'Annuelle',
  'platform.plans.monthly': 'Mensuelle',
  'platform.plans.priceField': 'Prix par période (TND)',
  'platform.plans.extraFarmPrice': 'Prix par exploitation supplémentaire',
  'platform.plans.trialDays': 'Jours d’essai',
  'platform.plans.emptyMeansUnlimited': 'Vide = sans limite. Zéro n’autoriserait rien.',
  'platform.plans.maxFarms': 'Exploitations',
  'platform.plans.maxUsers': 'Utilisateurs',
  'platform.plans.maxPlots': 'Parcelles',
  'platform.plans.maxArea': 'Surface (ha)',
  'platform.plans.includedModules': 'Activités incluses',
  'platform.plans.includedAll': 'Toutes celles cochées',
  'platform.plans.features': 'Fonctionnalités',
  'platform.plans.featuresHint': 'Chacune ferme réellement des écrans et des routes chez le client qui ne l’a pas.',
  'platform.plans.public': 'Publique',
  'platform.plans.publicHint': 'Décochée, la formule reste vendable depuis ce panneau sans être affichée au catalogue.',
  'platform.plans.order': 'Ordre d’affichage',
  'platform.client.features': 'Fonctionnalités',
  'platform.client.features.explain': 'Ce que la formule donne, les exceptions accordées ou retirées à ce client, et ce qu’il a aujourd’hui. Une exception prime sur la formule jusqu’à son échéance.',
  'platform.client.features.empty': 'Aucune fonctionnalité au catalogue.',
  'platform.client.features.feature': 'Fonctionnalité',
  'platform.client.features.plan': 'Formule',
  'platform.client.features.included': 'Comprise',
  'platform.client.features.notIncluded': 'Non comprise',
  'platform.client.features.override': 'Exception',
  'platform.client.features.granted': 'Accordée',
  'platform.client.features.withdrawn': 'Retirée',
  'platform.client.features.inactive': 'Hors période',
  'platform.client.features.until': 'jusqu’au',
  'platform.client.features.today': 'Aujourd’hui',
  'platform.client.features.on': 'Ouverte',
  'platform.client.features.off': 'Fermée',
  'platform.client.features.grant': 'Accorder',
  'platform.client.features.withdraw': 'Retirer',
  'platform.client.features.clear': 'Revenir à la formule',
  'platform.client.features.grantExplain': 'Accorder à ce client, hors formule :',
  'platform.client.features.withdrawExplain': 'Retirer à ce client, bien que sa formule la comprenne. Ses écrans et ses routes lui seront refusés :',
  'platform.client.features.expiresOn': 'Échéance',
  'platform.client.features.expiresHint': 'Vide = sans échéance. Le dernier jour compte.',
  'platform.client.features.saved': 'Exception enregistrée.',
  'platform.client.features.cleared': 'Exception retirée : la formule décide de nouveau.',

}

export type MessageKey = keyof typeof fr

type Messages = Record<MessageKey, string>

const ar: Messages = {
  'nav.audit': 'سجل التدقيق',
  'nav.sales': 'المبيعات',
  'audit.title': 'سجل التدقيق',
  'audit.description': 'من أنشأ أو عدّل أو حذف ماذا. يُحفظ ثلاث سنوات.',
  'audit.when': 'متى',
  'audit.who': 'الفاعل',
  'audit.what': 'البطاقة',
  'audit.action': 'العملية',
  'audit.action.insert': 'إنشاء',
  'audit.action.update': 'تعديل',
  'audit.action.delete': 'حذف',
  'audit.changed': 'ما الذي تغيّر',
  'audit.from': 'من تاريخ',
  'audit.to': 'إلى تاريخ',
  'audit.entity': 'الجدول',
  'audit.empty': 'لا توجد كتابة في هذه الفترة.',
  'audit.window': 'بدون تواريخ، النافذة هي الشهر المنقضي.',
  'audit.system': 'كتابة تقنية',
  'audit.admin_only': 'سجل التدقيق مخصّص للمديرين.',
  'season.title': 'المواسم',
  'season.description': 'الفتح والغلق المراقَب وإعادة الفتح الموثّقة (§10.5).',
  'season.open': 'فتح موسم',
  'season.opened': 'تم فتح الموسم.',
  'season.close': 'غلق',
  'season.closed': 'تم غلق الموسم.',
  'season.reopen': 'إعادة الفتح',
  'season.reopened': 'تمت إعادة فتح الموسم.',
  'season.reopenReason': 'سبب إعادة الفتح',
  'season.reopenHint': 'إعادة الفتح تحرّك أرقامًا قُرئت من قبل: السبب يُحفظ.',
  'season.reopenings': 'إعادات الفتح',
  'season.blockers': 'ما يمنع الغلق',
  'season.canClose': 'لا مانع: الغلق سيمرّ.',
  'season.state.open': 'مفتوح',
  'season.state.closed': 'مغلق',
  'season.period': 'الفترة',
  'agro.season.close.pending_operations': 'عمليات غير معتمدة',
  'agro.season.close.harvest_without_destination': 'محاصيل بلا وجهة',
  'agro.season.reopen_reason_required': 'سبب إعادة الفتح إجباري.',
  'agro.season.already_closed': 'هذا الموسم مغلق أصلًا.',
  'agro.season.not_closed': 'هذا الموسم ليس مغلقًا: لا شيء لإعادة فتحه.',
  'agro.season.manager_only': 'المسؤول أو المدير وحدهما يغلقان موسمًا.',
  'stock.alerts': 'التنبيهات',
  'stock.alerts.empty': 'لا توجد تنبيهات مخزون.',
  'stock.alerts.window': 'نافذة انتهاء الصلاحية (أيام)',
  'stock.alert.below_reorder_point': 'تحت عتبة إعادة التموين',
  'stock.alert.expiring_soon': 'صلاحية قريبة الانتهاء',
  'stock.alert.expired': 'منتهي الصلاحية',
  'stock.alert.negative_balance': 'رصيد سالب',
  'stock.movements': 'سجل الحركات',
  'stock.movements.empty': 'لا توجد حركة.',
  'stock.movements.direction': 'الاتجاه',
  'stock.movements.reason': 'الطبيعة',
  'stock.movements.source': 'الوثيقة الأصلية',
  'stock.movements.value': 'القيمة',
  'stock.lots': 'الدفعات والصلاحية',
  'stock.lots.empty': 'لا توجد دفعة متابَعة.',
  'stock.lots.code': 'الدفعة',
  'stock.lots.expiry': 'انتهاء الصلاحية',
  'stock.lots.remaining': 'المتبقّي',
  'stock.count': 'ورقة الجرد',
  'stock.count.theoretical': 'النظري',
  'stock.count.counted': 'المعدود',
  'stock.count.gap': 'الفارق',
  'stock.count.export': 'تصدير الورقة',
  'ops.list': 'العمليات',
  'ops.list.empty': 'لا توجد عملية.',
  'ops.filter.season': 'الموسم',
  'ops.filter.farm': 'الضيعة',
  'ops.filter.from': 'من تاريخ',
  'ops.filter.to': 'إلى تاريخ',
  'fieldbook.byPlot': 'الدفتر حسب القطعة',
  'fieldbook.export': 'تصدير إلى Excel',
  'contracts.title': 'عقود البيع',
  'contracts.description': 'يُتابَع العقد إلى آخر تسليم.',
  'contracts.empty': 'لا يوجد عقد.',
  'contracts.customer': 'العميل',
  'contracts.crop': 'الزراعة',
  'contracts.quantity': 'الكمية المتعاقد عليها',
  'contracts.delivered': 'المسلَّم',
  'contracts.remaining': 'المتبقّي للتسليم',
  'contracts.price': 'السعر المتفق عليه',
  'contracts.dueDate': 'الأجل',
  'contracts.deliveries': 'سندات التسليم',
  'contracts.newDelivery': 'سند تسليم جديد',
  'contracts.deliveryHint': 'بدون كمية، يسلّم السند ما تبقّى.',
  'contracts.complete': 'عقد مكتمل',
  'customers.outstanding': 'مستحقات العميل',
  'customers.outstanding.empty': 'لا توجد مستحقات.',
  'app.name': 'AgriculteurApp',
  'app.tagline': 'إدارة التكاليف الفلاحية',

  'nav.farms': 'الضيعات',
  'nav.plots': 'قطع الأرض',
  'nav.warehouses': 'المخازن',
  'nav.campaigns': 'الحملات الزراعية',
  'nav.campaignGroups': 'مجموعات الحملات',
  'nav.purchases': 'المشتريات',
  'nav.dashboard': 'لوحة القيادة',
  'nav.exports': 'الصادرات',
  'nav.products': 'المواد',
  'nav.signOut': 'تسجيل الخروج',
  'nav.group.reporting': 'التقارير',
  'nav.group.structure': 'الهيكلة',
  'nav.group.structure_params': 'الإعدادات العامة',
  'nav.group.structure_norms': 'المعايير',
  'nav.group.purchasing': 'المشتريات',
  'nav.group.sales': 'المبيعات',
  'nav.group.stock': 'المخازن والمخزون',
  'nav.group.editions': 'الوثائق',
  'nav.group.admin': 'الإدارة',
  'nav.qualityControls': 'مراقبة الجودة',
  'nav.productStandards': 'معايير المنتجات',
  'qc.title': 'مراقبة الجودة',
  'qc.subtitle': 'تُدخَل القياسات فيقارنها النظام بمعيار المادة ثم يُفرج عن الدفعة أو يحجزها. الدفعة قيد المراقبة أو المحجوزة أو المرفوضة لا تخرج.',
  'qc.new': 'مراقبة جديدة',
  'qc.history': 'السجل',
  'qc.lot': 'الدفعة',
  'qc.lotHint': 'اختر من الدفعات المتوفرة في المخزون.',
  'qc.product': 'المادة',
  'qc.warehouse': 'المخزن',
  'qc.date': 'التاريخ',
  'qc.type': 'النوع',
  'qc.result': 'النتيجة',
  'qc.decision': 'القرار',
  'qc.notes': 'ملاحظات',
  'qc.submit': 'تسجيل المراقبة',
  'qc.created': 'تم تسجيل مراقبة الجودة.',
  'qc.lotRequired': 'اختر دفعة.',
  'qc.empty': 'لا توجد مراقبة جودة.',
  'qc.guard': 'الرفض مثبّت في قاعدة البيانات: استهلاك أو نقل أو بيع دفعة محجوزة مرفوض مهما كانت الشاشة.',
  'qc.type.organoleptic': 'حسّي',
  'qc.type.physico_chemical': 'فيزيائي كيميائي',
  'qc.type.microbiological': 'ميكروبيولوجي',
  'qc.type.lait': 'حليب',
  'qc.type.other': 'آخر',
  // Lot Q1a (migration 0085) : le contrôle calculé et les normes de produit.
  'qc.result.compliant': 'مطابق',
  'qc.result.non_compliant': 'غير مطابق',
  'qc.result.incomplete': 'غير مكتمل',
  'qc.result.no_standard': 'بدون معيار',
  'qc.result.waived': 'استثناء',
  'qc.decision.in_control': 'قيد المراقبة',
  'qc.decision.released': 'مُفرَج عنه',
  'qc.decision.blocked': 'محجوز',
  'qc.decision.rejected': 'مرفوض',
  'qc.decision.conditional': 'إفراج مشروط',
  'qc.decision.downgraded': 'مخفّض',
  'qc.basis': 'الأساس',
  'qc.basis.measured': 'بالقياس',
  'qc.basis.no_standard': 'بدون معيار',
  'qc.basis.override': 'استثناء',
  'qc.basis.legacy': 'مراقبة سابقة',
  'qc.verdict': 'الحكم',
  'qc.verdict.compliant': 'مطابق',
  'qc.verdict.non_compliant': 'غير مطابق',
  'qc.verdict.not_measured': 'لم يُقَس',
  'qc.standard': 'المعيار',
  'qc.parameter': 'العنصر المراقَب',
  'qc.threshold': 'الحدّ',
  'qc.measured': 'القيمة المقاسة',
  'qc.mandatory': 'إلزامي',
  'qc.optional': 'اختياري',
  'qc.judgement.none': 'لم يُحكَم عليه',
  'qc.reason': 'السبب',
  'qc.last': 'آخر مراقبة مسجَّلة',
  'qc.sheetHint': 'أدخل القيم المقاسة: النظام يقارنها بالمعيار ويقرّر.',
  'qc.invalidNumber': 'إحدى القيم المقاسة ليست رقماً.',
  'qc.measure.submit': 'تسجيل القياسات',
  'qc.outcome.released': 'أُفرج عن الدفعة: كل القياسات الإلزامية مطابقة.',
  'qc.outcome.blocked': 'حُجزت الدفعة: أحد القياسات الإلزامية غير مطابق.',
  'qc.outcome.in_control': 'المراقبة غير مكتملة: ينقص قياس إلزامي، والدفعة تبقى قيد المراقبة.',
  'qc.rejectedFinal': 'هذه الدفعة مرفوضة: الرفض نهائي، ولا تغادر المخزون إلا كخسارة.',
  'qc.noStandard.title': 'هذه المادة ليس لها معيار ساري',
  'qc.noStandard.body': 'لا يوجد ما يُقاس: القرار لك، وستذكر البطاقة «بدون معيار» وليس «مطابق».',
  'qc.noStandard.forbidden': 'لا يوجد ما يُقاس. المسؤول وحده يقرّر مصير هذه الدفعة.',
  'qc.manual.submit': 'تسجيل القرار',
  'qc.manual.decisionRequired': 'اختر قراراً.',
  'qc.override.title': 'استثناء',
  'qc.override.body': 'الدفعة المحجوزة يمكن الإفراج عنها بشرط أو رفضها نهائياً. يُحفظ السبب مع اسمك.',
  'qc.override.reason': 'السبب',
  'qc.override.reasonRequired': 'السبب إلزامي.',
  'qc.override.conditional': 'إفراج مشروط',
  'qc.override.reject': 'رفض الدفعة',
  'stock.lots.filterQuality': 'حالة الجودة',
  'stock.lots.filterQuality.all': 'الكل',
  // Lot Q2 (migration 0087) : la scission de lot et le stock par statut.
  'stock.lots.origin': 'الدفعة الأصلية',
  'stock.lots.grade': 'الدرجة',
  'stock.split': 'تقسيم',
  'stock.split.title': 'تقسيم الدفعة',
  'stock.split.submit': 'فصل هذه الأجزاء',
  'stock.split.hint': 'افصل جزءاً أو أكثر: كل جزء يصير دفعة ترث حالة الدفعة الأصلية ثم تُحكَم بمراقبتها الخاصة. تحتفظ الدفعة الأصلية بالباقي.',
  'stock.split.quantity': 'كمية الجزء',
  'stock.split.grade': 'درجة الجزء',
  'stock.split.addPart': 'إضافة جزء',
  'stock.split.removePart': 'إزالة الجزء',
  'stock.split.reason': 'سبب التقسيم',
  'stock.split.done': 'قُسِّمت الدفعة: الأجزاء ترث حالتها.',
  'stock.split.partsRequired': 'أدخل جزءاً واحداً على الأقل بكمية موجبة.',
  'stock.split.exceeds': 'الأجزاء تتجاوز ما تحمله الدفعة في هذا المخزن.',
  'stock.summary.title': 'المخزون حسب حالة الجودة',
  'stock.summary.available': 'متاح',
  'stock.summary.inControl': 'قيد المراقبة',
  'stock.summary.quarantine': 'الحجر',
  'stock.summary.rejected': 'مرفوض',
  'qc.basis.inherited': 'موروث من الدفعة الأصلية',
  // Lot Q3a — l'achat crée son lot (migration 0088).
  'qc.basis.takeover': 'استرجاع — مخزون سابق للتتبع بالدفعة',
  // Lot Q4 — contrat puis livraison (migration 0090).
  'sales.delivery_note.stock_exit_required': 'هذا الوصل يذكر تسليم مادة مُدارة في المخزون دون أن يُخرج شيئًا منه.',
  'sales.delivery_note.lot_mismatch': 'الدفعة المحدَّدة ليست دفعة من مادة هذا السطر.',
  'contracts.deliver': 'تسليم',
  'contracts.deliver.warehouse': 'مخزن الانطلاق',
  'contracts.deliver.date': 'تاريخ التسليم',
  'contracts.deliver.carrier': 'الناقل',
  'contracts.deliver.plate': 'لوحة الترقيم',
  'contracts.deliver.quantity': 'الكمية المسلَّمة',
  'contracts.deliver.lot': 'الدفعة المسلَّمة',
  'contracts.deliver.lot.chooseWarehouse': 'اختر المخزن أولًا: لا تُسلَّم إلا الدفعات الموجودة فيه.',
  'contracts.deliver.lot.none': 'لا توجد دفعة محرَّرة من هذه المادة في هذا المخزن.',
  'contracts.deliver.submit': 'إصدار وصل التسليم',
  'contracts.deliver.close': 'إغلاق',
  'contracts.deliver.incomplete': 'اختر مخزنًا وكمية للتسليم في سطر واحد على الأقل.',
  'contracts.deliver.done': 'صدر وصل التسليم:',
  'contracts.deliver.done.hint': 'خرجت البضاعة من مخزون المخزن. تتبّع الدفعة يُظهر هذا التسليم.',
  'contracts.new.title': 'عقد بيع جديد',
  'contracts.new.number': 'رقم العقد',
  'contracts.new.number.hint': 'اختياري: إذا تُرك فارغًا يُسنَد الرقم تلقائيًا.',
  'contracts.new.product': 'المادة',
  'contracts.new.addLine': 'إضافة سطر',
  'contracts.new.submit': 'إنشاء العقد',
  'contracts.new.incomplete': 'اختر زبونًا وأدخل سطرًا واحدًا على الأقل: المادة والكمية والسعر.',
  'contracts.new.created': 'أُنشئ العقد:',
  // Lot Q3b — la traçabilité se lit (migration 0089).
  'nav.lotTrace': 'تتبّع الدفعات',
  'stock.lot_trace.term_too_short': 'أدخل حرفين على الأقل من رمز الدفعة أو من رقم دفعة المورِّد.',
  'trace.open': 'تتبّع هذه الدفعة',
  'trace.title': 'تتبّع الدفعات',
  'trace.description': 'الدفعة في الاتجاهين: من أين أتت، وإلى أين ذهبت.',
  'trace.search.title': 'البحث عن دفعة',
  'trace.search.term': 'رمز الدفعة أو رقم دفعة المورِّد',
  'trace.search.hint': 'رقم دفعة المورِّد هو الذي يذكره عند سحب تصنيع ما.',
  'trace.search.submit': 'بحث',
  'trace.search.none': 'لا توجد دفعة بهذا الرمز.',
  'trace.loading': 'جارٍ قراءة التتبّع…',
  'trace.lot': 'الدفعة',
  'trace.product': 'المادة',
  'trace.supplierLotCode': 'رقم دفعة المورِّد',
  'trace.receivedAt': 'تاريخ الاستلام',
  'trace.expiryDate': 'انتهاء الصلاحية',
  'trace.initialQuantity': 'الكمية الأصلية',
  'trace.grade': 'الدرجة',
  'trace.status': 'حالة الجودة',
  'trace.quantity': 'الكمية',
  'trace.warehouse': 'المخزن',
  'trace.upstream': 'المنبع — من أين أتت هذه الدفعة',
  'trace.downstream': 'المصبّ — إلى أين ذهبت هذه الدفعة',
  'trace.origin': 'المصدر',
  'trace.origin.purchase': 'شراء',
  'trace.origin.harvest': 'حصاد',
  'trace.origin.takeover': 'استرجاع',
  'trace.origin.direct': 'دخول دون طلبية',
  'trace.origin.split': 'جزء من دفعة',
  'trace.origin.unknown': 'مصدر غير معروف',
  'trace.origin.unknown.hint': 'لا شيء يدوِّن مصدر هذه الدفعة: لا استلام ولا حصاد ولا دفعة أصلية.',
  'trace.origin.ofAncestor': 'مصدر الدفعة التي فُصلت منها هذه الدفعة:',
  'trace.origin.takeover.hint': 'مخزون سابق للتتبع بالدفعة: مصدره غير معروف.',
  'trace.origin.direct.hint': 'دخل المخزون دون طلبية شراء: لا مورِّد مرتبط به.',
  'trace.supplier': 'المورِّد',
  'trace.receipt': 'الاستلام',
  'trace.receiptDate': 'تاريخ الاستلام',
  'trace.supplierDocument': 'وصل المورِّد',
  'trace.order': 'الطلبية',
  'trace.plot': 'القطعة',
  'trace.plots': 'القطع',
  'trace.season': 'الموسم',
  'trace.crop': 'المحصول',
  'trace.harvestDate': 'تاريخ الحصاد',
  'trace.takeoverDate': 'تاريخ الاسترجاع',
  'trace.directEntryDate': 'تاريخ الدخول',
  'trace.ancestors': 'الدفعات الأصلية',
  'trace.controls': 'مراقبات الجودة',
  'trace.controlDate': 'تاريخ المراقبة',
  'trace.decision': 'القرار',
  'trace.controls.none': 'لا مراقبة: هذه الدفعة تنتظر قرار الجودة.',
  'trace.fieldInputs': 'المُدخلات المطبَّقة على القطعة',
  'trace.fieldInputs.hint': 'ما طُبِّق على القطعة قبل الحصاد، دون التدخلات الملغاة. الرابط يمرّ عبر القطعة، والكمية هي كمية التدخل.',
  'trace.fieldInputs.noLot': 'دون دفعة',
  'trace.fieldInputs.none': 'لا مُدخل مسجَّل على هذه القطعة قبل الحصاد.',
  'trace.operation': 'التدخل',
  'trace.operationDate': 'تاريخ التدخل',
  'trace.operationState': 'الحالة',
  'trace.operation.cancelled': 'تدخل ملغى',
  'trace.parts': 'الأجزاء المفصولة',
  'trace.balances': 'أين توجد',
  'trace.balances.none': 'لم يبقَ منها شيء في أي مخزن.',
  'trace.consumptions': 'التدخلات التي استهلكتها',
  'trace.consumptions.none': 'لم يستهلكها أي تدخل.',
  'trace.harvests': 'حصاد القطع المعالَجة',
  'trace.harvests.hint': 'حصاد القطع التي طُبِّقت عليها هذه الدفعة قبله. الرابط يمرّ عبر القطعة لا عبر المادة.',
  'trace.harvests.notStocked': 'لم يدخل المخزون بعد',
  'trace.harvests.none': 'لا حصاد على قطعة طُبِّقت عليها هذه الدفعة.',
  'trace.deliveries': 'التسليمات',
  'trace.deliveryDate': 'تاريخ التسليم',
  'trace.deliveryNote': 'وصل التسليم',
  'trace.customer': 'الزبون',
  'trace.deliveries.none': 'لا تسليم.',
  'trace.movements': 'كل حركاتها',
  'trace.movementDate': 'التاريخ',
  'trace.reason': 'النوع',
  'trace.direction': 'الاتجاه',
  'trace.direction.in': 'دخول',
  'trace.direction.out': 'خروج',
  'trace.movements.none': 'لا حركة موزَّعة على هذه الدفعة.',
  'trace.reason.reception': 'استلام',
  'trace.reason.consumption': 'استهلاك',
  'trace.reason.sale': 'بيع',
  'trace.reason.loss': 'فقدان',
  'trace.reason.return_out': 'إرجاع إلى المورِّد',
  'trace.reason.transfer_in': 'تحويل مستلَم',
  'trace.reason.transfer_out': 'تحويل مُرسَل',
  'trace.reason.adjustment_in': 'تسوية بالزيادة',
  'trace.reason.adjustment_out': 'تسوية بالنقصان',
  'trace.reason.production_in': 'دخول حصاد',
  'trace.reason.split_in': 'جزء مستلَم من تقسيم',
  'trace.reason.split_out': 'مفصول بالتقسيم',
  'stock.lot.required': 'هذه المادة متتبَّعة بالدفعة: حدِّد الدفعة.',
  'stock.balance.lot_required': 'هذه المادة متتبَّعة بالدفعة: يُمسك مخزونها دفعةً بدفعة.',
  'stock.movement.lot_ventilation_required': 'هذه المادة متتبَّعة بالدفعة: يجب أن تحدِّد الحركة دفعاتها كاملةً.',
  'stock.lot.already_decided': 'هذه الدفعة صدر فيها قرار: البضاعة الجديدة تحصل على دفعتها الخاصة التي ستُراقَب.',
  'stock.lot.supplier_code_too_long': 'رقم دفعة المورِّد لا يتجاوز 50 حرفًا.',
  'stock.quality_control.takeover_is_automatic': 'تحرير دفعة الاسترجاع يضعه النظام مرة واحدة ولا يُكتب.',
  'operations.consumption.movement_not_recorded': 'خروج المخزون لم يُرجع حركته: لم يُربط المُدخل ولم يُستهلك شيء.',
  'purchase.landed_cost.no_balance': 'لا يوجد رصيد مخزون تُحمَّل عليه مصاريف الاقتراب.',
  'purchases.receive.lot': 'الدفعة المستلمة',
  'purchases.receive.supplierLotCode': 'رقم دفعة المورِّد',
  'purchases.receive.expiryDate': 'انتهاء صلاحية الدفعة',
  'purchases.receive.lotsCreated': 'دفعات أُنشئت، قيد المراقبة:',
  'purchases.receive.lotsCreated.hint': 'تُستهلك بعد تحريرها بمراقبة الجودة.',
  'warehouses.transferLot': 'الدفعة',
  'warehouses.transferLot.held': 'الدفعات قيد المراقبة أو المحجوزة أو المرفوضة غير معروضة: تنتظر قرار الجودة.',
  'stock.lot_split.parts_required': 'التقسيم يفصل جزءاً واحداً على الأقل.',
  'stock.lot_split.invalid_quantity': 'كمية الجزء يجب أن تكون موجبة تماماً.',
  'stock.lot_split.text_too_long': 'السبب لا يتجاوز 300 حرف والدرجة 30 حرفاً.',
  'stock.lot_split.not_neutral': 'التقسيم لا يُنشئ كمية ولا قيمة: مدخلاته يجب أن تساوي مخرجه.',
  'stock.lot.parent_is_final': 'الدفعة الأصلية تُحدَّد عند الإنشاء ولا تتغيّر.',
  'stock.lot.parent_mismatch': 'الجزء من المادة نفسها كدفعته الأصلية، وناتج عن تقسيم تلك الدفعة.',
  'stock.quality_control.inherited_is_automatic': 'وراثة الحالة تُثبَّت عند إنشاء الجزء ولا تُكتب يدوياً.',
  'ps.title': 'معايير المنتجات',
  'ps.subtitle': 'لكل مادة متابَعة بالدفعة: العناصر الواجب قياسها عند الاستلام وحدودها. المعيار النشط يحكم على الدفعات؛ لمراجعته أنشئ نسخة جديدة.',
  'ps.new': 'معيار منتج جديد',
  'ps.edit': 'تعديل المسودة',
  'ps.product': 'المادة',
  'ps.productHint': 'المواد المتابَعة بالدفعة فقط.',
  'ps.code': 'الرمز',
  'ps.name': 'التسمية',
  'ps.version': 'النسخة',
  'ps.effectiveFrom': 'تاريخ السريان',
  'ps.reference': 'المرجع',
  'ps.status.draft': 'مسودة',
  'ps.status.active': 'نشط',
  'ps.status.retired': 'مسحوب',
  'ps.parameters': 'العناصر المراقَبة',
  'ps.mandatoryCount': 'الإلزامية',
  'ps.norm': 'المقارنة',
  'ps.controlType': 'نوع المراقبة',
  'ps.addParameter': 'إضافة عنصر',
  'ps.remove': 'إزالة',
  'ps.save': 'حفظ المسودة',
  'ps.saved': 'تم حفظ معيار المنتج.',
  'ps.activate': 'تفعيل',
  'ps.activated': 'فُعِّل المعيار: صار يحكم على دفعات هذه المادة.',
  'ps.retire': 'سحب المعيار',
  'ps.retiredDone': 'سُحب المعيار.',
  'ps.empty': 'لا يوجد معيار منتج.',
  'ps.noParameters': 'لا يوجد عنصر: أضف عنصراً إلزامياً واحداً على الأقل قبل التفعيل.',
  'ps.lockedHint': 'هذا المعيار لم يعد مسودة فلا يُعدَّل. لمراجعة حدوده أنشئ نسخة جديدة.',
  'ps.required': 'المادة والرمز والتسمية والنسخة وتاريخ السريان إلزامية.',
  'stock.lots.quality': 'الجودة',
  'farms.codeGenerated': 'يُمنح عند الإنشاء (EXP01، EXP02…).',
  'farms.locality': 'البلدة',
  'farms.gps': 'الموقع الجغرافي',
  'products.lotTrackedHint': 'إلزامي في طريقة FIFO.',
  'stock.lot.blocked_by_quality': 'الدفعة محجوزة بسبب الجودة: يلزم قرار قبل أي خروج.',
  'stock.lot.insufficient_usable_quantity': 'المخزون القابل للاستعمال غير كاف.',
  'stock.lot.not_found': 'الدفعة غير موجودة.',
  'stock.lot.duplicate_code': 'رمز الدفعة موجود مسبقا لهذه المادة.',
  'stock.quality_control.invalid_decision': 'القرار غير معروف.',
  'stock.quality_control.invalid_sample': 'كمية العينة يجب أن تكون موجبة.',
  // Codes frappés par le lot Q1a, côté gestionnaires comme côté déclencheurs.
  'stock.quality_control.not_found': 'مراقبة الجودة غير موجودة.',
  'stock.quality_control.not_readable': 'كُتبت المراقبة لكن تعذّرت إعادة قراءتها.',
  'stock.quality_control.references_required': 'مراقبة الجودة تحدّد مؤسسة ودفعة ومادة.',
  'stock.quality_control.standard_in_force': 'لهذه المادة معيار ساري: دفعتها تُحكَم بالقياسات لا بقرار.',
  'stock.quality_control.no_standard_in_force': 'ليس لهذه المادة معيار ساري في هذا التاريخ: لا يوجد ما يُقاس.',
  'stock.quality_control.unknown_parameter': 'أحد العناصر المقاسة لا ينتمي إلى المعيار الساري.',
  'stock.quality_control.duplicate_parameter': 'العنصر نفسه قيس مرتين في هذه المراقبة.',
  'stock.quality_control.invalid_verdict': 'حكم غير معروف.',
  'stock.quality_control.comment_too_long': 'تعليق أحد القياسات يتجاوز 300 حرف.',
  'stock.quality_control.override_reason_required': 'الاستثناء يستوجب سبباً.',
  'stock.quality_control.override_reason_too_long': 'السبب يتجاوز 500 حرف.',
  'stock.quality_control.lot_not_blocked': 'الإفراج المشروط يخصّ دفعة محجوزة، والرفض يخصّ دفعة محجوزة أو قيد المراقبة.',
  'stock.quality_control.invalid_basis': 'المراقبة تكون بالقياس أو بدون معيار أو استثناءً.',
  'stock.quality_control.not_measurable': 'المراقبة بالقياس وحدها تحمل قياسات.',
  'stock.lot.rejected_is_final': 'هذه الدفعة مرفوضة، والرفض نهائي.',
  'stock.lot.status_is_decided': 'حالة جودة الدفعة لا تُعدَّل مباشرة: هي تتبع قرار المراقبة.',
  'agro.norm.not_found': 'المعيار غير موجود.',
  'agro.product_standard.not_found': 'معيار المنتج غير موجود.',
  'agro.product_standard.not_readable': 'كُتب معيار المنتج لكن تعذّرت إعادة قراءته.',
  'agro.product_standard.duplicate_version': 'هذا المعيار يحمل هذه النسخة مسبقاً.',
  'agro.product_standard.no_mandatory_parameter': 'لا يُفعَّل المعيار إلا بعنصر إلزامي واحد على الأقل.',
  'agro.product_standard.product_not_lot_tracked': 'المادة المتابَعة بالدفعة وحدها تقبل معيار منتج.',
  'agro.product_standard.product_not_found': 'المادة غير موجودة.',
  'agro.product_standard.not_draft': 'المسودة وحدها تُعدَّل؛ للمراجعة أنشئ نسخة جديدة.',
  'agro.product_standard.invalid_transition': 'ينتقل المعيار من مسودة إلى نشط ثم إلى مسحوب.',
  'agro.product_standard.unknown_norm': 'المعيار المشار إليه في هذا العنصر غير موجود.',
  'agro.product_standard.code_required': 'الرمز إلزامي.',
  'agro.product_standard.name_required': 'التسمية إلزامية.',
  'agro.product_standard.version_required': 'النسخة إلزامية.',
  'agro.product_standard.field_too_long': 'الرمز: 30 حرفاً على الأكثر؛ التسمية: 150؛ النسخة: 20؛ المرجع: 150.',
  'agro.product_standard.invalid_control_type': 'نوع المراقبة غير معروف.',
  'agro.product_standard.duplicate_parameter': 'المقارنة نفسها واردة مرتين بين العناصر.',
  'agro.product_standard.invalid_status': 'حالة غير معروفة.',
  'agro.product.fifo_requires_lots': 'طريقة FIFO تتطلب المتابعة بالدفعة.',
  'agro.product.lot_requires_stock': 'المتابعة بالدفعة تتطلب مادة مُدارة في المخزون.',
  'farm.farm.code_generation_failed': 'لا يوجد رمز ضيعة متاح: أدخل رمزا.',
  'nav.comparatifs': 'المقارنات',
  'nav.alertes': 'التنبيهات',
  'nav.partners': 'الحرفاء',
  'nav.units': 'الوحدات',
  'nav.normes': 'المعايير',
  'nav.normCategories': 'فئات المعايير',
  'nav.besoins': 'طلبات الحاجيات',
  'nav.devis': 'عروض الأسعار',
  'nav.bonCommande': 'أمر الشراء',
  'nav.bonLivraison': 'إذن التسليم',
  'nav.avoirs': 'المرتجعات',
  'nav.offrePrix': 'عرض السعر',
  'nav.vergers': 'البساتين',
  'nav.cheptel': 'القطيع',
  'nav.supplierInvoices': 'فواتير المزودين',
  'nav.subscription': 'الاشتراك',
  'topbar.allActivities': 'كل الأنشطة',
  'topbar.season': 'الموسم',
  'common.theme': 'السمة',
  'theme.light': 'التحول إلى السمة الفاتحة',
  'theme.dark': 'التحول إلى السمة الداكنة',

  'module.crop': 'المحاصيل الكبرى',
  'module.orchard': 'الأشجار المثمرة',
  'module.livestock': 'تربية الماشية',

  'login.title': 'تسجيل الدخول',
  'login.email': 'البريد الإلكتروني',
  'login.password': 'كلمة المرور',
  'login.submit': 'دخول',
  'login.pending': 'جاري الدخول…',
  'login.tagline': 'الإدارة التقنية والاقتصادية للضيعات',
  'login.remember': 'ابق متصلا',
  'login.forgot': 'نسيت كلمة السر؟',
  'login.welcome': 'مرحبا بعودتك',
  'login.subtitle': 'سجّل الدخول إلى فضاء مؤسستك.',
  'login.notice': 'الدخول مقتصر على المستخدمين المصرح لهم. قد تُسجَّل المحاولات لأغراض أمنية.',
  'login.hero.title': 'كل دينار في الموسم، مرتبط بقطعته.',
  'login.hero.point1': 'التكاليف في الهكتار والهوامش حسب الموسم',
  'login.hero.point2': 'المخزون والمشتريات والمبيعات مرتبطة بالتدخلات',
  'login.hero.point3': 'إدخال دون اتصال على الهاتف، في الحقل',
  'login.hero.activities': 'الزراعات الكبرى · الأشجار المثمرة · تربية الماشية',
  'login.showPassword': 'إظهار كلمة المرور',
  'login.hidePassword': 'إخفاء كلمة المرور',
  'login.capsLock': 'تثبيت الأحرف الكبيرة مفعّل.',
  'login.rememberHint': 'إن لم تُحدَّد، تُغلق الجلسة مع علامة التبويب — لجهاز مشترك.',
  'mdpo.title': 'نسيت كلمة السر؟',
  'mdpo.subtitle': 'أدخل عنوانك: إذا كان مرتبطا بحساب، ستصلك رسالة تتضمن رابطا لاختيار كلمة سر جديدة.',
  'mdpo.email': 'البريد الإلكتروني',
  'mdpo.submit': 'إرسال الرابط',
  'mdpo.pending': 'جاري الإرسال…',
  'mdpo.sent': 'إذا كان هذا العنوان مرتبطا بحساب، فقد أُرسل إليه بريد إلكتروني للتو. تحقق أيضا من مجلد الرسائل غير المرغوب فيها.',
  'mdpo.back': 'العودة إلى تسجيل الدخول',
  'mdpn.title': 'كلمة سر جديدة',
  'mdpn.subtitle': 'اختر كلمة السر الجديدة لحسابك.',
  'mdpn.password': 'كلمة السر الجديدة',
  'mdpn.confirm': 'تأكيد كلمة السر',
  'mdpn.submit': 'تغيير كلمة السر',
  'mdpn.pending': 'جاري التغيير…',
  'mdpn.mismatch': 'كلمتا السر غير متطابقتين.',
  'mdpn.missingToken': 'هذا الرابط ناقص: لا يتضمن رمز إعادة التهيئة. افتح الرابط كما ورد في البريد الإلكتروني، أو اطلب رابطا جديدا.',
  'mdpn.requestNew': 'طلب رابط جديد',
  'mdpn.done': 'تم تغيير كلمة السر. يمكنك الآن تسجيل الدخول.',
  'mdpn.toLogin': 'تسجيل الدخول',

  'farms.title': 'الضيعات',
  'farms.search': 'البحث عن ضيعة',
  'farms.code': 'الرمز',
  'farms.name': 'الاسم',
  'farms.governorate': 'الولاية',
  'farms.manager': 'المسؤول',
  'farms.area': 'المساحة (هكتار)',
  'farms.empty': 'لا توجد ضيعات مسجلة.',

  'plots.title': 'قطع الأرض',
  'plots.search': 'البحث عن قطعة',
  'plots.code': 'الرمز',
  'plots.name': 'الاسم',
  'plots.area': 'المساحة (هكتار)',
  'plots.irrigable': 'قابلة للري',
  'plots.empty': 'لا توجد قطع مسجلة.',
  'plots.moduleRequired.title': 'وحدة المحاصيل الكبرى غير مشترَك بها.',
  'plots.moduleRequired.description': 'اشتراكك لا يتضمن وحدة المحاصيل الكبرى. اتصل بمسؤولك للوصول إليها.',
  'farms.quotaExceeded': 'تسمح خطتك بعدد محدود من الضيعات. غيّر خطتك لإضافة ضيعة جديدة.',
  'plots.quotaExceeded': 'تسمح خطتك بعدد محدود من قطع الأرض أو بمساحة قصوى. غيّر خطتك للمتابعة.',

  'orchard.plots.title': 'البساتين',
  'orchard.plots.search': 'البحث عن بستان',
  'orchard.plots.empty': 'لا توجد بساتين مسجلة.',
  'orchard.plots.farm': 'الضيعة',
  'orchard.plots.parcel': 'القطعة الأرضية',
  'orchard.plots.species': 'النوع',
  'orchard.plots.variety': 'الصنف',
  'orchard.plots.plantingYear': 'سنة الغراسة',
  'orchard.plots.density': 'الكثافة (شجرة/هكتار)',
  'orchard.plots.status': 'الحالة',
  'orchard.plots.status.young': 'فتي',
  'orchard.plots.status.production': 'في الإنتاج',
  'orchard.plots.status.decline': 'في تراجع',
  'orchard.plots.status.removed': 'مقلوع',
  'orchard.interventions.status.planned': 'مبرمج',
  'orchard.interventions.status.validated': 'مصادق عليه',
  'orchard.interventions.status.reversed': 'ملغى بقيد عكسي',
  'orchard.interventions.status.cancelled': 'ملغى',
  'orchard.plots.create': 'تسجيل البستان',
  'orchard.plots.plantationCost': 'تكلفة الغراسة',
  'orchard.plots.usefulLife': 'مدة الإنتاج (سنوات)',
  'orchard.plot.incomplete_amortization': 'تُصرَّح تكلفة الغراسة ومدة إهلاكها معًا.',
  'orchard.plot.invalid_plantation_cost': 'لا يمكن أن تكون تكلفة الغراسة سالبة.',
  'orchard.plot.invalid_useful_life': 'مدة الإنتاج بين سنة و100 سنة.',
  'orchard.plots.created': 'تم تسجيل البستان.',
  'orchard.plots.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.plots.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.plot.duplicate_plot': 'هذه القطعة تحمل بستانا بالفعل.',
  'orchard.plot.planting_year_in_future': 'لا يمكن أن تكون سنة الغراسة في المستقبل.',
  'orchard.plot.invalid_density': 'يجب أن تكون الكثافة موجبة تماما، بالأشجار في الهكتار.',
  'orchard.plot.plot_out_of_scope': 'القطعة الأرضية المحددة لا تنتمي إلى هذه الضيعة.',

  'orchard.interventions.title': 'تدخلات البستان',
  'orchard.interventions.search': 'البحث عن تدخل',
  'orchard.interventions.empty': 'لا توجد تدخلات مسجلة.',
  'orchard.interventions.plot': 'البستان',
  'orchard.interventions.type': 'نوع الأشغال',
  'orchard.interventions.date': 'تاريخ التدخل',
  'orchard.interventions.product': 'المنتج',
  'orchard.interventions.quantity': 'الكمية الخارجة',
  'orchard.interventions.create': 'تسجيل التدخل',
  'orchard.interventions.created': 'تم تسجيل التدخل.',
  'orchard.interventions.validate': 'اعتماد',
  'orchard.interventions.validated': 'تم اعتماد التدخل : أصبح مغلقا.',
  'orchard.interventions.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.interventions.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.intervention.warehouse_required': 'يتطلب إخراج المخزون مستودعا : حدد المستودع الذي يخرج منه المنتج.',
  'orchard.intervention.invalid_quantity': 'يجب أن تكون الكمية الخارجة موجبة تماما.',
  'orchard.intervention.movement_required': 'التدخل يستهلك منتجا : إخراج المخزون إلزامي.',
  'orchard.intervention.dar_required': 'المنتج ليس له مدة ما قبل الجني مسجلة : المعالجة الصحية بدون مدة مرفوضة.',
  'orchard.intervention.plot_out_of_scope': 'البستان المحدد لا ينتمي إلى هذه الضيعة.',
  'orchard.intervention.not_found': 'التدخل غير موجود.',
  'orchard.harvest_pass.not_found': 'جولة الجني غير موجودة.',
  'orchard.harvest_pass.window_closed': 'هذا الجني خارج فترة الجني الخاصة بالجهة.',
  'orchard.intervention.farm_mismatch': 'التدخل والبستان لا يخصان نفس الضيعة.',
  'orchard.intervention.farm_required': 'يجب ربط التدخل بضيعة.',
  'orchard.intervention.invalid_area': 'يجب أن تكون المساحة المعالجة موجبة تمامًا، بالهكتار.',
  'orchard.intervention.invalid_dose': 'لا يمكن أن تكون الجرعة في الهكتار سالبة.',
  'orchard.intervention.invalid_labor_cost': 'لا يمكن أن تكون كلفة اليد العاملة سالبة.',
  'orchard.intervention.invalid_labor_hours': 'لا يمكن أن تكون ساعات اليد العاملة سالبة.',
  'orchard.intervention.invalid_reversal': 'لا يُلغى بقيد عكسي إلا تدخل مصادق عليه.',
  'orchard.intervention.invalid_status_transition': 'هذا التغيير في الحالة غير مسموح به لهذا التدخل.',
  'orchard.intervention.movement_mismatch': 'حركة الإخراج من المخزون لا تطابق منتوج التدخل.',
  'orchard.intervention.movement_not_recorded': 'لم تُسجَّل حركة الإخراج من المخزون: لا يمكن ربط التدخل بها.',
  'orchard.intervention.movement_out_of_scope': 'حركة الإخراج هذه لا تخص مؤسستك.',
  'orchard.intervention.organization_required': 'يجب ربط التدخل بمؤسسة.',
  'orchard.intervention.plot_required': 'يجب أن يحدد التدخل بستانًا.',
  'orchard.intervention.product_out_of_scope': 'هذا المنتوج لا يخص مؤسستك أو تم سحبه.',
  'orchard.intervention.product_required': 'المنتوج المحدد غير صالح.',
  'orchard.intervention.reversal_out_of_scope': 'التدخل المراد إلغاؤه لا يخص مؤسستك.',
  'orchard.intervention.season_out_of_scope': 'هذا الموسم لا يخص مؤسستك أو تم سحبه.',
  'orchard.intervention.type_out_of_scope': 'نوع الأشغال هذا ليس في الدليل المشترك ولا في مؤسستك.',
  'orchard.intervention.type_required': 'يجب أن يحدد التدخل نوع الأشغال.',
  'orchard.irrigation.not_found': 'السقي غير موجود.',
  'orchard.phenology.farm_mismatch': 'الملاحظة والبستان لا يخصان نفس الضيعة.',
  'orchard.phenology.not_found': 'ملاحظة الأطوار غير موجودة.',
  'orchard.plot.crop_not_orchard': 'هذا الصنف لا يتبع غراسة الأشجار المثمرة.',
  'orchard.plot.crop_out_of_scope': 'هذا الصنف ليس في الدليل المشترك ولا في مؤسستك.',
  'orchard.plot.crop_required': 'يجب أن يحدد البستان صنفًا من الأشجار المثمرة.',
  'orchard.plot.farm_mismatch': 'البستان والقطعة العقارية لا يخصان نفس الضيعة.',
  'orchard.plot.farm_required': 'يجب ربط البستان بضيعة.',
  'orchard.plot.invalid_certification': 'الشهادة تقليدية أو بيولوجية أو في طور التحويل.',
  'orchard.plot.invalid_planting_month': 'شهر الغراسة بين 1 و 12.',
  'orchard.plot.invalid_planting_year': 'يجب أن تكون سنة الغراسة بين 1900 و 2100.',
  'orchard.plot.invalid_status': 'حالة البستان غير معروفة.',
  'orchard.plot.invalid_status_transition': 'لا يعود البستان إلى الوراء في دورة حياته.',
  'orchard.plot.irrigation_required': 'نمط الري إلزامي: في البعلي اختر «مطري».',
  'orchard.plot.not_found': 'البستان غير موجود.',
  'orchard.plot.organization_required': 'يجب ربط البستان بمؤسسة.',
  'orchard.plot.plot_required': 'يجب ربط البستان بقطعة عقارية.',
  'orchard.plot.variety_crop_mismatch': 'هذا الصنف الفرعي لا ينتمي إلى صنف البستان.',
  'orchard.plot.variety_not_orchard': 'هذا الصنف الفرعي لا يتبع غراسة الأشجار المثمرة.',
  'orchard.plot.variety_out_of_scope': 'هذا الصنف الفرعي ليس في الدليل المشترك ولا في مؤسستك.',
  'orchard.plot.variety_required': 'الصنف الفرعي المحدد غير صالح.',
  'orchard.transformation.not_found': 'التحويل غير موجود.',
  'orchard.intervention.immutable': 'التدخل المعتمد غير قابل للتعديل : صححه بإلغاء مقابل.',

  'orchard.harvest.title': 'جني البستان',
  'orchard.harvest.search': 'البحث عن دفعة جني',
  'orchard.harvest.empty': 'لا توجد دفعات جني مسجلة.',
  'orchard.harvest.plot': 'البستان',
  'orchard.harvest.season': 'الموسم',
  'orchard.harvest.passNumber': 'رقم الدفعة',
  'orchard.harvest.date': 'تاريخ الجني',
  'orchard.harvest.gross': 'الوزن الإجمالي (كغ)',
  'orchard.harvest.tare': 'الوزن الفارغ (كغ)',
  'orchard.harvest.net': 'الوزن الصافي (كغ)',
  'orchard.harvest.workers': 'الجامعون',
  'orchard.harvest.create': 'تسجيل الدفعة',
  'orchard.harvest.created': 'تم تسجيل دفعة الجني.',
  'orchard.harvest.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.harvest.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.harvest_pass.organization_required': 'يجب أن تُربط دفعة الجني بمؤسسة.',
  'orchard.harvest_pass.farm_required': 'يجب أن تُربط دفعة الجني بضيعة.',
  'orchard.harvest_pass.plot_required': 'يجب أن تحدد دفعة الجني بستانا.',
  'orchard.harvest_pass.season_required': 'يجب أن تحدد دفعة الجني موسما.',
  'orchard.harvest_pass.invalid_pass_number': 'يجب أن يكون رقم الدفعة موجبا تماما.',
  'orchard.harvest_pass.invalid_weights': 'يجب أن يكون الوزن الإجمالي موجبا والوزن الفارغ أقل منه تماما.',
  'orchard.harvest_pass.invalid_worker_count': 'لا يمكن أن يكون عدد الجامعين سالبا.',
  'orchard.harvest_pass.invalid_labor_cost': 'لا يمكن أن تكون تكلفة اليد العاملة سالبة.',
  'orchard.harvest_pass.plot_out_of_scope': 'البستان المحدد لا ينتمي إلى هذه الضيعة.',
  'orchard.harvest_pass.farm_mismatch': 'الدفعة والبستان لا يشيران إلى نفس الضيعة.',
  'orchard.harvest_pass.season_out_of_scope': 'الموسم المحدد لا ينتمي إلى هذه الضيعة.',
  'orchard.harvest_pass.dar_active': 'القطعة لا تزال تحت مدة ما قبل الجني : الجني مرفوض قبل انقضائها.',
  'orchard.quality.title': 'مراقبة الجودة',
  'orchard.quality.search': 'البحث عن مراقبة جودة',
  'orchard.quality.empty': 'لا توجد مراقبات جودة مسجلة.',
  'orchard.quality.pass': 'دفعة الجني',
  'orchard.quality.date': 'تاريخ المراقبة',
  'orchard.quality.acidity': 'الحموضة (%)',
  'orchard.quality.peroxide': 'مؤشر البيروكسيد',
  'orchard.quality.brix': 'بريكس (%)',
  'orchard.quality.caliber': 'الحجم',
  'orchard.quality.defect': 'العيوب (%)',
  'orchard.quality.oilYield': 'مردود الزيت (%)',
  'orchard.quality.classification': 'التصنيف',
  'orchard.quality.create': 'تسجيل المراقبة',
  'orchard.quality.created': 'تم تسجيل مراقبة الجودة.',
  'orchard.quality.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.quality.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.quality_control.organization_required': 'يجب أن تُربط مراقبة الجودة بمؤسسة.',
  'orchard.quality_control.farm_required': 'يجب أن تُربط مراقبة الجودة بضيعة.',
  'orchard.quality_control.pass_required': 'يجب أن تحدد مراقبة الجودة دفعة جني.',
  'orchard.quality_control.invalid_acidity': 'لا يمكن أن تكون الحموضة سالبة.',
  'orchard.quality_control.invalid_peroxide': 'لا يمكن أن يكون مؤشر البيروكسيد سالبا.',
  'orchard.quality_control.invalid_brix': 'بريكس نسبة مئوية، بين 0 و100.',
  'orchard.quality_control.invalid_defect': 'نسبة العيوب مئوية، بين 0 و100.',
  'orchard.quality_control.invalid_oil_yield': 'مردود الزيت نسبة مئوية، بين 0 و100.',
  'orchard.quality_control.pass_out_of_scope': 'دفعة الجني المحددة لا تنتمي إلى هذه الضيعة.',
  'orchard.quality_control.farm_mismatch': 'المراقبة والدفعة لا تشيران إلى نفس الضيعة.',
  'orchard.irrigation.title': 'سقي البستان',
  'orchard.irrigation.search': 'البحث عن سقية',
  'orchard.irrigation.empty': 'لا توجد سقية مسجلة.',
  'orchard.irrigation.plot': 'البستان',
  'orchard.irrigation.date': 'تاريخ السقي',
  'orchard.irrigation.volume': 'الحجم (م³)',
  'orchard.irrigation.waterSource': 'مصدر المياه',
  'orchard.irrigation.costPerM3': 'سعر المتر المكعب',
  'orchard.irrigation.costTotal': 'الكلفة الجملية',
  'orchard.irrigation.create': 'تسجيل السقية',
  'orchard.irrigation.created': 'تم تسجيل السقية.',
  'orchard.irrigation.moduleRequired.title': 'وحدة البستنة غير مشترك بها.',
  'orchard.irrigation.moduleRequired.description': 'اشتراكك لا يشمل وحدة البستنة. اتصل بالمسؤول لتفعيلها.',
  'orchard.irrigation.organization_required': 'يجب ربط السقية بمؤسسة.',
  'orchard.irrigation.farm_required': 'يجب ربط السقية بضيعة.',
  'orchard.irrigation.plot_required': 'يجب أن تحدد السقية بستانا.',
  'orchard.irrigation.invalid_volume': 'يجب أن يكون حجم المياه أكبر من صفر.',
  'orchard.irrigation.water_source_required': 'يجب أن تحدد السقية مصدر المياه.',
  'orchard.irrigation.invalid_cost': 'لا يمكن أن يكون سعر المتر المكعب سلبيا.',
  'orchard.irrigation.plot_out_of_scope': 'البستان المحدد لا ينتمي إلى هذه الضيعة.',
  'orchard.irrigation.farm_mismatch': 'السقية والبستان لا يشيران إلى نفس الضيعة.',
  'orchard.irrigation.movement_out_of_scope': 'حركة المخزون المحددة لا تنتمي إلى هذه الضيعة.',
  'orchard.transformation.title': 'تحويل الزيتون',
  'orchard.transformation.search': 'البحث عن تحويل',
  'orchard.transformation.empty': 'لا يوجد تحويل مسجل.',
  'orchard.transformation.pass': 'دفعة الجني',
  'orchard.transformation.date': 'تاريخ التحويل',
  'orchard.transformation.mill': 'المعصرة',
  'orchard.transformation.input': 'دخول الزيتون (كغ)',
  'orchard.transformation.output': 'خروج الزيت (كغ)',
  'orchard.transformation.yield': 'المردود (%)',
  'orchard.transformation.acidity': 'الحموضة (%)',
  'orchard.transformation.classification': 'صنف الزيت',
  'orchard.transformation.batch': 'الدفعة',
  'orchard.transformation.create': 'تسجيل التحويل',
  'orchard.transformation.created': 'تم تسجيل التحويل.',
  'orchard.transformation.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.transformation.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.transformation.classification.extra_virgin': 'زيت زيتون بكر ممتاز',
  'orchard.transformation.classification.virgin': 'زيت زيتون بكر',
  'orchard.transformation.classification.lampante': 'زيت زيتون لامبانتي',
  'orchard.transformation.organization_required': 'يجب أن يُربط التحويل بمؤسسة.',
  'orchard.transformation.farm_required': 'يجب أن يُربط التحويل بضيعة.',
  'orchard.transformation.pass_required': 'يجب أن يحدد التحويل دفعة الجني.',
  'orchard.transformation.invalid_input': 'يجب أن تكون كمية الزيتون الداخلة أكبر من صفر.',
  'orchard.transformation.invalid_output': 'يجب أن تكون كمية الزيت الخارجة أكبر من صفر.',
  'orchard.transformation.output_exceeds_input': 'لا يمكن أن يتجاوز الزيت الخارج الزيتون الداخل.',
  'orchard.transformation.invalid_acidity': 'لا يمكن أن تكون حموضة الزيت سلبية.',
  'orchard.transformation.pass_out_of_scope': 'دفعة الجني المحددة لا تنتمي إلى هذه الضيعة.',
  'orchard.transformation.farm_mismatch': 'التحويل ودفعة الجني لا يشيران إلى نفس الضيعة.',

  'orchard.phenology.title': 'فينولوجيا البستان',
  'orchard.phenology.search': 'البحث عن ملاحظة',
  'orchard.phenology.empty': 'لا توجد ملاحظة مسجلة.',
  'orchard.phenology.plot': 'البستان',
  'orchard.phenology.date': 'تاريخ الملاحظة',
  'orchard.phenology.stage': 'مرحلة BBCH',
  'orchard.phenology.stage_label': 'تسمية المرحلة',
  'orchard.phenology.notes': 'ملاحظات',
  'orchard.phenology.create': 'تسجيل الملاحظة',
  'orchard.phenology.created': 'تم تسجيل الملاحظة.',
  'orchard.phenology.moduleRequired.title': 'وحدة الأشجار المثمرة غير مشترَك بها.',
  'orchard.phenology.moduleRequired.description': 'اشتراكك لا يتضمن وحدة الأشجار المثمرة. اتصل بمسؤولك للوصول إليها.',
  'orchard.phenology.organization_required': 'يجب أن تُربط الملاحظة بمؤسسة.',
  'orchard.phenology.farm_required': 'يجب أن تُربط الملاحظة بضيعة.',
  'orchard.phenology.plot_required': 'يجب أن تحدد الملاحظة بستانا.',
  'orchard.phenology.stage_required': 'مرحلة BBCH المرصودة إلزامية.',
  'orchard.phenology.plot_out_of_scope': 'البستان المحدد لا ينتمي إلى هذه الضيعة.',

  'orchard.reports.title': 'تكاليف بستان الأشجار المثمرة وربحيته',
  'orchard.reports.search': 'التكلفة حسب البستان والموسم',
  'orchard.reports.empty': 'لا توجد تكلفة بستان لهذا الموسم.',
  'orchard.reports.season': 'الموسم',
  'orchard.reports.campaign': 'الموسم',
  'orchard.reports.plot': 'البستان',
  'orchard.reports.area': 'المساحة',
  'orchard.reports.interventions': 'الأعمال',
  'orchard.reports.irrigation': 'الماء',
  'orchard.reports.harvest': 'الحصاد',
  'orchard.reports.amortized': 'حصة الإهلاك',
  'orchard.reports.total': 'التكلفة الإجمالية',
  'orchard.reports.yield': 'الحصاد (كغ)',
  'orchard.reports.costPerHa': 'التكلفة / هكتار',
  'orchard.reports.costPerKg': 'التكلفة / كغ',

  'orchard.region': 'المنطقة',
  'orchard.region.none': 'غير محددة',
  'orchard.region.nord': 'الشمال',
  'orchard.region.nord_ouest': 'الشمال الغربي',
  'orchard.region.centre': 'الوسط',
  'orchard.region.centre_sud': 'الوسط الجنوبي',
  'orchard.region.cap_bon': 'الوطن القبلي',
  'orchard.region.sahel': 'الساحل',
  'orchard.region.djerid_nefzaoua': 'الجريد ونفزاوة',
  'orchard.region.mornag': 'المرناق',
  'orchard.plot.invalid_region': 'منطقة البستان غير معروفة في الجهة.',

  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.loading': 'جاري التحميل…',
  'common.retry': 'إعادة المحاولة',
  'common.language': 'اللغة',
  'common.page': 'الصفحة',
  'common.previous': 'السابق',
  'common.next': 'التالي',

  'error.network': 'الخادم لا يستجيب. تحقق من اتصالك.',
  'error.unexpected': 'حدث خطأ غير متوقع.',
  'request.invalid_body': 'تعذّرت قراءة الإدخال: تحقّق من الأرقام (نقطة عشرية) والتواريخ.',
  'db.duplicate': 'هذه القيمة موجودة مسبقًا: يجب أن يكون الرمز أو البريد الإلكتروني فريدًا.',
  'db.reference_missing': 'أحد العناصر المحدّدة غير موجود (صنف أو وحدة أو طرف أو دفعة).',
  'db.check_violation': 'قيمة خارج الحدود المسموح بها.',
  'db.value_required': 'قيمة إلزامية مفقودة.',
  'saas.no_active_subscription': 'لا يوجد اشتراك نشط: اتصل بالمسؤول قبل إنشاء ضيعات أو قطع.',
  'saas.quota.farms_exceeded': 'خطتك لا تسمح بإضافة ضيعة أخرى.',
  'saas.quota.plots_exceeded': 'خطتك لا تسمح بإضافة قطعة أخرى.',
  'saas.quota.users_exceeded': 'خطتك لا تسمح بإضافة مستخدم آخر.',
  'saas.quota.area_exceeded': 'المساحة الإجمالية ستتجاوز سقف خطتك.',
  'purchase.order.no_lines': 'أضف سطرًا واحدًا على الأقل: طلب بلا أسطر لا يطلب شيئًا.',
  'purchases.receive.empty': 'أدخل كمية واصلة في سطر واحد على الأقل.',
  'purchases.cancel.confirm': 'إلغاء هذا الطلب؟ الإلغاء مسجَّل ونهائي.',
  'stock.warehouse.capacity_unit_required': 'يجب التعبير عن السعة بوحدة قياس.',
  'stock.transfer.incomplete': 'حدّد مستودع المصدر ومستودع الوجهة والصنف والكمية.',
  'operations.header_required': 'اختر الضيعة وطبيعة الأعمال قبل الحفظ.',
  'operations.consumption_incomplete': 'اختر صنفًا وكمية موجبة تمامًا.',
  'users.admin_only_page': 'إدارة المستخدمين مخصّصة للمسؤولين.',
  'subscription.none': 'لا يوجد اشتراك نشط مسجَّل لمؤسستك.',
  'subscription.plan': 'الخطة',
  'subscription.status': 'الحالة',
  'subscription.period': 'الفترة الجارية',
  'subscription.unlimited': 'بلا سقف',
  'subscription.module': 'الوحدة',
  'subscription.noModule': 'لا توجد وحدة نشاط مشترَك فيها.',
  'subscription.usage': 'الاستهلاك',
  'subscription.unpaidTitle': 'في حال عدم الخلاص',
  'subscription.unpaid1': 'بعد مهلة السماح يصبح الحساب للقراءة فقط.',
  'subscription.unpaid2': 'الاطلاع والتصدير يبقيان مفتوحين؛ الإدخال وحده يُغلق.',
  'subscription.unpaid3': 'لا تُحذف أي معطيات.',
  'common.active': 'نشط',
  'auth.invalid_credentials': 'بيانات الدخول غير صحيحة.',
  'auth.account_locked': 'الحساب مقفل مؤقتا. أعد المحاولة بعد بضع دقائق.',
  'auth.account_disabled': 'الحساب معطل. اتصل بمسؤول مؤسستك.',
  'auth.password_too_short': 'يجب أن تتكوّن كلمة المرور من عشرة أحرف على الأقل.',
  'auth.invalid_reset_token': 'لم يبق هذا الرابط صالحا: انتهت مدته أو سبق استخدامه. اطلب رابطا جديدا.',
  'growth.dash.title': 'لوحة القيادة',
  'growth.dash.description': 'الإيرادات المتكررة والعملاء والمستحقات غير المدفوعة والمقبوضات، محسوبة في اليوم نفسه.',
  'growth.dash.mrr': 'الإيراد الشهري المتكرر (دون أداءات)',
  'growth.dash.arr': 'سنويا',
  'growth.dash.clients': 'العملاء',
  'growth.dash.churn30': 'المغادرون خلال 30 يوما',
  'growth.dash.outstanding': 'المتبقي للاستخلاص (مع الأداءات)',
  'growth.dash.overdue': 'منها متأخر',
  'growth.dash.collectedMonth': 'المقبوض هذا الشهر',
  'growth.dash.collectedYear': 'هذه السنة',
  'growth.dash.renewals30': 'التجديدات خلال 30 يوما',
  'growth.dash.openRequests': 'الطلبات المفتوحة',
  'growth.dash.byStatus': 'الاشتراكات حسب الحالة',
  'growth.dash.byPlan': 'حسب الباقة',
  'growth.dash.plan': 'الباقة',
  'growth.dash.trialsEnding': 'فترات تجريبية تنتهي خلال 14 يوما',
  'growth.dash.trialEnds': 'نهاية الفترة التجريبية',
  'growth.dash.noTrials': 'لا تنتهي أي فترة تجريبية قريبا.',
  'growth.addons.title': 'الخيارات والتخفيض',
  'growth.addons.kind': 'الخيار',
  'growth.addons.unitPrice': 'السعر لكل فترة (دون أداءات)',
  'growth.addons.since': 'منذ',
  'growth.addons.empty': 'لا توجد خيارات جارية.',
  'growth.addons.add': 'إضافة خيار',
  'growth.addons.added': 'أُضيف الخيار ورُفع الحد.',
  'growth.addons.end': 'سحب',
  'growth.addons.endTitle': 'سحب هذا الخيار',
  'growth.addons.ended': 'سُحب الخيار.',
  'growth.addons.priceHint': 'اتركه فارغا للاستغلاليات: سعر الباقة.',
  'growth.addonKind.farms': 'استغلاليات إضافية',
  'growth.addonKind.users': 'مستخدمون إضافيون',
  'growth.addonKind.plots': 'قطع إضافية',
  'growth.addonKind.area': 'هكتارات إضافية',
  'growth.discount.title': 'التخفيض',
  'growth.discount.apply': 'تطبيق الرمز',
  'growth.discount.applied': 'طُبّق التخفيض على الفواتير القادمة.',
  'growth.discount.remove': 'سحب التخفيض',
  'growth.discount.removed': 'سُحب التخفيض.',
  'growth.discount.remaining': 'الفترات المتبقية:',
  'growth.codes.title': 'رموز التخفيض',
  'growth.codes.description': 'نسبة من 1 إلى 99، لعدد من الفترات أو دون نهاية. ما مُنح سابقا لا يتغيّر عند تعديل الرمز.',
  'growth.codes.list': 'الرموز',
  'growth.codes.form': 'إنشاء رمز أو تعديله',
  'growth.codes.code': 'الرمز',
  'growth.codes.percent': 'التخفيض (٪)',
  'growth.codes.percentHint': 'من 1 إلى 99.',
  'growth.codes.duration': 'المدة (فترات)',
  'growth.codes.durationHint': 'فارغ: ما دام الاشتراك قائما.',
  'growth.codes.forever': 'دون نهاية',
  'growth.codes.validUntil': 'صالح حتى',
  'growth.codes.max': 'أقصى عدد من الاستعمالات',
  'growth.codes.uses': 'الاستعمالات',
  'growth.codes.descriptionLabel': 'الوصف',
  'growth.codes.inactive': 'معطّل',
  'growth.codes.edit': 'تعديل',
  'growth.codes.empty': 'لا توجد رموز حاليا.',
  'growth.codes.saved': 'تم حفظ الرمز.',
  'growth.requests.title': 'طلبات العملاء',
  'growth.requests.description': 'ما يطلبه العملاء: تغيير الباقة أو الفسخ أو إضافة خيار. غلق الطلب لا ينفّذ الإجراء: يُنفَّذ في ملف العميل.',
  'growth.requests.list': 'الطلبات',
  'growth.requests.empty': 'لا توجد طلبات.',
  'growth.requests.date': 'التاريخ',
  'growth.requests.kind': 'الطلب',
  'growth.requests.message': 'الرسالة',
  'growth.requests.response': 'الرد',
  'growth.requests.handle': 'معالجة',
  'growth.requests.handleHint': 'يظهر الرد للعميل، وهو إجباري في حالة الرفض.',
  'growth.requests.done': 'وضع علامة كمنجز',
  'growth.requests.decline': 'رفض',
  'growth.requests.handled': 'أُغلق الطلب.',
  'growth.requestKind.plan_change': 'تغيير الباقة',
  'growth.requestKind.cancel': 'فسخ',
  'growth.requestKind.addon': 'خيار',
  'growth.requestKind.other': 'أخرى',
  'growth.requestStatus.open': 'قيد الانتظار',
  'growth.requestStatus.done': 'منجزة',
  'growth.requestStatus.declined': 'مرفوضة',
  'growth.leads.title': 'العملاء المحتملون من الموقع',
  'growth.leads.description': 'طلبات التواصل الواردة من الموقع الإلكتروني، من الأحدث إلى الأقدم. لا يُنبَّه أحد عند وصولها: هنا تُقرأ وتُصنَّف.',
  'growth.leads.list': 'العملاء المحتملون',
  'growth.leads.empty': 'لا يوجد أي عميل محتمل حاليا.',
  'growth.leads.date': 'التاريخ',
  'growth.leads.name': 'الاسم',
  'growth.leads.company': 'المؤسسة',
  'growth.leads.email': 'البريد الإلكتروني',
  'growth.leads.phone': 'الهاتف',
  'growth.leads.governorate': 'الولاية',
  'growth.leads.source': 'المصدر',
  'growth.leads.status': 'الحالة',
  'growth.leads.message': 'الرسالة',
  'growth.leadStatus.new': 'جديد',
  'growth.leadStatus.handled': 'مُعالَج',
  'growth.leadStatus.spam': 'غير مرغوب فيه',
  'growth.leads.action': 'التصنيف',
  'growth.leads.markHandled': 'وسم كمُعالَج',
  'growth.leads.markSpam': 'غير مرغوب فيه',
  'growth.leads.reopen': 'إعادة فتح',
  'growth.lead_status_invalid': 'العميل المحتمل إما جديد أو مُعالَج أو غير مرغوب فيه.',
  'growth.lead_not_found': 'هذا العميل المحتمل غير موجود.',
  'portal.invoices.title': 'فواتيري',
  'portal.plans.title': 'تغيير الباقة',
  'portal.plans.hint': 'الأسعار دون أداءات. تدرس المنصة طلب التغيير، ولا يتغيّر شيء قبل ردّها.',
  'portal.plans.current': 'الحالية',
  'portal.plans.price': 'السعر',
  'portal.plans.month': 'شهر',
  'portal.plans.year': 'سنة',
  'portal.plans.period': 'فترة',
  'portal.plans.features': 'الوظائف',
  'portal.plans.ask': 'طلب',
  'portal.requests.title': 'طلباتي',
  'portal.requests.send': 'إرسال الطلب',
  'portal.requests.sent': 'أُرسل الطلب إلى المنصة.',
  'growth.addon_kind_unknown': 'خيار غير معروف.',
  'growth.addon_quantity_invalid': 'الكمية موجبة وصحيحة إلا بالنسبة للهكتارات.',
  'growth.addon_price_required': 'حدّد سعر الخيار لكل فترة: الباقة لا تحدّده.',
  'growth.addon_not_found': 'هذا الخيار غير موجود أو سُحب مسبقا.',
  'growth.code_invalid': 'يتكوّن الرمز من 2 إلى 30 حرفا أو رقما أو شرطة.',
  'growth.percent_invalid': 'التخفيض بين 1 و99 ٪.',
  'growth.cycles_invalid': 'يدوم التخفيض من 1 إلى 120 فترة أو دون نهاية.',
  'growth.max_invalid': 'الحد الأقصى للاستعمالات موجب.',
  'growth.code_unavailable': 'هذا الرمز غير موجود أو معطّل أو منتهي الصلاحية.',
  'growth.code_exhausted': 'بلغ هذا الرمز الحد الأقصى لاستعمالاته.',
  'growth.discount_exists': 'يوجد تخفيض جار: اسحبه أولا.',
  'growth.request_status_invalid': 'يُغلق الطلب كمنجز أو مرفوض.',
  'growth.request_not_found': 'هذا الطلب غير موجود أو مغلق مسبقا.',
  'subscription.request_kind_unknown': 'نوع طلب غير معروف.',
  'subscription.request_message_required': 'اذكر ما تطلبه.',
  'subscription.request_open': 'يوجد طلب من هذا النوع قيد المعالجة.',
  'platform.nav.invoices': 'الفواتير',
  'billing.invoices.title': 'الفواتير والإشعارات الدائنة',
  'billing.invoices.description': 'ما فوترته المنصة لعملائها. لا تُعدَّل الفاتورة بعد إصدارها بل تُصحَّح بإشعار دائن.',
  'billing.invoices.list': 'الوثائق الصادرة',
  'billing.invoices.empty': 'لا توجد فواتير حاليا.',
  'billing.col.number': 'الرقم',
  'billing.col.client': 'العميل',
  'billing.col.issuedOn': 'تاريخ الإصدار',
  'billing.col.dueOn': 'تاريخ الاستحقاق',
  'billing.col.total': 'المجموع مع الأداءات',
  'billing.col.balance': 'المتبقي للدفع',
  'billing.col.status': 'الحالة',
  'billing.status.all': 'كل الحالات',
  'billing.status.unpaid': 'غير مدفوعة',
  'billing.status.partial': 'مدفوعة جزئيا',
  'billing.status.paid': 'مدفوعة',
  'billing.status.overdue': 'متأخرة',
  'billing.status.cancelled': 'ملغاة بإشعار دائن',
  'billing.status.credit_note': 'إشعار دائن',
  'billing.kind.invoice': 'فاتورة',
  'billing.kind.credit_note': 'إشعار دائن',
  'billing.seller.title': 'ملف الفوترة',
  'billing.seller.description': 'هوية المنصة على فواتيرها. تعديلها لا يغيّر أي فاتورة صادرة.',
  'billing.seller.incompleteTitle': 'لا تصدر أي فاتورة',
  'billing.seller.incomplete': 'ما دامت التسمية الاجتماعية والمعرف الجبائي وعنوان المنصة ناقصة، لا تصدر أي فاتورة، لا يدويا ولا عند التجديد.',
  'billing.seller.complete': 'إكمال الملف',
  'billing.seller.ready': 'جاهز للفوترة',
  'billing.seller.identity': 'الهوية',
  'billing.seller.terms': 'الشروط',
  'billing.seller.legalName': 'التسمية الاجتماعية',
  'billing.seller.taxId': 'المعرف الجبائي',
  'billing.seller.taxIdHint': 'كما هو مدوّن في بطاقة التعريف الجبائي.',
  'billing.seller.address': 'العنوان',
  'billing.seller.city': 'المدينة',
  'billing.seller.postalCode': 'الرمز البريدي',
  'billing.seller.phone': 'الهاتف',
  'billing.seller.email': 'البريد الإلكتروني',
  'billing.seller.bankName': 'البنك',
  'billing.seller.bankAccount': 'المعرف البنكي',
  'billing.seller.vatRate': 'نسبة الأداء على القيمة المضافة (٪)',
  'billing.seller.stampDuty': 'معلوم الطابع الجبائي (د.ت)',
  'billing.seller.paymentTermsDays': 'أجل الدفع (أيام)',
  'billing.seller.footer': 'ملاحظات أسفل الصفحة',
  'billing.seller.footerHint': 'الشكل القانوني، رأس المال، السجل التجاري…',
  'billing.seller.autoInvoice': 'الفوترة الآلية عند التجديد',
  'billing.seller.autoInvoiceHint': 'عند نهاية كل فترة تجريبية أو استحقاق، تصدر مهمة دورة الحياة فاتورة الفترة الجديدة.',
  'billing.seller.saved': 'تم حفظ ملف الفوترة.',
  'billing.detail.back': 'كل الفواتير',
  'billing.detail.pdf': 'تنزيل ملف PDF',
  'billing.detail.downloaded': 'تم تنزيل ملف PDF.',
  'billing.detail.summary': 'الوثيقة',
  'billing.detail.period': 'الفترة',
  'billing.detail.credited': 'إشعار دائن على الفاتورة',
  'billing.detail.lines': 'السطور والمجاميع',
  'billing.detail.description': 'البيان',
  'billing.detail.quantity': 'الكمية',
  'billing.detail.unitPrice': 'سعر الوحدة دون أداءات',
  'billing.detail.amountHt': 'المبلغ دون أداءات',
  'billing.detail.totalHt': 'المجموع دون أداءات',
  'billing.detail.vat': 'الأداء على القيمة المضافة',
  'billing.detail.stamp': 'الطابع الجبائي',
  'billing.detail.totalTtc': 'المجموع مع الأداءات',
  'billing.detail.payments': 'الدفوعات',
  'billing.detail.noPayments': 'لا توجد دفوعات مسجلة.',
  'billing.detail.credits': 'الإشعارات الدائنة',
  'billing.detail.noCredits': 'لا يوجد إشعار دائن على هذه الفاتورة.',
  'billing.payment.title': 'تسجيل دفعة',
  'billing.payment.amount': 'المبلغ (د.ت)',
  'billing.payment.date': 'تاريخ الدفع',
  'billing.payment.dateHint': 'فارغ: اليوم.',
  'billing.payment.method': 'وسيلة الدفع',
  'billing.payment.reference': 'المرجع',
  'billing.payment.submit': 'تسجيل الدفعة',
  'billing.payment.recorded': 'تم تسجيل الدفعة.',
  'billing.payment.reactivated': 'تم تسجيل الدفعة: سُدّدت الفاتورة واستُؤنف اشتراك العميل.',
  'billing.payment.void': 'إلغاء',
  'billing.payment.voidTitle': 'إلغاء هذه الدفعة',
  'billing.payment.voidReason': 'سبب الإلغاء',
  'billing.payment.voided': 'أُلغيت الدفعة، وتبقى ظاهرة مشطوبة.',
  'billing.payment.voidedBadge': 'ملغاة',
  'billing.method.transfer': 'تحويل بنكي',
  'billing.method.cheque': 'صك',
  'billing.method.cash': 'نقدا',
  'billing.method.card': 'بطاقة',
  'billing.method.other': 'أخرى',
  'billing.credit.title': 'إصدار إشعار دائن',
  'billing.credit.full': 'إلغاء الفاتورة كاملة (بما في ذلك الطابع الجبائي)',
  'billing.credit.amountHt': 'المبلغ دون أداءات المراد إرجاعه',
  'billing.credit.reason': 'السبب',
  'billing.credit.submit': 'إصدار الإشعار الدائن',
  'billing.credit.created': 'صدر الإشعار الدائن',
  'billing.new.note': 'ملاحظة',
  'billing.client.title': 'الفواتير',
  'billing.client.currentPeriod': 'فوترة الفترة الجارية',
  'billing.client.newInvoice': 'فاتورة جديدة',
  'billing.client.addLine': 'إضافة سطر',
  'billing.client.issue': 'إصدار الفاتورة',
  'billing.client.issued': 'صدرت الفاتورة.',
  'billing.client.priceHint': 'دون أداءات، بدقة المليم.',
  'billing.seller_incomplete': 'أدخل التسمية الاجتماعية والمعرف الجبائي وعنوان المنصة قبل إصدار أي فاتورة.',
  'billing.lines_required': 'يجب أن تتضمن الفاتورة سطرا واحدا على الأقل.',
  'billing.line_invalid': 'لكل سطر بيان وكمية موجبة وسعر بدقة المليم.',
  'billing.total_zero': 'لا تصدر فاتورة بمبلغ صفر.',
  'billing.date_in_future': 'لا يمكن تأريخ فاتورة في المستقبل.',
  'billing.date_before_last': 'الترقيم يتبع التسلسل الزمني: لا يمكن أن يسبق التاريخ تاريخ آخر فاتورة.',
  'billing.legal_name_required': 'التسمية الاجتماعية للمنصة إجبارية.',
  'billing.vat_invalid': 'نسبة الأداء على القيمة المضافة بين 0 و100 ٪.',
  'billing.stamp_invalid': 'معلوم الطابع بين 0 و100 د.ت.',
  'billing.terms_invalid': 'أجل الدفع بين 0 و120 يوما.',
  'billing.no_billable_subscription': 'ليس لهذا العميل اشتراك جار للفوترة.',
  'billing.period_already_invoiced': 'هذه الفترة من الاشتراك مفوترة مسبقا.',
  'billing.invoice_not_found': 'هذه الفاتورة غير موجودة.',
  'billing.method_unknown': 'وسيلة دفع غير معروفة.',
  'billing.payment_date_invalid': 'تاريخ الدفع مفقود أو في المستقبل أو لا علاقة له بالفاتورة.',
  'billing.amount_invalid': 'المبلغ موجب وبدقة المليم.',
  'billing.payment_exceeds_balance': 'المبلغ يتجاوز المتبقي للدفع على الفاتورة.',
  'billing.payment_not_found': 'هذه الدفعة غير موجودة أو ملغاة مسبقا.',
  'billing.already_credited': 'هذه الفاتورة مُرجعة بالكامل مسبقا.',
  'billing.credit_exceeds_invoice': 'الإشعار الدائن يتجاوز ما تبقى للإرجاع على الفاتورة.',
  'auth.use_admin_portal': 'هذا الحساب يدير المنصة: سجّل الدخول عبر بوابة الإدارة.',
  'auth.use_client_portal': 'هذه البوابة مخصصة لمسؤولي المنصة. سجّل الدخول عبر التطبيق.',
  'auth.challenge_invalid': 'انتهت صلاحية خطوة الدخول. أعد المحاولة بكلمة المرور.',
  'auth.totp_invalid': 'الرمز غير صحيح أو سبق استعماله.',
  'auth.totp_unavailable': 'تعذّرت قراءة المصادقة الثنائية لهذا الحساب. يجب على المشغّل إعادة تهيئتها.',
  'auth.wrong_portal': 'هذه الجلسة لا تفتح هذا الفضاء. أعد تسجيل الدخول.',
  'admin.title': 'بوابة الإدارة',
  'admin.login.subtitle': 'دخول مخصص لمسؤولي المنصة، مع مصادقة ثنائية.',
  'admin.login.continue': 'متابعة',
  'admin.enroll.title': 'تفعيل المصادقة الثنائية',
  'admin.enroll.intro': 'امسح هذا الرمز بتطبيق المصادقة (Google Authenticator أو Microsoft Authenticator أو Aegis…)، ثم أدخل الرمز المكوّن من ستة أرقام الذي يعرضه.',
  'admin.enroll.qrAlt': 'رمز QR لتطبيق المصادقة',
  'admin.enroll.manual': 'إدخال المفتاح يدويا',
  'admin.code.label': 'رمز من ستة أرقام',
  'admin.code.submit': 'تحقّق',
  'admin.verify.title': 'التحقق بخطوتين',
  'admin.verify.intro': 'أدخل الرمز الذي يعرضه تطبيق المصادقة.',
  'admin.verify.useRecovery': 'استعمال رمز احتياطي',
  'admin.verify.useApp': 'استعمال التطبيق',
  'admin.recovery.label': 'رمز احتياطي',
  'admin.recovery.title': 'رموزك الاحتياطية',
  'admin.recovery.intro': 'يُستعمل كل رمز مرة واحدة إذا فقدت هاتفك. لن تُعرض مجددا: احفظها خارج الإنترنت في مكان آمن.',
  'admin.recovery.copy': 'نسخ الرموز',
  'admin.recovery.copied': 'تم نسخ الرموز.',
  'admin.recovery.saved': 'لقد حفظتها',
  'admin.restart': 'البدء من جديد',
  'saas.module_not_subscribed': 'هذه الوحدة غير مشترك بها في مؤسستك.',

  'nav.library': 'المكتبة',
  'library.title': 'مكتبة الشاشات',
  'library.description': 'المكوّنات المشتركة، معروضة ببيانات تجريبية. غيّر اللغة والسمة: لا شيء مكتوب بشكل ثابت.',
  'library.theme': 'السمة',
  'library.theme.clair': 'فاتحة',
  'library.theme.sombre': 'داكنة',
  'library.section.layout': 'الشبكة والبطاقات',
  'library.section.table': 'الجدول',
  'library.section.form': 'الاستمارة',
  'library.section.feedback': 'الرسائل والإشعارات',
  'library.warehouse': 'المستودع',
  'library.warehouse.code': 'رمز المستودع',
  'library.warehouse.name': 'الاسم',
  'library.warehouse.farm': 'الضيعة',
  'library.warehouse.type': 'النوع',
  'library.warehouse.capacity': 'السعة',
  'library.warehouse.negative': 'السماح بمخزون سالب',
  'library.warehouse.negativeHint': 'يُخصّص للمستودعات التي تُسجَّل سحوباتها لاحقًا.',
  'library.warehouse.codeHint': 'ثلاثون حرفًا على الأكثر، تُحوَّل تلقائيًا إلى حروف كبيرة.',
  'library.warehouse.codeError': 'يوجد مستودع بهذا الرمز.',
  'library.stock.product': 'المادة',
  'library.stock.quantity': 'الكمية',
  'library.stock.unitCost': 'كلفة الوحدة',
  'library.stock.value': 'القيمة',
  'library.stock.empty': 'لا يوجد مخزون في هذا المستودع.',
  'library.save': 'حفظ',
  'library.saving': 'جارٍ الحفظ…',
  'library.cancel': 'إلغاء',
  'library.delete': 'حذف',
  'library.saved': 'تم حفظ المستودع.',
  'library.notifyInfo': 'إشعار معلومات',
  'library.notifySuccess': 'إشعار نجاح',
  'library.notifyWarning': 'إشعار تنبيه',
  'library.notifyError': 'إشعار خطأ',
  'library.alert.info': 'المبالغ محسوبة بالدينار التونسي.',
  'library.alert.warning': 'هذا المستودع معطَّل: لم يعد بإمكانه استقبال البضاعة.',
  'library.alert.error': 'المخزون غير كافٍ لإتمام هذا التحويل.',
  'library.active': 'نشط',
  'library.inactive': 'معطَّل',

  'nav.suppliers': 'المزوّدون',
  'suppliers.title': 'المزوّدون',
  'suppliers.description': 'المزوّد هو طرف تمّ تأهيله كذلك: البطاقة تُضاف إلى الطرف الموجود ولا تعوّضه.',
  'suppliers.search': 'البحث عن مزوّد',
  'suppliers.code': 'الرمز',
  'suppliers.name': 'الاسم',
  'suppliers.city': 'المدينة',
  'suppliers.phone': 'الهاتف',
  'suppliers.status': 'الاعتماد',
  'suppliers.approved': 'معتمَد',
  'suppliers.notApproved': 'غير معتمَد',
  'suppliers.empty': 'لا يوجد مزوّد مسجّل.',
  'suppliers.fiche': 'بطاقة المزوّد',
  'suppliers.selectHint': 'اختر مزوّدًا لعرض بطاقته.',
  'suppliers.notSupplier': 'هذا الطرف غير مصرّح به كمزوّد.',
  'suppliers.reference': 'مرجعنا لديهم',
  'suppliers.creditLimit': 'سقف الدين المسموح',
  'suppliers.outstanding': 'الدين الحالي',
  'suppliers.creditAvailable': 'الهامش المتاح',
  'suppliers.noLimit': 'بدون سقف',
  'suppliers.leadDays': 'أجل التسليم (أيام)',
  'suppliers.minimumOrder': 'الحد الأدنى للطلبية',
  'suppliers.bankName': 'البنك',
  'suppliers.bankAccount': 'رقم الحساب',
  'suppliers.iban': 'الرقم الدولي للحساب',
  'suppliers.rating': 'تقييم الجودة (1 إلى 5)',
  'suppliers.approve': 'مزوّد معتمَد',
  'suppliers.approveHint': 'يحتفظ الاعتماد بتاريخه وصاحبه: وهو أول سؤال في تدقيق الشراء.',
  'suppliers.save': 'حفظ البطاقة',
  'suppliers.saving': 'جارٍ الحفظ…',
  'suppliers.saved': 'تم حفظ بطاقة المزوّد.',

  'warehouses.title': 'المخازن',
  'warehouses.description': 'المخزن مكان للتخزين. التحويل ينقل القيمة من مخزن إلى آخر — ولا ينشئها أبدا.',
  'warehouses.search': 'البحث عن مخزن',
  'warehouses.code': 'الرمز',
  'warehouses.name': 'الاسم',
  'warehouses.farm': 'الضيعة',
  'warehouses.unattached': 'غير مرتبط',
  'warehouses.governorate': 'الولاية',
  'warehouses.manager': 'المسؤول',
  'warehouses.capacity': 'السعة',
  'warehouses.status': 'الحالة',
  'warehouses.active': 'نشط',
  'warehouses.inactive': 'غير نشط',
  'warehouses.default': 'افتراضي',
  'warehouses.showInactive': 'إظهار المخازن غير النشطة',
  'warehouses.empty': 'لا يوجد أي مخزن مسجل.',
  'warehouses.new': 'مخزن جديد',
  'warehouses.create': 'إنشاء المخزن',
  'warehouses.creating': 'جاري الإنشاء…',
  'warehouses.created': 'تم إنشاء المخزن.',
  'warehouses.allowNegative': 'السماح بالمخزون السالب',
  'warehouses.allowNegativeHint': 'لا يُفعّل إلا في مخزن ميداني، حيث يسبق الإخراج تسجيل الإدخال غالبا.',
  'warehouses.attach': 'ربط',
  'warehouses.attached': 'تم تسجيل الربط.',
  'warehouses.detach': 'فصل',
  'warehouses.transfer': 'تحويل المخزون',
  'warehouses.transferFrom': 'مخزن المصدر',
  'warehouses.transferTo': 'مخزن الوجهة',
  'warehouses.transferProduct': 'المادة',
  'warehouses.transferQuantity': 'الكمية',
  'warehouses.transferSubmit': 'تحويل',
  'warehouses.transferring': 'جاري التحويل…',
  'warehouses.unitCost': 'قيمة الوحدة',
  'warehouses.transferredValue': 'القيمة المنقولة',
  'warehouses.transferNoValue': 'تبقى قيمة الوحدة كما هي: التحويل ينقل المخزون ولا يعيد تقييمه.',
  'warehouses.remainingSource': 'الباقي في المصدر',
  'warehouses.remainingTarget': 'الباقي في الوجهة',

  'nav.operations': 'التدخلات',
  'operations.title': 'التدخلات',
  'operations.description': 'عمل، وقطع أرض، وتكلفة: هنا يلتقي الإنفاق بالموسم الذي يحمله.',
  'operations.new': 'تدخل جديد',
  'operations.farm': 'الضيعة',
  'operations.workType': 'نوع الأعمال',
  'operations.date': 'التاريخ',
  'operations.notes': 'ملاحظات',
  'operations.targets': 'القطع المعالجة',
  'operations.area': 'المساحة (هكتار)',
  'operations.noPlots': 'لا توجد أي قطعة في هذه الضيعة: أنشئ واحدة قبل التدخل فيها.',
  'operations.machines': 'العتاد',
  'operations.addMachine': 'إضافة عتاد',
  'operations.machine': 'الآلة',
  'operations.hours': 'الساعات',
  'operations.hourlyCost': 'كلفة الساعة',
  'operations.labor': 'اليد العاملة',
  'operations.addLabor': 'إضافة يد عاملة',
  'operations.partner': 'المتعهد',
  'operations.workers': 'عدد العمال',
  'operations.quantity': 'الكمية',
  'operations.payBasis': 'أساس الأجر',
  'operations.payBasis.hourly': 'بالساعة',
  'operations.payBasis.daily': 'باليوم',
  'operations.payBasis.monthly': 'بالشهر',
  'operations.payBasis.piece_rate': 'بالإنجاز',
  'operations.unitCost': 'الكلفة الوحدوية',
  'operations.responsible': 'المسؤول',
  'operations.meterKind': 'نوع العداد',
  'operations.meterKind.none': 'بدون قراءة',
  'operations.meterKind.hours': 'عداد الساعات',
  'operations.meterKind.km': 'عداد الكيلومترات',
  'operations.meterStart': 'العداد عند الانطلاق',
  'operations.meterEnd': 'العداد عند العودة',
  'operations.distanceKm': 'المسافة بالكيلومتر',
  'operations.fromMeter': 'يُستنتج من العداد: لا يُدخل بجانبه.',
  'operations.employee': 'العامل',
  'operations.startTime': 'ساعة البداية',
  'operations.endTime': 'ساعة النهاية',
  'operations.fromTimes': 'تُستنتج من التوقيت: لا تُدخل بجانبه.',
  'operations.unitCost.fromEmployee': 'إذا تُرك فارغا تُطبَّق كلفة بطاقة العامل.',
  'operations.plannedInputs': 'المدخلات المتوقعة',
  'operations.addPlannedInput': 'توقّع مادة',
  'operations.plannedProduct': 'المادة المتوقعة',
  'operations.plannedQuantity': 'الكمية المتوقعة',
  'operations.driver': 'السائق',
  'operations.plan.title': 'المتوقع والفعلي',
  'operations.plan.planned': 'المتوقع',
  'operations.plan.actual': 'الفعلي',
  'operations.plan.gap': 'الفارق',
  'operations.charges': 'الخدمات والتكاليف الأخرى',
  'operations.addCharge': 'إضافة تكلفة',
  'operations.charge.kind': 'نوع التكلفة',
  'operations.charge.kind.service': 'خدمة بالمناولة',
  'operations.charge.kind.other': 'تكلفة أخرى',
  'operations.charge.partner': 'المؤسسة أو الطرف',
  'operations.charge.description': 'طبيعة الخدمة أو البيان',
  'operations.charge.quantity': 'عدد الوحدات',
  'operations.charge.unitPrice': 'سعر الوحدة',
  'operations.charge.total': 'المبلغ:',
  'operations.breakdown.prestations': 'الخدمات بالمناولة',
  'operations.breakdown.autres': 'تكاليف أخرى',
  'nav.employees': 'العمال',
  'employees.title': 'العمال',
  'employees.description': 'عمال الضيعة: من يتحمل مسؤولية التدخل، ومن يعمل فيه، وكم يكلّف.',
  'employees.new': 'عامل جديد',
  'employees.edit': 'تعديل العامل',
  'employees.code': 'رقم التسجيل',
  'employees.fullName': 'الاسم واللقب',
  'employees.jobTitle': 'الوظيفة',
  'employees.employmentType': 'صنف العمال',
  'employees.farm': 'الضيعة المرتبط بها',
  'employees.farm.all': 'كل الضيعات',
  'employees.hourlyCost': 'كلفة الساعة',
  'employees.dailyCost': 'كلفة اليوم',
  'employees.monthlyCost': 'كلفة الشهر',
  'employees.active': 'في الخدمة',
  'employees.active.hint': 'العامل غير النشط لا يُعيَّن في تدخل جديد؛ وتبقى سطوره السابقة.',
  'employees.empty': 'لا يوجد عامل مسجل.',
  'employees.saved': 'تم حفظ العامل.',
  'employees.required': 'رقم التسجيل والاسم وصنف العمال مطلوبة.',
  'employees.readOnly': 'للقراءة فقط: تعديل بطاقة العامل يتطلب صلاحية إدارة المراجع.',
  'operations.saveDraft': 'حفظ كمسودة',
  'operations.saveValidate': 'حفظ والمصادقة',
  'operations.saving': 'جارٍ الحفظ…',
  'operations.created': 'تم إنشاء التدخل.',
  'operations.validated': 'تمت مصادقة التدخل: صار غير قابل للتغيير.',
  'operations.validatedBadge': 'مصادق عليه — غير قابل للتغيير',
  'operations.result': 'نتيجة التدخل',
  'operations.number': 'الرقم',
  'operations.directCost': 'الكلفة المباشرة',
  'operations.validateDraft': 'مصادقة التدخل',
  'operations.consumption': 'المواد الخارجة من المخزن',
  'operations.consumptionHint': 'الاستهلاك يخرج المادة من المخزن بثمن المخزون ويربطها بالتدخل: الثمن لا يُعاد إدخاله أبدا. ثم صادق على التدخل.',
  'operations.warehouse': 'المخزن',
  'operations.product': 'المادة',
  'operations.consume': 'استهلاك',
  'operations.consuming': 'جارٍ الاستهلاك…',
  'operations.consumed': 'تم ربط المادة بالتدخل.',
  'operations.breakdown': 'الكلفة المباشرة، سطرا بسطر',
  'operations.breakdown.intrants': 'المدخلات',
  'operations.breakdown.machines': 'الآلات',
  "operations.breakdown.main-d'œuvre": 'يد العاملة',
  'operations.lineLabel': 'السطر',
  'operations.unitPrice': 'ثمن الوحدة',
  'operations.total': 'المجموع',
  'operations.importTitle': 'استيراد التدخلات (CSV)',
  'operations.importHint': 'كل سطر في الملف = تدخل واحد، يُصادق عليه عند الاستيراد. نزّل النموذج: يحمل التعليمات في أسطره الأولى.',

  'references.importTitle': 'استيراد جماعي (CSV)',
  'references.importHint': 'الأسطر الصحيحة تُدرَج، والخاطئة تُبلَّغ برقمها. النموذج يحمل الأعمدة المطلوبة.',
  'references.importHintVariety': 'للأصناف، عمود crop_code يحدد المحصول برمزه الدقيق — أنشئ المحاصيل أولا.',

  'import.template': 'تنزيل النموذج',
  'import.pending': 'جارٍ الاستيراد…',
  'import.allDone': 'تم استيراد {created} سطرا من أصل {total}.',
  'import.partial': 'تم استيراد {created} سطرا من أصل {total}؛ ورُفض {failed} — صحّحها وأرسلها وحدها.',
  'import.columnLine': 'السطر',
  'import.columnError': 'الخطأ',
  'import.columnDetail': 'التفصيل',

  'csv.empty': 'الملف فارغ.',
  'csv.header_missing': 'يجب أن يحمل السطر الأول أسماء الأعمدة.',
  'csv.header_invalid': 'أعمدة الملف تخالف النموذج المطلوب.',
  'csv.no_data_rows': 'الملف لا يحمل أي بيانات تحت العنوان.',
  'csv.column_count': 'سطر يحمل قيما أكثر مما يقبل النموذج من أعمدة.',
  'csv.too_many_rows': 'أسطر كثيرة جدا: قسّم الملف.',

  'import.crop_unknown': 'محصول مجهول: أنشئه أولا في مرجع المحاصيل.',
  'import.farm_unknown': 'ضيعة مجهولة: تحقق من رمزها.',
  'import.work_type_unknown': 'نوع أعمال مجهول: تحقق من رمزه.',
  'import.plot_unknown': 'قطعة مجهولة في هذه الضيعة.',
  'import.plot_duplicate': 'القطعة نفسها وردت مرتين في السطر.',
  'import.no_open_season': 'لا يوجد موسم مفتوح على هذه القطعة.',
  'import.machine_unknown': 'عتاد مجهول: تحقق من رمزه.',
  'import.hours_required': 'ساعات موجبة مطلوبة عندما يُحدَّد عتاد.',
  'import.partner_unknown': 'متعهد مجهول: تحقق من رمزه.',
  'import.pay_basis_invalid': 'أساس أجر غير صالح (hourly, daily, monthly, piece_rate).',
  'import.number_invalid': 'رقم غير مقروء.',
  'import.date_invalid': 'تاريخ غير مقروء (YYYY-MM-DD أو DD/MM/YYYY).',
  'import.quantity_required': 'كمية موجبة مطلوبة.',

  'farm.plot.no_open_season': 'لا يوجد موسم مفتوح على هذه القطعة: أنشئ واحدا قبل إسناد أعمال إليها.',
  'farm.plot.not_found': 'القطعة غير موجودة.',
  'farm.plot.irrigation_requires_irrigable': 'القطعة البعلية ليس لها طريقة ري ولا مصدر مياه: اجعلها قابلة للري أو احذفهما.',
  'reference.not_found': 'العنصر المختار غير موجود، أو لم يعد موجودا، في المرجع.',
  'farm.farm.not_found': 'الضيعة غير موجودة.',
  'purchase.requisition.organization_required': 'طلب الشراء ينتمي إلى مؤسسة.',
  'purchase.requisition.document_number_required': 'رقم الطلب إجباري (30 حرفا على الأكثر).',
  'purchase.requisition.already_numbered': 'هذا الطلب له رقمه مسبقا.',
  'purchase.requisition.needed_before_request': 'لا يمكن انتظار الحاجة قبل تاريخ طلبها.',
  'purchase.requisition.text_too_long': 'سبب الطلب لا يتجاوز 500 حرف، وسبب السطر أو القرار 300.',
  'purchase.requisition.no_lines': 'طلب لا يطلب شيئا لا يُنشأ ولا يُوافق عليه.',
  'purchase.requisition.rejection_reason_required': 'الرفض يستوجب ذكر السبب.',
  'purchase.requisition.decided': 'هذا الطلب تم البتّ فيه: لا يُعدَّل ولا يُعاد البتّ فيه.',
  'purchase.requisition.not_requester': 'لا يسحب الطلب إلا صاحبه. أمّا غيره فيرفضه مع ذكر السبب.',
  'purchase.requisition.not_found': 'طلب الشراء غير موجود.',
  'purchase.requisition.unknown_status': 'الطلب إما مقدَّم أو موافق عليه أو مرفوض أو مسحوب.',
  'purchase.requisition.line_not_found': 'سطر الطلب المعيَّن غير موجود.',
  'purchase.requisition.not_approved': 'الطلبية لا تستجيب إلا لطلب موافق عليه.',
  'purchase.requisition.product_mismatch': 'سطر الطلبية لا يحمل مادة الطلب الذي يستجيب له.',
  'purchase.requisition.unit_mismatch': 'السطر الذي يستجيب لطلب يُحتسب بوحدة المادة.',
  'purchase.requisition_line.invalid': 'سطر الطلب يعيّن مادة وكمية موجبة تماما.',
  'purchase.requisition_line.duplicate_product': 'هذه المادة مطلوبة مسبقا: المادة تُطلب مرة واحدة في كل طلب.',
  'purchase.requisition_line.product_not_found': 'مادة مطلوبة غير موجودة.',
  'agro.budget.season_closed': 'هذا الموسم مغلق: ميزانيته لم تعد قابلة للتعديل.',
  'agro.budget.invalid_yield': 'المردود المتوقع لا يكون سالبا.',
  'agro.budget.invalid_price': 'السعر التقديري لا يكون سالبا.',
  'agro.budget.unit_required': 'المردود أو السعر التقديري يُذكر مع وحدته.',
  'agro.budget.text_too_long': 'ملاحظة الميزانية لا تتجاوز 300 حرف، وتسمية السطر 120.',
  'agro.budget.not_found': 'هذه الزراعة لا ميزانية لها في هذا الموسم.',
  'agro.budget_line.invalid_nature': 'سطر الميزانية إما مدخلات أو معدات أو يد عاملة أو عبء آخر.',
  'agro.budget_line.label_required': 'سطر الميزانية يذكر ما يخصص له: تسميته إجبارية.',
  'agro.budget_line.invalid_quantity': 'الكمية للهكتار يجب أن تكون أكبر من الصفر.',
  'agro.budget_line.price_required': 'سعر سطر الميزانية إجباري: الصفر سعر، أما الخانة الفارغة فليست سعرا.',
  'agro.budget_line.invalid_price': 'السعر لا يكون سالبا.',
  'agro.budget_line.duplicate': 'لهذه الميزانية سطر بنفس التسمية ونفس الطبيعة.',
  'agro.budget_line.not_found': 'هذا السطر ليس من أسطر هذه الميزانية.',
  'sales.invoice.not_found': 'الفاتورة غير موجودة.',
  'sales.invoice.due_before_invoice': 'لا يحلّ أجل الفاتورة قبل تاريخها.',
  'sales.invoice.date_in_future': 'لا تؤرَّخ الفاتورة في المستقبل.',
  'sales.invoice.no_deliveries': 'الفاتورة تتضمن وصل تسليم واحدا على الأقل.',
  'sales.invoice.delivery_not_invoicable': 'أحد الوصولات غير موجود أو غير مسلَّم أو لحريف آخر أو مفوتر مسبقا: لم تقع فوترة أي شيء.',
  'sales.invoice.mixed_partners': 'الفاتورة لا تجمع بين الحرفاء.',
  'sales.invoice.tax_rate_invalid': 'نسبة الأداء هذه غير سارية.',
  'sales.payment.amount_must_be_positive': 'مبلغ المقبوض يجب أن يكون أكبر من الصفر.',
  'sales.payment.date_in_future': 'لا يؤرَّخ المقبوض في المستقبل.',
  'sales.payment.text_too_long': 'مرجع المقبوض لا يتجاوز 60 حرفا، وملاحظته أو سبب إلغائه 300.',
  'sales.payment.customer_not_found': 'الحريف غير موجود.',
  'sales.payment.method_not_found': 'طريقة الدفع غير موجودة.',
  'sales.payment.reference_required': 'طريقة الدفع هذه تستوجب مرجعا.',
  'sales.payment.allocation_invalid': 'مبلغ التخصيص يجب أن يكون أكبر من الصفر.',
  'sales.payment.allocation_duplicate': 'لا تُخصَّص الفاتورة إلا مرة واحدة في المقبوض الواحد.',
  'sales.payment.over_allocated': 'التخصيصات تتجاوز ما بقي من قيمة المقبوض.',
  'sales.payment.invoice_not_payable': 'إحدى الفواتير المعيّنة ليست فاتورة صادرة لهذا الحريف.',
  'sales.payment.exceeds_remaining': 'أحد التخصيصات يتجاوز ما بقي على الفاتورة بعد طرح فواتير الإرجاع.',
  'sales.payment.not_found': 'المقبوض غير موجود.',
  'sales.payment.cancelled': 'هذا المقبوض ملغى: لم يعد يسدد شيئا.',
  'sales.payment.already_cancelled': 'هذا المقبوض ملغى مسبقا.',
  'sales.payment.cancellation_reason_required': 'إلغاء المقبوض يستوجب ذكر السبب.',
  'sales.payment.partner_mismatch': 'المقبوض والفاتورة ليسا لنفس الحريف.',
  'sales.payment.allocation_immutable': 'التخصيص لا يُعدَّل ولا يُحذف: يُلغى المقبوض نفسه.',
  'sales.payment.immutable': 'المقبوض لا تُعاد كتابته: يُلغى.',
  'agro.plot_season.not_found': 'موسم القطعة غير موجود.',
  'agro.plot_season.area_must_be_positive': 'يجب أن تكون المساحة المزروعة موجبة تماما.',
  'agro.plot_season.area_exceeds_plot': 'المساحة المزروعة تفوق مساحة القطعة.',
  'agro.plot_season.area_unknown': 'مساحة هذه القطعة غير معروفة: حدّدها صراحة.',
  'agro.plot_season.already_assigned': 'هذه القطعة تحمل زراعة في هذا الموسم.',
  'agro.plot_season.invalid_sequence': 'رتبة الزراعة تساوي 1 (رئيسية) أو 2 (ثانوية).',
  'agro.plot_season.double_cropping_disabled': 'الزراعة المزدوجة غير مفعّلة: القطعة تحمل زراعة واحدة في الموسم.',
  'agro.plot_season.invalid_period': 'يجب أن تأتي نهاية الموسم بعد بدايته.',
  'agro.plot_season.period_outside_season': 'تواريخ الحملة خارج الموسم الذي يحملها.',
  'agro.season.not_found': 'الموسم غير موجود.',
  'agro.plot_season.code_too_long': 'رمز الموسم يتجاوز 30 حرفا.',
  'agro.plot_season.duplicate_code': 'يوجد موسم يحمل هذا الرمز.',
  'agro.plot_season.name_too_long': 'اسم الموسم يتجاوز 150 حرفا.',
  'agro.plot_season.description_too_long': 'وصف الموسم يتجاوز 500 حرف.',
  'agro.cropping_plan.no_plot_selected': 'لم تُختَر أي قطعة: لا شيء لإسناده.',
  'agro.cropping_plan.too_many_plots': 'عدد القطع كبير جدا (200 على الأكثر): تابع حسب الضيعة.',

  'stock.warehouse_required': 'يجب تحديد مخزن.',

  'ops.operation.no_target': 'حدّد قطعة واحدة على الأقل: التدخل يُسنَد إلى شيء ما.',
  'ops.operation.no_line': 'أضف عتادا أو يدا عاملة أو تكلفة: تدخل بلا كلفة لا يُصادق عليه.',
  'ops.operation.already_validated': 'التدخل مصادق عليه سابقا.',
  'ops.operation.immutable': 'التدخل مصادق عليه: هو غير قابل للتغيير، صحّحه بعكس القيد.',
  'ops.operation.cancelled': 'التدخل الملغى لا يُصادق عليه.',
  'ops.operation.references_required': 'اختر ضيعة ونوع أعمال.',
  'ops.operation.invalid_area': 'يجب أن تكون المساحة المغطاة موجبة تماما.',
  'ops.operation_target.cost_object_required': 'الهدف يعيّن كائن كلفة.',
  'ops.operation_target.allocation_key_required': 'كل هدف يحمل مساحة أو حصة.',
  'ops.operation_target.invalid_allocation': 'المساحة يجب أن تكون موجبة والحصة بين 0 و100.',
  'ops.operation_target.duplicate': 'هذه القطعة مشمولة مسبقا بالتدخل.',
  'ops.operation_input.quantity_must_be_positive': 'الكمية المستهلكة يجب أن تكون موجبة تماما.',
  'ops.operation_input.movement_already_used': 'حركة المخزون هذه مرتبطة مسبقا باستهلاك.',
  'ops.operation_input.references_required': 'المدخل يعيّن مادة وحركتها ووحدة.',
  'ops.operation_machine.invalid': 'آلة وساعات موجبة وكلفة ساعة مطلوبة.',
  'ops.operation_labor.who_required': 'عيّن عاملا أو متعهدا، وليس الاثنين معا.',
  'ops.operation_labor.invalid': 'عدد العمال والكمية يجب أن يكونا موجبين.',
  'ops.operation_machine.invalid_meter': 'يُقرأ العداد عند الانطلاق وعند العودة، وقراءة العودة أكبر، مع تحديد نوعه.',
  'ops.operation_machine.hours_from_meter': 'الساعات تُستنتج من عداد الساعات: لا تُدخل بجانبه.',
  'ops.operation_machine.distance_from_meter': 'المسافة تُستنتج من عداد الكيلومترات: لا تُدخل بجانبه.',
  'ops.operation_machine.invalid_distance': 'المسافة يجب أن تكون موجبة تماما.',
  'ops.operation_labor.times_together': 'ساعة البداية وساعة النهاية تُدخلان معا.',
  'ops.operation_labor.times_order': 'ساعة النهاية تأتي بعد ساعة البداية. السطر لا يتجاوز منتصف الليل: أدخل سطرين.',
  'ops.operation_labor.hours_from_times': 'الساعات تُستنتج من التوقيت: لا تُدخل بجانبه.',
  'agro.employee.not_found': 'العامل غير موجود.',
  'agro.employee.code_required': 'رقم التسجيل مطلوب.',
  'agro.employee.name_required': 'الاسم مطلوب.',
  'agro.employee.text_too_long': 'رقم التسجيل لا يتجاوز 30 حرفا، والاسم 150، والوظيفة 100.',
  'agro.employee.negative_cost': 'الكلفة لا يمكن أن تكون سالبة.',
  'agro.employee.employment_type_not_found': 'صنف العمال المعيَّن غير موجود.',
  'agro.employee.duplicate_code': 'رقم التسجيل هذا مستعمل.',
  'livestock.animal.organization_required': 'الحيوان تابع لمؤسسة.',
  'livestock.animal.farm_required': 'الحيوان يعيش في ضيعة.',
  'livestock.animal.tag_required': 'الرقم (رقم الحلقة) إجباري.',
  'livestock.animal.species_required': 'النوع إجباري.',
  'livestock.animal.breed_required': 'السلالة إجبارية.',
  'livestock.animal.sex_invalid': 'الجنس « male » أو « female ».',
  'livestock.animal.entry_reason_required': 'سبب الدخول (ولادة، شراء…) إجباري.',
  'livestock.animal.entry_date_required': 'تاريخ الدخول إجباري.',
  'livestock.animal.text_too_long': 'نص يتجاوز الطول الأقصى.',
  'livestock.animal.species_not_found': 'نوع غير معروف.',
  'livestock.animal.breed_not_found': 'سلالة غير معروفة.',
  'livestock.animal.duplicate_tag': 'هذا الرقم مستعمل في هذه الضيعة.',
  'livestock.movement.animal_not_found': 'الحيوان غير موجود.',
  'livestock.movement.direction_invalid': 'الحركة دخول (« in ») أو خروج (« out »).',
  'livestock.movement.date_required': 'تاريخ الحركة إجباري.',
  'livestock.movement.reason_required': 'سبب الحركة إجباري.',
  'livestock.movement.animal_exited': 'هذا الحيوان خارج القطيع.',
  'livestock.movement.exit_without_entry': 'لا يمكن إخراج حيوان لم يدخل أبدا.',
  'livestock.movement.already_inside': 'هذا الحيوان داخل القطيع.',
  'livestock.registry.title': 'سجل الحيوانات',
  'livestock.registry.description': 'كل رأس موسوم برقمه : التسجيل والدخول والخروج.',
  'livestock.registry.new': 'حيوان جديد',
  'livestock.registry.tag': 'الرقم (الحلقة)',
  'livestock.registry.species': 'النوع',
  'livestock.registry.breed': 'السلالة',
  'livestock.registry.sex': 'الجنس',
  'livestock.registry.sex.male': 'ذكر',
  'livestock.registry.sex.female': 'أنثى',
  'livestock.registry.farm': 'الضيعة',
  'livestock.registry.entryDate': 'تاريخ الدخول',
  'livestock.registry.entryReason': 'سبب الدخول',
  'livestock.registry.saved': 'تم تسجيل الحيوان.',
  'livestock.registry.required': 'الرقم والنوع والسلالة وسبب الدخول مطلوبة.',
  'livestock.registry.movement.title': 'دخول / خروج',
  'livestock.registry.movement.animal': 'الحيوان',
  'livestock.registry.movement.direction': 'الاتجاه',
  'livestock.registry.movement.in': 'دخول',
  'livestock.registry.movement.out': 'خروج',
  'livestock.registry.movement.date': 'التاريخ',
  'livestock.registry.movement.reason': 'السبب',
  'livestock.registry.movement.saved': 'تم تقييد الحركة.',
  'livestock.registry.status': 'الحالة',
  'livestock.registry.status.active': 'موجود',
  'livestock.registry.status.exited': 'خارج',
  'livestock.inventory.title': 'جرد القطيع',
  'livestock.inventory.description': 'الحيوانات الموجودة محسوبة حسب النوع والصنف.',
  'livestock.inventory.species': 'النوع',
  'livestock.inventory.category': 'الصنف',
  'livestock.inventory.count': 'العدد',
  'livestock.farm.all': 'كل الضيعات',
  'nav.livestock.registry': 'السجل',
  'nav.livestock.inventory': 'الجرد',
  'references.kind.species': 'الأنواع',
  'references.kind.breed': 'السلالات',
  'references.kind.animal_category': 'أصناف الحيوانات',
  'livestock.registry.empty': 'لا حيوان في السجل.',
  'livestock.registry.register': 'سجّل',
  'livestock.registry.movement.record': 'قيّد',
  'livestock.registry.movement.required': 'الحيوان والاتجاه والسبب مطلوبة.',
  'livestock.registry.movement.price': 'السعر (د.ت)',
  'livestock.inventory.empty': 'القطيع فارغ.',
  'livestock.movement.withdrawal_active': 'هذا الحيوان في مدة الانتظار: خروجه إلى المذبح مرفوض.',
  'livestock.movement_immutable': 'لا تُعدَّل حركة القطيع ولا تُحذف: تُصحَّح بحركة معاكسة.',
  'livestock.movement.price_invalid': 'سعر الحركة لا يمكن أن يكون سالبا.',
  'livestock.health.organization_required': 'التدخل الصحي تابع لمؤسسة.',
  'livestock.health.animal_required': 'التدخل الصحي يخص حيوانا.',
  'livestock.health.date_required': 'تاريخ التدخل إجباري.',
  'livestock.health.type_required': 'نوع التدخل (علاج، تلقيح…) إجباري.',
  'livestock.health.withdrawal_invalid': 'مدة الانتظار لا يمكن أن تكون سالبة.',
  'livestock.health.dose_invalid': 'الجرعة، عند إدخالها، موجبة تماما.',
  'livestock.health.text_too_long': 'نص يتجاوز الطول الأقصى.',
  'livestock.health.animal_not_found': 'الحيوان غير موجود.',
  'livestock.health.after_exit': 'هذا الحيوان خارج القطيع: لا علاج بعد الخروج.',
  'livestock.health.title': 'المتابعة الصحية',
  'livestock.health.description': 'العلاجات والتلقيحات والزيارات — ومدة الانتظار قبل الذبح.',
  'livestock.health.new': 'تدخل جديد',
  'livestock.health.animal': 'الحيوان',
  'livestock.health.type': 'نوع التدخل',
  'livestock.health.type.treatment': 'علاج',
  'livestock.health.type.vaccination': 'تلقيح',
  'livestock.health.type.visit': 'زيارة',
  'livestock.health.type.deworming': 'التخلص من الديدان',
  'livestock.health.date': 'تاريخ التدخل',
  'livestock.health.product': 'المنتج',
  'livestock.health.dose': 'الجرعة',
  'livestock.health.withdrawal': 'مدة الانتظار (أيام)',
  'livestock.health.vet': 'البيطري',
  'livestock.health.saved': 'تم تقييد التدخل.',
  'livestock.health.required': 'الحيوان والتاريخ والنوع مطلوبة.',
  'livestock.health.empty': 'لا تدخل لهذا الحيوان.',
  'livestock.health.record': 'قيّد',
  'livestock.health.cost': 'الكلفة (د.ت)',
  'livestock.health.cost_invalid': 'كلفة التدخل لا يمكن أن تكون سالبة.',
  'livestock.alerts.title': 'تلقيحات مستحقة',
  'livestock.alerts.description': 'الحيوانات الموجودة دون تلقيح حديث.',
  'livestock.alerts.empty': 'لا تلقيح مستحق.',
  'livestock.alerts.animal': 'الحيوان',
  'livestock.alerts.last': 'آخر تلقيح',
  'livestock.alerts.never': 'أبدا',
  'nav.livestock.health': 'الصحة',
  'nav.livestock.production': 'الإنتاج',
  'livestock.production.title': 'إنتاج الحليب',
  'livestock.production.description': 'الحلب اليومي، صباحا ومساءً - والمجموع اليومي.',
  'livestock.production.table': 'الحلب المسجّل',
  'livestock.production.new': 'حلب جديد',
  'livestock.production.animal': 'الحيوان',
  'livestock.production.date': 'تاريخ الحلب',
  'livestock.production.quantity': 'الكمية (ل)',
  'livestock.production.quality': 'درجة الجودة (0-100)',
  'livestock.production.session': 'الجلسة',
  'livestock.production.session.morning': 'صباحا',
  'livestock.production.session.evening': 'مساءً',
  'livestock.production.date_total': 'اليوم المجمّع',
  'livestock.production.total': 'المجموع اليومي',
  'livestock.production.saved': 'تم تسجيل الحلب.',
  'livestock.production.after_exit': 'خرج هذا الحيوان من القطيع: لا يُحلب بعد خروجه.',
  'livestock.production.animal_not_found': 'الحيوان غير موجود.',
  'livestock.production.animal_required': 'الحلبة تخص حيوانًا.',
  'livestock.production.date_required': 'تاريخ الحلبة إلزامي.',
  'livestock.production.organization_required': 'الحلبة تابعة لمؤسسة.',
  'livestock.production.product_required': 'المنتوج (حليب، صوف، بيض) إلزامي.',
  'livestock.production.quality_invalid': 'درجة الجودة بين 0 و 100.',
  'livestock.production.quantity_invalid': 'كمية الحلبة يجب أن تكون موجبة تمامًا.',
  'livestock.production.text_too_long': 'نص يتجاوز طوله الأقصى.',
  'livestock.production.withdrawal_active': 'هذا الحيوان في مدة الانتظار: حليبه مستبعد من التجميع.',
  'livestock.production.required': 'الحيوان والتاريخ والكمية مطلوبة.',
  'livestock.production.empty': 'لا يوجد حلب لهذا الحيوان.',
  'livestock.production.record': 'سجّل',
  'livestock.production.daily_total': 'المجموع اليومي',
  'nav.livestock.feeding': 'التغذية',
  'livestock.feeding.title': 'التغذية',
  'livestock.feeding.description': 'ما يُعطى لكل حيوان: المنتج والكمية والكلفة.',
  'livestock.feeding.table': 'التغذيات المقيدة',
  'livestock.feeding.new': 'علف جديد',
  'livestock.feeding.animal': 'الحيوان',
  'livestock.feeding.date': 'التاريخ',
  'livestock.feeding.product': 'المنتج الغذائي',
  'livestock.feeding.quantity': 'الكمية (كغ)',
  'livestock.feeding.cost_total': 'الكلفة الإجمالية (د.ت)',
  'livestock.feeding.saved': 'تم تقييد التغذية.',
  'livestock.feeding.required': 'الحيوان والتاريخ والكمية مطلوبة.',
  'livestock.feeding.empty': 'لا تغذية مقيدة لهذا الحيوان.',
  'livestock.feeding.record': 'قيّد',
  'livestock.feeding.animal_not_found': 'الحيوان غير موجود.',
  'livestock.feeding.organization_required': 'التغذية تابعة لمؤسسة.',
  'livestock.feeding.animal_required': 'التغذية تخص حيوانا.',
  'livestock.feeding.date_required': 'تاريخ التغذية إجباري.',
  'livestock.feeding.product_required': 'المنتج الغذائي إجباري.',
  'livestock.feeding.quantity_invalid': 'الكمية بالكيلوغرام موجبة تماما.',
  'livestock.feeding.after_exit': 'هذا الحيوان خارج القطيع: لا يُغذى بعد خروجه.',
  'livestock.feeding.insufficient_stock': 'المخزون غير كاف لهذه التغذية.',
  'livestock.feeding.no_warehouse': 'ليس لضيعة هذا الحيوان مخزن افتراضي: لا يمكن إخراج العلف من المخزون.',
  'livestock.feeding.text_too_long': 'لا تتجاوز الملاحظة 500 حرف.',
  'nav.livestock.reproduction': 'التكاثر',
  'livestock.reproduction.title': 'التكاثر والأنساب',
  'livestock.reproduction.description': 'الزواج الطبيعي وتاريخ الميلاد المتوقع — والحمل حسب النوع (غنم ≈ 150 يوم، بقر ≈ 285 يوم).',
  'livestock.reproduction.table': 'الزواج المسجل',
  'livestock.reproduction.new': 'زواج جديد',
  'livestock.reproduction.animal': 'الأنثى',
  'livestock.reproduction.sire': 'الأب (كباش، ثور)',
  'livestock.reproduction.date': 'تاريخ الزواج',
  'livestock.reproduction.type': 'النوع',
  'livestock.reproduction.type.sai': 'الزواج الطبيعي',
  'livestock.reproduction.type.natural': 'الزواج الطبيعي',
  'livestock.reproduction.type.ivt': 'التلقيح الاصطناعي',
  'livestock.reproduction.expected_due': 'تاريخ الميلاد المتوقع',
  'livestock.reproduction.notes': 'ملاحظات',
  'livestock.reproduction.saved': 'تم تسجيل الزواج.',
  'livestock.reproduction.required': 'الحيوان والتاريخ والأب مطلوبة.',
  'livestock.reproduction.empty': 'لا يوجد زواج لهذا الحيوان.',
  'livestock.reproduction.record': 'قيّد',
  'livestock.reproduction.animal_not_found': 'الحيوان غير موجود.',
  'livestock.reproduction.organization_required': 'حدث التكاثر تابع لمؤسسة.',
  'livestock.reproduction.animal_required': 'حدث التكاثر يخص أنثى.',
  'livestock.reproduction.sire_required': 'الأب (كباش، ثور) إجباري.',
  'livestock.reproduction.date_required': 'تاريخ الزواج إجباري.',
  'livestock.reproduction.type_required': 'نوع الحدث (زواج، تلقيح…) إجباري.',
  'livestock.reproduction.same_animal': 'لا يمكن أن يكون الأب والأم نفس الحيوان.',
  'livestock.reproduction.dam_sex_invalid': 'الأم في التلقيح أنثى.',
  'livestock.reproduction.sire_sex_invalid': 'الأب في التلقيح ذكر.',
  'livestock.reproduction.sire_not_found': 'الأب غير موجود.',
  'livestock.reproduction.text_too_long': 'نص يتجاوز طوله الأقصى.',
  'livestock.reproduction.after_exit': 'هذا الحيوان خارج القطيع: لا يتم تكثيره بعد الخروج.',
  'nav.livestock.reports': 'الكلفة والهامش',
  'livestock.reports.title': 'كلفة القطيع وهوامشه',
  'livestock.reports.description': 'كلفة الرأس وكلفة اللتر — رقمان، قراءتان.',
  'livestock.reports.head': 'الرؤوس',
  'livestock.reports.total_cost': 'الكلفة المتراكمة',
  'livestock.reports.liters': 'الحليب المنتج (ل)',
  'livestock.reports.per_head': 'كلفة الرأس',
  'livestock.reports.per_liter': 'كلفة اللتر',
  'livestock.reports.table': 'الكلفة حسب الرأس',
  'livestock.reports.animal': 'الحيوان',
  'livestock.reports.purchase': 'الشراء',
  'livestock.reports.feeding': 'التغذية',
  'livestock.reports.health': 'الصحة',
  'livestock.reports.total': 'الكلفة الإجمالية',
  'livestock.reports.sale': 'سعر البيع',
  'livestock.reports.margin': 'الهامش',
  'livestock.reports.empty': 'لا حيوان في السجل.',
  'livestock.reports.forbidden': 'دورك لا يسمح بالاطلاع على الكلفة.',
  'ops.operation_planned_input.invalid': 'المادة المتوقعة تعيّن مادة وكمية موجبة تماما.',
  'ops.operation_planned_input.duplicate': 'هذه المادة متوقعة مسبقا في التدخل: المادة تُتوقع مرة واحدة.',
  'ops.operation_planned_input.product_not_stocked': 'مادة متوقعة غير مسيّرة في المخزون: لا تُستهلك، إذن لا تُتوقع.',
  'ops.operation_charge.invalid_kind': 'التكلفة إما خدمة بالمناولة أو تكلفة أخرى.',
  'ops.operation_charge.description_required': 'بيّن ما تم دفعه: طبيعة الخدمة أو بيان التكلفة.',
  'ops.operation_charge.description_too_long': 'بيان التكلفة لا يتجاوز 200 حرف.',
  'ops.operation_charge.partner_required': 'الخدمة بالمناولة تسمّي متعهدها.',
  'ops.operation_charge.invalid_amount': 'التكلفة تحمل كمية موجبة تماما وسعرا غير سالب.',
  'ops.operation_charge.price_required': 'أدخل السعر: الصفر سعر، والحقل الفارغ ليس سعرا.',
  'agro.partner.not_found': 'الطرف غير موجود.',
  'operations.consumption.no_lines': 'أضف مادة واحدة على الأقل لاستهلاكها.',
  'operations.operation.not_found': 'التدخل غير موجود.',

  'nav.references': 'المراجع',
  'nav.stock': 'المخزون',
  'ops.rule.outside_season': 'هذا التاريخ خارج الموسم: ستُحمَّل تكلفة العملية على محصول ليس محصولها.',
  'ops.rule.inactive_plot': 'هذه القطعة لم تعد نشطة: لا يمكن العمل فيها.',
  'references.title': 'المراجع الفلاحية',
  'references.description': 'الكتالوج المُسلَّم متاح للجميع ولا يمكن لأحد تعديله: أعد تسميته عبر التجاوز، وأضف مدخلاتك الخاصة بجانبه.',
  'references.kind': 'المرجع',
  'references.kind.crop_family': 'عائلات المحاصيل',
  'references.kind.crop': 'المحاصيل',
  'references.kind.variety': 'الأصناف',
  'references.kind.work_category': 'فئات الأعمال',
  'references.kind.work_type': 'أنواع الأعمال',
  'references.kind.machine_category': 'العتاد',
  'references.kind.employment_type': 'يد العاملة',
  'references.kind.farm_type': 'أنواع الضيعات',
  'references.kind.soil_type': 'أنواع التربة',
  'references.kind.irrigation_mode': 'طرق الري',
  'references.kind.water_source': 'مصادر المياه',
  'common.notSpecified': 'غير محدد',
  'farms.type': 'نوع الضيعة',
  'farms.type.choose': 'اختيار النوع',
  'farms.type.saved': 'تم حفظ نوع الضيعة.',
  'plots.soilType': 'نوع التربة',
  'plots.irrigation.title': 'التربة والري',
  'plots.irrigation.irrigable': 'قطعة قابلة للري',
  'plots.irrigation.mode': 'طريقة الري',
  'plots.irrigation.source': 'مصدر المياه',
  'plots.irrigation.chooseMode': 'اختيار طريقة الري',
  'plots.irrigation.chooseSource': 'اختيار مصدر المياه',
  'plots.irrigation.dryHint': 'القطعة البعلية ليس لها طريقة ري ولا مصدر مياه.',
  'plots.irrigation.saved': 'تم حفظ بيانات الري.',
  'requisitions.title': 'طلبات الشراء',
  'requisitions.description': 'تحديد الحاجيات، الموافقة عليها، ثم طلبها من المزوّد.',
  'requisitions.new': 'طلب جديد',
  'requisitions.list': 'الطلبات',
  'requisitions.one': 'الطلب',
  'requisitions.empty': 'لا يوجد طلب شراء.',
  'requisitions.number': 'الرقم',
  'requisitions.requestDate': 'تاريخ الطلب',
  'requisitions.neededBy': 'مطلوب قبل',
  'requisitions.farm': 'الضيعة',
  'requisitions.farm.central': 'حاجة مركزية',
  'requisitions.warehouse': 'مخزن الوجهة',
  'requisitions.notes': 'سبب الطلب',
  'requisitions.product': 'المادة',
  'requisitions.quantity': 'الكمية المطلوبة',
  'requisitions.lineNotes': 'توضيح',
  'requisitions.addLine': 'إضافة مادة',
  'requisitions.submit': 'إرسال الطلب',
  'requisitions.created': 'تم إرسال الطلب.',
  'requisitions.open': 'فتح',
  'requisitions.status.submitted': 'مقدَّم',
  'requisitions.status.approved': 'موافق عليه',
  'requisitions.status.rejected': 'مرفوض',
  'requisitions.status.cancelled': 'مسحوب',
  'requisitions.fulfilment': 'التغطية بالطلبيات',
  'requisitions.fulfilment.none': 'لم يُطلب شيء',
  'requisitions.fulfilment.partial': 'مطلوبة جزئيا',
  'requisitions.fulfilment.full': 'مطلوبة كليا',
  'requisitions.requested': 'المطلوب',
  'requisitions.ordered': 'المغطّى بطلبيات',
  'requisitions.remaining': 'الباقي للطلب',
  'requisitions.decisionReason': 'سبب القرار',
  'requisitions.rejectReason': 'سبب الرفض',
  'requisitions.rejectReason.hint': 'إجباري عند الرفض.',
  'requisitions.approve': 'موافقة',
  'requisitions.reject': 'رفض',
  'requisitions.cancel': 'سحب الطلب',
  'requisitions.approved': 'تمت الموافقة على الطلب.',
  'requisitions.rejected': 'تم رفض الطلب.',
  'requisitions.cancelled': 'تم سحب الطلب.',
  'requisitions.order.title': 'طلب الباقي من المزوّد',
  'requisitions.order.supplier': 'المزوّد',
  'requisitions.order.quantity': 'الكمية المراد طلبها',
  'requisitions.order.unitPrice': 'السعر المتفق عليه',
  'requisitions.order.submit': 'إنشاء الطلبية',
  'requisitions.order.created': 'تم إنشاء الطلبية.',
  'requisitions.order.complete': 'كل ما طُلب تمت تغطيته بطلبيات.',
  'requisitions.order.supplierRequired': 'اختر المزوّد.',
  'requisitions.order.nothing': 'أدخل كمية واحدة على الأقل.',
  'requisitions.order.priceRequired': 'أدخل سعر كل سطر مطلوب: الصفر سعر، والحقل الفارغ ليس سعرا.',
  'nav.budget': 'ميزانية الموسم',
  'budget.title': 'ميزانية الموسم',
  'budget.description': 'ما نتوقع إنفاقه وإنتاجه، زراعةً بزراعة وللهكتار الواحد — ثم ما تحقق فعلا.',
  'budget.season': 'الموسم',
  'budget.closed': 'هذا الموسم مغلق: ميزانيته لم تعد قابلة للتعديل.',
  'budget.forbidden': 'الميزانية تتضمن مبالغ: هي مخصصة لمن يطّلع على التكاليف.',
  'budget.noSeason': 'لا يوجد أي موسم. افتح موسما لإعداد ميزانيته.',
  'budget.noCrop': 'لا توجد أي زراعة في الدورة الزراعية لهذا الموسم، ولا أي زراعة لها ميزانية.',
  'budget.area': 'المساحة في الدورة الزراعية',
  'budget.plots': 'القطع',
  'budget.notBudgeted': 'لا ميزانية لهذه الزراعة. إضافة سطر أو فرضيات تنشئ ميزانيتها.',
  'budget.notPlanted': 'لا توجد أي قطعة في الدورة الزراعية تحمل هذه الزراعة: هذه الميزانية لا وزن لها ما لم تُدرج في الدورة.',
  'budget.assumptions': 'فرضيات البيع',
  'budget.expectedYield': 'المردود المتوقع للهكتار',
  'budget.yieldUnit': 'وحدة المردود',
  'budget.forecastPrice': 'السعر التقديري للوحدة',
  'budget.saveAssumptions': 'حفظ الفرضيات',
  'budget.assumptionsSaved': 'تم حفظ الميزانية.',
  'budget.lines': 'التكاليف المتوقعة للهكتار',
  'budget.noLine': 'لا يوجد أي سطر تكلفة.',
  'budget.nature': 'الطبيعة',
  'budget.nature.input': 'المدخلات',
  'budget.nature.machine': 'المعدات',
  'budget.nature.labor': 'اليد العاملة',
  'budget.nature.other': 'أعباء أخرى',
  'budget.label': 'التسمية',
  'budget.quantityPerHa': 'الكمية للهكتار',
  'budget.unit': 'الوحدة',
  'budget.unitPrice': 'سعر الوحدة',
  'budget.costPerHa': 'التكلفة للهكتار',
  'budget.totalPerHa': 'المجموع للهكتار',
  'budget.addLine': 'إضافة السطر',
  'budget.lineAdded': 'تمت إضافة السطر.',
  'budget.removeLine': 'سحب',
  'budget.lineRemoved': 'تم سحب السطر.',
  'budget.remove': 'سحب الميزانية',
  'budget.removed': 'تم سحب الميزانية.',
  'budget.otherCrop': 'إعداد ميزانية زراعة أخرى',
  'budget.otherCrop.hint': 'يمكن إعداد ميزانية زراعة قبل إدراجها في الدورة الزراعية.',
  'budget.otherCrop.submit': 'إعداد الميزانية',
  'budget.crop': 'الزراعة',
  'budget.comparison': 'المتوقع والمنجز',
  'budget.comparison.empty': 'لا توجد أي زراعة لها ميزانية: لا شيء للمقارنة.',
  'budget.post': 'البند',
  'budget.planned': 'المتوقع',
  'budget.actual': 'المنجز',
  'budget.gap': 'الفارق',
  'budget.gapPercent': 'الفارق النسبي',
  'budget.post.input': 'المدخلات',
  'budget.post.machine': 'المعدات',
  'budget.post.labor': 'اليد العاملة',
  'budget.post.other': 'أعباء أخرى',
  'budget.post.direct_cost': 'التكلفة المباشرة',
  'budget.post.yield': 'الإنتاج',
  'budget.post.yield_per_ha': 'المردود للهكتار',
  'budget.post.revenue': 'رقم المعاملات',
  'budget.post.gross_margin': 'الهامش الإجمالي',
  'budget.total': 'مجموع الزراعات التي لها ميزانية',
  'budget.unitMismatch': 'أحد محاصيل هذه الزراعة موزون بوحدة غير وحدة الميزانية: الإنتاج المنجز غير معروض.',
  'budget.unbudgeted': 'زراعات بلا ميزانية',
  'budget.unbudgeted.hint': 'منجزها لا يدخل في المجموع: لا شيء يقابله.',
  'budget.actualDirectCost': 'التكلفة المباشرة المنجزة',
  'budget.actualRevenue': 'رقم المعاملات المنجز',
  // Lot 8b (migration 0098) : les dettes fournisseurs et leurs règlements.
  // Lot 8c (migration 0099) : l'échéancier.
  'nav.group.treasury': 'الخزينة',
  'nav.schedule': 'جدول الاستحقاقات',
  'nav.treasuryAccounts': 'البنك والصندوق',
  'nav.commitments': 'الالتزامات',
  'nav.reconciliation': 'المقاربة البنكية',
  'nav.executive': 'الإدارة',
  'schedule.title': 'جدول الاستحقاقات',
  'schedule.description': 'ما بقي للقبض وما بقي للدفع، كلٌّ في أجله.',
  'schedule.forbidden': 'جدول الاستحقاقات مخصص لمن يطّلع على المبالغ.',
  'schedule.asOf': 'الوضعية بتاريخ',
  'schedule.asOf.hint': 'إن تُرك فارغا: اليوم.',
  'schedule.situation': 'وضعية الاستحقاقات',
  'schedule.situation.asOf': 'مرتّبة بتاريخ',
  'schedule.bucket': 'الشريحة',
  'schedule.bucket.overdue': 'حلّ أجله',
  'schedule.bucket.0-7': 'خلال 7 أيام',
  'schedule.bucket.8-30': 'من 8 إلى 30 يوما',
  'schedule.bucket.31-60': 'من 31 إلى 60 يوما',
  'schedule.bucket.61-90': 'من 61 إلى 90 يوما',
  'schedule.bucket.90+': 'أكثر من 90 يوما',
  'schedule.bucket.no_due_date': 'بلا أجل',
  'schedule.bucket.total': 'المجموع',
  'schedule.receivable': 'للقبض',
  'schedule.payable': 'للدفع',
  'schedule.net': 'الصافي',
  'schedule.treasury': 'الخزينة اليوم:',
  'schedule.projected': 'الرصيد المتوقع',
  'schedule.projected.hint': 'الرصيد المتوقع هو ما سيكون لدينا في نهاية كل شريحة لو دفع كل طرف في أجله: خزينة اليوم، زائد المنتظر، ناقص المستحق. ما فات أجله يُحتسب فوراً؛ وما لا أجل له لا يدخل فيه.',
  'schedule.entries': 'الاستحقاقات',
  'schedule.entries.hint': 'الفاتورة مدرجة بما بقي عليها. والصك أو الكمبيالة المسلَّمة تبقى في أجلها الخاص إلى أن يُسجَّل خصمها في شاشة الدفوعات.',
  'schedule.empty': 'لا يوجد أي استحقاق: لا شيء للقبض ولا للدفع.',
  // Lot 8d (migration 0102) : les comptes de banque et de caisse.
  'treasury.title': 'البنك والصندوق',
  'treasury.description': 'الحسابات البنكية والصناديق، أرصدتها، وكشف كل واحد منها.',
  'treasury.forbidden': 'الخزينة مخصصة لمن يطّلع على المبالغ.',
  'treasury.accounts': 'الحسابات',
  'treasury.accounts.empty': 'لا يوجد أي حساب: افتح بنكك وصندوقك أدناه.',
  'treasury.account': 'الحساب',
  'treasury.code': 'الرمز',
  'treasury.name': 'اسم الحساب',
  'treasury.kind': 'النوع',
  'treasury.kind.bank': 'بنك',
  'treasury.kind.cash': 'صندوق',
  'treasury.bankReference': 'البنك ورقم الحساب',
  'treasury.bankReference.hint': 'كما يظهران في كشف الحساب.',
  'treasury.openingDate': 'تاريخ الافتتاح',
  'treasury.openingBalance': 'رصيد الافتتاح',
  'treasury.openingBalance.hint': 'ما كان يحتويه الحساب في ذلك اليوم. الحساب الفارغ يُفتح بـ 0 مكتوبة؛ والمكشوف بقيمة سالبة.',
  'treasury.inflow': 'المداخيل',
  'treasury.outflow': 'المصاريف',
  'treasury.balance': 'الرصيد',
  'treasury.amount': 'المبلغ',
  'treasury.status.open': 'مفتوح',
  'treasury.status.closed': 'مغلق',
  'treasury.totals': 'الخزينة',
  'treasury.totals.bank': 'في البنك',
  'treasury.totals.cash': 'في الصندوق',
  'treasury.totals.total': 'مجموع الخزينة',
  'treasury.account.new': 'فتح حساب',
  'treasury.account.new.hint': 'الافتتاح — الرصيد والتاريخ — لا يُصحَّح: كل أرصدة الحساب تنطلق منه. الدفع المؤرَّخ قبل الافتتاح موجود أصلاً في هذا الرصيد: يحدّد الحساب دون أن يحرّكه. الحساب المُدخل خطأً يُحذف ما دام لا يشير إليه أي دفع.',
  'treasury.account.create': 'فتح الحساب',
  'treasury.account.created': 'تم فتح الحساب.',
  'treasury.account.rename': 'حفظ الاسم',
  'treasury.account.renamed': 'تم تصحيح الحساب.',
  'treasury.account.close': 'إغلاق',
  'treasury.account.closed': 'أُغلق الحساب: يحتفظ برصيده ولا يستقبل شيئاً بعد الآن.',
  'treasury.account.reopen': 'إعادة الفتح',
  'treasury.account.reopened': 'أُعيد فتح الحساب.',
  'treasury.account.delete': 'حذف',
  'treasury.account.deleted': 'تم حذف الحساب.',
  'treasury.statement': 'كشف الحساب',
  'treasury.statement.open': 'كشف الحساب',
  'treasury.statement.date': 'التاريخ',
  'treasury.statement.counterparty': 'الطرف',
  'treasury.statement.reference': 'الوثيقة',
  'treasury.statement.opening': 'الرصيد عند البداية',
  'treasury.statement.closing': 'الرصيد عند النهاية',
  'treasury.statement.from': 'من',
  'treasury.statement.from.hint': 'فارغ: منذ افتتاح الحساب.',
  'treasury.statement.to': 'إلى',
  'treasury.statement.empty': 'لا توجد أي حركة في هذه الفترة.',
  'treasury.direction': 'الاتجاه',
  'treasury.direction.in': 'دخول',
  'treasury.direction.out': 'خروج',
  'treasury.unassigned': 'دفوعات تنتظر تحديد حسابها',
  'treasury.unassigned.hint': 'أُدخلت قبل الحسابات، فلا تدخل في أي رصيد. بعد تحديد الحساب لا يغيّر الدفع حسابه.',
  'treasury.unassigned.assign': 'تحديد الحساب',
  'treasury.unassigned.assigned': 'تم تحديد حساب الدفع.',
  'treasury.field.bank': 'الحساب البنكي',
  'treasury.field.cash': 'الصندوق',
  'treasury.field.hint': 'الحساب الذي يحرّكه هذا الدفع.',
  'treasury.field.none.bank': 'لا يوجد أي حساب بنكي مفتوح: طريقة الدفع هذه تتطلب واحداً.',
  'treasury.field.none.cash': 'لا يوجد أي صندوق مفتوح: النقد يتطلب واحداً.',
  'treasury.field.open': 'فتح حساب',
  'treasury.field.any': 'بنك أو صندوق',
  'treasury.field.none.any': 'لا يوجد أي حساب مفتوح: هذا الدفع يتطلب واحداً.',
  // Lot 8d (migration 0104) : les mouvements saisis, et le virement d'un compte à l'autre.
  'treasury.entries': 'الحركات المُدخلة',
  'treasury.entries.hint': 'ما يحرّك حساباً دون أن يكون دفعاً لفاتورة: مصاريف بنكية، أجر، مساهمة، سحب. والتحويل من حساب إلى آخر — نقد مودَع في البنك مثلاً.',
  'treasury.entry.category': 'النوع',
  'treasury.entry.category.transfer': 'تحويل بين حسابين',
  'treasury.entry.category.bank_charges': 'مصاريف بنكية',
  'treasury.entry.category.interest': 'فوائد',
  'treasury.entry.category.owner_contribution': 'مساهمة المستغل',
  'treasury.entry.category.owner_withdrawal': 'سحب المستغل',
  'treasury.entry.category.salary': 'أجور',
  'treasury.entry.category.rent': 'إيجارات',
  'treasury.entry.category.insurance': 'تأمينات',
  'treasury.entry.category.tax': 'ضرائب وأداءات',
  'treasury.entry.category.other': 'أخرى',
  'treasury.entry.target': 'حساب الوجهة',
  'treasury.entry.target.hint': 'المال يخرج من الحساب الأول ويدخل في هذا.',
  'treasury.entry.label': 'البيان',
  'treasury.entry.reference': 'المرجع',
  'treasury.entry.submit': 'تسجيل الحركة',
  'treasury.entry.recorded': 'تم تسجيل الحركة.',
  'treasury.entry.cancel': 'إلغاء',
  'treasury.entry.cancelled': 'أُلغيت الحركة: الحساب يعود.',
  'treasury.entry.reason': 'سبب الإلغاء',
  'treasury.entry.reason.hint': 'إلزامي لإلغاء حركة. الحركة لا تُصحَّح: تُلغى.',
  'treasury.entry.status.posted': 'مسجَّل',
  'treasury.entry.status.cancelled': 'ملغى',
  // Lot 8d (migration 0105) : les autres engagements — salaires, loyers, assurances, impôts.
  'commitments.title': 'الالتزامات',
  'commitments.description': 'الأجور والإيجارات والتأمينات والضرائب والمصاريف الأخرى: ما علينا، في تاريخه، وما دُفع منه.',
  'commitments.forbidden': 'الالتزامات مخصصة لمن يطّلع على المبالغ.',
  'commitments.due': 'للدفع',
  'commitments.due.empty': 'لا يوجد أي التزام للدفع.',
  'commitments.totalDue': 'الباقي للدفع:',
  'commitments.dueDate': 'الأجل',
  'commitments.category': 'النوع',
  'commitments.category.salary': 'أجور',
  'commitments.category.rent': 'إيجار',
  'commitments.category.insurance': 'تأمين',
  'commitments.category.tax': 'ضرائب وأداءات',
  'commitments.category.other': 'مصروف آخر',
  'commitments.label': 'البيان',
  'commitments.counterparty': 'لمن',
  'commitments.counterparty.hint': 'مؤجّر، مؤمِّن، قباضة المالية. فارغ بالنسبة لأجر.',
  'commitments.paidAmount': 'المدفوع',
  'commitments.remaining': 'الباقي',
  'commitments.pay': 'دفع',
  'commitments.payment': 'المبلغ المدفوع',
  'commitments.paid': 'تم تسجيل الدفع.',
  'commitments.paidOn': 'يوم الدفع',
  'commitments.cancel': 'إلغاء الالتزام',
  'commitments.cancelled': 'أُلغي الالتزام: لم يعد عليه شيء.',
  'commitments.reason': 'سبب الإلغاء',
  'commitments.reason.hint': 'إلزامي لإلغاء التزام. ما دُفع منه يبقى مدفوعاً.',
  'commitments.new': 'إدخال التزام',
  'commitments.new.hint': 'مبلغ دفع فارغ يدفع كل الباقي. السلسلة تكتب عدداً من الالتزامات، كل واحد في تاريخه: اثنا عشر إيجاراً، كل شهر، هي اثنا عشر سطراً. الالتزام لا يُصحَّح، يُلغى.',
  'commitments.firstDueDate': 'الأجل — الأول في السلسلة',
  'commitments.count': 'عدد الآجال',
  'commitments.count.hint': 'فارغ: واحد فقط.',
  'commitments.interval': 'يتكرر',
  'commitments.interval.monthly': 'كل شهر',
  'commitments.interval.quarterly': 'كل ثلاثة أشهر',
  'commitments.interval.yearly': 'كل سنة',
  'commitments.create': 'تسجيل الالتزام',
  'commitments.created': 'تم تسجيل الالتزام.',
  'commitments.closed': 'المسدَّدة والملغاة',
  'commitments.settlement.settled': 'مسدَّد',
  'commitments.settlement.cancelled': 'ملغى',
  // Lot 8e (migration 0106) : le rapprochement bancaire — un relevé, et les mouvements qu'on y pointe.
  'reconciliation.title': 'المقاربة البنكية',
  'reconciliation.description': 'إدخال كشف البنك، وتأشير حركات الحساب الواردة فيه، وقراءة ما يفصل بينهما.',
  'reconciliation.forbidden': 'المقاربة البنكية مخصصة لمن يطّلع على المبالغ.',
  'reconciliation.noBank': 'لا يوجد أي حساب بنكي: افتح حساباً لمقاربته.',
  'reconciliation.noBank.link': 'فتح حساب',
  'reconciliation.account': 'الحساب البنكي',
  'reconciliation.statements': 'الكشوف',
  'reconciliation.statements.empty': 'لم يُدخل أي كشف لهذا الحساب.',
  'reconciliation.statementDate': 'موقوف في',
  'reconciliation.reference': 'المرجع',
  'reconciliation.reference.hint': 'رقم الكشف إن وُجد.',
  'reconciliation.openingBalance': 'الرصيد الأولي',
  'reconciliation.closingBalance': 'الرصيد النهائي',
  'reconciliation.pointedCount': 'المؤشَّرة',
  'reconciliation.gap': 'الفارق',
  'reconciliation.state.reconciled': 'تمّت المقاربة',
  'reconciliation.state.open': 'للمقاربة',
  'reconciliation.chain.broken': 'لا يتبع الكشف السابق',
  'reconciliation.open': 'فتح',
  'reconciliation.delete': 'حذف',
  'reconciliation.deleted': 'حُذف الكشف.',
  'reconciliation.new': 'كشف جديد',
  'reconciliation.new.hint': 'انسخ الرصيدين المطبوعين على الكشف ولو كانا صفراً. السحب على المكشوف يُدخل بالسالب.',
  'reconciliation.create': 'إدخال الكشف',
  'reconciliation.created': 'أُدخل الكشف.',
  'reconciliation.detail': 'مقاربة الكشف',
  'reconciliation.line.date': 'موقوف في:',
  'reconciliation.line.opening': 'الرصيد الأولي:',
  'reconciliation.line.pointed': 'الحركات المؤشَّرة:',
  'reconciliation.line.pointedBalance': 'الرصيد الأولي زائد الحركات المؤشَّرة:',
  'reconciliation.line.closing': 'الرصيد النهائي للكشف:',
  'reconciliation.line.gap': 'الفارق:',
  'reconciliation.reconciled.hint': 'الرصيد الأولي زائد الحركات المؤشَّرة يساوي الرصيد النهائي: تمّت مقاربة الكشف.',
  'reconciliation.open.hint': 'أشِّر الحركات الواردة في الكشف. وما ورد فيه ولم يوجد هنا — مصاريف، فوائد مدينة، فوائد — يُدخل في «البنك والصندوق» ثم يؤشَّر.',
  'reconciliation.chain.alert': 'الرصيد الأولي لا يطابق الرصيد النهائي للكشف السابق: كشف ناقص أو رصيد منسوخ خطأً. الفارق:',
  'reconciliation.correct': 'تصحيح الكشف',
  'reconciliation.correct.hint': 'التاريخ والمرجع والرصيدان تُصحَّح دون إزالة أي تأشير. أما الحساب فلا.',
  'reconciliation.corrected': 'صُحِّح الكشف.',
  'reconciliation.pointed': 'الحركات المؤشَّرة',
  'reconciliation.pointed.empty': 'لا توجد أي حركة مؤشَّرة على هذا الكشف.',
  'reconciliation.unpointed': 'حركات للتأشير',
  'reconciliation.unpointed.hint': 'كل حركات الحساب التي لم يؤكدها أي كشف بعد، مهما كان تاريخها.',
  'reconciliation.unpointed.empty': 'كل حركات هذا الحساب مؤشَّرة.',
  'reconciliation.source': 'الوثيقة',
  'reconciliation.source.customer_payment': 'قبض',
  'reconciliation.source.supplier_payment': 'تسديد مورّد',
  'reconciliation.source.lease_payment': 'قسط إيجار مالي',
  'reconciliation.source.treasury_entry': 'حركة مُدخلة',
  'reconciliation.point': 'تأشير',
  'reconciliation.pointedDone': 'أُشِّرت الحركة.',
  'reconciliation.unpoint': 'إزالة التأشير',
  'reconciliation.unpointedDone': 'أُزيل التأشير.',
  // Lot 9 (migration 0107) : le centre d'alertes.
  'alerts.title': 'التنبيهات',
  'alerts.description': 'كل ما يستدعي المعالجة في مكان واحد: آجال فائتة، تدخلات غير مقيَّمة، تنبيهات زراعية، مخزون، إنتاج غير مراقَب، دفعات بلا مصدر.',
  'alerts.asOf': 'بتاريخ',
  'alerts.total': 'التنبيهات:',
  'alerts.none': 'لا شيء يُذكر.',
  'alerts.family.count': 'العدد:',
  'alerts.family.total': 'المجموع:',
  'alerts.shown': 'المعروضة:',
  'alerts.shown.of': 'من',
  'alerts.open': 'فتح:',
  'alerts.col.dueDate': 'الأجل',
  'alerts.col.daysOverdue': 'أيام التأخير',
  'alerts.col.kind': 'النوع',
  'alerts.col.counterparty': 'الطرف',
  'alerts.col.reference': 'الوثيقة',
  'alerts.col.amount': 'المبلغ',
  'alerts.col.date': 'التاريخ',
  'alerts.col.workType': 'العمل',
  'alerts.col.rule': 'القاعدة المتجاوَزة',
  'alerts.col.subject': 'على',
  'alerts.col.observed': 'المقيس',
  'alerts.col.threshold': 'العتبة',
  'alerts.col.product': 'المنتوج',
  'alerts.col.warehouse': 'المستودع',
  'alerts.col.lot': 'الدفعة',
  'alerts.col.quantity': 'الكمية',
  'alerts.col.expiryDate': 'انتهاء الصلاحية',
  'alerts.col.receivedOn': 'دخل في',
  'alert.payable_overdue': 'آجال دفع فائتة',
  'alert.payable_overdue.hint': 'ما علينا وقد فات أجله: فواتير، أوراق تجارية مسلَّمة، أقساط إيجار مالي، التزامات.',
  'alert.receivable_overdue': 'حرفاء متأخرون عن الدفع',
  'alert.receivable_overdue.hint': 'ما على الحرفاء وقد فات أجله.',
  'ops.operation_unvalued': 'تدخلات غير مقيَّمة',
  'ops.operation_unvalued.hint': 'منجزة أو مصادَق عليها، وفيها مدخل أو آلة أو يد عاملة أو مصروف بلا قيمة: كلفتها ناقصة.',
  'ops.warning': 'تنبيهات زراعية',
  'ops.warning.hint': 'رُفعت عند إدخال التدخلات ما دام موسمها مفتوحاً: ما قيس، والعتبة المتجاوَزة.',
  'stock.lot_uncontrolled': 'إنتاج غير مراقَب',
  'stock.lot_uncontrolled.hint': 'محاصيل في المخزون لم يراقبها أحد بعد.',
  'stock.lot_without_origin': 'دفعات بلا مصدر',
  'stock.lot_without_origin.hint': 'دفعات في المخزون لا يفسّرها استلام ولا حصاد ولا تقسيم ولا استرجاع ولا إدخال.',
  'orchard.window_open': 'نافذة الجني مفتوحة',
  'orchard.window_open.hint': 'نافذة الجني الجهوية للمحصول مفتوحة ولم يُجْنَ شيء بعد على البستان.',
  'orchard.dar_active': 'مهلة ما قبل الجني جارية',
  'orchard.dar_active.hint': 'معالجة وقائية مصادق عليها على البستان: يبقى الجني موقوفا حتى انتهاء مهلة ما قبل الجني.',
  // Lot 9 (migration 0108) : le tableau de bord de direction.
  'executive.title': 'لوحة قيادة الإدارة',
  'executive.description': 'مؤشرات المستغلة لموسم: المساحات، التكاليف والهامش للهكتار، الإنتاج، المخزون، الديون على الحرفاء، الديون للمورّدين، الخزينة.',
  'executive.forbidden': 'لوحة قيادة الإدارة مخصصة لمن يطّلع على المبالغ.',
  'executive.season': 'الموسم',
  'executive.season.current': 'الموسم الجاري',
  'executive.season.none': 'لا يوجد موسم جارٍ: مؤشرات الموسم صامتة. اختر موسماً.',
  'executive.asOf': 'بتاريخ',
  'executive.readSeason': 'الموسم المقروء:',
  'executive.areas': 'المساحات',
  'executive.farmedArea': 'المساحة المستغلة (هك):',
  'executive.cultivatedArea': 'المساحة المزروعة (هك):',
  'executive.perHa': 'للهكتار',
  'executive.perHa.hint': 'منسوبة إلى المساحة المزروعة في الموسم.',
  'executive.costPerHa': 'متوسط الكلفة:',
  'executive.machineCostPerHa': 'كلفة الآلات:',
  'executive.fuelCostPerHa': 'كلفة الوقود:',
  'executive.marginPerHa': 'الهامش:',
  'executive.production': 'الإنتاج',
  'executive.production.empty': 'لا يوجد أي حصاد في هذا الموسم.',
  'executive.production.hint': 'المردود منسوب إلى مساحة القطع المحصودة، بوحدة الحصاد.',
  'executive.production.product': 'المنتوج',
  'executive.production.quantity': 'الكمية',
  'executive.production.area': 'المساحة المحصودة (هك)',
  'executive.production.yield': 'المردود للهكتار',
  'executive.fuel': 'الوقود',
  'executive.fuelLiters': 'الوقود المستهلك (لتر):',
  'executive.stock': 'المخزون',
  'executive.stockInput': 'مخزون المدخلات:',
  'executive.stockProduct': 'مخزون المنتوجات:',
  'executive.money': 'الأطراف والخزينة',
  'executive.receivables': 'الديون على الحرفاء:',
  'executive.payables': 'الديون للمورّدين:',
  'executive.treasury': 'الخزينة:',
  'executive.silent': 'الشرطة: المؤشر لا يملك ما يُحسب به — لا موسم، لا مساحة مزروعة، لا وقود مسجَّل، لا حساب مفتوح. وليست صفراً.',
  'schedule.dueDate': 'أجل الاستحقاق',
  'schedule.noDueDate': 'بلا أجل',
  'schedule.overdueDays': 'أيام التأخير',
  'schedule.direction': 'الاتجاه',
  'schedule.direction.in': 'للقبض',
  'schedule.direction.out': 'للدفع',
  'schedule.kind': 'النوع',
  'schedule.kind.customer_invoice': 'فاتورة حريف',
  'schedule.kind.supplier_invoice': 'فاتورة مزوّد',
  'schedule.kind.lease_installment': 'قسط إيجار مالي',
  'schedule.kind.supplier_instrument': 'صك أو كمبيالة مسلَّمة',
  'schedule.kind.commitment_salary': 'أجور',
  'schedule.kind.commitment_rent': 'إيجار',
  'schedule.kind.commitment_insurance': 'تأمين',
  'schedule.kind.commitment_tax': 'ضرائب وأداءات',
  'schedule.kind.commitment_other': 'مصروف آخر',
  'schedule.counterparty': 'الطرف',
  'schedule.reference': 'الوثيقة',
  'schedule.amount': 'المبلغ',
  'nav.payables': 'ديون المزودين والدفوعات',
  'payables.title': 'ديون المزودين والدفوعات',
  'payables.description': 'ما ندين به للمزودين، ومنذ متى — وما يقع دفعه.',
  'payables.forbidden': 'الديون والدفوعات مخصصة لمن يمسك الوثائق.',
  'payables.aging': 'ما ندين به للمزودين',
  'payables.empty': 'لا توجد فاتورة مصادق عليها تنتظر الدفع، ولا تسبقة تنتظر التخصيص.',
  'payables.supplier': 'المزوّد',
  'payables.outstanding': 'الباقي المستحق',
  'payables.unallocated': 'تسبقات للتخصيص',
  'payables.overpaid': 'مدفوع زائد',
  'payables.net': 'الصافي',
  'payables.total': 'مجموع المزودين',
  'payables.open': 'فتح',
  'payables.openOther': 'فتح مزوّد آخر',
  'payables.openOther.hint': 'لدفع عربون لمزوّد ليست له بعد أي فاتورة مصادق عليها.',
  'payables.invoices': 'فواتير مصادق عليها غير مسددة',
  'payables.invoices.empty': 'ليس لهذا المزوّد أي فاتورة مصادق عليها غير مسددة.',
  'payables.invoice': 'الفاتورة',
  'payables.invoiceDate': 'تاريخ الفاتورة',
  'payables.dueDate': 'أجل الاستحقاق',
  'payables.noDueDate': 'بلا أجل',
  'payables.setDueDate': 'تحديد الأجل',
  'payables.dueDate.saved': 'تم حفظ الأجل.',
  'payables.invoiced': 'المفوتر',
  'payables.credited': 'الإشعارات الدائنة',
  'payables.paid': 'المدفوع',
  'payables.remaining': 'الباقي المستحق',
  'payables.allocate': 'تخصيص',
  'payables.advances': 'تسبقات للتخصيص',
  'payables.advances.hint': 'أدخل المبالغ المراد تخصيصها في جدول الفواتير، ثم خصّص التسبقة.',
  'payables.advance.allocate': 'تخصيص هذه التسبقة',
  'payables.advance.allocated': 'تم تخصيص التسبقة.',
  'payables.payment': 'الدفع',
  'payables.payment.new': 'دفع جديد',
  'payables.payment.hint': 'المبالغ المدخلة في عمود «تخصيص» تبيّن الفواتير التي يسددها هذا الدفع. وما لم يُخصَّص يبقى تسبقة — عربونا مثلا.',
  'payables.payment.submit': 'تسجيل الدفع',
  'payables.payment.recorded': 'تم تسجيل الدفع.',
  'payables.payment.cancel': 'إلغاء',
  'payables.payment.cancelled': 'تم إلغاء الدفع.',
  'payables.payment.clear': 'تم الخصم',
  'payables.payment.cleared': 'تم تسجيل الخصم: خرج الدفع من جدول الاستحقاقات.',
  'payables.clearedOn': 'يوم الخصم',
  'payables.clearedOn.hint': 'لتسجيل أن البنك خصم صكا أو كمبيالة. إن تُرك فارغا: اليوم.',
  'payables.paymentDate': 'تاريخ الدفع',
  'payables.method': 'طريقة الدفع',
  'payables.method.cash': 'نقدا',
  'payables.method.cheque': 'صك',
  'payables.method.transfer': 'تحويل بنكي',
  'payables.method.bill_of_exchange': 'كمبيالة',
  'payables.method.card': 'بطاقة بنكية',
  'payables.method.compensation': 'مقاصة',
  'payables.reference': 'المرجع',
  'payables.instrumentDueDate': 'أجل الصك أو الكمبيالة',
  'payables.instrumentDueDate.hint': 'اليوم الذي يخرج فيه المال: ليس هو تاريخ الدفع.',
  'payables.amount': 'المبلغ',
  'payables.allocated': 'المخصَّص',
  'payables.notes': 'ملاحظة',
  'payables.payments': 'الدفوعات',
  'payables.payments.empty': 'لا يوجد أي دفع.',
  'payables.cancelReason': 'سبب الإلغاء',
  'payables.cancelReason.hint': 'إجباري للإلغاء: صك راجع، خطأ في الإدخال…',
  'payables.status.issued': 'صادر',
  'payables.status.presented': 'مقدَّم للخلاص',
  'payables.status.cleared': 'مخصوم من الحساب',
  'payables.status.bounced': 'راجع دون خلاص',
  'payables.status.cancelled': 'ملغى',
  'nav.receivables': 'الديون والمقبوضات',
  'receivables.title': 'الديون والمقبوضات',
  'receivables.description': 'ما يدين به الحرفاء، ومنذ متى — وما يقع قبضه.',
  'receivables.forbidden': 'الديون والمقبوضات مخصصة لمن يمسك الوثائق.',
  'receivables.aging': 'ما يدين به الحرفاء',
  'receivables.empty': 'لا يوجد حريف مدين، ولا تسبقة تنتظر التخصيص.',
  'receivables.customer': 'الحريف',
  'receivables.outstanding': 'الباقي المستحق',
  'receivables.bucket.not_due': 'لم يحلّ أجله',
  'receivables.bucket.1-30': 'متأخر من 1 إلى 30 يوما',
  'receivables.bucket.31-60': 'متأخر من 31 إلى 60 يوما',
  'receivables.bucket.61-90': 'متأخر من 61 إلى 90 يوما',
  'receivables.bucket.90+': 'متأخر أكثر من 90 يوما',
  'receivables.bucket.no_due_date': 'بلا أجل',
  'receivables.unallocated': 'تسبقات للتخصيص',
  'receivables.overpaid': 'مقبوض زائد',
  'receivables.net': 'الصافي',
  'receivables.total': 'مجموع الحرفاء',
  'receivables.open': 'فتح',
  'receivables.openOther': 'فتح حريف آخر',
  'receivables.openOther.hint': 'لقبض تسبقة من حريف ليست له بعد أي فاتورة صادرة.',
  'receivables.invoices': 'فواتير غير مسددة',
  'receivables.invoices.empty': 'لا توجد لهذا الحريف أي فاتورة غير مسددة.',
  'receivables.invoice': 'الفاتورة',
  'receivables.invoiceDate': 'تاريخ الفاتورة',
  'receivables.dueDate': 'أجل الاستحقاق',
  'receivables.noDueDate': 'بلا أجل',
  'receivables.setDueDate': 'تحديد الأجل',
  'receivables.dueDate.saved': 'تم حفظ الأجل.',
  'receivables.invoiced': 'المفوتر',
  'receivables.credited': 'فواتير الإرجاع',
  'receivables.paid': 'المقبوض',
  'receivables.remaining': 'الباقي المستحق',
  'receivables.allocate': 'تخصيص',
  'receivables.advances': 'تسبقات للتخصيص',
  'receivables.advances.hint': 'أدخل المبالغ المراد تخصيصها في جدول الفواتير، ثم خصّص التسبقة.',
  'receivables.advance.allocate': 'تخصيص هذه التسبقة',
  'receivables.advance.allocated': 'تم تخصيص التسبقة.',
  'receivables.payment': 'المقبوض',
  'receivables.payment.new': 'مقبوض جديد',
  'receivables.payment.hint': 'المبالغ المدخلة في عمود «تخصيص» تبيّن الفواتير التي يسددها هذا المقبوض. وما لم يُخصَّص يبقى تسبقة.',
  'receivables.payment.submit': 'تسجيل المقبوض',
  'receivables.payment.recorded': 'تم تسجيل المقبوض.',
  'receivables.payment.cancel': 'إلغاء',
  'receivables.payment.cancelled': 'تم إلغاء المقبوض.',
  'receivables.paymentDate': 'تاريخ القبض',
  'receivables.method': 'طريقة الدفع',
  'receivables.reference': 'المرجع',
  'receivables.reference.required': 'طريقة الدفع هذه تستوجب مرجعا.',
  'receivables.amount': 'المبلغ',
  'receivables.allocated': 'المخصَّص',
  'receivables.notes': 'ملاحظة',
  'receivables.payments': 'المقبوضات',
  'receivables.payments.empty': 'لا يوجد أي مقبوض.',
  'receivables.cancelReason': 'سبب الإلغاء',
  'receivables.cancelReason.hint': 'إجباري للإلغاء: صك دون رصيد، خطأ في الإدخال…',
  'receivables.status.posted': 'مسجَّل',
  'receivables.status.cancelled': 'ملغى',
  'references.origin': 'المصدر',
  'references.origin.catalog': 'الكتالوج',
  'references.origin.own': 'خاص',
  'references.status.active': 'نشط',
  'references.status.inactive': 'غير نشط',
  'references.status.hidden': 'مخفي',
  'references.renamed': 'معاد تسميته',
  'references.search': 'ابحث في هذا المرجع',
  'references.showInactive': 'اعرض المدخلات غير النشطة والمخفية',
  'references.empty': 'لا توجد مدخلات في هذا المرجع.',
  'references.fiche': 'البطاقة',
  'references.baseName': 'التسمية المُسلَّمة',
  'references.effectiveName': 'الاسم المعروض',
  'references.arabicLabel': 'التسمية العربية',
  'references.englishLabel': 'التسمية الإنجليزية',
  'references.crop': 'المحصول',
  'references.maturityDays': 'المبكّرية (أيام)',
  'references.certified': 'بذرة معتمدة',
  'references.referencesOriginField': 'المنشأ',
  'references.activate': 'تنشيط',
  'references.deactivate': 'تعطيل',
  'references.activated': 'تم تنشيط المدخلة.',
  'references.deactivated': 'تم تعطيل المدخلة.',
  'references.overrideTitle': 'تجاوز الكتالوج',
  'references.overrideHint': 'يبقى السطر المُسلَّم مطابقا عند جميع الزبائن: تسميتك تعرض بدله دون أن تستبدلها.',
  'references.overrideSave': 'حفظ التجاوز',
  'references.overrideSaved': 'تم حفظ التجاوز.',
  'references.newEntry': 'إضافة مدخلة',
  'references.create': 'إنشاء المدخلة',
  'references.creating': 'جاري الإنشاء…',
  'references.created': 'تمت إضافة المدخلة إلى مرجعك.',

  'stock.balance.insufficient_quantity': 'المخزون غير كافٍ: الخروج يتجاوز ما يحمله المخزن.',
  'harvest.batch.net_exceeds_gross': 'لا يمكن أن يتجاوز الصافي القائم: هل انعكست البيانات؟',
  'purchase.receipt.over_delivery': 'تسليم زائد فوق السماح المحدد في الاشتراك.',
  'purchase.receipt.order_required': 'الاستلام بدون طلب ممنوع (V16): أنشئ طلب شراء أولاً.',
  'purchase.invoice.requires_receipt': 'الفاتورة بدون استلام ممنوعة: طابق الاستلام أولاً.',
  'purchase.invoice_match.no_counterpart': 'سطر فاتورة بدون طلب ولا استلام: لا يوجد ما تتم مطابقته.',
  'purchase.invoice_match.variance_reason_required': 'تم كشف فرق: يجب تقديم سبب لقبوله.',
  // Codes frappés par le lot 8b, côté gestionnaires comme côté déclencheurs.
  'purchase.invoice_match.rejection_reason_required': 'رفض الفاتورة يستوجب ذكر السبب.',
  'purchase.invoice_match.no_lines': 'ليس لهذه الفاتورة أي سطر للمطابقة.',
  'purchase.invoice_match.quantity_must_be_positive': 'أحد الأسطر يفوتر كمية معدومة أو سالبة.',
  'purchase.invoice_match.not_found': 'لا توجد مطابقة لهذه الفاتورة.',
  'purchase.supplier_invoice.not_found': 'فاتورة المزوّد غير موجودة.',
  'purchase.supplier_invoice.not_an_invoice': 'الإشعار الدائن لا يُطابَق وليس له أجل.',
  'purchase.supplier_invoice.already_decided': 'تم البتّ في هذه الفاتورة: لا يُعاد البتّ فيها.',
  'purchase.supplier_invoice.due_before_invoice': 'لا تُستحق الفاتورة قبل تاريخها.',
  'purchase.supplier_invoice.text_too_long': 'سبب القرار لا يتجاوز 300 حرف.',
  'purchase.supplier_invoice.immutable': 'هذه الفاتورة لم تعد مسودة: لا تُعاد كتابتها.',
  'purchase.supplier_invoice.match_undecided': 'ليس لهذه الفاتورة مطابقة محسومة لصالحها: لا يمكن المصادقة عليها.',
  'purchase.supplier_invoice.no_lines': 'فاتورة بلا أسطر لا تفوتر شيئا.',
  'purchase.supplier_invoice.quantity_must_be_positive': 'كل سطر في الفاتورة يحمل كمية أكبر من الصفر.',
  'purchase.supplier_invoice.negative_amount': 'لا يكون السعر ولا الأداء سالبا: ما ينقص من الفاتورة هو إشعار دائن.',
  'purchase.supplier_invoice.duplicate_product': 'لا تظهر المادة إلا مرة واحدة في الفاتورة: اجمع كمياتها.',
  'purchase.supplier_invoice.date_in_future': 'لا تؤرَّخ الفاتورة في المستقبل.',
  'purchase.supplier_invoice.reference_too_long': 'رقم فاتورة المزوّد لا يتجاوز 50 حرفا.',
  'purchase.supplier_invoice.order_not_found': 'الطلبية غير موجودة.',
  'purchase.supplier_invoice.order_of_another_supplier': 'هذه الطلبية وُجّهت إلى مزوّد آخر.',
  'purchase.supplier_invoice.nothing_to_invoice': 'إحدى المواد المفوترة ليس لها في هذه الطلبية ما هو مستلم ولم يُفوتر بعد.',
  'purchase.payment.amount_must_be_positive': 'مبلغ الدفع يجب أن يكون أكبر من الصفر.',
  'purchase.payment.unknown_method': 'يكون الدفع نقدا أو بصك أو تحويل أو كمبيالة أو بطاقة أو مقاصة.',
  'purchase.payment.due_date_required': 'الصك أو الكمبيالة يُقدَّم مع أجله.',
  'purchase.payment.date_in_future': 'لا يؤرَّخ الدفع في المستقبل.',
  'purchase.payment.text_too_long': 'مرجع الدفع لا يتجاوز 50 حرفا، وملاحظته أو سبب إلغائه 300.',
  'purchase.payment.supplier_not_found': 'المزوّد غير موجود.',
  'purchase.payment.allocation_invalid': 'مبلغ التخصيص يجب أن يكون أكبر من الصفر.',
  'purchase.payment.allocation_duplicate': 'لا تُخصَّص الفاتورة إلا مرة واحدة في الدفع الواحد.',
  'purchase.payment.over_allocated': 'التخصيصات تتجاوز ما بقي من قيمة الدفع.',
  'purchase.payment.invoice_not_payable': 'إحدى الفواتير المعيّنة ليست فاتورة مصادقا عليها لهذا المزوّد.',
  'purchase.payment.exceeds_remaining': 'أحد التخصيصات يتجاوز ما بقي على الفاتورة بعد طرح الإشعارات الدائنة.',
  'purchase.payment.not_found': 'الدفع غير موجود.',
  'purchase.payment.cancelled': 'هذا الدفع ملغى: لم يعد يسدد شيئا.',
  'purchase.payment.already_cancelled': 'هذا الدفع ملغى مسبقا.',
  'purchase.payment.cancellation_reason_required': 'إلغاء الدفع يستوجب ذكر السبب.',
  'purchase.payment.partner_mismatch': 'الدفع والفاتورة ليسا لنفس المزوّد.',
  'purchase.payment.allocation_immutable': 'التخصيص لا يُعدَّل ولا يُحذف: يُلغى الدفع نفسه.',
  'purchase.payment.immutable': 'الدفع لا تُعاد كتابته: يُلغى.',
  'purchase.payment.already_cleared': 'هذا الدفع مخصوم مسبقا.',
  'purchase.payment.not_an_instrument': 'لا يُصرَّح بخصم إلا صك أو كمبيالة.',
  'purchase.payment.cleared_before_payment': 'لا يُخصم الصك قبل تسليمه.',
  // Codes frappés par le lot 8d, côté gestionnaires comme côté déclencheurs.
  'finance.treasury.account_required': 'الدفع الذي يحرّك مالاً يحدّد حسابه البنكي أو صندوقه.',
  'finance.treasury.account_not_applicable': 'طريقة الدفع هذه لا تحرّك أي حساب؛ فلا تحدّد واحداً.',
  'finance.treasury.account_not_found': 'حساب الخزينة غير موجود.',
  'finance.treasury.account_kind_mismatch': 'النقد يمرّ عبر صندوق؛ والصك والتحويل والكمبيالة والبطاقة عبر بنك.',
  'finance.treasury.account_inactive': 'هذا الحساب مغلق: لا يستقبل ولا يدفع شيئاً بعد الآن.',
  'finance.treasury.account_immutable': 'نوع الحساب وافتتاحه لا يُعاد كتابتهما، ولا حساب الدفع.',
  'finance.treasury.account_in_use': 'هذا الحساب استُعمل: يُغلق ولا يُحذف.',
  'finance.treasury.code_required': 'لحساب الخزينة رمز.',
  'finance.treasury.name_required': 'لحساب الخزينة اسم.',
  'finance.treasury.unknown_kind': 'حساب الخزينة بنك أو صندوق.',
  'finance.treasury.opening_balance_required': 'رصيد الافتتاح يُدخل ولو كان صفراً: كل أرصدة الحساب تنطلق منه.',
  'finance.treasury.cash_cannot_open_negative': 'الصندوق لا يُفتح برصيد سالب.',
  'finance.treasury.opening_in_future': 'الحساب لا يُفتح بتاريخ مستقبلي.',
  'finance.treasury.code_taken': 'هذا الرمز يخصّ حساب خزينة آخر.',
  'finance.treasury.text_too_long': 'رمز الحساب في 30 حرفاً، واسمه في 120، ومرجعه البنكي في 60.',
  'finance.treasury.period_invalid': 'الفترة لا تنتهي قبل أن تبدأ.',
  'finance.treasury.nothing_to_assign': 'هذا الدفع لا ينتظر تحديد حساب: مجهول، ملغى، أو له حساب أصلاً.',
  'finance.treasury.unknown_source': 'لا يُحدَّد حساب إلا لقبض أو لدفع أو لدفع قسط.',
  'finance.treasury.unknown_category': 'نوع حركة الخزينة من القائمة.',
  'finance.treasury.unknown_direction': 'حركة الخزينة تدخل أو تخرج.',
  'finance.treasury.entry_amount_must_be_positive': 'مبلغ حركة الخزينة موجب تماماً.',
  'finance.treasury.entry_in_future': 'حركة الخزينة لا تؤرَّخ في المستقبل.',
  'finance.treasury.label_required': 'لحركة الخزينة بيان.',
  'finance.treasury.entry_text_too_long': 'بيان الحركة في 200 حرف، ومرجعها في 60، وسبب إلغائها في 300.',
  'finance.treasury.transfer_target_required': 'التحويل يحدّد الحساب الذي يدخله المال.',
  'finance.treasury.transfer_same_account': 'لا يُحوَّل من حساب إلى نفسه.',
  'finance.treasury.cancellation_reason_required': 'إلغاء حركة الخزينة يتطلب سبباً.',
  'finance.treasury.entry_not_found': 'حركة الخزينة غير موجودة.',
  'finance.treasury.entry_already_cancelled': 'حركة الخزينة هذه ملغاة من قبل.',
  'finance.treasury.entry_immutable': 'حركة الخزينة لا يُعاد كتابتها ولا تُحذف: تُلغى، ولا يُتراجع عن ذلك.',
  'finance.commitment.unknown_category': 'الالتزام أجر أو إيجار أو تأمين أو ضريبة أو مصروف آخر.',
  'finance.commitment.label_required': 'للالتزام بيان.',
  'finance.commitment.text_too_long': 'بيان الالتزام وطرفه في 200 حرف، وسبب إلغائه في 300.',
  'finance.commitment.amount_must_be_positive': 'مبلغ الالتزام موجب تماماً.',
  'finance.commitment.due_date_required': 'للالتزام أجل.',
  'finance.commitment.invalid_count': 'السلسلة من 1 إلى 60 أجلاً.',
  'finance.commitment.unknown_interval': 'السلسلة تتكرر كل شهر أو كل ثلاثة أشهر أو كل سنة.',
  'finance.commitment.not_found': 'الالتزام غير موجود.',
  'finance.commitment.not_payable': 'هذا الالتزام لم يعد عليه شيء: مسدَّد أو ملغى.',
  'finance.commitment.payment_must_be_positive': 'دفع الالتزام موجب تماماً.',
  'finance.commitment.exceeds_remaining': 'هذا الدفع يتجاوز ما بقي على الالتزام.',
  'finance.commitment.payment_mismatch': 'دفع الالتزام يخرج من حساب، وهو من نوعه.',
  'finance.commitment.cancellation_reason_required': 'إلغاء الالتزام يتطلب سبباً.',
  'finance.commitment.already_cancelled': 'هذا الالتزام ملغى من قبل.',
  'finance.commitment.immutable': 'الالتزام لا يُعاد كتابته ولا يُحذف: يُلغى، ولا يُتراجع عن ذلك.',
  'finance.reconciliation.account_not_bank': 'هذا الحساب صندوق: الصندوق يُعدّ ولا يُقارَب.',
  'finance.reconciliation.statement_date_required': 'للكشف البنكي تاريخ: اليوم الذي يتوقف عنده.',
  'finance.reconciliation.statement_in_future': 'الكشف البنكي لا يُؤرَّخ في المستقبل.',
  'finance.reconciliation.balance_required': 'الرصيد الأولي والرصيد النهائي يُدخلان ولو كانا صفراً: الفارق يتوقف عليهما.',
  'finance.reconciliation.reference_too_long': 'مرجع الكشف لا يتجاوز 60 حرفاً.',
  'finance.reconciliation.statement_exists': 'لهذا الحساب كشف موقوف في هذا التاريخ.',
  'finance.reconciliation.statement_not_found': 'الكشف البنكي غير موجود.',
  'finance.reconciliation.statement_has_marks': 'توجد حركات مؤشَّرة على هذا الكشف: أزِل تأشيرها قبل حذفه.',
  'finance.reconciliation.statement_immutable': 'الكشف لا يغيّر حسابه: يُحذف ويُعاد إدخاله.',
  'finance.reconciliation.movement_not_found': 'هذه الوثيقة ليست حركة في حساب هذا الكشف.',
  'finance.reconciliation.already_pointed': 'هذه الحركة مؤشَّرة مسبقاً على كشف.',
  'finance.reconciliation.not_pointed': 'هذه الحركة غير مؤشَّرة على هذا الكشف.',
  'finance.reconciliation.movement_pointed': 'هذه الوثيقة مؤشَّرة على كشف بنكي: أزِل تأشيرها قبل إلغائها.',
  'finance.reconciliation.mark_immutable': 'التأشير لا يُعاد كتابته: يُزال ويُعاد.',
  'purchase.order.not_approved': 'الطلب غير مؤكد: لا يمكن الاستلام.',
  'core.capability_denied': 'الوصول مرفوض: دورك لا يسمح بهذه العملية.',
  'operations.operation.not_planned': 'التدخل مصادق عليه: يُعكس ولا يُستهلك بعد الآن.',
  'users.create.admin_only': 'إنشاء المستخدمين من حصر للمسؤول.',

  'farms.detail.title': 'بطاقة الضيعة',
  'farms.detail.back': 'العودة للقائمة',
  'farms.create.title': 'ضيعة جديدة',
  'farms.create.submit': 'إنشاء',
  'farms.create.creating': 'جارٍ الإنشاء…',
  'farms.firstPlot.title': 'القطعة الأولى',
  'farms.firstPlot.hint':
    'لا تكون الضيعة نشطة إلا بقطعة واحدة على الأقل. إذا تُركت فارغة، تُسجَّل كمسودة ولا تظهر إلا عند عرض غير النشطة.',
  'farms.firstPlot.code': 'رمز القطعة',
  'farms.firstPlot.name': 'اسم القطعة',
  'farms.firstPlot.area': 'المساحة العقارية (هكتار)',
  'farms.firstPlot.irrigable': 'قطعة قابلة للري',
  'farms.firstPlot.incomplete': 'لإنشاء القطعة الأولى، يلزم رمزها ومساحتها.',
  'farms.description': 'الوصف',
  'farms.owner': 'مالك العقار',
  'farms.address': 'العنوان',
  'farms.draft': 'مسودة',
  'farms.created': 'تم إنشاء الضيعة.',
  'farms.location': 'الموقع',
  'farms.latitude': 'خط العرض',
  'farms.longitude': 'خط الطول',
  'farms.map.open': 'فتح في OpenStreetMap',
  'farms.fields.codeHint': 'رمز فريد، حروف كبيرة.',
  'common.save': 'حفظ',
  'common.create': 'إنشاء',
  'common.cancel': 'إلغاء',
  'common.details': 'التفاصيل',
  'common.actions': 'الإجراءات',

  'plots.detail.title': 'بطاقة القطعة',
  'plots.detail.back': 'العودة للقائمة',
  'plots.create.title': 'قطعة جديدة',
  'plots.create.submit': 'إنشاء القطعة',
  'plots.create.creating': 'جارٍ الإنشاء…',
  'plots.created': 'تم إنشاء القطعة.',
  'plots.fields.farm': 'الضيعة',
  'plots.fields.soilDepth': 'عمق التربة (سم)',
  'plots.fields.slope': 'الميل (%)',
  'plots.fields.parent': 'القطعة الأم',
  'plots.yields.title': 'مردود الموسم',
  'plots.yields.brut': 'المردود الإجمالي',
  'plots.yields.net': 'المردود الصافي',
  'plots.yields.normalized': 'المردود المعياري (14%)',
  'plots.yields.perHa': 'المردود بالهكتار',
  'plots.yields.comparison': 'المقارنة',
  'plots.yields.farmAvg': 'متوسط الضيعة',
  'plots.yields.seasonAvg': 'متوسط الموسم',
  'plots.yields.deviation': 'الانحراف',
  'plots.location.title': 'الموقع',
  'plots.map.view': 'عرض القطعة على الخريطة',
  'plots.mapLabel': 'خريطة تحديد موقع القطعة',
  'plots.mapHint': 'انقر على الخريطة لتحديد موقع القطعة؛ تبقى حقلا خط العرض وخط الطول قابلين للتعديل يدويًا لدقة الرفع الميداني.',
  'plots.pickOnMap': 'الاختيار على الخريطة',
  'plots.latitude': 'خط العرض',
  'plots.longitude': 'خط الطول',

  'campaigns.title': 'الحملات الزراعية',
  'campaigns.description': 'تحمل كل حملة رمزًا واسمًا ومجموعة موحّدة وتواريخها الخاصة، وترتبط دائمًا بقطعة أرض.',
  'campaigns.list': 'قائمة الحملات الزراعية',
  'campaigns.code': 'الرمز',
  'campaigns.name': 'الاسم',
  'campaigns.period': 'الفترة',
  'campaigns.plot': 'القطعة',
  'campaigns.group': 'المجموعة',
  'campaigns.invalidPeriod': 'يجب أن يأتي نهاية الحملة بعد بدايتها.',
  'campaigns.outsidePeriod': 'يجب أن تقع تواريخ الحملة داخل تواريخ الموسم:',
  'campaigns.status': 'الحالة',
  'campaigns.status.planned': 'مخطَّطة',
  'campaigns.status.sown': 'مزروعة',
  'campaigns.status.growing': 'قيد النمو',
  'campaigns.status.harvested': 'محصودة',
  'campaigns.status.closed': 'مغلقة',
  'campaigns.status.abandoned': 'مهجورة',
  'campaigns.current': 'جارٍ',
  'campaigns.closed': 'مغلق',
  'campaigns.empty': 'لا توجد حملة زراعية.',
  'campaigns.create.title': 'حملة جديدة',
  'campaigns.create.submit': 'إنشاء الحملة',
  'campaigns.created': 'تم إنشاء الحملة الزراعية.',
  'campaigns.plan.title': 'خطة التداول',
  'campaigns.plan.empty': 'لا توجد قطع-مواسم لهذا الفلتر.',
  'campaigns.plan.assign': 'إسناد محصول',
  'campaigns.plan.crop': 'المحصول',
  'campaigns.plan.sownArea': 'المساحة المزروعة (هكتار)',
  'campaigns.plan.assigned': 'تم تسجيل التداول.',

  'campaignGroups.title': 'مجموعات الحملات الزراعية',
  'campaignGroups.description': 'الاسم الموحّد الذي يمنع تفرّق «طماطم» و«طماطة» و«الطماطم» في ثلاث مجموعات مختلفة.',
  'campaignGroups.empty': 'لا توجد مجموعة حملات.',

  'normCategories.title': 'فئات المعايير',
  'normCategories.description': 'تصنيف المعايير — الجودة، الصحة النباتية، الفلاحة، البيئة، السلامة، الإنتاج — مشترك لكل المؤسسة.',
  'normCategories.empty': 'لا توجد فئة معايير.',

  'norms.title': 'المعايير',
  'norms.description': 'المعيار مقارنة قابلة للتقييم — عتبات ومقارنات ووحدة اختيارية — وليس جملة: العبارة المعروضة تُركَّب من طرف القاعدة، ولا تُكتب يدويا.',
  'norms.empty': 'لا توجد معايير.',
  'norms.name': 'الاسم',
  'norms.category': 'الفئة',
  'norms.parameter': 'المعامل المُلاحَظ',
  'norms.kind': 'الشكل',
  'norms.minValue': 'العتبة الدنيا',
  'norms.maxValue': 'العتبة القصوى',
  'norms.exactValue': 'القيمة المضبوطة',
  'norms.tolerance': 'هامش التسامح',
  'norms.inclusive': 'الحدود مُدرَجة',
  'norms.expression': 'العبارة',
  'norms.expressionHint': 'تُركَّب تلقائيا انطلاقا من العتبات: لا تُكتب يدويا.',
  'norms.create.title': 'معيار جديد',
  'norms.create.submit': 'إنشاء المعيار',
  'norms.created': 'تم إنشاء المعيار.',

  'normKinds.minimum': 'الحد الأدنى',
  'normKinds.maximum': 'الحد الأقصى',
  'normKinds.range': 'نطاق',
  'normKinds.exact': 'قيمة مضبوطة',
  'normKinds.qualitative': 'نوعية',

  'verdicts.compliant': 'مطابق',
  'verdicts.non_compliant': 'غير مطابق',
  'verdicts.not_measured': 'غير مُقاس',
  'verdicts.not_applicable': 'غير معني',

  'campaignNorms.title': 'المعايير المطبَّقة',
  'campaignNorms.empty': 'لا يوجد معيار مطبَّق على هذه الحملة.',
  'campaignNorms.norm': 'المعيار',
  'campaignNorms.observedValue': 'القيمة المُلاحَظة',
  'campaignNorms.appliedOn': 'تاريخ التطبيق',
  'campaignNorms.comment': 'ملاحظة',
  'campaignNorms.verdict': 'الحكم',
  'campaignNorms.apply.title': 'تطبيق معيار',
  'campaignNorms.apply.submit': 'تطبيق',
  'campaignNorms.applied': 'تم تطبيق المعيار.',
  'campaignNorms.remove': 'إزالة',
  'campaignNorms.removeTitle': 'إزالة هذا المعيار',
  'campaignNorms.removeMessage': 'لن يُطبَّق المعيار بعد الآن على هذه الحملة. القياس المسجَّل سابقا لن يظهر بعد ذلك.',
  'campaignNorms.removed': 'تمت إزالة المعيار.',

  'purchases.title': 'أوامر الشراء',
  'purchases.description': 'الطلب → الاستلام دون مغادرة الشاشة: الباقي يتحدث مع كل سطر.',
  'purchases.list.empty': 'لا يوجد طلب.',
  'purchases.search': 'بحث',
  'purchases.new': 'طلب جديد',
  'purchases.code': 'الرقم',
  'purchases.partner': 'المزوّد',
  'purchases.status': 'الحالة',
  'purchases.status.draft': 'مسودة',
  'purchases.status.approved': 'مؤكد',
  'purchases.status.received': 'مستلم',
  'purchases.status.cancelled': 'ملغى',
  'purchases.total': 'المجموع بدون ضريبة',
  'purchases.detail.title': 'بطاقة الطلب',
  'purchases.detail.back': 'رجوع',
  'purchases.lines': 'الأسطر',
  'purchases.lines.product': 'المادة',
  'purchases.lines.quantity': 'الكمية',
  'purchases.lines.unitPrice': 'سعر الوحدة',
  'purchases.lines.received': 'المستلم',
  'purchases.lines.remaining': 'الباقي',
  'purchases.lines.total': 'مجموع السطر',
  'purchases.approve': 'تأكيد الطلب',
  'purchases.approving': 'جارٍ التأكيد…',
  'purchases.approved': 'تم تأكيد الطلب.',
  'purchases.cancel': 'إلغاء الطلب',
  'purchases.cancelReason': 'سبب الإلغاء',
  'purchases.cancelled': 'تم إلغاء الطلب.',
  'purchases.receive.title': 'الاستلام',
  'purchases.receive.warehouse': 'مخزن الاستلام',
  'purchases.receive.quantityArrived': 'الكمية الواصلة',
  'purchases.receive.rejected': 'الكمية المرفوضة',
  'purchases.receive.submit': 'تسجيل الاستلام',
  'purchases.receive.receiving': 'جارٍ الاستلام…',
  'purchases.received': 'تم تسجيل الاستلام.',
  'purchases.update': 'حفظ الرأس',
  'purchases.updated': 'تم تحديث الطلب.',
  'purchases.create.title': 'طلب جديد',
  'purchases.create.submit': 'إنشاء الطلب',
  'purchases.created': 'تم إنشاء الطلب.',

  'dashboard.title': 'لوحة القيادة',
  'dashboard.description': 'مؤشرات الموسم، التكاليف حسب البند، الهوامش والمقارنة بين القطع.',
  'dashboard.filters': 'الفلاتر',
  'dashboard.season': 'الموسم',
  'dashboard.farm': 'الضيعة',
  'dashboard.level': 'المستوى',
  'dashboard.level.plot': 'القطعة',
  'dashboard.level.farm': 'الضيعة',
  'dashboard.level.crop': 'المحصول',
  'dashboard.level.season': 'الموسم',
  'dashboard.loading': 'جارٍ التحميل…',
  'dashboard.kpis.totalArea': 'المساحة الإجمالية (هكتار)',
  'dashboard.kpis.directCost': 'التكلفة المباشرة',
  'dashboard.kpis.netCost': 'التكلفة الصافية',
  'dashboard.kpis.revenue': 'الإيراد',
  'dashboard.kpis.margin': 'الهامش',
  'dashboard.kpis.marginRate': 'نسبة الهامش',
  'dashboard.kpis.avgCostPerHa': 'متوسط التكلفة/هكتار',
  'dashboard.kpis.avgMarginPerHa': 'الهامش/هكتار',
  'dashboard.kpis.yieldTotal': 'إجمالي الحصاد (قنطار)',
  'dashboard.costByPost': 'التكاليف حسب البند',
  'dashboard.costByPost.input': 'المدخلات',
  'dashboard.costByPost.machine': 'الآلات',
  'dashboard.costByPost.labor': 'اليد العاملة',
  'dashboard.costByPost.other': 'أخرى',
  'dashboard.comparison': 'المقارنة بين القطع',
  'dashboard.comparison.empty': 'لا توجد بيانات للمقارنة.',
  'dashboard.table.label': 'التسمية',
  'dashboard.table.area': 'المساحة',
  'dashboard.table.cost': 'التكلفة الصافية',
  'dashboard.table.margin': 'الهامش',
  'dashboard.table.yield': 'المردود',
  'dashboard.refresh': 'تحديث',

  'exports.title': 'الصادرات',
  'exports.description': 'تصدير كامل قابل للاسترجاع (§16.5): CSV UTF-8 + دليل، قابل لإعادة التحميل على قاعدة فارغة.',
  'exports.download': 'تنزيل تصدير ZIP',
  'exports.downloading': 'جارٍ الإنشاء…',
  'exports.downloaded': 'تم تنزيل التصدير.',
  'exports.manifest': 'معاينة الدليل',


  // --- Plan d'assolement (§7.3) ---
  'assolement.title': 'خطة الدورة الزراعية',
  'assolement.description': 'خصّص زراعة لعدة قطع دفعة واحدة.',
  'assolement.season': 'الموسم',
  'assolement.farm': 'الضيعة',
  'assolement.allFarms': 'كل الضيعات',
  'assolement.crop': 'الزراعة',
  'assolement.variety': 'الصنف',
  'assolement.unassigned': 'القطع الشاغرة في هذا الموسم',
  'assolement.empty': 'لا توجد قطعة شاغرة: كل شيء مخصّص في هذا الموسم.',
  'assolement.selectAll': 'اختيار الكل',
  'assolement.clearAll': 'إلغاء الاختيار',
  'assolement.selected': 'مختارة',
  'assolement.area': 'المساحة المزروعة (هك)',
  'assolement.areaHint': 'إذا تُرك فارغًا: تُزرع كل قطعة على مساحتها الخاصة.',
  'assolement.assign': 'تخصيص الزراعة',
  'assolement.assigning': 'جارٍ التخصيص…',
  'assolement.chooseSeason': 'اختر موسمًا لعرض القطع الشاغرة.',
  'assolement.chooseCrop': 'اختر زراعة قبل التخصيص.',
  'assolement.result': 'النتيجة',
  'assolement.result.succeeded': 'مخصّصة',
  'assolement.result.failed': 'مرفوضة',
  'assolement.result.area': 'هكتار مخصّص',
  'assolement.irrigable': 'قابلة للري',

  // --- Reprise des données (L12-04) ---
  'reprise.title': 'استئناف البيانات',
  'reprise.description': 'استرجاع، تنظيف، تحميل، مراقبة — يستعيد العميل بياناته.',
  'reprise.kind': 'النوع',
  'reprise.kind.plot': 'القطع',
  'reprise.kind.product': 'المواد',
  'reprise.kind.partner': 'الأطراف',
  'reprise.file': 'ملف CSV',
  'reprise.template': 'الأعمدة المطلوبة',
  'reprise.analyse': '١. تحليل (دون كتابة)',
  'reprise.analysing': 'جارٍ التحليل…',
  'reprise.load': '٢. تحميل الأسطر المقبولة',
  'reprise.loading': 'جارٍ التحميل…',
  'reprise.rowCount': 'الأسطر المقروءة',
  'reprise.accepted': 'الأسطر المقبولة',
  'reprise.loaded': 'الأسطر المحمّلة',
  'reprise.rejected': 'الأسطر المرفوضة',
  'reprise.duplicates': 'المكرّرة',
  'reprise.orphans': 'اليتيمة',
  'reprise.issues': 'ما تم رفضه',
  'reprise.line': 'السطر',
  'reprise.history': 'عمليات سابقة',
  'reprise.empty': 'لا توجد عملية مسجّلة.',
  'reprise.noFile': 'اختر ملفًا قبل التحليل.',
  'reprise.status.analysed': 'محلَّل',
  'reprise.status.loaded': 'محمَّل',
  'reprise.status.failed': 'فشل',

  // --- Utilisateurs et portées (§4.3) ---
  'users.title': 'المستخدمون',
  'users.description': 'الحسابات والأدوار والنطاقات. المدير وحده ينشئ الحسابات.',
  'users.create': 'إنشاء مستخدم',
  'users.email': 'البريد الإلكتروني',
  'users.fullName': 'الاسم الكامل',
  'users.role': 'الدور',
  'users.password': 'كلمة المرور',
  'users.active': 'نشط',
  'users.inactive': 'معطّل',
  'users.list': 'القائمة',
  'users.empty': 'لا يوجد مستخدم.',
  'users.created': 'تم إنشاء المستخدم.',
  'users.updated': 'تم حفظ التعديلات.',
  'users.detail': 'بطاقة المستخدم',
  'users.lastSignIn': 'آخر دخول',
  'users.never': 'أبدًا',
  'users.locale': 'اللغة',
  'users.scopes': 'النطاقات',
  'users.scopes.farms': 'الضيعات',
  'users.scopes.activities': 'الأنشطة',
  'users.scopes.all': 'لا نطاق محدّد: الوصول إلى كل ما تملكه المؤسسة.',
  'users.scopes.restricted': 'الوصول مقصور على الضيعات المحدّدة.',
  'users.scopes.save': 'حفظ النطاقات',
  'users.scopes.saved': 'تم حفظ النطاقات.',
  'users.capabilities': 'ما يسمح به هذا الدور',
  'users.role.admin': 'مدير',
  'users.role.manager': 'مسؤول ضيعة',
  'users.role.accountant': 'محاسب',
  'users.role.operator': 'عامل',
  'users.role.viewer': 'قارئ',

  // --- Profil ---
  'profile.title': 'ملفي الشخصي',
  'profile.description': 'معلوماتك ودورك وصلاحياتك.',
  'profile.organization': 'المؤسسة',
  'profile.capabilities': 'صلاحياتك',
  'profile.capabilities.hint': 'يطبّقها الخادم: المسار الممنوع يردّ ٤٠٣، لا مبالغ منقوصة.',
  'profile.granted': 'ممنوح',
  'profile.denied': 'مرفوض',
  'profile.password.title': 'تغيير كلمة المرور',
  'profile.password.current': 'كلمة المرور الحالية',
  'profile.password.new': 'كلمة المرور الجديدة',
  'profile.password.confirm': 'تأكيد كلمة المرور الجديدة',
  'profile.password.rule': 'عشرة أحرف على الأقل.',
  'profile.password.submit': 'تغيير كلمة المرور',
  'profile.password.pending': 'جارٍ التغيير…',
  'profile.password.mismatch': 'الإدخالان غير متطابقين.',
  'profile.password.other_sessions': 'لن تتمكّن جلساتك الأخرى من التمديد وسيلزمها تسجيل الدخول من جديد؛ وقد يبقى جهاز مفتوح بالفعل مفتوحًا إلى حين انتهاء جلسته الجارية. أمّا هذه الجلسة فتبقى مفتوحة.',
  'profile.password.changed': 'تم تغيير كلمة المرور. لم تعد جلساتك الأخرى قادرة على التمديد؛ وقد يبقى جهاز مفتوح بالفعل مفتوحًا إلى حين انتهاء جلسته الجارية.',
  'capability.view_costs': 'الاطلاع على التكاليف والأسعار والهوامش',
  'capability.record_work': 'تسجيل العمليات والمحاصيل وحركات المخزون',
  'capability.manage_documents': 'تسجيل واعتماد وثائق الشراء والبيع',
  'capability.manage_master_data': 'إدارة المراجع والضيعات والقطع',
  'capability.administer': 'إدارة المستخدمين والأدوار والاشتراك',

  // --- Avoirs fournisseur (L4-11) ---
  'nav.invoices': 'فواتير البيع',
  'invoices.description': 'فواتير البيع وإشعاراتها الدائنة. الإشعار يصحّح بأسطر سالبة ولا يعيد كتابة الأصل.',
  // Migration 0101 : émettre une facture client depuis ce qui est livré et pas encore facturé.
  'invoicing.title': 'إصدار فاتورة',
  'invoicing.hint': 'الفاتورة تتضمن وصولات تسليم منجزة لم تتضمنها أي فاتورة. اختر وصولات هذا الحريف التي تتضمنها؛ الأداء والمجاميع والرقم تُحدَّد عند الإصدار.',
  'invoicing.customer': 'الحريف',
  'invoicing.nothing': 'لا شيء مسلَّم لهذا الحريف لم تقع فوترته بعد.',
  'invoicing.pick': 'فوترة',
  'invoicing.deliveryDate': 'تاريخ التسليم',
  'invoicing.lines': 'المواد المسلَّمة',
  'invoicing.total': 'الباقي للفوترة دون أداء',
  'invoicing.date': 'تاريخ الفاتورة',
  'invoicing.dueDate': 'الأجل المتفق عليه',
  'invoicing.dueDate.hint': 'اختياري: بدونه تكون الفاتورة «بلا أجل».',
  'invoicing.taxRate': 'نسبة الأداء',
  'invoicing.taxRate.none': 'نسبة وصولات التسليم',
  'invoicing.notes': 'ملاحظة',
  'invoicing.submit': 'إصدار الفاتورة',
  'invoicing.created': 'تم إصدار الفاتورة:',
  'invoices.empty': 'لا توجد فاتورة بيع.',
  'invoices.lineId': 'السطر المفوتر',
  'invoices.creditHint': 'الإشعار يحدّد السطر الأصلي وسببه: هذا ما يجعل المطابقة ممكنة بعد ستة أشهر.',
  'credit.title': 'إشعار دائن للمورّد',
  'credit.reason': 'السبب',
  'credit.reason.return': 'إرجاع بضاعة',
  'credit.reason.damaged': 'بضاعة تالفة',
  'credit.reason.billing_error': 'خطأ في الفوترة',
  'credit.reason.rebate': 'تخفيض لاحق',
  'credit.warehouse': 'المستودع الذي تخرج منه البضاعة',
  'credit.warehouseHint': 'مطلوب للإرجاع أو البضاعة التالفة: هما وحدهما يخرجان من المخزون.',
  'credit.lines': 'أسطر الإشعار',
  'credit.create': 'إصدار الإشعار',
  'credit.created': 'تم إصدار الإشعار.',
  'credit.movesStock': 'خرجت البضاعة من المخزون.',
  'credit.valueOnly': 'لا حركة مخزون: القيمة وحدها تتغيّر.',
  'invoices.kind': 'النوع',
  'invoices.kind.invoice': 'فاتورة',
  'invoices.kind.credit_note': 'إشعار دائن',
  'invoices.paid': 'المدفوع',
  'matching.title': 'المطابقة بين ثلاث وثائق',
  'matching.product': 'المادة',
  'matching.ordered': 'المطلوب',
  'matching.received': 'المستلم',
  'matching.invoiced': 'المفوتر',
  'matching.variance': 'الفارق',
  'matching.empty': 'لا توجد مطابقة لهذه الفاتورة.',
  // Lot 8b : la décision qui fait d'une facture fournisseur une dette, ou un litige.
  'matching.status.pending': 'في انتظار القرار',
  'matching.status.matched': 'مطابق',
  'matching.status.variance_accepted': 'فرق مقبول',
  'matching.status.rejected': 'مرفوض',
  'matching.quantityVariance': 'فرق الكمية',
  'matching.valueVariance': 'فرق السعر',
  'matching.reason': 'السبب',
  'invoices.status.draft': 'في انتظار القرار',
  'invoices.status.approved': 'مصادق عليها',
  'invoices.status.disputed': 'محل نزاع',
  'invoices.payablesHint': 'ما هو مستحق وما يقع دفعه يوجدان في شاشة:',
  'invoices.decision.title': 'البتّ في هذه الفاتورة',
  'invoices.decision.clean': 'كل شيء مطابق للاستلام: يمكن المصادقة على الفاتورة.',
  'invoices.decision.variance': 'تم كشف فرق مع الاستلام: المصادقة عليها تستوجب سببا مكتوبا.',
  'invoices.decision.blocked': 'لا يمكن المصادقة على هذه الفاتورة، يمكن رفضها فقط:',
  'invoices.decision.dueDate': 'أجل استحقاق الفاتورة',
  'invoices.decision.dueDate.hint': 'اختياري: يمكن تحديده أو تغييره لاحقا.',
  'invoices.decision.varianceReason': 'سبب قبول الفرق',
  'invoices.decision.approve': 'المصادقة على الفاتورة',
  'invoices.decision.approved': 'تمت المصادقة على الفاتورة: أصبحت دينا.',
  'invoices.decision.rejectReason': 'سبب الرفض',
  'invoices.decision.rejectReason.hint': 'إجباري للرفض: ستصبح الفاتورة محل نزاع.',
  'invoices.decision.reject': 'رفض الفاتورة',
  'invoices.decision.rejected': 'تم رفض الفاتورة: هي محل نزاع.',
  // Migration 0100 : saisir une facture fournisseur depuis ce qui est reçu et pas encore facturé.
  'invoices.entry.title': 'إدخال فاتورة مزوّد',
  'invoices.entry.hint': 'تُدخَل الفاتورة انطلاقا مما وقع استلامه ولم يُفوتر بعد. الكميات والأسعار المقترحة هي كميات وأسعار الاستلام: صحّحها حسب فاتورة المزوّد — والفرق، إن وُجد، يُعايَن عند المصادقة.',
  'invoices.entry.supplier': 'المزوّد',
  'invoices.entry.order': 'الطلبية المستلمة',
  'invoices.entry.noOrder': 'لا شيء مستلم من هذا المزوّد لم تقع فوترته بعد.',
  'invoices.entry.product': 'المادة',
  'invoices.entry.received': 'مستلم ولم يُفوتر بعد',
  'invoices.entry.receiptPrice': 'سعر الاستلام',
  'invoices.entry.quantity': 'الكمية المفوترة',
  'invoices.entry.unitPrice': 'السعر المفوتر',
  'invoices.entry.lineHint': 'أفرغ كمية المادة التي لا تتضمنها هذه الفاتورة.',
  'invoices.entry.reference': 'رقم فاتورة المزوّد',
  'invoices.entry.date': 'تاريخ الفاتورة',
  'invoices.entry.dueDate': 'الأجل المتفق عليه',
  'invoices.entry.dueDate.hint': 'اختياري: يمكن تحديده عند المصادقة أيضا.',
  'invoices.entry.tax': 'مبلغ الأداء',
  'invoices.entry.submit': 'تسجيل الفاتورة',
  'invoices.entry.created': 'تم تسجيل الفاتورة، في انتظار القرار:',

  // --- Articles ---
  'products.title': 'المواد',
  'products.description': 'بطاقة المادة وطريقة التقييم وتاريخ الأسعار.',
  'products.search': 'البحث عن مادة',
  'products.code': 'الكود',
  'products.name': 'التسمية',
  'products.type': 'العائلة',
  'products.unit': 'الوحدة',
  'products.valuation': 'طريقة التقييم',
  'products.valuation.weighted_average': 'المتوسط المرجّح',
  'products.valuation.fifo': 'الوارد أولًا صادر أولًا',
  'products.valuation.lifo': 'LIFO — آخر داخل، أول خارج',
  'products.valuation.standard': 'التكلفة المعيارية',
  'products.valuation.last_purchase': 'آخر سعر شراء',
  'products.valuationHint': 'تُحدَّد عند الإنشاء (V20: المتوسط المرجّح افتراضيًا).',
  'products.standardCost': 'التكلفة المعيارية',
  'products.stockManaged': 'مُدار في المخزون',
  'products.lotTracked': 'متابَع بالدفعة',
  'products.trackingFromFamily': 'تتبع المخزون يأتي من العائلة المختارة.',
  'products.outOfStockFamily': 'عائلة خارج المخزون: لا تقييم ولا تتبع بالدفعات.',
  'products.create': 'إنشاء مادة',
  'products.created': 'تم إنشاء المادة.',
  'products.empty': 'لا توجد مادة.',
  'products.detail': 'بطاقة المادة',
  'products.prices': 'تاريخ الأسعار',
  'products.prices.partner': 'الطرف',
  'products.prices.price': 'سعر الوحدة',
  'products.prices.from': 'ابتداءً من',
  'products.prices.to': 'إلى غاية',
  'products.prices.add': 'إضافة سعر',
  'products.prices.empty': 'لا يوجد سعر مسجّل.',

  // --- Récolte ---
  'harvests.title': 'المحصول',
  'harvests.description': 'دفعات المحصول والوزن الخام والصافي وتقييم غير المباع.',
  'harvests.plotSeason': 'قطعة-موسم',
  'harvests.product': 'المادة المحصودة',
  'harvests.gross': 'الوزن الخام',
  'harvests.net': 'الوزن الصافي',
  'harvests.moisture': 'الرطوبة (٪)',
  'harvests.date': 'تاريخ الحصاد',
  'harvests.normalized': 'الوزن المعياري',
  'harvests.create': 'تسجيل الدفعة',
  'harvests.created': 'تم تسجيل الدفعة.',
  'harvests.list': 'الدفعات المحصودة',
  'harvests.empty': 'لا توجد دفعة محصول.',
  // Lot Q1b (migration 0086) : la récolte entre en stock.
  'harvests.stock': 'المخزون',
  'harvests.stock.warehouse': 'مخزن الإدخال',
  'harvests.stock.warehouseHint': 'اختياري: بدون مخزن يُسجَّل الوزن وينتظر المحصول إدخاله إلى المخزون. يُستعمل أيضاً لزر «إدخال إلى المخزون» في القائمة.',
  'harvests.stock.later': 'لاحقاً',
  'harvests.stock.enter': 'إدخال إلى المخزون',
  'harvests.stock.exempt': 'سابق لنظام المخزون',
  'harvests.stock.unvalued': 'غير مُقيَّم',
  'harvests.stock.warehouseRequired': 'اختر أولاً مخزن الإدخال.',
  'harvests.stock.done': 'أُدخل المحصول إلى المخزون: دفعته تنتظر مراقبة الجودة.',
  'harvests.stock.doneUnvalued': 'أُدخل المحصول بالكمية فقط: ليس للمادة تكلفة معيارية.',
  'harvest.batch.not_found': 'المحصول غير موجود.',
  'harvest.batch.already_stocked': 'هذا المحصول موجود في المخزون مسبقاً.',
  'harvest.batch.stock_exempt': 'هذا المحصول سابق لإدخال المحاصيل إلى المخزون: يُسلَّم بدون مخزون.',
  'harvest.batch.product_not_stock_managed': 'هذه المادة غير مُدارة في المخزون: لا مخزون يدخله محصولها.',
  'harvest.batch.unit_mismatch': 'الوحدة لا تُحوَّل إلى وحدة المادة.',
  'harvest.batch.stock_fields_are_final': 'هذا المحصول في المخزون: مخزنه ودفعته وقيمة إدخاله لا تُعاد كتابتها.',
  'harvest.batch.stock_exempt_is_final': 'إعفاء المحصول من المخزون لا يُمنح ولا يُسحب.',
  'harvest.batch.not_readable': 'أُدخل المحصول لكن تعذّرت إعادة قراءته.',
  'sales.delivery_note.harvest_not_in_stock': 'هذا المحصول لم يدخل المخزون بعد: أدخله قبل تسليمه.',
  'sales.delivery_note.harvest_not_released': 'دفعة هذا المحصول لم يُفرج عنها: يلزم قرار جودة قبل أي تسليم.',
  'stock.balance.not_found': 'لا يوجد مخزون من هذه المادة في هذا المخزن.',
  'stock.movement.inactive_warehouse': 'المخزن المعطَّل لا يستقبل بضاعة.',
  'stock.warehouse.not_found': 'المخزن غير موجود.',
  'harvests.valuation': 'تقييم غير المباع (V3)',
  'harvests.valuation.unsold': 'الكمية غير المباعة',
  'harvests.valuation.avgPrice': 'متوسط سعر البيع',
  'harvests.valuation.stockValue': 'قيمة المخزون',
  'harvests.valuation.load': 'احتساب',
  'harvest.valuation.no_sale_price': 'لا بيع في هذا الموسم: متوسط السعر غير موجود بعد، ولا يمكن تقييم المخزون بدونه.',
  'harvest.valuation.no_harvest': 'لا محصول مسجّل: لا يوجد مخزون للتقييم.',

  // --- Subventions (V7) ---
  'subsidies.title': 'المنح',
  'subsidies.description': 'المنح المتحصّل عليها وتوزيعها بنسبة المساحات المؤهلة.',
  'subsidies.code': 'الكود',
  'subsidies.name': 'التسمية',
  'subsidies.amount': 'المبلغ الممنوح',
  'subsidies.type': 'النوع',
  'subsidies.status': 'الحالة',
  'subsidies.allocated': 'الموزّع',
  'subsidies.remaining': 'المتبقّي للتوزيع',
  'subsidies.create': 'تسجيل منحة',
  'subsidies.created': 'تم تسجيل المنحة.',
  'subsidies.allocate': 'توزيع',
  'subsidies.allocate.basis': 'أساس التوزيع',
  'subsidies.allocate.area': 'بنسبة المساحات المؤهلة',
  'subsidies.allocate.equal': 'بالتساوي',
  'subsidies.allocate.run': 'تنفيذ التوزيع',
  'subsidies.allocate.done': 'تم حفظ التوزيع.',
  'subsidies.allocate.hint': 'إعادة التوزيع تستبدل السابق ولا تُضاف إليه.',
  'subsidies.allocations': 'الحصص الموزّعة',
  'subsidies.allocations.plot': 'القطعة',
  'subsidies.allocations.area': 'المساحة المؤهلة (هك)',
  'subsidies.allocations.amount': 'الحصة',
  'subsidies.allocations.empty': 'لا يوجد توزيع مسجّل.',
  'subsidies.empty': 'لا توجد منحة.',
  'finance.subsidy.zero_basis': 'كل المساحات المؤهلة صفر: اختر «بالتساوي» إن كان هذا المقصود.',
  'finance.subsidy.no_cost_object': 'لا توجد قطعة-موسم مؤهلة لهذه المنحة.',

  // --- Champs personnalisés ---
  'customFields.title': 'الحقول المخصّصة',
  'customFields.description': 'أضف حقولك إلى البطاقات دون مطوّر.',
  'customFields.entity': 'البطاقة',
  'customFields.key': 'المفتاح',
  'customFields.name': 'التسمية',
  'customFields.type': 'النوع',
  'customFields.required': 'إجباري',
  'customFields.options': 'الخيارات الممكنة (مفصولة بفواصل)',
  'customFields.create': 'تعريف الحقل',
  'customFields.created': 'تم تعريف الحقل.',
  'customFields.empty': 'لا حقل مخصّص على هذه البطاقة.',
  'customFields.values': 'الحقول المخصّصة',
  'customFields.save': 'حفظ',
  'customFields.saved': 'تم حفظ القيم.',
  'customFields.export': 'تصدير CSV',
  'customFields.type.string': 'نص',
  'customFields.type.integer': 'عدد صحيح',
  'customFields.type.decimal': 'عدد عشري',
  'customFields.type.boolean': 'نعم / لا',
  'customFields.type.date': 'تاريخ',
  'customFields.type.list': 'قائمة خيارات',
  'meta.custom_field.required': 'هذا الحقل إجباري.',
  'meta.custom_field.not_in_options': 'هذه القيمة ليست ضمن الخيارات المقترحة.',
  'meta.custom_field.invalid_value': 'القيمة لا تطابق النوع المنتظر.',
  'meta.custom_field.above_maximum': 'القيمة تتجاوز الحد الأقصى المسموح.',
  'meta.custom_field.below_minimum': 'القيمة أقل من الحد الأدنى المسموح.',

  // --- Cahier de plaine et alertes (§10.3) ---
  'fieldbook.title': 'دفتر الحقل',
  'fieldbook.date': 'التاريخ',
  'fieldbook.work': 'العمل',
  'fieldbook.products': 'المدخلات',
  'fieldbook.area': 'المساحة (هك)',
  'fieldbook.warnings': 'التنبيهات',
  'fieldbook.empty': 'لا عملية مسجّلة على هذه القطعة-الموسم.',
  'fieldbook.noWarning': 'لا تنبيه',
  'ops.warning.dose_exceeded': 'الجرعة أعلى من جرعة بطاقة المنتج.',
  'ops.warning.pre_harvest_interval': 'مهلة ما قبل الحصاد: الحصاد المتوقّع يقع مبكرًا جدًا.',
  'ops.warning.nitrogen_cumulative': 'الآزوت المتراكم تجاوز سقف الموسم.',
  'ops.warning.cost_anomaly': 'تكلفة أعلى بكثير من العمليات المماثلة.',

  // --- Rapports (§15.1) ---
  'reports.title': 'التقارير',
  'reports.description': 'خمسة ملفات Excel بأرقام مطابقة لأرقام الشاشات.',
  'reports.download': 'تنزيل',
  'reports.language': 'لغة التقرير',
  'reports.hint': 'العناوين مترجمة والأرقام تبقى أرقامًا: يمكن جمع العمود كما هو.',
  'report.couts-detailles': 'التكاليف المفصّلة',
  'report.stocks': 'المخزون',
  'report.achats': 'المشتريات',
  'report.ventes': 'المبيعات',
  'report.marges': 'الهوامش',

  'nav.stockJournal': 'سجل المخزون',
  'nav.stockBalances': 'أرصدة المخزون',
  'nav.seasons': 'دورة المواسم',

  'common.search': 'بحث',
  'common.searchPlaceholder': 'اكتب للتصفية…',
  'common.showInactive': 'إظهار غير النشطين',
  'common.confirm': 'تأكيد',
  'common.edit': 'تعديل',
  'common.deactivate': 'تعطيل',
  'common.activate': 'تفعيل',
  'common.inactive': 'غير نشط',

  'auth.too_many_attempts': 'محاولات كثيرة من هذا الجهاز. أعد المحاولة بعد دقيقة.',
  'dash.cost.inputs': 'المدخلات',
  'dash.cost.machinery': 'المعدات',
  'dash.cost.labor': 'اليد العاملة',
  'dash.cost.other': 'أخرى',
  'dash.costBreakdown': 'توزيع الأعباء',
  'dash.realData': 'من البيانات الحقيقية',
  'dash.byNature': 'حسب الطبيعة',
  'dash.byItem': 'حسب المهمة',
  'dash.noCharges': 'لا توجد أعباء مسجلة',
  'dash.alerts': 'التنبيهات',
  'dash.alertCount': 'تنبيه',
  'dash.noAlerts': 'لا توجد تنبيهات',
  'dash.recentOps': 'آخر التدخلات',
  'dash.netMarginPerHa': 'الهامش الصافي/هك',
  'dash.totalYield': 'المردود الجملي',
  'dash.noAggregated': 'لا توجد بيانات مجمعة لهذه الفلاتر. أنشئ تدخلات وحصادًا لرؤية الأرقام.',
  'common.noData': 'لا توجد بيانات',
  'common.noDataToShow': 'لا توجد بيانات للعرض',
  'common.comingSoon': 'قريبا',
  'common.comingSoonHint': 'هذا القسم سيتوفر قريبا.',
  'common.total': 'المجموع',
  'common.date': 'التاريخ',
  'common.work': 'العمل',
  'common.number': 'الرقم',
  'common.cost': 'التكلفة',
  'common.status': 'الحالة',
  'ops.checkAlerts': 'فحص التنبيهات §10.3',
  'ops.alertHint': 'التنبيه يُعلم دون منع الحفظ، ويبقى ظاهراً في سجل الحقل',
  'plots.titleFull': 'قطع الأرض',
  'plots.countLabel': 'قطعة',
  'plots.subtitle': 'القطعة دائمة والزراعة الموسمية هي مركز التكلفة.',
  'subscription.title': 'الاشتراك',
  'subscription.description': 'الخطة والحصص',
  'subscription.modules': 'الوحدات',
  'subscription.area': 'المساحة',
  'subscription.support': 'عند تجاوز الحد يُرفض الإنشاء. لتغيير الباقة أو إضافة خيار، يرسل مسؤول المؤسسة طلبا من هذه الصفحة.',

  'common.inTotal': 'في المجموع',
  'common.area': 'المساحة',
  'common.crop': 'المحصول',
  'common.variety': 'الصنف',
  'common.yield': 'المردود',
  'common.costPerHa': 'التكلفة/هك',
  'common.marginPerHa': 'الهامش/هك',
  'common.action': 'إجراء',
  'common.label': 'التسمية',
  'common.description': 'الوصف',
  'common.start': 'البداية',
  'common.end': 'النهاية',
  'common.filter': 'تصفية',
  'common.open': 'فتح',
  'common.toCheck': 'للتحقق',
  'common.inProgress': 'جارية',
  'common.closed': 'مقفلة',
  'common.toReview': 'للمراجعة',
  'farms.countLabel': 'ضيعة',
  'farms.subtitle': 'متابعة القطع والأنشطة حسب الضيعة.',
  'farms.new': 'ضيعة جديدة',
  'farms.list': 'قائمة الضيعات',
  'farms.delegation': 'المعتمدية',
  'farms.workshop': 'النشاط',
  'farms.phone': 'الهاتف',
  'farms.sheet': 'بطاقة',
  'farms.codeHint': 'رمز فريد بأحرف كبيرة.',
  'farms.areaBreakdown': 'توزيع المساحات',
  'farms.hectaresPerFarm': 'هكتار لكل ضيعة',
  'plots.allFarms': 'كل الضيعات',
  'plots.allCrops': 'كل المحاصيل',
  'plots.allStatuses': 'كل الحالات',
  'plots.previousCrop': 'المحصول السابق',
  'plots.cropBreakdown': 'التوزيع حسب المحصول',
  'plots.consistency': 'مراقبة التناسق',
  'plots.rule1': 'مجموع المساحات المزروعة في قطعة لا يتجاوز مساحتها العقارية.',
  'plots.rule2': 'المحصول السابق يُملأ تلقائيا من الموسم الفارط.',
  'plots.rule3': 'القطعة غير النشطة لا تُستغل — تنبيه RA-02.',
  'season.countLabel': 'مواسم',
  'season.costObjects': 'مراكز التكلفة',
  'season.checks': 'المراقبات قبل الإقفال',
  'season.selectHint': 'اختر موسما من الجدول لعرض مراقباته.',
  'season.check.operations': 'التدخلات المصادق عليها',
  'season.check.harvests': 'الحصاد المرتبط',
  'season.check.costs': 'الأعباء الموزعة',
  'season.check.stock': 'المخزون المجرود',
  'campaigns.doubleCrop': 'الزراعة المزدوجة (V5)',
  'campaigns.doubleCrop.on': 'مفعّلة — التسلسلان 1 و2 مسموحان',
  'campaigns.doubleCrop.off': 'معطّلة — محصول واحد لكل موسم (التسلسل 1)',

  'partners.counter': 'العدّاد',
  'partners.code': 'الرمز',
  'partners.name': 'الاسم',
  'partners.legalName': 'التسمية الاجتماعية',
  'partners.taxId': 'المعرّف الجبائي',
  'partners.secondaryTaxId': 'معرّف جبائي إضافي',
  'partners.category': 'الصفة',
  'partners.category.company': 'شركة',
  'partners.category.individual': 'شخص طبيعي',
  'partners.category.other': 'أخرى',
  'partners.firstName': 'الاسم الشخصي',
  'partners.lastName': 'اللقب',
  'partners.address': 'العنوان',
  'partners.city': 'المدينة',
  'partners.governorate': 'الولاية',
  'partners.postalCode': 'الرمز البريدي',
  'partners.country': 'البلد',
  'partners.phone': 'الهاتف',
  'partners.phoneSecondary': 'هاتف ثانوي',
  'partners.email': 'البريد الإلكتروني',
  'partners.website': 'الموقع الإلكتروني',
  'partners.contact': 'جهة الاتصال الرئيسية',
  'partners.bankName': 'البنك',
  'partners.bankAccount': 'الحساب البنكي',
  'partners.iban': 'الرقم الدولي للحساب',
  'partners.rib': 'الهوية البنكية',
  'partners.paymentTermsDays': 'أجل الدفع (أيام)',
  'partners.creditLimit': 'سقف الائتمان',
  'partners.notes': 'ملاحظات',
  'partners.empty': 'لا يوجد طرف مطابق.',
  'partners.searchHint': 'العدّاد أو المعرّف الجبائي أو الرمز أو التسمية أو الهاتف أو البريد.',
  'partners.counterHint': 'يُنشأ عند الإحداث على عشرة محارف، ولا يمكن تغييره.',
  'referentiels.codeSuggere': 'مقترح انطلاقًا من آخر رمز. قابل للتعديل.',
  'partners.deactivateTitle': 'تعطيل هذا الطرف',
  'partners.deactivateMessage': 'لن يظهر {nom} في قوائم الإدخال. تبقى وثائقه السابقة كما هي.',
  'customers.title': 'الحرفاء',
  'customers.description': 'الأطراف الذين تبيع لهم. يبدأ العدّاد بـ 411.',
  'customers.empty': 'لا يوجد حريف مطابق.',
  'partners.saved': 'تم حفظ الطرف.',
  'agro.partner.code_required': 'رمز الطرف إلزامي (30 محرفًا كحد أقصى).',
  'agro.partner.name_required': 'اسم الطرف إلزامي (200 محرف كحد أقصى).',
  'agro.partner.legal_name_required': 'التسمية الاجتماعية إجبارية (200 محرف كحد أقصى).',
  'agro.partner.tax_id_required': 'المعرّف الجبائي إلزامي، إلا لشخص طبيعي.',
  'agro.partner.duplicate_code': 'يوجد طرف آخر يحمل هذا الرمز.',
  'agro.partner.duplicate_tax_id': 'يوجد طرف آخر يحمل هذا المعرّف الجبائي.',
  'agro.partner.negative_payment_terms': 'لا يمكن أن يكون أجل الدفع سالبًا.',
  'agro.partner.negative_credit_limit': 'لا يمكن أن يكون سقف الائتمان سالبًا.',

  'behaviors.none': 'خارج المخزون',
  'behaviors.quantity': 'الكمية فقط',
  'behaviors.lot': 'حصة بحصة',
  'behaviors.weighted_average': 'التكلفة المتوسطة المرجحة',
  'behaviors.fifo': 'الوارد أولا يصرف أولا',
  'behaviors.lifo': 'الوارد أخيرا يصرف أولا',
  'behaviors.last_purchase': 'آخر سعر شراء',
  'behaviors.standard': 'التكلفة المعيارية',
  'behaviors.hint': 'ما تحسبه الطريقة فعليا. القائمة مغلقة.',
  'units.title': 'وحدات القياس',
  'units.description': 'الوحدات التي يُحسب بها الصنف أو يُشترى أو يُباع.',
  'units.empty': 'لا توجد وحدة مطابقة.',
  'units.symbol': 'الرمز',
  'units.dimension': 'البعد',
  'units.factorToBase': 'معامل التحويل إلى الوحدة الأساسية',
  'units.isBase': 'الوحدة الأساسية',
  'dimensions.mass': 'الكتلة',
  'dimensions.volume': 'الحجم',
  'dimensions.area': 'المساحة',
  'dimensions.length': 'الطول',
  'dimensions.count': 'العدّ',
  'dimensions.time': 'الوقت',
  'families.title': 'عائلات الأصناف',
  'families.description': 'التجميع الذي يحمل نمط تتبع المخزون ونسب الأداء الافتراضية للصنف.',
  'families.empty': 'لا توجد عائلة مطابقة.',
  'families.counter': 'العدّاد',
  'families.nature': 'النوع',
  'families.nature.input': 'مدخل',
  'families.nature.harvest': 'محصول',
  'families.nature.service': 'خدمة',
  'families.nature.asset': 'أصل',
  'families.trackingMode': 'نمط التتبع',
  'families.purchaseTax': 'أداء الشراء',
  'families.salesTax': 'أداء البيع',
  'trackingModes.title': 'أنماط تتبع المخزون',
  'trackingModes.description': 'ما يعرف المخزون فعلا القيام به بالصنف: خارج المخزون، بالكمية، أو حصة بحصة.',
  'trackingModes.empty': 'لا يوجد نمط تتبع مطابق.',
  'trackingModes.behavior': 'السلوك',
  'valuationMethods.title': 'طرق التقييم',
  'valuationMethods.description': 'الحساب المعتمد لتقييم المخزون: التكلفة المتوسطة المرجحة، الوارد أولا، الوارد أخيرا، آخر سعر شراء أو التكلفة المعيارية.',
  'valuationMethods.empty': 'لا توجد طريقة مطابقة.',
  'valuationMethods.behavior': 'السلوك',
  'references.deactivateTitle': 'تعطيل هذا العنصر',
  'references.deactivateMessage': 'لن يظهر {nom} في قوائم الإدخال. ما يستخدمه بالفعل يبقى دون تغيير.',
  'nav.families': 'العائلات',
  'nav.trackingModes': 'تتبع المخزون',
  'nav.valuationMethods': 'التقييم',
  'platform.title': 'لوحة المنصة',
  'platform.nav.clients': 'العملاء',
  'platform.forbidden': 'هذه اللوحة مخصصة لمديري المنصة.',
  'platform.legal_name_required': 'الاسم القانوني للعميل إجباري.',
  'platform.modules_required': 'اختر نشاطًا واحدًا على الأقل: عميل بلا نشاط مفتوح لا يمكنه إنجاز أي شيء.',
  'platform.module_unknown': 'هذا النشاط غير موجود في الفهرس.',
  'platform.plan_unknown': 'هذه الخطة غير موجودة أو لم تعد معروضة.',
  'platform.email_taken': 'هذا البريد الإلكتروني مستعمل من قبل حساب آخر، وهو فريد في كامل المنتج لدى جميع العملاء.',
  'platform.client_not_found': 'هذا العميل لم يعد موجودًا أو لا يمكن إدارته.',
  'platform.clients.title': 'العملاء',
  'platform.clients.description': 'المؤسسات التي تستعمل المنتج، وخطتها، وما تنجزه بها.',
  'platform.clients.list': 'قائمة العملاء',
  'platform.clients.legalName': 'الاسم القانوني',
  'platform.clients.governorate': 'الولاية',
  'platform.clients.plan': 'الخطة',
  'platform.clients.farms': 'الضيعات',
  'platform.clients.users': 'المستعملون',
  'platform.clients.status': 'الحالة',
  'platform.clients.createdAt': 'أُنشئ في',
  'platform.clients.noPlan': 'لا يوجد اشتراك',
  'platform.clients.empty': 'لا يوجد أي عميل حاليًا.',
  'platform.clients.new': 'عميل جديد',
  'platform.clients.new.description': 'المؤسسة وأول مدير لها واشتراكها تُنشأ معًا: إما الأربعة كلها أو لا شيء.',
  'platform.clients.group.company': 'المؤسسة',
  'platform.clients.group.admin': 'أول مدير لها',
  'platform.clients.group.plan': 'ما تشتريه',
  'platform.clients.email': 'البريد الإلكتروني للمؤسسة',
  'platform.clients.adminName': 'اسم المدير',
  'platform.clients.adminEmail': 'البريد الإلكتروني للمدير',
  'platform.clients.adminPassword': 'كلمة السر الأولى',
  'platform.clients.passwordWarning': 'تُسلَّم كلمة السر هذه خارج البرنامج، شفاهةً. لا توجد اليوم أي شاشة لتغييرها، ولا تُرسَل أي رسالة لإعادة تعيينها: دوّنها قبل التأكيد.',
  'platform.clients.passwordHint': 'عشرة محارف على الأقل.',
  'platform.clients.modules': 'الأنشطة المفتوحة',
  'platform.clients.modulesHint': 'نشاط واحد على الأقل. يمكن فتح نشاط آخر لاحقًا.',
  'platform.clients.created': 'تم إنشاء العميل. يمكن لمديره فتح جلسة في الحين.',
  'platform.clients.activated': 'تم تفعيل العميل: يمكن لمستعمليه فتح جلسة من جديد.',
  'platform.clients.deactivated': 'تم تعطيل العميل: لم يعد بإمكان أحد فتح جلسة لديه.',
  'platform.clients.activate.title': 'إعادة تفعيل هذا العميل؟',
  'platform.clients.activate.message': 'سيتمكن مستعملوه من فتح جلسة من جديد. لم تُفقد أي معطيات أثناء التعطيل.',
  'platform.clients.deactivate.title': 'تعطيل هذا العميل؟',
  'platform.clients.deactivate.message': 'لن يتمكن أحد من فتح جلسة لدى هذا العميل، لا مديره ولا عماله، ما دام معطَّلًا. لا تُحذف أي معطيات.',
  'platform.plan.essentiel': 'أساسي',
  'platform.plan.exploitation': 'ضيعة',
  'platform.plan.domaine': 'ضيعة كبرى',
  'platform.plan.conseil': 'مكتب استشارات',
  'platform.plan.sur_mesure': 'حسب الطلب',
  'platform.subscription.trial': 'فترة تجريبية',
  'platform.subscription.active': 'نشط',
  'platform.subscription.past_due': 'غير مخلَّص',
  'platform.subscription.suspended': 'موقوف',
  'platform.subscription.cancelled': 'ملغى',
  'platform.subscription.expired': 'منتهٍ',
  'platform.client.back': 'العودة إلى العملاء',
  'platform.client.levers.title': 'رافعتان، ولا تفعلان الشيء نفسه',
  'platform.client.levers.organization': 'تعطيل المؤسسة يغلق الباب: لم يعد بإمكان أحد فتح جلسة لدى هذا العميل، لا مديره ولا عمّاله. ولا تُحذف أي بيانات.',
  'platform.client.levers.subscription': 'إيقاف الاشتراك لا يغلق أي جلسة: العميل يتصل، ويقرأ كل بياناته، لكنه لم يعد يستطيع إنشاء أي شيء — لا ضيعة ولا قطعة. هذه رافعة عدم الخلاص: نقطع الإضافة لا الدخول.',
  'platform.client.company': 'المؤسسة',
  'platform.client.orgType': 'الصنف',
  'platform.client.taxId': 'المعرّف الجبائي',
  'platform.client.city': 'المدينة',
  'platform.client.phone': 'الهاتف',
  'platform.client.subscription': 'الاشتراك',
  'platform.client.startedOn': 'البداية',
  'platform.client.periodEnd': 'تاريخ الاستحقاق',
  'platform.client.endedOn': 'النهاية',
  'platform.client.price': 'الثمن',
  'platform.client.autoRenew': 'التجديد التلقائي',
  'platform.client.cancellationReason': 'سبب الإلغاء',
  'platform.client.noSubscription': 'هذا العميل بلا اشتراك. لا ينطبق عليه أي سقف، ولا يمكنه إنشاء أي شيء ما دام بلا اشتراك نشط.',
  'platform.client.noExpiry': 'تتقدم الاشتراكات تلقائيًا: عند نهاية التجربة أو الاستحقاق تُنتظر الدفعة؛ وبعد فترة السماح يصبح العميل للقراءة فقط. إعادة تفعيل الاشتراك تعني تسجيل الدفعة.',
  'platform.client.suspend': 'إيقاف',
  'platform.client.resume': 'إعادة التنشيط',
  'platform.client.terminate': 'إلغاء',
  'platform.client.changePlan': 'تغيير الخطة',
  'platform.client.extend': 'تمديد',
  'platform.client.months': 'عدد الأشهر',
  'platform.client.reason': 'السبب',
  'platform.client.reasonRequired': 'إجباري: قاعدة البيانات تشترطه، وهو ما سيُشرح للعميل عند اتصاله.',
  'platform.client.suspend.title': 'إيقاف هذا الاشتراك؟',
  'platform.client.suspend.message': 'سيواصل العميل فتح جلسته وقراءة كل بياناته. لن يستطيع إنشاء ضيعة ولا قطعة. ولا يُقطع الاتصال عن أحد: لإغلاق الباب يجب تعطيل المؤسسة.',
  'platform.client.resume.title': 'إعادة تنشيط هذا الاشتراك؟',
  'platform.client.resume.message': 'سيتمكّن العميل من جديد من إنشاء الضيعات والقطع، في حدود سقوف خطته.',
  'platform.client.terminate.title': 'إلغاء هذا الاشتراك؟',
  'platform.client.terminate.message': 'يُضبط تاريخ النهاية على اليوم ولا يعود العميل ينشئ شيئًا. يحتفظ ببياناته ويواصل قراءتها ما دامت المؤسسة نشطة.',
  'platform.client.changePlan.title': 'تغيير خطة هذا العميل؟',
  'platform.client.extend.title': 'تأجيل تاريخ الاستحقاق؟',
  'platform.client.extend.message': 'يتأخّر تاريخ الاستحقاق انطلاقًا من قيمته الحالية لا من اليوم: تمديد اشتراك جارٍ لا يقصّره أبدًا.',
  'platform.client.extended': 'تم تأجيل تاريخ الاستحقاق.',
  'platform.client.suspended': 'أُوقف الاشتراك: العميل ما زال يقرأ، ولم يعد ينشئ.',
  'platform.client.resumed': 'أُعيد تنشيط الاشتراك.',
  'platform.client.terminated': 'أُلغي الاشتراك.',
  'platform.client.planChanged': 'تم تغيير الخطة.',
  'platform.client.grant': 'منح اشتراك',
  'platform.client.grant.title': 'منح هذا العميل اشتراكًا؟',
  'platform.client.grant.message': 'يبدأ الاشتراك اليوم، نشطًا، لفترة واحدة من الخطة المختارة، ويتجدّد تلقائيًا. السعر والدورية والعملة هي تلك الخاصة بالخطة.',
  'platform.client.granted': 'تم منح الاشتراك.',
  'platform.client.modules': 'الأنشطة',
  'platform.client.modules.description': 'ما يحقّ للعميل استغلاله. النشاط المغلق يختفي من قائمته.',
  'platform.client.module.guards': 'إغلاق هذا النشاط يمنع فعلًا الوصول إلى شاشاته.',
  'platform.client.module.cosmetic': 'إغلاق هذا النشاط لا يغيّر اليوم إلا القائمة: فهو لا يحرس أي شاشة، لأنه غير موجود بعد في المنتج.',
  'platform.client.module.open': 'فتح',
  'platform.client.module.close': 'إغلاق',
  'platform.client.module.openedAt': 'فُتح في',
  'platform.client.module.closedAt': 'أُغلق في',
  'platform.client.module.open.title': 'فتح هذا النشاط؟',
  'platform.client.module.open.message': 'سيرى العميل هذا النشاط في قائمته وسيستعمل شاشاته، إن وُجدت.',
  'platform.client.module.close.title': 'إغلاق هذا النشاط؟',
  'platform.client.module.opened': 'فُتح النشاط.',
  'platform.client.module.closed': 'أُغلق النشاط.',
  'platform.client.module.unchanged': 'كان هذا النشاط في هذه الحالة أصلًا: لم يتغيّر شيء.',
  'platform.client.usage': 'الاستهلاك مقابل السقوف',
  'platform.client.usage.description': 'يُحتسب مباشرة على الضيعات والقطع والحسابات — لا يُقرأ من عدّاد يُحيَّن يدويًا، لأنه ينحرف.',
  'platform.client.usage.limit': 'السقف',
  'platform.client.usage.used': 'المستهلك',
  'platform.client.usage.unlimited': 'بلا حدّ',
  'platform.client.usage.noPlan': 'بلا اشتراك، لا سقف يُعرض.',
  'platform.client.usage.usersNotEnforced': 'هذا السقف غير مطبَّق: إنشاء مستعمل لا يمرّ بأي حارس. عميل محدود بثلاثة مستعملين يستطيع إنشاء ثلاثمائة. الرقم إرشادي، وليس حدًّا.',
  'platform.client.limit.farms': 'الضيعات',
  'platform.client.limit.plots': 'القطع',
  'platform.client.limit.area': 'المساحة',
  'platform.client.limit.users': 'المستعملون',
  'platform.client.downgrade.title': 'ما تسحبه هذه الخطة من هذا العميل',
  'platform.client.downgrade.intro': 'يحتفظ بكل بياناته ويواصل قراءتها. ما لن يستطيع فعله هو إنشاء بيانات جديدة تتجاوز هذه السقوف.',
  'platform.client.downgrade.has': 'لديه',
  'platform.client.downgrade.allows': 'الخطة المقصودة تسمح بـ',
  'platform.client.downgrade.none': 'هذه الخطة لا تسحب شيئًا من هذا العميل: استهلاكه يسع في كل سقوفها.',
  'platform.client.downgrade.notEnforced': 'بلا أثر فعلي: هذا السقف غير مطبَّق',
  'platform.months_invalid': 'التمديد يقتضي عدد أشهر موجبًا تمامًا.',
  'platform.no_subscription': 'هذا العميل ليس له اشتراك يُعدَّل.',
  'platform.subscription_exists': 'لهذا العميل اشتراك بالفعل: أعد تفعيله أو غيّر خطته.',
  'platform.status_unknown': 'يُضبط الاشتراك يدويًا على نشط أو موقوف أو ملغى، لا غير.',
  'platform.reason_required': 'السبب إجباري: هو ما سيُشرح للعميل.',
  'platform.last_module': 'هذا آخر نشاط مفتوح لدى هذا العميل: إغلاقه يتركه عاجزًا عن كل شيء. ألغِ الاشتراك إن كان ذلك هو المقصود.',
  'notice.region': 'إشعارات حول اشتراكك',
  'notice.renewal_reminder': 'ينتهي اشتراكك يوم',
  'notice.trial_ending': 'تنتهي فترتك التجريبية يوم',
  'notice.past_due': 'دفعة منتظرة: بدون تسديد، سيصبح حسابك للقراءة فقط يوم',
  'notice.suspended': 'اشتراكك معلّق: يمكنك الاطلاع على بياناتك دون إضافة جديدة. منذ',
  'notice.expired': 'انتهى اشتراكك يوم',
  'notice.cancelled': 'أُلغي اشتراكك يوم',
  'notice.inDays': 'بعد',
  'notice.see': 'عرض الاشتراك',
  'notice.dismiss': 'إخفاء',
  'platform.plan_unchanged': 'العميل مشترك بالفعل في هذه الباقة.',
  'platform.no_period_end': 'هذا الاشتراك بلا تاريخ استحقاق: غيّر الباقة فورًا.',
  'platform.grace_invalid': 'تتراوح فترة السماح بين 0 و90 يومًا.',
  'platform.reminders_invalid': 'عشرة تذكيرات على الأكثر، كل منها بين 1 و365 يومًا قبل الاستحقاق.',
  'subscription.notice_not_found': 'هذا الإشعار غير موجود أو سبقت قراءته.',
  'platform.client.lifecycle': 'دورة الحياة',
  'platform.client.lifecycle.none': 'لا يوجد اشتراك: لا شيء للبرمجة.',
  'platform.client.lifecycle.pastDue': 'دفعة منتظرة منذ',
  'platform.client.lifecycle.readOnlyOn': 'للقراءة فقط ابتداءً من',
  'platform.client.lifecycle.reactivateHint': 'أعد تفعيل الاشتراك فور استلام الدفعة.',
  'platform.client.lifecycle.trialUntil': 'تجربة حتى',
  'platform.client.lifecycle.nextEnd': 'الاستحقاق القادم يوم',
  'platform.client.lifecycle.willRenew': 'تجديد تلقائي، ثم دفعة منتظرة.',
  'platform.client.lifecycle.willEnd': 'سيتوقف الاشتراك في هذا التاريخ.',
  'platform.client.lifecycle.scheduled': 'تغيير الباقة عند الاستحقاق',
  'platform.client.lifecycle.switchTo': 'الانتقال إلى',
  'platform.client.lifecycle.on': 'يوم',
  'platform.client.lifecycle.cancelChange': 'إلغاء هذا التغيير',
  'platform.client.lifecycle.targetPlan': 'الباقة المستهدفة',
  'platform.client.lifecycle.choosePlan': 'اختر باقة…',
  'platform.client.lifecycle.scheduleChange': 'برمجة عند الاستحقاق',
  'platform.client.lifecycle.changeScheduled': 'تمت برمجة التغيير.',
  'platform.client.lifecycle.changeCancelled': 'أُلغي التغيير المبرمج.',
  'platform.client.lifecycle.cancellation': 'الإلغاء عند الاستحقاق',
  'platform.client.lifecycle.cancelScheduled': 'إلغاء مبرمج عند الاستحقاق. السبب:',
  'platform.client.lifecycle.withdrawCancellation': 'سحب الإلغاء',
  'platform.client.lifecycle.cancelAtEnd': 'الإلغاء عند الاستحقاق',
  'platform.client.lifecycle.cancellationScheduled': 'تمت برمجة الإلغاء.',
  'platform.client.lifecycle.cancellationWithdrawn': 'سُحب الإلغاء.',
  'platform.client.lifecycle.history': 'السجل',
  'platform.client.lifecycle.noHistory': 'لا توجد أحداث مسجلة.',
  'platform.client.lifecycle.when': 'متى',
  'platform.client.lifecycle.what': 'ماذا',
  'platform.client.lifecycle.who': 'بواسطة',
  'platform.client.lifecycle.byJob': 'آلي',
  'platform.client.lifecycle.byPlatform': 'اللوحة',
  'platform.client.lifecycle.event.created': 'إنشاء الاشتراك',
  'platform.client.lifecycle.event.status_changed': 'تغيير الحالة',
  'platform.client.lifecycle.event.plan_changed': 'تغيير الباقة',
  'platform.client.lifecycle.event.period_changed': 'تغيير تاريخ الاستحقاق',
  'platform.client.lifecycle.event.plan_change_scheduled': 'برمجة تغيير الباقة',
  'platform.client.lifecycle.event.plan_change_cancelled': 'إلغاء التغيير المبرمج',
  'platform.client.lifecycle.event.cancellation_scheduled': 'برمجة الإلغاء',
  'platform.client.lifecycle.event.cancellation_withdrawn': 'سحب الإلغاء',
  'platform.policy.title': 'الاستحقاقات والسماح والتذكيرات',
  'platform.policy.description': 'كل ست ساعات تُحدِّث مهمة دورة الحياة الاشتراكات: نهاية التجربة والاستحقاق ← دفعة منتظرة، ثم القراءة فقط بعد فترة السماح؛ وتذكيرات قبل الاستحقاق. لا تُرسَل أي رسالة بريدية: تظهر الإشعارات في تطبيق العميل.',
  'platform.policy.graceDays': 'أيام السماح',
  'platform.policy.graceHint': 'أيام الدفعة المنتظرة قبل القراءة فقط (من 0 إلى 90).',
  'platform.policy.reminderDays': 'التذكيرات (أيام قبل الاستحقاق)',
  'platform.policy.reminderHint': 'مفصولة بفواصل، مثلًا 30، 7، 1.',
  'platform.policy.saved': 'حُفظت السياسة.',
  'platform.policy.runNow': 'التنفيذ الآن',
  'platform.policy.ran': 'نُفِّذت المهمة. العمليات:',
  'feature.stock': 'المخزون والمستودعات',
  'feature.purchasing': 'المشتريات',
  'feature.sales': 'المبيعات والفوترة',
  'feature.assets': 'الأصول الثابتة',
  'feature.subsidies': 'المنح',
  'feature.audit': 'سجل التدقيق',
  'feature.mobile_sync': 'التطبيق الجوال دون اتصال',
  'saas.feature_not_included': 'هذه الخاصية غير مشمولة في باقتك. اتصل بالمسؤول لتغيير الباقة.',
  'platform.plan_code_invalid': 'يبدأ الرمز بحرف ولا يحتوي إلا على أحرف لاتينية صغيرة وأرقام وشرطات سفلية (من 2 إلى 30 حرفًا).',
  'platform.plan_code_taken': 'توجد باقة تحمل هذا الرمز، حتى وإن كانت مؤرشفة. اختر رمزًا آخر.',
  'platform.plan_name_required': 'لا تُباع باقة بلا اسم: أعطها اسمًا.',
  'platform.billing_period_invalid': 'الدورية شهرية أو سنوية.',
  'platform.plan_price_invalid': 'السعر إلزامي ولا يمكن أن يكون سالبًا.',
  'platform.plan_quota_invalid': 'السقف إما فارغ (بلا حد) أو موجب تمامًا: الصفر لا يسمح بأي شيء.',
  'platform.plan_trial_invalid': 'تتراوح مدة التجربة بين 0 و365 يومًا.',
  'platform.plan_module_count_invalid': 'لا يمكن أن يتجاوز عدد الأنشطة المشمولة عدد الأنشطة المقترحة.',
  'platform.feature_unknown': 'هذه الخاصية غير موجودة في الفهرس.',
  'platform.plan_not_found': 'هذه الباقة غير موجودة.',
  'platform.plan_stale': 'عُدِّلت الباقة في الأثناء من قِبل شخص آخر. أعد تحميل الصفحة قبل الحفظ.',
  'platform.expiry_invalid': 'لا يمكن أن ينتهي استثناء قبل اليوم.',
  'platform.nav.plans': 'الباقات',
  'platform.plans.title': 'الباقات',
  'platform.plans.description': 'ما يُباع: الأسعار والسقوف والأنشطة والخصائص. تعديل باقة لا يغيّر سعر مشترك حالي ولا حقوقه.',
  'platform.plans.list': 'فهرس الباقات',
  'platform.plans.empty': 'لا توجد باقات.',
  'platform.plans.new': 'باقة جديدة',
  'platform.plans.name': 'الباقة',
  'platform.plans.price': 'السعر',
  'platform.plans.perMonth': 'شهر',
  'platform.plans.perYear': 'سنة',
  'platform.plans.content': 'المحتوى',
  'platform.plans.subscribers': 'المشتركون',
  'platform.plans.status': 'الحالة',
  'platform.plans.state.public': 'علنية',
  'platform.plans.state.hidden': 'مخفية',
  'platform.plans.state.archived': 'مؤرشفة',
  'platform.plans.archive': 'أرشفة',
  'platform.plans.restore': 'استرجاع',
  'platform.plans.archive.title': 'أرشفة هذه الباقة؟',
  'platform.plans.archive.message': 'لن تُقترح بعد الآن عند إنشاء عميل أو تغيير الباقة. يحتفظ مشتركوها الحاليون بباقتهم وسعرهم وخصائصهم.',
  'platform.plans.archive.subscribers': 'المشتركون الحاليون:',
  'platform.plans.restore.title': 'استرجاع هذه الباقة؟',
  'platform.plans.restore.message': 'ستُقترح من جديد عند إنشاء عميل وعند تغيير الباقة.',
  'platform.plans.archived': 'أُرشفت الباقة.',
  'platform.plans.restored': 'استُرجعت الباقة.',
  'platform.plans.created': 'أُنشئت الباقة.',
  'platform.plans.saved': 'حُفظت الباقة.',
  'platform.plans.editor.description': 'سعر الباقة وسقوفها وأنشطتها وخصائصها.',
  'platform.plans.editor.grandfathered': 'يحتفظ المشتركون الحاليون بسعرهم: أي تغيير هنا لا يسري إلا على الاشتراكات الجديدة. المشتركون الحاليون:',
  'platform.plans.group.identity': 'الهوية',
  'platform.plans.group.price': 'السعر والتجربة',
  'platform.plans.group.limits': 'السقوف',
  'platform.plans.group.content': 'المحتوى',
  'platform.plans.group.visibility': 'الظهور',
  'platform.plans.code': 'الرمز',
  'platform.plans.codeHint': 'أحرف لاتينية صغيرة وأرقام وشرطات سفلية. لن يمكن تغييره لاحقًا.',
  'platform.plans.codeFrozen': 'الرمز ثابت بعد الإنشاء.',
  'platform.plans.nameFr': 'الاسم (بالفرنسية)',
  'platform.plans.nameAr': 'الاسم (بالعربية)',
  'platform.plans.nameEn': 'الاسم (بالإنجليزية)',
  'platform.plans.descriptionField': 'الوصف',
  'platform.plans.period': 'الدورية',
  'platform.plans.yearly': 'سنوية',
  'platform.plans.monthly': 'شهرية',
  'platform.plans.priceField': 'السعر لكل فترة (د.ت)',
  'platform.plans.extraFarmPrice': 'سعر كل ضيعة إضافية',
  'platform.plans.trialDays': 'أيام التجربة',
  'platform.plans.emptyMeansUnlimited': 'فارغ = بلا حد. الصفر لا يسمح بأي شيء.',
  'platform.plans.maxFarms': 'الضيعات',
  'platform.plans.maxUsers': 'المستخدمون',
  'platform.plans.maxPlots': 'القطع',
  'platform.plans.maxArea': 'المساحة (هك)',
  'platform.plans.includedModules': 'الأنشطة المشمولة',
  'platform.plans.includedAll': 'كل الأنشطة المختارة',
  'platform.plans.features': 'الخصائص',
  'platform.plans.featuresHint': 'كل خاصية تغلق فعليًا شاشات ومسارات لدى العميل الذي لا يملكها.',
  'platform.plans.public': 'علنية',
  'platform.plans.publicHint': 'إن لم تُحدَّد، تبقى الباقة قابلة للبيع من هذه اللوحة دون أن تظهر في الفهرس.',
  'platform.plans.order': 'ترتيب العرض',
  'platform.client.features': 'الخصائص',
  'platform.client.features.explain': 'ما تمنحه الباقة، والاستثناءات الممنوحة أو المسحوبة من هذا العميل، وما لديه اليوم. يعلو الاستثناء على الباقة حتى تاريخ انتهائه.',
  'platform.client.features.empty': 'لا توجد خصائص في الفهرس.',
  'platform.client.features.feature': 'الخاصية',
  'platform.client.features.plan': 'الباقة',
  'platform.client.features.included': 'مشمولة',
  'platform.client.features.notIncluded': 'غير مشمولة',
  'platform.client.features.override': 'الاستثناء',
  'platform.client.features.granted': 'ممنوحة',
  'platform.client.features.withdrawn': 'مسحوبة',
  'platform.client.features.inactive': 'خارج الفترة',
  'platform.client.features.until': 'حتى',
  'platform.client.features.today': 'اليوم',
  'platform.client.features.on': 'مفتوحة',
  'platform.client.features.off': 'مغلقة',
  'platform.client.features.grant': 'منح',
  'platform.client.features.withdraw': 'سحب',
  'platform.client.features.clear': 'العودة إلى الباقة',
  'platform.client.features.grantExplain': 'منح هذا العميل، خارج الباقة:',
  'platform.client.features.withdrawExplain': 'سحب من هذا العميل رغم أن باقته تشملها. ستُرفض عليه شاشاتها ومساراتها:',
  'platform.client.features.expiresOn': 'تاريخ الانتهاء',
  'platform.client.features.expiresHint': 'فارغ = بلا تاريخ انتهاء. اليوم الأخير محسوب.',
  'platform.client.features.saved': 'حُفظ الاستثناء.',
  'platform.client.features.cleared': 'سُحب الاستثناء: الباقة تقرر من جديد.',

  'nav.assets': 'الأصول',
  'assets.title': 'الأصول الثابتة',
  'assets.description': 'سجل الأصول والتحويلات والمخارج.',
  'assets.empty': 'لا توجد أصول.',
  'assets.code': 'الرمز',
  'assets.designation': 'البيان',
  'assets.entryValue': 'قيمة الدخول',
  'assets.status': 'الحالة',
  'assets.status.draft': 'مسودة',
  'assets.status.in_progress': 'قيد الإنجاز',
  'assets.status.in_service': 'في الخدمة',
  'assets.status.inactive': 'غير نشط',
  'assets.status.disposed': 'متنازل عنه',
  'assets.create': 'أصل جديد',
  'assets.created': 'تم إنشاء الأصل.',
  'assets.transfer': 'تحويل',
  'assets.transfer.done': 'تم تسجيل التحويل.',
  'assets.transfer.to': 'الوجهة',
  'assets.dispose': 'التنازل',
  'assets.dispose.buyer': 'المقتني',
  'assets.dispose.capitalGain': 'نتيجة التفويت',
  'assets.dispose.date': 'تاريخ التفويت',
  'assets.dispose.method': 'طريقة الإخراج',
  'assets.dispose.method.destruction': 'إتلاف',
  'assets.dispose.method.reform': 'إحالة على الإصلاح',
  'assets.dispose.method.sale': 'بيع',
  'assets.dispose.method.theft': 'سرقة',
  'assets.dispose.nbv': 'القيمة المحاسبية الصافية',
  'assets.dispose.noPrice': 'دون ثمن تفويت',
  'assets.dispose.notValued': 'تفويت غير مقيم: الأصل لم يكن له مخطط اهتلاك.',
  'assets.dispose.price': 'ثمن التفويت',
  'assets.dispose.reason': 'سبب الإخراج',
  'assets.inProgress': 'مصاريف الإنجاز',
  'assets.inProgress.add': 'إضافة المصروف',
  'assets.inProgress.amount': 'المبلغ',
  'assets.inProgress.date': 'التاريخ',
  'assets.inProgress.done': 'تم تسجيل المصروف.',
  'assets.inProgress.label': 'البيان',
  'assets.inProgress.none': 'لا يوجد أي مصروف مسجل حاليا.',
  'assets.inProgress.supplier': 'المزود',
  'assets.inProgress.total': 'المجموع، الذي سيصبح قيمة الدخول',
  'assets.lifecycle.putInProgress': 'التصريح بأنه قيد الإنجاز',
  'assets.dispose.done': 'تم تسجيل الخروج.',
  'assets.categories': 'الفئات',
  'assets.dashboard.assets': 'الأصول',
  'assets.dashboard.nbv': 'صافي القيمة',
  'assets.dashboard.hours': 'الساعات المنتجة',
  'assets.dashboard.alerts': 'التنبيهات',
  'assets.dashboard.title': 'لوحة الأصول',
  'assets.dashboard.fleet': 'الأسطول',
  'assets.dashboard.inService': 'في الخدمة',
  'assets.dashboard.gross': 'القيمة الإجمالية',
  'assets.dashboard.accumulated': 'الاهتلاك المتراكم',
  'assets.dashboard.depreciation.none': 'لا يوجد في الحظيرة أي أصل له مخطط اهتلاك: المتراكم والقيمة المحاسبية الصافية لا يحتسبان.',
  'assets.dashboard.work': 'الاستغلال',
  'assets.dashboard.maintenance': 'الصيانة',
  'assets.dashboard.fuel': 'الوقود',
  'assets.dashboard.missing': 'بدون جرد',
  'assets.dashboard.noAlerts': 'لا توجد تنبيهات.',
  'assets.meters': 'العدادات',
  'assets.meters.kind': 'نوع العداد',
  'assets.meters.hours': 'الساعات',
  'assets.meters.km': 'الكيلومترات',
  'assets.meters.value': 'قيمة القراءة',
  'assets.meters.record': 'تسجيل',
  'assets.meters.done': 'تم تسجيل القراءة.',
  'assets.meters.empty': 'لا توجد قراءات.',
  'assets.usages': 'الاستخدامات',
  'assets.usages.empty': 'لا توجد ساعات مرتبطة.',
  'assets.usages.operation': 'رقم التدخل',
  'assets.usages.line': 'رقم سطر العتاد',
  'assets.usages.attach': 'ربط',
  'assets.usages.done': 'تم ربط الساعات بالأصل.',
  'assets.depreciation': 'الاهتلاك',
  'assets.depreciation.year': 'السنة',
  'assets.depreciation.item': 'العنصر',
  'assets.depreciation.charge': 'المخصص',
  'assets.depreciation.cumulative': 'التراكمي',
  'assets.depreciation.nbv': 'صافي القيمة',
  'assets.depreciation.recompute': 'إعادة توليد الجدول',
  'assets.depreciation.recomputed': 'تم إعادة توليد الجدول على أساس القيمة الحالية.',
  'assets.depreciation.duration': 'المدة (سنوات)',
  'assets.depreciation.startHint': 'يبدأ المخطط في تاريخ دخول الخدمة، وإلا ففي تاريخ الاقتناء:',
  'assets.depreciation.startMissing': 'لم يُعبَّأ أي منهما.',
  'assets.acquisition': 'الاقتناء ودخول الخدمة',
  'assets.acquisitionDate': 'تاريخ الاقتناء',
  'assets.commissioningDate': 'تاريخ دخول الخدمة',
  'assets.acquisition.hint': 'تاريخ دخول الخدمة يفتح الاهتلاك: هو الذي يحدد سنته الأولى.',
  'assets.acquisition.save': 'حفظ التواريخ',
  'assets.acquisition.done': 'تم حفظ التواريخ.',
  'assets.depreciation.create': 'إنشاء الخطة',
  'assets.depreciation.done': 'تم إنشاء الجدول.',
  'assets.components': 'المكونات',
  'assets.components.empty': 'لا توجد مكونات.',
  'assets.components.add': 'إضافة',
  'assets.components.done': 'تمت إضافة المكون: أعد توليد الجدول.',
  'assets.revaluation.reason': 'سبب إعادة التقييم',
  'assets.revaluation.apply': 'إعادة التقييم',
  'assets.revaluation.done': 'تمت إعادة كتابة القيمة وإعادة توليد الجدول.',
  'assets.hourly': 'التكلفة بالساعة',
  'assets.hourly.unavailable': 'لا توجد ساعات منتجة بعد.',
  'assets.hourly.fuel': 'الوقود',
  'assets.fuel.theoretical': 'الاستهلاك النظري (ل/س)',
  'assets.fuel.set': 'حفظ',
  'assets.fuel.done': 'تم حفظ الاستهلاك النظري.',
  'assets.maintenance': 'الصيانة',
  'assets.maintenance.empty': 'لا توجد تدخلات.',
  'assets.maintenance.title': 'عنوان التدخل',
  'assets.maintenance.cost': 'تكلفة القطع',
  'assets.maintenance.kind': 'النوع',
  'assets.maintenance.corrective': 'علاجية',
  'assets.maintenance.preventive': 'وقائية',
  'assets.maintenance.meter': 'قراءة العداد',
  'assets.maintenance.nextMeter': 'الاستحقاق القادم (عداد)',
  'assets.maintenance.add': 'إضافة',
  'assets.maintenance.done': 'تم تسجيل التدخل.',
  'assets.tyres': 'الإطارات',
  'assets.tyres.empty': 'لا توجد إطارات.',
  'assets.tyres.removed': 'مفكك',
  'assets.tyres.add': 'تركيب إطار',
  'assets.tyres.done': 'تم تسجيل الإطار.',
  'assets.tyres.position': 'الموضع',
  'assets.insurance': 'التأمين',
  'assets.insurance.empty': 'لا توجد عقود.',
  'assets.insurance.add': 'إضافة عقد',
  'assets.insurance.done': 'تم تسجيل العقد.',
  'assets.insurance.insurer': 'شركة التأمين',
  'assets.insurance.policy': 'رقم العقد',
  'assets.insurance.start': 'البداية',
  'assets.insurance.end': 'النهاية',
  'assets.lease': 'الإيجار التمويلي',
  'assets.lease.empty': 'لا يوجد عقد.',
  'assets.lease.add': 'ربط عقد',
  'assets.lease.done': 'تم تسجيل العقد.',
  'assets.lease.lessor': 'المؤسسة المالية',
  'assets.lease.ref': 'مرجع العقد',
  'assets.lease.start': 'بداية العقد',
  'assets.lease.end': 'نهاية العقد',
  'assets.lease.assetValue': 'قيمة الأصل',
  'assets.lease.downPayment': 'الدفعة الأولى',
  'assets.lease.count': 'عدد الأقساط المتوقعة',
  'assets.lease.amount': 'مبلغ القسط',
  'assets.lease.installments.entered': 'قسطا مُدخلا، من أصل',
  'assets.lease.installments.planned': 'متوقعة في العقد.',
  'assets.lease.installments.empty': 'لا يوجد أي قسط مُدخل: لا جدول الاستحقاقات ولا تكلفة الساعة يريان هذا العقد.',
  'assets.lease.installment.new': 'إضافة قسط',
  'assets.lease.installment.dueDate': 'أجل القسط',
  'assets.lease.installment.principal': 'رأس المال',
  'assets.lease.installment.interest': 'الفوائد',
  'assets.lease.installment.vat': 'الأداء',
  'assets.lease.installment.paid.amount': 'المدفوع',
  'assets.lease.installment.balance': 'الباقي المستحق',
  'assets.lease.installment.add': 'إضافة القسط',
  'assets.lease.installment.added': 'تمت إضافة القسط.',
  'assets.lease.installment.payment': 'المبلغ المدفوع',
  'assets.lease.installment.paidOn': 'يوم الدفع',
  'assets.lease.installment.pay': 'دفع',
  'assets.lease.installment.paid': 'تم تسجيل الدفع.',
  'assets.lease.installment.settled': 'مسدَّد',
  // Lot 8d (migration 0103) : un paiement d'échéance est une pièce, qui s'annule.
  'assets.lease.payments': 'الدفوعات',
  'assets.lease.payment.date': 'دُفع في',
  'assets.lease.payment.installment': 'قسط يوم',
  'assets.lease.payment.status.posted': 'مسجَّل',
  'assets.lease.payment.status.cancelled': 'ملغى',
  'assets.lease.payment.cancel': 'إلغاء',
  'assets.lease.payment.cancelled': 'أُلغي الدفع: القسط يعود مستحقاً.',
  'assets.lease.payment.reason': 'سبب الإلغاء',
  'assets.lease.payment.reason.hint': 'إلزامي لإلغاء دفع. الدفع لا يُصحَّح: يُلغى.',
  'assets.fixedCosts': 'التكاليف الثابتة السنوية',
  'assets.fixedCosts.label': 'البيان',
  'assets.fixedCosts.amount': 'المبلغ',
  'assets.fixedCosts.add': 'إضافة',
  'assets.fixedCosts.done': 'تم تسجيل التكلفة.',
  'assets.documents': 'الملف الرقمي',
  'assets.documents.empty': 'لا توجد وثائق.',
  'assets.documents.type': 'النوع',
  'assets.documents.name': 'اسم الملف',
  'assets.documents.add': 'تصنيف',
  'assets.documents.done': 'تم تصنيف الوثيقة.',
  'assets.stocktake': 'الجرد',
  'assets.stocktake.empty': 'لا توجد محاضر.',
  'assets.stocktake.record': 'تحرير محضر',
  'assets.stocktake.done': 'تم تسجيل المحضر.',
  'assets.lifecycle': 'دورة الحياة',
  'assets.lifecycle.commission': 'وضع في الخدمة',
  'assets.lifecycle.done': 'تم تحديث الحالة.',
  'operations.fixedAsset': 'الأصل المتتبع',
  'nav.assetsDashboard': 'لوحة الأصول',
  'asset.fixed_asset.duplicate_code': 'يوجد أصل بهذا الرمز.',
  'asset.fixed_asset.not_found': 'الأصل المطلوب غير موجود.',
  'asset.fixed_asset.commissioning_before_acquisition': 'لا يمكن أن يسبق التشغيل الاقتناء.',
  'asset.transfer_on_disposed': 'الأصل متنازل عنه ولا يمكن تحويله.',
  'asset.disposal.already_disposed': 'هذا الأصل متنازل عنه.',
  'asset.disposal.reason_required': 'سبب الخروج مطلوب.',
  'asset.category.duplicate_code': 'توجد فئة بهذا الرمز.',
  'asset.meter.decrease_refused': 'العداد يعرض أكثر : هذه القراءة تراجع.',
  'asset.meter_on_disposed': 'الأصل المتنازل عنه لا يقبل قراءات.',
  'asset.depreciation.plan_exists': 'هذا الأصل لديه خطة.',
  'asset.depreciation.start_required': 'الخطة تحتاج تاريخ بداية.',
  'asset.hourly_cost.unavailable': 'لا توجد ساعات منتجة.',
  'asset.maintenance.negative_cost': 'لا يمكن أن تكون التكاليف سلبية.',
  'asset.lease.already_exists': 'هذا الأصل لديه تمويل إيجاري.',
  'asset.usage.asset_required': 'الربط يتطلب أصلا متتبعا.',
  'asset.component.duplicate_code': 'يوجد مكون بهذا الرمز.',
  'asset.document.type_required': 'نوع الوثيقة مطلوب.',
  'asset.fuel.anomaly': 'استهلاك غير طبيعي تجب مراقبته.',
  'asset.maintenance.due_meter': 'صيانة مستحقة حسب العداد.',
  'assets.referenceRate': 'التعريفة المرجعية',
  'assets.referenceRate.none': 'لا توجد تعريفة مرجعية: سطر بدون تعريفة مُدخلة سيُحتسب بصفر.',
  'assets.referenceRate.observed': 'الكلفة بالساعة المعاينة',
  'assets.referenceRate.drift': 'يُقيَّم الفارق بين هذه التعريفة والكلفة المعاينة في لوحة قيادة الأصول.',
  'assets.referenceRate.sourceLabel': 'مصدر التعريفة',
  'assets.referenceRate.source.budget': 'تقديرية',
  'assets.referenceRate.source.fiscalYear': 'مأخوذة من سنة محاسبية',
  'assets.referenceRate.year': 'السنة المحاسبية الأصلية',
  'assets.referenceRate.takenFromYear': 'تُحتسب التعريفة من جديد على السنة المختارة: لا شيء يُدخل.',
  'assets.referenceRate.rate': 'التعريفة (د.ت/ساعة)',
  'assets.referenceRate.set': 'تحديد التعريفة',
  'assets.referenceRate.done': 'تم تسجيل التعريفة المرجعية.',
  'assets.hours': 'ساعات السنة المحاسبية',
  'assets.hours.empty': 'ساعات هذه السنة غير قابلة للقراءة.',
  'assets.hours.noCapacity': 'الطاقة السنوية غير مصرح بها.',
  'assets.hours.theoretical': 'الطاقة السنوية',
  'assets.hours.unavailable': 'غير متاحة',
  'assets.hours.available': 'متاحة',
  'assets.hours.productive': 'منتجة',
  'assets.hours.utilization': 'نسبة الاستخدام',
  'assets.hours.availability': 'نسبة التوفر',
  'assets.downtimes': 'سجل التوقف',
  'assets.downtimes.empty': 'لا يوجد أي توقف في هذه السنة.',
  'assets.downtimes.add': 'تسجيل توقف',
  'assets.downtimes.done': 'تم تسجيل التوقف.',
  'assets.downtimes.from': 'البداية',
  'assets.downtimes.to': 'النهاية',
  'assets.downtimes.hours': 'الساعات',
  'assets.downtimes.reason': 'السبب',
  'assets.downtimes.notes': 'ملاحظة',
  'assets.downtimes.deduced': 'فترة مستنتجة',
  'assets.downtimes.deducedHint': 'فترة مستنتجة من دفتر الصيانة: تُحتسب النهاية على أساس أربع وعشرين ساعة لكل يوم تقويمي، ولم تُدخل يدويا.',
  'assets.downtimes.reason.breakdown': 'عطب',
  'assets.downtimes.reason.maintenance': 'صيانة',
  'assets.downtimes.reason.weather': 'سوء الأحوال الجوية',
  'assets.downtimes.reason.idle': 'توقف عن العمل',
  'assets.hourlyCost.tyres': 'الإطارات',
  'assets.hourlyCost.tyresIncomplete': 'إطار مستهلك واحد على الأقل بدون ثمن: حصة الإطارات أقل من الواقع.',
  'assets.hourlyCost.maintenancePerHour': 'الصيانة بالساعة',
  'assets.hourlyCost.maintenancePerHectare': 'الصيانة بالهكتار',
  'assets.hourlyCost.noArea': 'لا توجد مساحة مشتغل عليها: الكلفة بالهكتار لا تُحتسب.',
  'assets.dashboard.fleetHourlyCost': 'الكلفة بالساعة للحظيرة',
  'assets.dashboard.fleetHourlyCost.none': 'لا يوجد في الحظيرة أي أصل له كلفة بالساعة قابلة للاحتساب.',
  'assets.dashboard.alertsCapped': 'كل عائلة من التنبيهات تُقطع عند عشرين سطرا، ولا شيء يبيّن كم استُبعد منها.',
  'operations.referenceRate.hint': 'التعريفة المرجعية للأصل، تُطبق إذا بقي هذا الحقل فارغا:',
  'operations.referenceRate.none': 'هذا الأصل ليس له تعريفة مرجعية: حقل فارغ سيُحتسب بصفر.',
  'asset.category.code_required': 'رمز الفئة إلزامي (30 حرفا على الأكثر).',
  'asset.category.invalid_depreciation_years': 'مدة الإهتلاك الافتراضية يجب أن تكون بين سنة و100 سنة.',
  'asset.category.name_required': 'اسم الفئة إلزامي (100 حرف على الأكثر).',
  'asset.category.organization_required': 'يجب ربط فئة الأصول بمؤسسة.',
  'asset.category.parent_not_found': 'الفئة الأم غير موجودة.',
  'asset.category.parent_required': 'الفئة الأم غير صالحة.',
  'asset.component.asset_required': 'يجب أن يشير المكون إلى أصل.',
  'asset.component.code_required': 'رمز المكون إلزامي (30 حرفا على الأكثر).',
  'asset.component.designation_required': 'تسمية المكون إلزامية (150 حرفا على الأكثر).',
  'asset.component.invalid_duration': 'مدة المكون يجب أن تكون بين سنة و100 سنة.',
  'asset.component.negative_value': 'لا يمكن أن تكون قيمة المكون سلبية.',
  'asset.component.organization_required': 'يجب ربط المكون بمؤسسة.',
  'asset.depreciation.asset_required': 'يجب أن تشير الخطة إلى أصل.',
  'asset.depreciation.empty_schedule': 'يتطلب الجدول عنصرا واحدا على الأقل قابلا للإهتلاك.',
  'asset.depreciation.invalid_duration': 'المدة يجب أن تكون بين سنة و100 سنة.',
  'asset.depreciation.invalid_month': 'شهر الإهتلاك يجب أن يكون بين 1 و12.',
  'asset.depreciation.method_unsupported': 'يُحتسب حاليا الإهتلاك الخطي فقط.',
  'asset.depreciation.month_required': 'الخطة الشهرية لا تحمل إلا أسطرا شهرية: الشهر إلزامي.',
  'asset.depreciation.month_unexpected': 'الخطة السنوية لا تحمل إلا أسطرا سنوية: الشهر زائد.',
  'asset.depreciation.negative_base': 'لا يمكن أن يكون أساس الإهتلاك سلبيا.',
  'asset.depreciation.organization_required': 'يجب ربط الخطة بمؤسسة.',
  'asset.depreciation.period_unsupported': 'الإهتلاك سنوي أو شهري، لا غير.',
  'asset.depreciation.plan_not_found': 'هذا الأصل لا يحمل أي خطة إهتلاك.',
  'asset.disposal.asset_required': 'يجب أن يشير الإخراج إلى أصل.',
  'asset.disposal.before_commissioning': 'تاريخ التفويت لا يمكن أن يسبق تاريخ تشغيل الأصل.',
  'asset.disposal.future_date': 'لا يمكن تسجيل تفويت بتاريخ لاحق لليوم.',
  'asset.disposal.method_required': 'طريقة الإخراج إلزامية: بيع أو إتلاف أو سرقة أو إحالة على الإصلاح.',
  'asset.disposal.negative_net_book_value': 'القيمة المحاسبية الصافية لا يمكن أن تكون سلبية.',
  'asset.disposal.negative_price': 'لا يمكن أن يكون ثمن التفويت سلبيا.',
  'asset.disposal.organization_required': 'يجب ربط الإخراج بمؤسسة.',
  'asset.document.asset_required': 'يجب أن تشير الوثيقة إلى أصل.',
  'asset.document.file_required': 'اسم الملف إلزامي (200 حرف على الأكثر).',
  'asset.document.organization_required': 'يجب ربط الوثيقة بمؤسسة.',
  'asset.document.path_required': 'مسار الملف إلزامي.',
  'asset.downtime.asset_required': 'يجب أن يشير التوقف إلى أصل.',
  'asset.downtime.hours_exceed_period': 'ساعات التوقف هذه تتجاوز ما يمكن أن تغطيه الفترة.',
  'asset.downtime.invalid_hours': 'يدوم التوقف عددا من الساعات أكبر تماما من الصفر.',
  'asset.downtime.invalid_period': 'ينتهي التوقف بعد بدايته.',
  'asset.downtime.invalid_reason': 'السبب هو عطب أو صيانة أو سوء أحوال جوية أو توقف عن العمل.',
  'asset.downtime.organization_required': 'يجب ربط التوقف بمؤسسة.',
  'asset.downtime.overlap': 'يوجد توقف يغطي كامل هذه الفترة أو جزءا منها.',
  'asset.fixed_asset.code_required': 'رمز الأصل إلزامي (30 حرفا على الأكثر).',
  'asset.fixed_asset.designation_required': 'تسمية الأصل إلزامية (150 حرفا على الأكثر).',
  'asset.fixed_asset.disposed_locked': 'الأصل المفوت فيه لا يُعدل بعد ذلك.',
  'asset.fixed_asset.entry_value_blocks_in_progress': 'أعد قيمة الدخول إلى الصفر قبل إرجاع الأصل إلى قيد الإنجاز: سيعاد احتسابها من مصاريفه عند التشغيل.',
  'asset.fixed_asset.invalid_entry_value': 'قيمة الدخول يجب أن تكون موجبة ومعقولة.',
  'asset.fixed_asset.invalid_responsibility_center': 'مركز المسؤولية لا يتجاوز 100 حرف.',
  'asset.fixed_asset.invalid_theoretical_hours': 'الطاقة السنوية يجب أن تكون أكبر تماما من الصفر، أو غير مصرح بها.',
  'asset.fixed_asset.invalid_year': 'سنة الصنع يجب أن تكون بين 1900 و2100.',
  'asset.fixed_asset.negative_amount': 'لا يمكن أن تكون مبالغ الاقتناء سلبية.',
  'asset.fixed_asset.negative_theoretical_fuel': 'لا يمكن أن يكون الاستهلاك النظري سلبيا.',
  'asset.fixed_asset.organization_required': 'يجب ربط الأصل بمؤسسة.',
  'asset.fixed_cost.asset_required': 'يجب أن تشير الكلفة القارة إلى أصل.',
  'asset.fixed_cost.invalid_year': 'السنة يجب أن تكون بين 1900 و2100.',
  'asset.fixed_cost.label_required': 'التسمية إلزامية (100 حرف على الأكثر).',
  'asset.fixed_cost.negative_amount': 'لا يمكن أن يكون المبلغ سلبيا.',
  'asset.fixed_cost.organization_required': 'يجب ربط الكلفة القارة بمؤسسة.',
  'asset.hourly_cost.drift': 'التعريفة المطبقة تبتعد عن الكلفة المعاينة: التعريفة هي التي تجب مراجعتها.',
  'asset.hourly_cost.unrecorded': 'اشتغلت الآلة ولم تُسجل أي كلفة خلال السنة المحاسبية: الأعباء هي التي يجب إدخالها.',
  'asset.hourly_cost.invalid_year': 'السنة يجب أن تكون محصورة بين 1900 و2100.',
  'asset.hourly_cost.negative_input': 'لا يمكن أن تكون مكونات الكلفة بالساعة سلبية.',
  'asset.hours.negative': 'لا يمكن أن تكون أي من هذه الساعات سلبية.',
  'asset.in_progress.amount_below_millime': 'لا يمكن تمثيل مصروف أقل من المليم.',
  'asset.in_progress.asset_required': 'يجب أن يشير المصروف إلى أصل.',
  'asset.in_progress.commissioning_date_required': 'تشغيل تثبيت قيد الإنجاز يتطلب تحديد تاريخه.',
  'asset.in_progress.entry_value_not_editable':
    'قيمة دخول تثبيت قيد الإنجاز هي مجموع مصاريفه، ولا تُدخل يدويا.',
  'asset.in_progress.expense_after_commissioning': 'لا يمكن أن يكون المصروف لاحقا لتاريخ التشغيل.',
  'asset.in_progress.invalid_amount': 'مبلغ المصروف يجب أن يكون موجبا تماما.',
  'asset.in_progress.label_required': 'بيان المصروف إلزامي (150 حرفا على الأكثر).',
  'asset.in_progress.no_expense': 'لا يمكن تشغيل تثبيت قيد الإنجاز بدون أي مصروف.',
  'asset.in_progress.not_in_progress': 'الأصل ليس تثبيتا قيد الإنجاز.',
  'asset.in_progress.organization_required': 'يجب ربط المصروف بمؤسسة.',
  'asset.in_progress.supplier_not_found': 'مزود المصروف غير موجود.',
  'asset.insurance.asset_required': 'يجب أن يشير التأمين إلى أصل.',
  'asset.insurance.incoherent_dates': 'نهاية عقد التأمين يجب أن تلي بدايته.',
  'asset.insurance.insurer_required': 'شركة التأمين إلزامية (150 حرفا على الأكثر).',
  'asset.insurance.negative_premium': 'لا يمكن أن يكون القسط سلبيا.',
  'asset.insurance.organization_required': 'يجب ربط التأمين بمؤسسة.',
  'asset.lease.asset_required': 'يجب أن يشير الإيجار المالي إلى أصل.',
  'asset.lease.incoherent_dates': 'نهاية العقد يجب أن تلي بدايته.',
  'asset.lease.installment_not_found': 'القسط غير موجود.',
  'asset.lease.empty_installment': 'القسط يحمل مبلغا واحدا على الأقل.',
  'asset.lease.installment_outside_contract': 'أجل القسط خارج مدة العقد.',
  'asset.lease.invalid_count': 'عدد الأقساط يجب أن يكون بين 1 و600.',
  'asset.lease.invalid_payment': 'يجب أن يكون الدفع موجبا وألا يتجاوز الرصيد المستحق.',
  'asset.lease.payment_in_future': 'دفع القسط لا يؤرَّخ في المستقبل.',
  'asset.lease.payment_immutable': 'دفع القسط لا يُعاد كتابته ولا يُحذف: يُلغى، ولا يُتراجع عن ذلك.',
  'asset.lease.paid_amount_is_derived': 'ما دُفع من القسط هو مجموع دفوعاته؛ لا يُكتب يدوياً.',
  'asset.lease.payment_not_found': 'دفع القسط غير موجود.',
  'asset.lease.payment_already_cancelled': 'دفع القسط هذا ملغى من قبل.',
  'asset.lease.cancellation_reason_required': 'إلغاء دفع القسط يتطلب سبباً.',
  'asset.lease.text_too_long': 'ملاحظة دفع القسط، أو سبب إلغائه، في 300 حرف.',
  'asset.lease.invalid_terms': 'الشروط المالية غير متناسقة.',
  'asset.lease.lease_required': 'يجب أن يشير القسط إلى عقد.',
  'asset.lease.lessor_required': 'المؤجر إلزامي.',
  'asset.lease.negative_installment': 'لا يمكن أن يكون الأصل والفوائد سلبيين.',
  'asset.lease.not_found': 'عقد الإيجار المالي غير موجود.',
  'asset.lease.organization_required': 'يجب ربط الإيجار المالي بمؤسسة.',
  'asset.maintenance.asset_required': 'يجب أن تشير الصيانة إلى أصل.',
  'asset.maintenance.kind_required': 'النوع إلزامي: وقائية أو إصلاحية.',
  'asset.maintenance.negative_meter': 'لا يمكن أن تكون العدادات وساعات التوقف سلبية.',
  'asset.maintenance.organization_required': 'يجب ربط الصيانة بمؤسسة.',
  'asset.maintenance.title_required': 'العنوان إلزامي (150 حرفا على الأكثر).',
  'asset.meter.asset_required': 'يجب أن تشير القراءة إلى أصل.',
  'asset.meter.future_date': 'لا يمكن أن يكون تاريخ القراءة في المستقبل.',
  'asset.meter.kind_required': 'نوع العداد إلزامي: ساعات أو كيلومترات.',
  'asset.meter.negative_value': 'لا يمكن أن تكون قيمة العداد سلبية.',
  'asset.meter.organization_required': 'يجب ربط القراءة بمؤسسة.',
  'asset.reference_rate.excessive': 'التعريفة المرجعية تتجاوز السقف المعقول.',
  'asset.reference_rate.invalid_source': 'مصدر التعريفة المرجعية يجب أن يكون تقديريا أو مأخوذا من سنة محاسبية.',
  'asset.reference_rate.invalid_year': 'السنة المحاسبية الأصلية للتعريفة يجب أن تكون بين 1900 و2100.',
  'asset.reference_rate.negative': 'لا يمكن أن تكون التعريفة المرجعية سلبية.',
  'asset.reference_rate.rate_required': 'التعريفة المرجعية إلزامية عند تحديد مصدر.',
  'asset.reference_rate.year_required': 'التعريفة المأخوذة من سنة محاسبية يجب أن تسمي تلك السنة.',
  'asset.revaluation.asset_required': 'يجب أن تشير إعادة التقييم إلى أصل.',
  'asset.revaluation.negative_value': 'لا يمكن أن تكون القيم المعاد تقييمها سلبية.',
  'asset.revaluation.organization_required': 'يجب ربط إعادة التقييم بمؤسسة.',
  'asset.revaluation.reason_required': 'سبب إعادة التقييم إلزامي (300 حرف على الأكثر).',
  'asset.stocktake.asset_required': 'يجب أن تشير المعاينة إلى أصل.',
  'asset.stocktake.condition_required': 'الحالة المعاينة إلزامية: جيدة أو مهترئة أو متضررة أو خارج الخدمة.',
  'asset.stocktake.organization_required': 'يجب ربط المعاينة بمؤسسة.',
  'asset.transfer.asset_required': 'يجب أن يشير النقل إلى أصل.',
  'asset.transfer.destination_required': 'وجهة النقل إلزامية (200 حرف على الأكثر).',
  'asset.transfer.future_date': 'لا يمكن أن يكون تاريخ النقل في المستقبل.',
  'asset.transfer.organization_required': 'يجب ربط النقل بمؤسسة.',
  'asset.tyre.asset_required': 'يجب أن يشير الإطار إلى أصل.',
  'asset.tyre.incoherent_dates': 'لا يمكن أن يسبق النزع التركيب.',
  'asset.tyre.incoherent_meter': 'عداد النزع لا يمكن أن يسبق عداد التركيب.',
  'asset.tyre.invalid_life': 'مدة الحياة المرجعية يجب أن تكون أكبر تماما من الصفر، أو غير محددة.',
  'asset.tyre.invalid_meter_kind': 'نوع العداد هو ساعات أو كيلومترات.',
  'asset.tyre.label_too_long': 'علامة الإطار ومرجعه لا يتجاوزان 100 حرف.',
  'asset.tyre.negative_meter': 'لا يمكن أن يكون عداد التركيب سلبيا.',
  'asset.tyre.negative_price': 'لا يمكن أن يكون ثمن الإطار سلبيا.',
  'asset.tyre.not_found': 'الإطار غير موجود.',
  'asset.tyre.organization_required': 'يجب ربط الإطار بمؤسسة.',
  'asset.tyre.position_required': 'موضع الإطار إلزامي (30 حرفا على الأكثر).',
}

const en: Messages = {
  'nav.audit': 'Audit trail',
  'nav.sales': 'Sales',
  'audit.title': 'Audit trail',
  'audit.description': 'Who created, changed or deleted what. Kept for three years.',
  'audit.when': 'When',
  'audit.who': 'Author',
  'audit.what': 'Record',
  'audit.action': 'Action',
  'audit.action.insert': 'Creation',
  'audit.action.update': 'Change',
  'audit.action.delete': 'Deletion',
  'audit.changed': 'What changed',
  'audit.from': 'From',
  'audit.to': 'Until',
  'audit.entity': 'Table',
  'audit.empty': 'No write in this period.',
  'audit.window': 'Without dates, the window is the past month.',
  'audit.system': 'Technical write',
  'audit.admin_only': 'The audit trail is for administrators only.',
  'season.title': 'Seasons',
  'season.description': 'Opening, controlled closing and traced reopening (§10.5).',
  'season.open': 'Open a season',
  'season.opened': 'Season opened.',
  'season.close': 'Close',
  'season.closed': 'Season closed.',
  'season.reopen': 'Reopen',
  'season.reopened': 'Season reopened.',
  'season.reopenReason': 'Reason for reopening',
  'season.reopenHint': 'Reopening sets already-read figures moving again: the reason is kept.',
  'season.reopenings': 'Reopenings',
  'season.blockers': 'What blocks the closing',
  'season.canClose': 'Nothing in the way: the closing will go through.',
  'season.state.open': 'Open',
  'season.state.closed': 'Closed',
  'season.period': 'Period',
  'agro.season.close.pending_operations': 'Operations not yet approved',
  'agro.season.close.harvest_without_destination': 'Harvests with no destination',
  'agro.season.reopen_reason_required': 'The reason for reopening is required.',
  'agro.season.already_closed': 'This season is already closed.',
  'agro.season.not_closed': 'This season is not closed: there is nothing to reopen.',
  'agro.season.manager_only': 'Only a manager or an administrator closes a season.',
  'stock.alerts': 'Alerts',
  'stock.alerts.empty': 'No stock alert.',
  'stock.alerts.window': 'Expiry window (days)',
  'stock.alert.below_reorder_point': 'Below the reorder point',
  'stock.alert.expiring_soon': 'Expiring soon',
  'stock.alert.expired': 'Already expired',
  'stock.alert.negative_balance': 'Negative balance',
  'stock.movements': 'Movement journal',
  'stock.movements.empty': 'No movement.',
  'stock.movements.direction': 'Direction',
  'stock.movements.reason': 'Reason',
  'stock.movements.source': 'Source document',
  'stock.movements.value': 'Value',
  'stock.lots': 'Lots and expiry',
  'stock.lots.empty': 'No tracked lot.',
  'stock.lots.code': 'Lot',
  'stock.lots.expiry': 'Expiry',
  'stock.lots.remaining': 'Remaining',
  'stock.count': 'Count sheet',
  'stock.count.theoretical': 'Theoretical',
  'stock.count.counted': 'Counted',
  'stock.count.gap': 'Gap',
  'stock.count.export': 'Export the sheet',
  'ops.list': 'Operations',
  'ops.list.empty': 'No operation.',
  'ops.filter.season': 'Season',
  'ops.filter.farm': 'Farm',
  'ops.filter.from': 'From',
  'ops.filter.to': 'Until',
  'fieldbook.byPlot': 'Field book by plot',
  'fieldbook.export': 'Export to Excel',
  'contracts.title': 'Sales contracts',
  'contracts.description': 'A contract is followed to its last delivery.',
  'contracts.empty': 'No contract.',
  'contracts.customer': 'Customer',
  'contracts.crop': 'Crop',
  'contracts.quantity': 'Contracted quantity',
  'contracts.delivered': 'Delivered',
  'contracts.remaining': 'Left to deliver',
  'contracts.price': 'Agreed price',
  'contracts.dueDate': 'Due date',
  'contracts.deliveries': 'Delivery notes',
  'contracts.newDelivery': 'New delivery note',
  'contracts.deliveryHint': 'Without a quantity, the note delivers what is left.',
  'contracts.complete': 'Contract fulfilled',
  'customers.outstanding': 'Customer outstanding',
  'customers.outstanding.empty': 'No outstanding.',
  'app.name': 'AgriculteurApp',
  'app.tagline': 'Agricultural cost management',

  'nav.farms': 'Farms',
  'nav.plots': 'Plots',
  'nav.warehouses': 'Warehouses',
  'nav.campaigns': 'Campaigns',
  'nav.campaignGroups': 'Campaign groups',
  'nav.purchases': 'Purchases',
  'nav.dashboard': 'Dashboard',
  'nav.exports': 'Exports',
  'nav.products': 'Products',
  'nav.signOut': 'Sign out',
  'nav.group.reporting': 'Reporting',
  'nav.group.structure': 'Structure',
  'nav.group.structure_params': 'General settings',
  'nav.group.structure_norms': 'Standards',
  'nav.group.purchasing': 'Purchasing',
  'nav.group.sales': 'Sales',
  'nav.group.stock': 'Warehouses and stock',
  'nav.group.editions': 'Statements',
  'nav.group.admin': 'Administration',
  'nav.qualityControls': 'Quality control',
  'nav.productStandards': 'Product standards',
  'qc.title': 'Quality control',
  'qc.subtitle': 'You enter measurements; the system compares them with the product standard and releases or blocks the lot. A lot in control, blocked or rejected does not leave stock.',
  'qc.new': 'New control',
  'qc.history': 'History',
  'qc.lot': 'Lot',
  'qc.lotHint': 'Pick among lots in stock.',
  'qc.product': 'Product',
  'qc.warehouse': 'Warehouse',
  'qc.date': 'Date',
  'qc.type': 'Type',
  'qc.result': 'Result',
  'qc.decision': 'Decision',
  'qc.notes': 'Notes',
  'qc.submit': 'Save control',
  'qc.created': 'Quality control saved.',
  'qc.lotRequired': 'Choose a lot.',
  'qc.empty': 'No quality control yet.',
  'qc.guard': 'The refusal lives in the database: consuming, transferring or selling a blocked lot is rejected from any screen.',
  'qc.type.organoleptic': 'Organoleptic',
  'qc.type.physico_chemical': 'Physico-chemical',
  'qc.type.microbiological': 'Microbiological',
  'qc.type.lait': 'Milk',
  'qc.type.other': 'Other',
  // Lot Q1a (migration 0085) : le contrôle calculé et les normes de produit.
  'qc.result.compliant': 'Compliant',
  'qc.result.non_compliant': 'Non-compliant',
  'qc.result.incomplete': 'Incomplete',
  'qc.result.no_standard': 'No standard',
  'qc.result.waived': 'Derogation',
  'qc.decision.in_control': 'In control',
  'qc.decision.released': 'Released',
  'qc.decision.blocked': 'Blocked',
  'qc.decision.rejected': 'Rejected',
  'qc.decision.conditional': 'Conditional release',
  'qc.decision.downgraded': 'Downgraded',
  'qc.basis': 'Basis',
  'qc.basis.measured': 'Measured',
  'qc.basis.no_standard': 'No standard',
  'qc.basis.override': 'Override',
  'qc.basis.legacy': 'Earlier control',
  'qc.verdict': 'Verdict',
  'qc.verdict.compliant': 'Compliant',
  'qc.verdict.non_compliant': 'Non-compliant',
  'qc.verdict.not_measured': 'Not measured',
  'qc.standard': 'Standard',
  'qc.parameter': 'Parameter',
  'qc.threshold': 'Threshold',
  'qc.measured': 'Measured value',
  'qc.mandatory': 'Mandatory',
  'qc.optional': 'Optional',
  'qc.judgement.none': 'Not judged',
  'qc.reason': 'Reason',
  'qc.last': 'Last control recorded',
  'qc.sheetHint': 'Enter the measured values: the system compares them with the standard and decides.',
  'qc.invalidNumber': 'A measured value is not a number.',
  'qc.measure.submit': 'Save measurements',
  'qc.outcome.released': 'Lot released: every mandatory measurement is compliant.',
  'qc.outcome.blocked': 'Lot blocked: a mandatory measurement is non-compliant.',
  'qc.outcome.in_control': 'Control incomplete: a mandatory measurement is missing, the lot stays in control.',
  'qc.rejectedFinal': 'This lot is rejected: a rejection is final. It only leaves stock as a loss.',
  'qc.noStandard.title': 'This product has no standard in force',
  'qc.noStandard.body': 'There is nothing to measure: the decision is yours, and the record will say “no standard”, never “compliant”.',
  'qc.noStandard.forbidden': 'There is nothing to measure. Only a manager can decide what happens to this lot.',
  'qc.manual.submit': 'Save decision',
  'qc.manual.decisionRequired': 'Choose a decision.',
  'qc.override.title': 'Override',
  'qc.override.body': 'A blocked lot can be released conditionally or rejected for good. The reason is kept with your name.',
  'qc.override.reason': 'Reason',
  'qc.override.reasonRequired': 'The reason is required.',
  'qc.override.conditional': 'Conditional release',
  'qc.override.reject': 'Reject the lot',
  'stock.lots.filterQuality': 'Quality status',
  'stock.lots.filterQuality.all': 'All',
  // Lot Q2 (migration 0087) : la scission de lot et le stock par statut.
  'stock.lots.origin': 'Origin lot',
  'stock.lots.grade': 'Grade',
  'stock.split': 'Split',
  'stock.split.title': 'Split lot',
  'stock.split.submit': 'Detach these parts',
  'stock.split.hint': 'Detach one or more parts: each becomes a lot that inherits its origin lot’s status and is then judged by its own control. The origin lot keeps the remainder.',
  'stock.split.quantity': 'Part quantity',
  'stock.split.grade': 'Part grade',
  'stock.split.addPart': 'Add part',
  'stock.split.removePart': 'Remove part',
  'stock.split.reason': 'Split reason',
  'stock.split.done': 'Lot split: the parts inherit its status.',
  'stock.split.partsRequired': 'Enter at least one part, with a positive quantity.',
  'stock.split.exceeds': 'The parts exceed what the lot holds in this warehouse.',
  'stock.summary.title': 'Stock by quality status',
  'stock.summary.available': 'Available',
  'stock.summary.inControl': 'In control',
  'stock.summary.quarantine': 'Quarantine',
  'stock.summary.rejected': 'Rejected',
  'qc.basis.inherited': 'Inherited from origin lot',
  // Lot Q3a — l'achat crée son lot (migration 0088).
  'qc.basis.takeover': 'Takeover — stock older than lot tracking',
  // Lot Q4 — contrat puis livraison (migration 0090).
  'sales.delivery_note.stock_exit_required': 'This note claims to have delivered a stock-managed product and took nothing out of stock.',
  'sales.delivery_note.lot_mismatch': 'The chosen lot is not a lot of this line’s product.',
  'contracts.deliver': 'Deliver',
  'contracts.deliver.warehouse': 'Departure warehouse',
  'contracts.deliver.date': 'Delivery date',
  'contracts.deliver.carrier': 'Carrier',
  'contracts.deliver.plate': 'Vehicle plate',
  'contracts.deliver.quantity': 'Quantity delivered',
  'contracts.deliver.lot': 'Lot delivered',
  'contracts.deliver.lot.chooseWarehouse': 'Choose the warehouse first: only the lots held there can be delivered.',
  'contracts.deliver.lot.none': 'No released lot of this product in this warehouse.',
  'contracts.deliver.submit': 'Issue the delivery note',
  'contracts.deliver.close': 'Close',
  'contracts.deliver.incomplete': 'Choose a warehouse and a quantity to deliver on at least one line.',
  'contracts.deliver.done': 'Delivery note issued:',
  'contracts.deliver.done.hint': 'The goods have left the warehouse stock. The lot’s trace shows this delivery.',
  'contracts.new.title': 'New sales contract',
  'contracts.new.number': 'Contract number',
  'contracts.new.number.hint': 'Optional: left empty, the number is assigned automatically.',
  'contracts.new.product': 'Product',
  'contracts.new.addLine': 'Add a line',
  'contracts.new.submit': 'Create the contract',
  'contracts.new.incomplete': 'Choose a customer and enter at least one line: product, quantity and price.',
  'contracts.new.created': 'Contract created:',
  // Lot Q3b — la traçabilité se lit (migration 0089).
  'nav.lotTrace': 'Traceability',
  'stock.lot_trace.term_too_short': 'Enter at least 2 characters of the lot code or the supplier lot number.',
  'trace.open': 'Trace this lot',
  'trace.title': 'Lot traceability',
  'trace.description': 'A lot, both ways: where it came from, and where it went.',
  'trace.search.title': 'Find a lot',
  'trace.search.term': 'Lot code or supplier lot number',
  'trace.search.hint': 'The supplier lot number is the one a supplier quotes when recalling a batch.',
  'trace.search.submit': 'Search',
  'trace.search.none': 'No lot carries this code.',
  'trace.loading': 'Reading the trace…',
  'trace.lot': 'Lot',
  'trace.product': 'Product',
  'trace.supplierLotCode': 'Supplier lot number',
  'trace.receivedAt': 'Received on',
  'trace.expiryDate': 'Expiry',
  'trace.initialQuantity': 'Original quantity',
  'trace.grade': 'Grade',
  'trace.status': 'Quality status',
  'trace.quantity': 'Quantity',
  'trace.warehouse': 'Warehouse',
  'trace.upstream': 'Upstream — where this lot came from',
  'trace.downstream': 'Downstream — where this lot went',
  'trace.origin': 'Origin',
  'trace.origin.purchase': 'Purchase',
  'trace.origin.harvest': 'Harvest',
  'trace.origin.takeover': 'Takeover',
  'trace.origin.direct': 'Entry without order',
  'trace.origin.split': 'Part of a lot',
  'trace.origin.unknown': 'Unknown origin',
  'trace.origin.unknown.hint': 'Nothing records where this lot came from: no receipt, no harvest, no origin lot.',
  'trace.origin.ofAncestor': 'Origin of the lot this one was detached from:',
  'trace.origin.takeover.hint': 'Stock older than lot tracking: its origin is not known.',
  'trace.origin.direct.hint': 'Entered stock without a purchase order: no supplier is attached.',
  'trace.supplier': 'Supplier',
  'trace.receipt': 'Receipt',
  'trace.receiptDate': 'Receipt date',
  'trace.supplierDocument': 'Supplier delivery note',
  'trace.order': 'Order',
  'trace.plot': 'Plot',
  'trace.plots': 'Plots',
  'trace.season': 'Season',
  'trace.crop': 'Crop',
  'trace.harvestDate': 'Harvest date',
  'trace.takeoverDate': 'Taken over on',
  'trace.directEntryDate': 'Entered on',
  'trace.ancestors': 'Origin lots',
  'trace.controls': 'Quality controls',
  'trace.controlDate': 'Control date',
  'trace.decision': 'Decision',
  'trace.controls.none': 'No control: this lot awaits its quality decision.',
  'trace.fieldInputs': 'Inputs applied to the plot',
  'trace.fieldInputs.hint': 'What was applied to the plot before the harvest, cancelled operations excluded. The link goes through the plot; the quantity is the operation’s.',
  'trace.fieldInputs.noLot': 'No lot',
  'trace.fieldInputs.none': 'No input recorded on this plot before the harvest.',
  'trace.operation': 'Operation',
  'trace.operationDate': 'Operation date',
  'trace.operationState': 'State',
  'trace.operation.cancelled': 'Operation cancelled',
  'trace.parts': 'Detached parts',
  'trace.balances': 'Where it is',
  'trace.balances.none': 'None of it remains in any warehouse.',
  'trace.consumptions': 'Operations that consumed it',
  'trace.consumptions.none': 'No operation consumed it.',
  'trace.harvests': 'Harvests of the treated plots',
  'trace.harvests.hint': 'Harvests of plots where this lot was applied before them. The link goes through the plot, not the material.',
  'trace.harvests.notStocked': 'Not in stock yet',
  'trace.harvests.none': 'No harvest on a plot where this lot was applied.',
  'trace.deliveries': 'Deliveries',
  'trace.deliveryDate': 'Delivery date',
  'trace.deliveryNote': 'Delivery note',
  'trace.customer': 'Customer',
  'trace.deliveries.none': 'No delivery.',
  'trace.movements': 'All its movements',
  'trace.movementDate': 'Date',
  'trace.reason': 'Kind',
  'trace.direction': 'Direction',
  'trace.direction.in': 'In',
  'trace.direction.out': 'Out',
  'trace.movements.none': 'No movement allocated to this lot.',
  'trace.reason.reception': 'Receipt',
  'trace.reason.consumption': 'Consumption',
  'trace.reason.sale': 'Sale',
  'trace.reason.loss': 'Loss',
  'trace.reason.return_out': 'Return to supplier',
  'trace.reason.transfer_in': 'Transfer received',
  'trace.reason.transfer_out': 'Transfer sent',
  'trace.reason.adjustment_in': 'Upward adjustment',
  'trace.reason.adjustment_out': 'Downward adjustment',
  'trace.reason.production_in': 'Harvest entry',
  'trace.reason.split_in': 'Part received from a split',
  'trace.reason.split_out': 'Detached by a split',
  'stock.lot.required': 'This product is lot-tracked: choose the lot.',
  'stock.balance.lot_required': 'This product is lot-tracked: its stock is held lot by lot.',
  'stock.movement.lot_ventilation_required': 'This product is lot-tracked: its movement must name its lots, in full.',
  'stock.lot.already_decided': 'This lot has already been judged: new goods get their own lot, which will be controlled.',
  'stock.lot.supplier_code_too_long': 'The supplier lot number is at most 50 characters.',
  'stock.quality_control.takeover_is_automatic': 'The release of a takeover lot is set by the system, once; it cannot be written.',
  'operations.consumption.movement_not_recorded': 'The stock issue did not return its movement: the input was not attached and nothing was consumed.',
  'purchase.landed_cost.no_balance': 'No stock balance to carry these landed costs.',
  'purchases.receive.lot': 'Lot received',
  'purchases.receive.supplierLotCode': 'Supplier lot number',
  'purchases.receive.expiryDate': 'Lot expiry date',
  'purchases.receive.lotsCreated': 'Lots created, in control:',
  'purchases.receive.lotsCreated.hint': 'They can be consumed once released by a quality control.',
  'warehouses.transferLot': 'Lot',
  'warehouses.transferLot.held': 'Lots in control, blocked or rejected are not offered: they await a quality decision.',
  'stock.lot_split.parts_required': 'A split detaches at least one part.',
  'stock.lot_split.invalid_quantity': 'A part’s quantity must be strictly positive.',
  'stock.lot_split.text_too_long': 'The reason fits in 300 characters, a grade in 30.',
  'stock.lot_split.not_neutral': 'A split creates neither quantity nor value: its entries must equal its exit.',
  'stock.lot.parent_is_final': 'A lot’s origin lot is set at birth and never changes.',
  'stock.lot.parent_mismatch': 'A part is of the same product as its origin lot, and comes from a split of that lot.',
  'stock.quality_control.inherited_is_automatic': 'Status inheritance is set when a part is born; it cannot be written.',
  'ps.title': 'Product standards',
  'ps.subtitle': 'For each lot-tracked product: what to measure at reception, and the thresholds. An active standard judges lots; to revise it, create a version.',
  'ps.new': 'New product standard',
  'ps.edit': 'Edit draft',
  'ps.product': 'Product',
  'ps.productHint': 'Lot-tracked products only.',
  'ps.code': 'Code',
  'ps.name': 'Name',
  'ps.version': 'Version',
  'ps.effectiveFrom': 'Effective from',
  'ps.reference': 'Reference',
  'ps.status.draft': 'Draft',
  'ps.status.active': 'Active',
  'ps.status.retired': 'Retired',
  'ps.parameters': 'Parameters',
  'ps.mandatoryCount': 'Mandatory',
  'ps.norm': 'Comparison',
  'ps.controlType': 'Control type',
  'ps.addParameter': 'Add parameter',
  'ps.remove': 'Remove',
  'ps.save': 'Save draft',
  'ps.saved': 'Product standard saved.',
  'ps.activate': 'Activate',
  'ps.activated': 'Standard activated: it now judges this product’s lots.',
  'ps.retire': 'Retire standard',
  'ps.retiredDone': 'Standard retired.',
  'ps.empty': 'No product standard yet.',
  'ps.noParameters': 'No parameter: add at least one mandatory parameter before activating.',
  'ps.lockedHint': 'This standard is no longer a draft and cannot be edited. To revise its thresholds, create a version.',
  'ps.required': 'Product, code, name, version and effective date are required.',
  'stock.lots.quality': 'Quality',
  'farms.codeGenerated': 'Assigned on creation (EXP01, EXP02…).',
  'farms.locality': 'Locality',
  'farms.gps': 'GPS position',
  'products.lotTrackedHint': 'Required for FIFO.',
  'stock.lot.blocked_by_quality': 'Lot blocked by quality: a decision is required before it can leave.',
  'stock.lot.insufficient_usable_quantity': 'Insufficient usable stock.',
  'stock.lot.not_found': 'Lot not found.',
  'stock.lot.duplicate_code': 'This lot code already exists for this product.',
  'stock.quality_control.invalid_decision': 'Unknown decision.',
  'stock.quality_control.invalid_sample': 'Sample quantity must be positive.',
  // Codes frappés par le lot Q1a, côté gestionnaires comme côté déclencheurs.
  'stock.quality_control.not_found': 'Quality control not found.',
  'stock.quality_control.not_readable': 'The control was written but cannot be read back.',
  'stock.quality_control.references_required': 'A quality control names a company, a lot and a product.',
  'stock.quality_control.standard_in_force': 'This product has a standard in force: its lot is judged by measurements, not by a decision.',
  'stock.quality_control.no_standard_in_force': 'This product has no standard in force on that date: there is nothing to measure.',
  'stock.quality_control.unknown_parameter': 'A measured parameter does not belong to the standard in force.',
  'stock.quality_control.duplicate_parameter': 'The same parameter is measured twice in this control.',
  'stock.quality_control.invalid_verdict': 'Unknown judgement.',
  'stock.quality_control.comment_too_long': 'A measurement comment exceeds 300 characters.',
  'stock.quality_control.override_reason_required': 'An override needs a reason.',
  'stock.quality_control.override_reason_too_long': 'The reason exceeds 500 characters.',
  'stock.quality_control.lot_not_blocked': 'A conditional release applies to a blocked lot; a rejection, to a blocked lot or one in control.',
  'stock.quality_control.invalid_basis': 'A control is measured, without standard, or an override.',
  'stock.quality_control.not_measurable': 'Only a measured control carries measurements.',
  'stock.lot.rejected_is_final': 'This lot is rejected, and a rejection is final.',
  'stock.lot.status_is_decided': 'A lot’s quality status cannot be edited directly: it follows a control’s decision.',
  'agro.norm.not_found': 'Norm not found.',
  'agro.product_standard.not_found': 'Product standard not found.',
  'agro.product_standard.not_readable': 'The product standard was written but cannot be read back.',
  'agro.product_standard.duplicate_version': 'This standard already has that version.',
  'agro.product_standard.no_mandatory_parameter': 'A standard can only be activated with at least one mandatory parameter.',
  'agro.product_standard.product_not_lot_tracked': 'Only a lot-tracked product can have a product standard.',
  'agro.product_standard.product_not_found': 'Product not found.',
  'agro.product_standard.not_draft': 'Only a draft standard can be edited; to revise, create a version.',
  'agro.product_standard.invalid_transition': 'A standard goes from draft to active, then to retired.',
  'agro.product_standard.unknown_norm': 'The norm referenced by this parameter was not found.',
  'agro.product_standard.code_required': 'The code is required.',
  'agro.product_standard.name_required': 'The name is required.',
  'agro.product_standard.version_required': 'The version is required.',
  'agro.product_standard.field_too_long': 'Code: 30 characters at most; name: 150; version: 20; reference: 150.',
  'agro.product_standard.invalid_control_type': 'Unknown control type.',
  'agro.product_standard.duplicate_parameter': 'The same comparison appears twice among the parameters.',
  'agro.product_standard.invalid_status': 'Unknown status.',
  'agro.product.fifo_requires_lots': 'FIFO requires lot tracking.',
  'agro.product.lot_requires_stock': 'Lot tracking requires a stock-managed product.',
  'farm.farm.code_generation_failed': 'No free farm code: enter one.',
  'nav.comparatifs': 'Comparatives',
  'nav.alertes': 'Alerts',
  'nav.partners': 'Customers',
  'nav.units': 'Units',
  'nav.normes': 'Standards',
  'nav.normCategories': 'Norm categories',
  'nav.besoins': 'Needs',
  'nav.devis': 'Quotes',
  'nav.bonCommande': 'Purchase order',
  'nav.bonLivraison': 'Delivery note',
  'nav.avoirs': 'Credit notes',
  'nav.offrePrix': 'Price offer',
  'nav.vergers': 'Orchards',
  'nav.cheptel': 'Livestock',
  'nav.supplierInvoices': 'Supplier invoices',
  'nav.subscription': 'Subscription',
  'topbar.allActivities': 'All activities',
  'topbar.season': 'Season',
  'common.theme': 'Theme',
  'theme.light': 'Switch to the light theme',
  'theme.dark': 'Switch to the dark theme',

  'module.crop': 'Field crops',
  'module.orchard': 'Orchards',
  'module.livestock': 'Livestock',

  'login.title': 'Sign in',
  'login.email': 'Email address',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.pending': 'Signing in…',
  'login.tagline': 'Technical and economic management of farm holdings',
  'login.remember': 'Stay signed in',
  'login.forgot': 'Forgot your password?',
  'login.welcome': 'Welcome back',
  'login.subtitle': 'Sign in to your company’s workspace.',
  'login.notice': 'Access is limited to authorized users. Attempts may be recorded for security purposes.',
  'login.hero.title': 'Every dinar of the season, traced to its plot.',
  'login.hero.point1': 'Cost per hectare and margins per season',
  'login.hero.point2': 'Stock, purchases and sales tied to field operations',
  'login.hero.point3': 'Offline entry on mobile, in the field',
  'login.hero.activities': 'Field crops · Orchards · Livestock',
  'login.showPassword': 'Show password',
  'login.hidePassword': 'Hide password',
  'login.capsLock': 'Caps Lock is on.',
  'login.rememberHint': 'Unchecked, the session ends with the tab — for a shared computer.',
  'mdpo.title': 'Forgot your password?',
  'mdpo.subtitle': 'Enter your address: if it matches an account, you will receive a link to choose a new password.',
  'mdpo.email': 'Email',
  'mdpo.submit': 'Send the link',
  'mdpo.pending': 'Sending…',
  'mdpo.sent': 'If this address matches an account, an email has just been sent. Remember to check your spam folder.',
  'mdpo.back': 'Back to sign in',
  'mdpn.title': 'New password',
  'mdpn.subtitle': 'Choose the new password for your account.',
  'mdpn.password': 'New password',
  'mdpn.confirm': 'Confirm password',
  'mdpn.submit': 'Change the password',
  'mdpn.pending': 'Changing…',
  'mdpn.mismatch': 'The two passwords do not match.',
  'mdpn.missingToken': 'This link is incomplete: it does not contain the reset token. Open the link exactly as it appears in the email, or request a new one.',
  'mdpn.requestNew': 'Request a new link',
  'mdpn.done': 'Your password has been changed. You can now sign in.',
  'mdpn.toLogin': 'Sign in',

  'farms.title': 'Farms',
  'farms.search': 'Search a farm',
  'farms.code': 'Code',
  'farms.name': 'Name',
  'farms.governorate': 'Governorate',
  'farms.manager': 'Manager',
  'farms.area': 'Area (ha)',
  'farms.empty': 'No farm recorded yet.',

  'plots.title': 'Plots',
  'plots.search': 'Search a plot',
  'plots.code': 'Code',
  'plots.name': 'Name',
  'plots.area': 'Area (ha)',
  'plots.irrigable': 'Irrigable',
  'plots.empty': 'No plot recorded yet.',
  'plots.moduleRequired.title': 'Field crops module not subscribed.',
  'plots.moduleRequired.description': 'Your subscription does not include the Field crops module. Contact your administrator to access it.',
  'farms.quotaExceeded': 'Your plan allows a limited number of farms. Upgrade your plan to add another one.',
  'plots.quotaExceeded': 'Your plan allows a limited number of plots or a maximum area. Upgrade your plan to continue.',

  'orchard.plots.title': 'Orchards',
  'orchard.plots.search': 'Search an orchard plot',
  'orchard.plots.empty': 'No orchard plot recorded yet.',
  'orchard.plots.farm': 'Farm',
  'orchard.plots.parcel': 'Land plot',
  'orchard.plots.species': 'Species',
  'orchard.plots.variety': 'Variety',
  'orchard.plots.plantingYear': 'Planting year',
  'orchard.plots.density': 'Density (trees/ha)',
  'orchard.plots.status': 'Status',
  'orchard.plots.status.young': 'Young',
  'orchard.plots.status.production': 'In production',
  'orchard.plots.status.decline': 'Declining',
  'orchard.plots.status.removed': 'Removed',
  'orchard.interventions.status.planned': 'Planned',
  'orchard.interventions.status.validated': 'Validated',
  'orchard.interventions.status.reversed': 'Reversed',
  'orchard.interventions.status.cancelled': 'Cancelled',
  'orchard.plots.create': 'Register the orchard plot',
  'orchard.plots.plantationCost': 'Planting cost',
  'orchard.plots.usefulLife': 'Productive life (years)',
  'orchard.plot.incomplete_amortization': 'The planting cost and its amortization period are declared together.',
  'orchard.plot.invalid_plantation_cost': 'The planting cost cannot be negative.',
  'orchard.plot.invalid_useful_life': 'The productive life is between 1 and 100 years.',
  'orchard.plots.created': 'Orchard plot registered.',
  'orchard.plots.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.plots.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to access it.',
  'orchard.plot.duplicate_plot': 'This land plot already carries an orchard.',
  'orchard.plot.planting_year_in_future': 'The planting year cannot be in the future.',
  'orchard.plot.invalid_density': 'Density must be strictly positive, in trees per hectare.',
  'orchard.plot.plot_out_of_scope': 'The designated land plot does not belong to this farm.',

  'orchard.interventions.title': 'Orchard interventions',
  'orchard.interventions.search': 'Search an intervention',
  'orchard.interventions.empty': 'No intervention recorded yet.',
  'orchard.interventions.plot': 'Orchard plot',
  'orchard.interventions.type': 'Work type',
  'orchard.interventions.date': 'Intervention date',
  'orchard.interventions.product': 'Product',
  'orchard.interventions.quantity': 'Issued quantity',
  'orchard.interventions.create': 'Record the intervention',
  'orchard.interventions.created': 'Intervention recorded.',
  'orchard.interventions.validate': 'Validate',
  'orchard.interventions.validated': 'Intervention validated: it is now locked.',
  'orchard.interventions.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.interventions.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to access it.',
  'orchard.intervention.warehouse_required': 'Issuing stock requires a warehouse: designate where the product comes from.',
  'orchard.intervention.invalid_quantity': 'The issued quantity must be strictly positive.',
  'orchard.intervention.movement_required': 'The intervention consumes a product: its stock issue is mandatory.',
  'orchard.intervention.dar_required': 'The product has no pre-harvest interval on file: a phytosanitary treatment without a PHI is refused.',
  'orchard.intervention.plot_out_of_scope': 'The designated orchard plot does not belong to this farm.',
  'orchard.intervention.not_found': 'Intervention not found.',
  'orchard.harvest_pass.not_found': 'Harvest pass not found.',
  'orchard.harvest_pass.window_closed': 'This harvest falls outside the region’s harvest window.',
  'orchard.intervention.farm_mismatch': 'The intervention and the orchard do not belong to the same farm.',
  'orchard.intervention.farm_required': 'The intervention must belong to a farm.',
  'orchard.intervention.invalid_area': 'The worked area must be strictly positive, in hectares.',
  'orchard.intervention.invalid_dose': 'The dose per hectare cannot be negative.',
  'orchard.intervention.invalid_labor_cost': 'The labour cost cannot be negative.',
  'orchard.intervention.invalid_labor_hours': 'Labour hours cannot be negative.',
  'orchard.intervention.invalid_reversal': 'Only a validated intervention can be reversed.',
  'orchard.intervention.invalid_status_transition': 'This status change is not allowed for this intervention.',
  'orchard.intervention.movement_mismatch': 'The stock issue does not match the intervention’s product.',
  'orchard.intervention.movement_not_recorded': 'The stock issue was not recorded: the intervention cannot be linked to it.',
  'orchard.intervention.movement_out_of_scope': 'This stock issue does not belong to your company.',
  'orchard.intervention.organization_required': 'The intervention must belong to a company.',
  'orchard.intervention.plot_required': 'The intervention must name an orchard.',
  'orchard.intervention.product_out_of_scope': 'This product does not belong to your company, or was removed.',
  'orchard.intervention.product_required': 'The selected product is not valid.',
  'orchard.intervention.reversal_out_of_scope': 'The intervention to reverse does not belong to your company.',
  'orchard.intervention.season_out_of_scope': 'This season does not belong to your company, or was removed.',
  'orchard.intervention.type_out_of_scope': 'This work type is neither in the shared catalogue nor in your company.',
  'orchard.intervention.type_required': 'The intervention must name a work type.',
  'orchard.irrigation.not_found': 'Irrigation not found.',
  'orchard.phenology.farm_mismatch': 'The observation and the orchard do not belong to the same farm.',
  'orchard.phenology.not_found': 'Phenology observation not found.',
  'orchard.plot.crop_not_orchard': 'This species is not a fruit-tree crop.',
  'orchard.plot.crop_out_of_scope': 'This species is neither in the shared catalogue nor in your company.',
  'orchard.plot.crop_required': 'The orchard must name a fruit-tree species.',
  'orchard.plot.farm_mismatch': 'The orchard and the land plot do not belong to the same farm.',
  'orchard.plot.farm_required': 'The orchard must belong to a farm.',
  'orchard.plot.invalid_certification': 'The certification is conventional, organic or in conversion.',
  'orchard.plot.invalid_planting_month': 'The planting month is between 1 and 12.',
  'orchard.plot.invalid_planting_year': 'The planting year must be between 1900 and 2100.',
  'orchard.plot.invalid_status': 'The orchard status is unknown.',
  'orchard.plot.invalid_status_transition': 'An orchard cannot go back in its life cycle.',
  'orchard.plot.irrigation_required': 'The irrigation mode is required: for dry farming, choose “rain-fed”.',
  'orchard.plot.not_found': 'Orchard not found.',
  'orchard.plot.organization_required': 'The orchard must belong to a company.',
  'orchard.plot.plot_required': 'The orchard must belong to a land plot.',
  'orchard.plot.variety_crop_mismatch': 'This variety does not belong to the orchard’s species.',
  'orchard.plot.variety_not_orchard': 'This variety is not a fruit-tree variety.',
  'orchard.plot.variety_out_of_scope': 'This variety is neither in the shared catalogue nor in your company.',
  'orchard.plot.variety_required': 'The selected variety is not valid.',
  'orchard.transformation.not_found': 'Transformation not found.',
  'orchard.intervention.immutable': 'A validated intervention is immutable: correct it with a counter-entry.',

  'orchard.harvest.title': 'Orchard harvest',
  'orchard.harvest.search': 'Search a harvest pass',
  'orchard.harvest.empty': 'No harvest pass recorded yet.',
  'orchard.harvest.plot': 'Orchard plot',
  'orchard.harvest.season': 'Season',
  'orchard.harvest.passNumber': 'Pass number',
  'orchard.harvest.date': 'Harvest date',
  'orchard.harvest.gross': 'Gross weight (kg)',
  'orchard.harvest.tare': 'Tare (kg)',
  'orchard.harvest.net': 'Net weight (kg)',
  'orchard.harvest.workers': 'Pickers',
  'orchard.harvest.create': 'Record the pass',
  'orchard.harvest.created': 'Harvest pass recorded.',
  'orchard.harvest.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.harvest.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to access it.',
  'orchard.harvest_pass.organization_required': 'The harvest pass must be attached to an organization.',
  'orchard.harvest_pass.farm_required': 'The harvest pass must be attached to a farm.',
  'orchard.harvest_pass.plot_required': 'The harvest pass must designate an orchard plot.',
  'orchard.harvest_pass.season_required': 'The harvest pass must designate a season.',
  'orchard.harvest_pass.invalid_pass_number': 'The pass number must be strictly positive.',
  'orchard.harvest_pass.invalid_weights': 'Gross weight must be positive and tare strictly below gross.',
  'orchard.harvest_pass.invalid_worker_count': 'The number of pickers cannot be negative.',
  'orchard.harvest_pass.invalid_labor_cost': 'The labour cost cannot be negative.',
  'orchard.harvest_pass.plot_out_of_scope': 'The designated orchard plot does not belong to this farm.',
  'orchard.harvest_pass.farm_mismatch': 'The pass and the orchard plot do not designate the same farm.',
  'orchard.harvest_pass.season_out_of_scope': 'The designated season does not belong to this farm.',
  'orchard.harvest_pass.dar_active': 'The plot is still under a pre-harvest interval: harvest is refused until it elapses.',
  'orchard.quality.title': 'Quality control',
  'orchard.quality.search': 'Search a quality control',
  'orchard.quality.empty': 'No quality control recorded yet.',
  'orchard.quality.pass': 'Harvest pass',
  'orchard.quality.date': 'Control date',
  'orchard.quality.acidity': 'Acidity (%)',
  'orchard.quality.peroxide': 'Peroxide index',
  'orchard.quality.brix': 'Brix (%)',
  'orchard.quality.caliber': 'Caliber',
  'orchard.quality.defect': 'Defects (%)',
  'orchard.quality.oilYield': 'Oil yield (%)',
  'orchard.quality.classification': 'Classification',
  'orchard.quality.create': 'Record the control',
  'orchard.quality.created': 'Quality control recorded.',
  'orchard.quality.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.quality.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to access it.',
  'orchard.quality_control.organization_required': 'The quality control must be attached to an organization.',
  'orchard.quality_control.farm_required': 'The quality control must be attached to a farm.',
  'orchard.quality_control.pass_required': 'The quality control must designate a harvest pass.',
  'orchard.quality_control.invalid_acidity': 'Acidity cannot be negative.',
  'orchard.quality_control.invalid_peroxide': 'The peroxide index cannot be negative.',
  'orchard.quality_control.invalid_brix': 'Brix is a percentage, between 0 and 100.',
  'orchard.quality_control.invalid_defect': 'The defect rate is a percentage, between 0 and 100.',
  'orchard.quality_control.invalid_oil_yield': 'Oil yield is a percentage, between 0 and 100.',
  'orchard.quality_control.pass_out_of_scope': 'The designated harvest pass does not belong to this farm.',
  'orchard.quality_control.farm_mismatch': 'The control and the pass do not designate the same farm.',
  'orchard.irrigation.title': 'Orchard irrigation',
  'orchard.irrigation.search': 'Search an irrigation',
  'orchard.irrigation.empty': 'No irrigation recorded yet.',
  'orchard.irrigation.plot': 'Orchard plot',
  'orchard.irrigation.date': 'Irrigation date',
  'orchard.irrigation.volume': 'Volume (m³)',
  'orchard.irrigation.waterSource': 'Water source',
  'orchard.irrigation.costPerM3': 'Price per m³',
  'orchard.irrigation.costTotal': 'Total cost',
  'orchard.irrigation.create': 'Record the irrigation',
  'orchard.irrigation.created': 'Irrigation recorded.',
  'orchard.irrigation.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.irrigation.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to enable it.',
  'orchard.irrigation.organization_required': 'The irrigation must be attached to an organization.',
  'orchard.irrigation.farm_required': 'The irrigation must be attached to a farm.',
  'orchard.irrigation.plot_required': 'The irrigation must designate an orchard plot.',
  'orchard.irrigation.invalid_volume': 'The water volume must be strictly positive.',
  'orchard.irrigation.water_source_required': 'The irrigation must designate its water source.',
  'orchard.irrigation.invalid_cost': 'The price per m³ cannot be negative.',
  'orchard.irrigation.plot_out_of_scope': 'The designated orchard plot does not belong to this farm.',
  'orchard.irrigation.farm_mismatch': 'The irrigation and the orchard plot do not designate the same farm.',
  'orchard.irrigation.movement_out_of_scope': 'The designated stock movement does not belong to this farm.',
  'orchard.transformation.title': 'Olive transformation',
  'orchard.transformation.search': 'Search a transformation',
  'orchard.transformation.empty': 'No transformation recorded yet.',
  'orchard.transformation.pass': 'Harvest pass',
  'orchard.transformation.date': 'Processing date',
  'orchard.transformation.mill': 'Mill',
  'orchard.transformation.input': 'Olive input (kg)',
  'orchard.transformation.output': 'Oil output (kg)',
  'orchard.transformation.yield': 'Yield (%)',
  'orchard.transformation.acidity': 'Acidity (%)',
  'orchard.transformation.classification': 'Oil grade',
  'orchard.transformation.batch': 'Batch',
  'orchard.transformation.create': 'Record the transformation',
  'orchard.transformation.created': 'Transformation recorded.',
  'orchard.transformation.moduleRequired.title': 'Orchard module not subscribed.',
  'orchard.transformation.moduleRequired.description': 'Your subscription does not include the Orchard module. Contact your administrator to enable it.',
  'orchard.transformation.classification.extra_virgin': 'Extra-virgin olive oil',
  'orchard.transformation.classification.virgin': 'Virgin olive oil',
  'orchard.transformation.classification.lampante': 'Lampante olive oil',
  'orchard.transformation.organization_required': 'The transformation must be attached to an organization.',
  'orchard.transformation.farm_required': 'The transformation must be attached to a farm.',
  'orchard.transformation.pass_required': 'The transformation must designate its harvest pass.',
  'orchard.transformation.invalid_input': 'The olive input must be strictly positive.',
  'orchard.transformation.invalid_output': 'The oil output must be strictly positive.',
  'orchard.transformation.output_exceeds_input': 'The oil output cannot exceed the olive input.',
  'orchard.transformation.invalid_acidity': 'The oil acidity cannot be negative.',
  'orchard.transformation.pass_out_of_scope': 'The designated harvest pass does not belong to this farm.',
  'orchard.transformation.farm_mismatch': 'The transformation and the harvest pass do not designate the same farm.',

  'orchard.phenology.title': 'Orchard phenology',
  'orchard.phenology.search': 'Search for an observation',
  'orchard.phenology.empty': 'No observation recorded yet.',
  'orchard.phenology.plot': 'Orchard',
  'orchard.phenology.date': 'Observation date',
  'orchard.phenology.stage': 'BBCH stage',
  'orchard.phenology.stage_label': 'Stage label',
  'orchard.phenology.notes': 'Notes',
  'orchard.phenology.create': 'Record the observation',
  'orchard.phenology.created': 'Observation recorded.',
  'orchard.phenology.moduleRequired.title': 'Arboriculture module not subscribed.',
  'orchard.phenology.moduleRequired.description': 'Your subscription does not include the Arboriculture module. Ask your administrator to enable it.',
  'orchard.phenology.organization_required': 'The observation must be attached to an organization.',
  'orchard.phenology.farm_required': 'The observation must be attached to a farm.',
  'orchard.phenology.plot_required': 'The observation must designate an orchard.',
  'orchard.phenology.stage_required': 'The observed BBCH stage is required.',
  'orchard.phenology.plot_out_of_scope': 'The designated orchard does not belong to this farm.',

  'orchard.reports.title': 'Orchard costs and profitability',
  'orchard.reports.search': 'Cost by orchard and campaign',
  'orchard.reports.empty': 'No orchard cost for this campaign.',
  'orchard.reports.season': 'Campaign',
  'orchard.reports.campaign': 'Campaign',
  'orchard.reports.plot': 'Orchard',
  'orchard.reports.area': 'Area',
  'orchard.reports.interventions': 'Works',
  'orchard.reports.irrigation': 'Water',
  'orchard.reports.harvest': 'Harvest',
  'orchard.reports.amortized': 'Amortized share',
  'orchard.reports.total': 'Total cost',
  'orchard.reports.yield': 'Harvest (kg)',
  'orchard.reports.costPerHa': 'Cost / ha',
  'orchard.reports.costPerKg': 'Cost / kg',

  'orchard.region': 'Region',
  'orchard.region.none': 'Not declared',
  'orchard.region.nord': 'North',
  'orchard.region.nord_ouest': 'North-West',
  'orchard.region.centre': 'Centre',
  'orchard.region.centre_sud': 'Centre-South',
  'orchard.region.cap_bon': 'Cap Bon',
  'orchard.region.sahel': 'Sahel',
  'orchard.region.djerid_nefzaoua': 'Djerid & Nefzaoua',
  'orchard.region.mornag': 'Mornag',
  'orchard.plot.invalid_region': 'The orchard region is unknown to the terroir.',

  'common.yes': 'Yes',
  'common.no': 'No',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.language': 'Language',
  'common.page': 'Page',
  'common.previous': 'Previous',
  'common.next': 'Next',

  'error.network': 'The server did not respond. Check your connection.',
  'error.unexpected': 'An unexpected error occurred.',
  'request.invalid_body': 'The input could not be read: check numbers (decimal point) and dates.',
  'db.duplicate': 'This value already exists: the code or e-mail must be unique.',
  'db.reference_missing': 'A referenced item does not exist (product, unit, partner or lot).',
  'db.check_violation': 'A value is outside the allowed bounds.',
  'db.value_required': 'A required value is missing.',
  'saas.no_active_subscription': 'No active subscription: contact your administrator before creating farms or plots.',
  'saas.quota.farms_exceeded': 'Your plan does not allow another farm.',
  'saas.quota.plots_exceeded': 'Your plan does not allow another plot.',
  'saas.quota.users_exceeded': 'Your plan does not allow another user.',
  'saas.quota.area_exceeded': 'The total area would exceed your plan ceiling.',
  'purchase.order.no_lines': 'Add at least one line: an order without lines orders nothing.',
  'purchases.receive.empty': 'Enter an arrived quantity on at least one line.',
  'purchases.cancel.confirm': 'Cancel this order? Cancellation is recorded and final.',
  'stock.warehouse.capacity_unit_required': 'A capacity must be expressed in a unit of measure.',
  'stock.transfer.incomplete': 'Choose the source warehouse, the destination warehouse, the product and the quantity.',
  'operations.header_required': 'Choose the farm and the type of work before saving.',
  'operations.consumption_incomplete': 'Choose a product and a strictly positive quantity.',
  'users.admin_only_page': 'User management is reserved to administrators.',
  'subscription.none': 'No active subscription is recorded for your organisation.',
  'subscription.plan': 'Plan',
  'subscription.status': 'Status',
  'subscription.period': 'Current period',
  'subscription.unlimited': 'No ceiling',
  'subscription.module': 'Module',
  'subscription.noModule': 'No activity module subscribed.',
  'subscription.usage': 'Usage',
  'subscription.unpaidTitle': 'If unpaid',
  'subscription.unpaid1': 'After the grace period, the organisation becomes read-only.',
  'subscription.unpaid2': 'Viewing and export stay open; only data entry is closed.',
  'subscription.unpaid3': 'No data is deleted.',
  'common.active': 'Active',
  'auth.invalid_credentials': 'Invalid credentials.',
  'auth.account_locked': 'Account temporarily locked. Try again in a few minutes.',
  'auth.account_disabled': 'Account disabled. Contact your organisation administrator.',
  'auth.password_too_short': 'The password must be at least ten characters long.',
  'auth.invalid_reset_token': 'This link is no longer valid: it has expired or has already been used. Request a new one.',
  'growth.dash.title': 'Dashboard',
  'growth.dash.description': 'Recurring revenue, clients, unpaid amounts and collections, computed as of today.',
  'growth.dash.mrr': 'Monthly recurring revenue (excl. tax)',
  'growth.dash.arr': 'Annual',
  'growth.dash.clients': 'Clients',
  'growth.dash.churn30': 'Churned in 30 days',
  'growth.dash.outstanding': 'Outstanding (incl. tax)',
  'growth.dash.overdue': 'of which overdue',
  'growth.dash.collectedMonth': 'Collected this month',
  'growth.dash.collectedYear': 'this year',
  'growth.dash.renewals30': 'Renewals in 30 days',
  'growth.dash.openRequests': 'Open requests',
  'growth.dash.byStatus': 'Subscriptions by status',
  'growth.dash.byPlan': 'By plan',
  'growth.dash.plan': 'Plan',
  'growth.dash.trialsEnding': 'Trials ending within 14 days',
  'growth.dash.trialEnds': 'Trial ends',
  'growth.dash.noTrials': 'No trials ending soon.',
  'growth.addons.title': 'Add-ons and discount',
  'growth.addons.kind': 'Add-on',
  'growth.addons.unitPrice': 'Price per period (excl. tax)',
  'growth.addons.since': 'Since',
  'growth.addons.empty': 'No add-ons in place.',
  'growth.addons.add': 'Add an add-on',
  'growth.addons.added': 'Add-on added; the limit is raised.',
  'growth.addons.end': 'Remove',
  'growth.addons.endTitle': 'Remove this add-on',
  'growth.addons.ended': 'Add-on removed.',
  'growth.addons.priceHint': 'Empty for farms: the plan’s price.',
  'growth.addonKind.farms': 'Extra farms',
  'growth.addonKind.users': 'Extra users',
  'growth.addonKind.plots': 'Extra plots',
  'growth.addonKind.area': 'Extra hectares',
  'growth.discount.title': 'Discount',
  'growth.discount.apply': 'Apply code',
  'growth.discount.applied': 'Discount applied to upcoming invoices.',
  'growth.discount.remove': 'Remove discount',
  'growth.discount.removed': 'Discount removed.',
  'growth.discount.remaining': 'periods left:',
  'growth.codes.title': 'Discount codes',
  'growth.codes.description': 'A percentage from 1 to 99, for a number of periods or with no end. What was already granted does not change when a code is edited.',
  'growth.codes.list': 'Codes',
  'growth.codes.form': 'Create or edit a code',
  'growth.codes.code': 'Code',
  'growth.codes.percent': 'Discount (%)',
  'growth.codes.percentHint': 'From 1 to 99.',
  'growth.codes.duration': 'Duration (periods)',
  'growth.codes.durationHint': 'Empty: for as long as the subscription lasts.',
  'growth.codes.forever': 'No end',
  'growth.codes.validUntil': 'Valid until',
  'growth.codes.max': 'Maximum uses',
  'growth.codes.uses': 'Uses',
  'growth.codes.descriptionLabel': 'Description',
  'growth.codes.inactive': 'Disabled',
  'growth.codes.edit': 'Edit',
  'growth.codes.empty': 'No codes yet.',
  'growth.codes.saved': 'Code saved.',
  'growth.requests.title': 'Client requests',
  'growth.requests.description': 'What clients ask for: change plan, cancel, add an add-on. Closing a request does not perform the change: that is done on the client’s page.',
  'growth.requests.list': 'Requests',
  'growth.requests.empty': 'No requests.',
  'growth.requests.date': 'Date',
  'growth.requests.kind': 'Request',
  'growth.requests.message': 'Message',
  'growth.requests.response': 'Response',
  'growth.requests.handle': 'Handle',
  'growth.requests.handleHint': 'The response is shown to the client. It is required to decline.',
  'growth.requests.done': 'Mark as done',
  'growth.requests.decline': 'Decline',
  'growth.requests.handled': 'Request closed.',
  'growth.requestKind.plan_change': 'Plan change',
  'growth.requestKind.cancel': 'Cancellation',
  'growth.requestKind.addon': 'Add-on',
  'growth.requestKind.other': 'Other',
  'growth.requestStatus.open': 'Pending',
  'growth.requestStatus.done': 'Done',
  'growth.requestStatus.declined': 'Declined',
  'growth.leads.title': 'Website leads',
  'growth.leads.description': 'Contact requests from the showcase website, newest first. Nobody is notified when one arrives: this is where they are read and sorted.',
  'growth.leads.list': 'Leads',
  'growth.leads.empty': 'No leads yet.',
  'growth.leads.date': 'Date',
  'growth.leads.name': 'Name',
  'growth.leads.company': 'Company',
  'growth.leads.email': 'Email',
  'growth.leads.phone': 'Phone',
  'growth.leads.governorate': 'Governorate',
  'growth.leads.source': 'Source',
  'growth.leads.status': 'Status',
  'growth.leads.message': 'Message',
  'growth.leadStatus.new': 'New',
  'growth.leadStatus.handled': 'Handled',
  'growth.leadStatus.spam': 'Spam',
  'growth.leads.action': 'Triage',
  'growth.leads.markHandled': 'Mark handled',
  'growth.leads.markSpam': 'Spam',
  'growth.leads.reopen': 'Reopen',
  'growth.lead_status_invalid': 'A lead is new, handled or spam.',
  'growth.lead_not_found': 'This lead cannot be found.',
  'portal.invoices.title': 'My invoices',
  'portal.plans.title': 'Change plan',
  'portal.plans.hint': 'Prices exclude tax. A change request is reviewed by the platform; nothing changes before its answer.',
  'portal.plans.current': 'current',
  'portal.plans.price': 'Price',
  'portal.plans.month': 'month',
  'portal.plans.year': 'year',
  'portal.plans.period': 'period',
  'portal.plans.features': 'Features',
  'portal.plans.ask': 'Request',
  'portal.requests.title': 'My requests',
  'portal.requests.send': 'Send request',
  'portal.requests.sent': 'Request sent to the platform.',
  'growth.addon_kind_unknown': 'Unknown add-on.',
  'growth.addon_quantity_invalid': 'The quantity must be positive, and whole except for hectares.',
  'growth.addon_price_required': 'Enter the add-on’s price per period: the plan doesn’t set one.',
  'growth.addon_not_found': 'This add-on cannot be found or was already removed.',
  'growth.code_invalid': 'A code has 2 to 30 letters, digits or dashes.',
  'growth.percent_invalid': 'A discount must be between 1 and 99%.',
  'growth.cycles_invalid': 'A discount lasts 1 to 120 periods, or has no end.',
  'growth.max_invalid': 'The maximum number of uses must be positive.',
  'growth.code_unavailable': 'This code does not exist, is disabled or has expired.',
  'growth.code_exhausted': 'This code has reached its maximum number of uses.',
  'growth.discount_exists': 'A discount is already in place: remove it first.',
  'growth.request_status_invalid': 'A request is closed as done or declined.',
  'growth.request_not_found': 'This request cannot be found or is already closed.',
  'subscription.request_kind_unknown': 'Unknown kind of request.',
  'subscription.request_message_required': 'Say what you are asking for.',
  'subscription.request_open': 'A request of this kind is already being processed.',
  'platform.nav.invoices': 'Invoices',
  'billing.invoices.title': 'Invoices and credit notes',
  'billing.invoices.description': 'What the platform has invoiced its clients. An issued invoice cannot be edited: it is corrected with a credit note.',
  'billing.invoices.list': 'Issued documents',
  'billing.invoices.empty': 'No invoices yet.',
  'billing.col.number': 'Number',
  'billing.col.client': 'Client',
  'billing.col.issuedOn': 'Issued on',
  'billing.col.dueOn': 'Due on',
  'billing.col.total': 'Total incl. tax',
  'billing.col.balance': 'Balance due',
  'billing.col.status': 'Status',
  'billing.status.all': 'All statuses',
  'billing.status.unpaid': 'Unpaid',
  'billing.status.partial': 'Partly paid',
  'billing.status.paid': 'Paid',
  'billing.status.overdue': 'Overdue',
  'billing.status.cancelled': 'Cancelled by credit note',
  'billing.status.credit_note': 'Credit note',
  'billing.kind.invoice': 'Invoice',
  'billing.kind.credit_note': 'Credit note',
  'billing.seller.title': 'Billing profile',
  'billing.seller.description': 'The platform’s identity on its invoices. Changing it does not rewrite any invoice already issued.',
  'billing.seller.incompleteTitle': 'No invoices are being issued',
  'billing.seller.incomplete': 'As long as the platform’s legal name, tax ID and address are missing, no invoice is issued, whether by hand or at renewal.',
  'billing.seller.complete': 'Complete the profile',
  'billing.seller.ready': 'Ready to invoice',
  'billing.seller.identity': 'Identity',
  'billing.seller.terms': 'Terms',
  'billing.seller.legalName': 'Legal name',
  'billing.seller.taxId': 'Tax ID',
  'billing.seller.taxIdHint': 'As shown on the tax registration card.',
  'billing.seller.address': 'Address',
  'billing.seller.city': 'City',
  'billing.seller.postalCode': 'Postal code',
  'billing.seller.phone': 'Phone',
  'billing.seller.email': 'Email',
  'billing.seller.bankName': 'Bank',
  'billing.seller.bankAccount': 'Bank account (RIB)',
  'billing.seller.vatRate': 'VAT rate (%)',
  'billing.seller.stampDuty': 'Stamp duty (TND)',
  'billing.seller.paymentTermsDays': 'Payment terms (days)',
  'billing.seller.footer': 'Footer text',
  'billing.seller.footerHint': 'Legal form, share capital, trade register…',
  'billing.seller.autoInvoice': 'Invoice automatically at renewal',
  'billing.seller.autoInvoiceHint': 'At each trial end or renewal, the lifecycle job issues the invoice for the new period.',
  'billing.seller.saved': 'Billing profile saved.',
  'billing.detail.back': 'All invoices',
  'billing.detail.pdf': 'Download PDF',
  'billing.detail.downloaded': 'PDF downloaded.',
  'billing.detail.summary': 'Document',
  'billing.detail.period': 'Period',
  'billing.detail.credited': 'Credit note on invoice',
  'billing.detail.lines': 'Lines and totals',
  'billing.detail.description': 'Description',
  'billing.detail.quantity': 'Quantity',
  'billing.detail.unitPrice': 'Unit price excl. tax',
  'billing.detail.amountHt': 'Amount excl. tax',
  'billing.detail.totalHt': 'Total excl. tax',
  'billing.detail.vat': 'VAT',
  'billing.detail.stamp': 'Stamp duty',
  'billing.detail.totalTtc': 'Total incl. tax',
  'billing.detail.payments': 'Payments',
  'billing.detail.noPayments': 'No payments recorded.',
  'billing.detail.credits': 'Credit notes',
  'billing.detail.noCredits': 'No credit notes on this invoice.',
  'billing.payment.title': 'Record a payment',
  'billing.payment.amount': 'Amount (TND)',
  'billing.payment.date': 'Payment date',
  'billing.payment.dateHint': 'Empty: today.',
  'billing.payment.method': 'Payment method',
  'billing.payment.reference': 'Reference',
  'billing.payment.submit': 'Record payment',
  'billing.payment.recorded': 'Payment recorded.',
  'billing.payment.reactivated': 'Payment recorded: the invoice is settled and the client’s subscription has resumed.',
  'billing.payment.void': 'Cancel',
  'billing.payment.voidTitle': 'Cancel this payment',
  'billing.payment.voidReason': 'Reason for cancelling',
  'billing.payment.voided': 'Payment cancelled; it stays visible, struck through.',
  'billing.payment.voidedBadge': 'Cancelled',
  'billing.method.transfer': 'Bank transfer',
  'billing.method.cheque': 'Cheque',
  'billing.method.cash': 'Cash',
  'billing.method.card': 'Card',
  'billing.method.other': 'Other',
  'billing.credit.title': 'Issue a credit note',
  'billing.credit.full': 'Cancel the whole invoice (stamp duty included)',
  'billing.credit.amountHt': 'Amount to credit, excl. tax',
  'billing.credit.reason': 'Reason',
  'billing.credit.submit': 'Issue credit note',
  'billing.credit.created': 'Credit note issued',
  'billing.new.note': 'Note',
  'billing.client.title': 'Invoices',
  'billing.client.currentPeriod': 'Invoice the current period',
  'billing.client.newInvoice': 'New invoice',
  'billing.client.addLine': 'Add a line',
  'billing.client.issue': 'Issue invoice',
  'billing.client.issued': 'Invoice issued.',
  'billing.client.priceHint': 'Excluding tax, to the millime.',
  'billing.seller_incomplete': 'Enter the platform’s legal name, tax ID and address before issuing an invoice.',
  'billing.lines_required': 'An invoice needs at least one line.',
  'billing.line_invalid': 'Each line needs a description, a positive quantity and a price to the millime.',
  'billing.total_zero': 'A zero invoice cannot be issued.',
  'billing.date_in_future': 'An invoice cannot be dated in the future.',
  'billing.date_before_last': 'Numbering follows the calendar: the date cannot be earlier than the last invoice’s.',
  'billing.legal_name_required': 'The platform’s legal name is required.',
  'billing.vat_invalid': 'The VAT rate must be between 0 and 100%.',
  'billing.stamp_invalid': 'Stamp duty must be between 0 and 100 TND.',
  'billing.terms_invalid': 'Payment terms must be between 0 and 120 days.',
  'billing.no_billable_subscription': 'This client has no current subscription to invoice.',
  'billing.period_already_invoiced': 'This subscription period has already been invoiced.',
  'billing.invoice_not_found': 'This invoice cannot be found.',
  'billing.method_unknown': 'Unknown payment method.',
  'billing.payment_date_invalid': 'The payment date is missing, in the future or unrelated to the invoice.',
  'billing.amount_invalid': 'The amount must be positive, to the millime.',
  'billing.payment_exceeds_balance': 'The amount exceeds what remains to be paid on the invoice.',
  'billing.payment_not_found': 'This payment cannot be found or is already cancelled.',
  'billing.already_credited': 'This invoice has already been fully credited.',
  'billing.credit_exceeds_invoice': 'The credit note exceeds what remains to be credited on the invoice.',
  'auth.use_admin_portal': 'This account administers the platform: sign in through the admin portal.',
  'auth.use_client_portal': 'This portal is reserved for platform administrators. Sign in through the application.',
  'auth.challenge_invalid': 'Sign-in step expired. Start again with your password.',
  'auth.totp_invalid': 'Incorrect or already used code.',
  'auth.totp_unavailable': 'Two-factor authentication for this account is unreadable. An operator must reset it.',
  'auth.wrong_portal': 'This session does not open this area. Sign in again.',
  'admin.title': 'Admin portal',
  'admin.login.subtitle': 'Restricted to platform administrators, with two-factor authentication.',
  'admin.login.continue': 'Continue',
  'admin.enroll.title': 'Turn on two-factor authentication',
  'admin.enroll.intro': 'Scan this code with your authenticator app (Google Authenticator, Microsoft Authenticator, Aegis…), then enter the six-digit code it shows.',
  'admin.enroll.qrAlt': 'Authenticator app QR code',
  'admin.enroll.manual': 'Manual key entry',
  'admin.code.label': 'Six-digit code',
  'admin.code.submit': 'Verify',
  'admin.verify.title': 'Two-step verification',
  'admin.verify.intro': 'Enter the code shown by your authenticator app.',
  'admin.verify.useRecovery': 'Use a recovery code',
  'admin.verify.useApp': 'Use the app',
  'admin.recovery.label': 'Recovery code',
  'admin.recovery.title': 'Your recovery codes',
  'admin.recovery.intro': 'Each one works once, if you lose your phone. They will never be shown again: keep them offline, somewhere safe.',
  'admin.recovery.copy': 'Copy codes',
  'admin.recovery.copied': 'Codes copied.',
  'admin.recovery.saved': 'I have saved them',
  'admin.restart': 'Start over',
  'saas.module_not_subscribed': 'Your organisation has not subscribed to this module.',

  'nav.library': 'Library',
  'library.title': 'Screen library',
  'library.description': 'The shared components, shown with demonstration data. Switch language and theme: nothing is hard-coded.',
  'library.theme': 'Theme',
  'library.theme.clair': 'Light',
  'library.theme.sombre': 'Dark',
  'library.section.layout': 'Grid and cards',
  'library.section.table': 'Table',
  'library.section.form': 'Form',
  'library.section.feedback': 'Banners and notifications',
  'library.warehouse': 'Warehouse',
  'library.warehouse.code': 'Warehouse code',
  'library.warehouse.name': 'Name',
  'library.warehouse.farm': 'Farm',
  'library.warehouse.type': 'Kind',
  'library.warehouse.capacity': 'Capacity',
  'library.warehouse.negative': 'Allow negative stock',
  'library.warehouse.negativeHint': 'For warehouses whose withdrawals are recorded after the fact.',
  'library.warehouse.codeHint': 'Thirty characters at most, upper-cased automatically.',
  'library.warehouse.codeError': 'A warehouse with this code already exists.',
  'library.stock.product': 'Product',
  'library.stock.quantity': 'Quantity',
  'library.stock.unitCost': 'Unit cost',
  'library.stock.value': 'Value',
  'library.stock.empty': 'No stock in this warehouse.',
  'library.save': 'Save',
  'library.saving': 'Saving…',
  'library.cancel': 'Cancel',
  'library.delete': 'Delete',
  'library.saved': 'Warehouse saved.',
  'library.notifyInfo': 'Information notification',
  'library.notifySuccess': 'Success notification',
  'library.notifyWarning': 'Warning notification',
  'library.notifyError': 'Error notification',
  'library.alert.info': 'Amounts are expressed in Tunisian dinars.',
  'library.alert.warning': 'This warehouse is disabled: it can no longer receive goods.',
  'library.alert.error': 'Not enough stock to perform this transfer.',
  'library.active': 'Active',
  'library.inactive': 'Disabled',

  'nav.suppliers': 'Suppliers',
  'suppliers.title': 'Suppliers',
  'suppliers.description': 'A supplier is a partner qualified as one: the profile is added to the existing partner, it does not replace it.',
  'suppliers.search': 'Search for a supplier',
  'suppliers.code': 'Code',
  'suppliers.name': 'Name',
  'suppliers.city': 'City',
  'suppliers.phone': 'Phone',
  'suppliers.status': 'Approval',
  'suppliers.approved': 'Approved',
  'suppliers.notApproved': 'Not approved',
  'suppliers.empty': 'No supplier recorded.',
  'suppliers.fiche': 'Supplier profile',
  'suppliers.selectHint': 'Pick a supplier to see their profile.',
  'suppliers.notSupplier': 'This partner is not declared as a supplier.',
  'suppliers.reference': 'Our reference with them',
  'suppliers.creditLimit': 'Credit limit',
  'suppliers.outstanding': 'Outstanding balance',
  'suppliers.creditAvailable': 'Available margin',
  'suppliers.noLimit': 'No limit',
  'suppliers.leadDays': 'Delivery lead time (days)',
  'suppliers.minimumOrder': 'Minimum order',
  'suppliers.bankName': 'Bank',
  'suppliers.bankAccount': 'Account number',
  'suppliers.iban': 'IBAN',
  'suppliers.rating': 'Quality rating (1 to 5)',
  'suppliers.approve': 'Approved supplier',
  'suppliers.approveHint': 'Approval keeps its date and author: the first question of a purchasing audit.',
  'suppliers.save': 'Save profile',
  'suppliers.saving': 'Saving…',
  'suppliers.saved': 'Supplier profile saved.',

  'warehouses.title': 'Warehouses',
  'warehouses.description': 'A warehouse is a storage place. A transfer moves value from one to another — it never creates any.',
  'warehouses.search': 'Search a warehouse',
  'warehouses.code': 'Code',
  'warehouses.name': 'Name',
  'warehouses.farm': 'Farm',
  'warehouses.unattached': 'Unattached',
  'warehouses.governorate': 'Governorate',
  'warehouses.manager': 'Manager',
  'warehouses.capacity': 'Capacity',
  'warehouses.status': 'Status',
  'warehouses.active': 'Active',
  'warehouses.inactive': 'Inactive',
  'warehouses.default': 'Default',
  'warehouses.showInactive': 'Show inactive warehouses',
  'warehouses.empty': 'No warehouse recorded.',
  'warehouses.new': 'New warehouse',
  'warehouses.create': 'Create warehouse',
  'warehouses.creating': 'Creating…',
  'warehouses.created': 'Warehouse created.',
  'warehouses.allowNegative': 'Allow negative stock',
  'warehouses.allowNegativeHint': 'Only for a field store, where issues often precede the recording of receipts.',
  'warehouses.attach': 'Attach',
  'warehouses.attached': 'Attachment saved.',
  'warehouses.detach': 'Detach',
  'warehouses.transfer': 'Transfer stock',
  'warehouses.transferFrom': 'Source warehouse',
  'warehouses.transferTo': 'Target warehouse',
  'warehouses.transferProduct': 'Product',
  'warehouses.transferQuantity': 'Quantity',
  'warehouses.transferSubmit': 'Transfer',
  'warehouses.transferring': 'Transferring…',
  'warehouses.unitCost': 'Unit cost',
  'warehouses.transferredValue': 'Value moved',
  'warehouses.transferNoValue': 'Unit cost is preserved: a transfer moves stock, it does not revalue it.',
  'warehouses.remainingSource': 'Left at source',
  'warehouses.remainingTarget': 'Left at target',

  'nav.operations': 'Operations',
  'operations.title': 'Field operations',
  'operations.description': 'A job, some plots, a cost: spending meets the season that carries it here.',
  'operations.new': 'New operation',
  'operations.farm': 'Farm',
  'operations.workType': 'Work type',
  'operations.date': 'Date',
  'operations.notes': 'Notes',
  'operations.targets': 'Plots worked',
  'operations.area': 'Area (ha)',
  'operations.noPlots': 'This farm has no plot yet: create one before working here.',
  'operations.machines': 'Machinery',
  'operations.addMachine': 'Add machinery',
  'operations.machine': 'Machine',
  'operations.hours': 'Hours',
  'operations.hourlyCost': 'Hourly cost',
  'operations.labor': 'Labor',
  'operations.addLabor': 'Add labor',
  'operations.partner': 'Contractor',
  'operations.workers': 'Workers',
  'operations.quantity': 'Quantity',
  'operations.payBasis': 'Pay basis',
  'operations.payBasis.hourly': 'Hourly',
  'operations.payBasis.daily': 'Daily',
  'operations.payBasis.monthly': 'Monthly',
  'operations.payBasis.piece_rate': 'Piece rate',
  'operations.unitCost': 'Unit cost',
  'operations.responsible': 'Person in charge',
  'operations.meterKind': 'Meter type',
  'operations.meterKind.none': 'No reading',
  'operations.meterKind.hours': 'Hour meter',
  'operations.meterKind.km': 'Odometer',
  'operations.meterStart': 'Meter at start',
  'operations.meterEnd': 'Meter at return',
  'operations.distanceKm': 'Distance (km)',
  'operations.fromMeter': 'Derived from the meter: not typed alongside.',
  'operations.employee': 'Employee',
  'operations.startTime': 'Start time',
  'operations.endTime': 'End time',
  'operations.fromTimes': 'Derived from the times: not typed alongside.',
  'operations.unitCost.fromEmployee': 'Left empty, the cost on the employee record applies.',
  'operations.plannedInputs': 'Planned inputs',
  'operations.addPlannedInput': 'Plan an input',
  'operations.plannedProduct': 'Planned product',
  'operations.plannedQuantity': 'Planned quantity',
  'operations.driver': 'Driver',
  'operations.plan.title': 'Planned and actual',
  'operations.plan.planned': 'Planned',
  'operations.plan.actual': 'Actual',
  'operations.plan.gap': 'Gap',
  'operations.charges': 'Services and other charges',
  'operations.addCharge': 'Add a charge',
  'operations.charge.kind': 'Charge type',
  'operations.charge.kind.service': 'Subcontracted service',
  'operations.charge.kind.other': 'Other charge',
  'operations.charge.partner': 'Company or third party',
  'operations.charge.description': 'Nature or label',
  'operations.charge.quantity': 'Number of units',
  'operations.charge.unitPrice': 'Unit price',
  'operations.charge.total': 'Amount:',
  'operations.breakdown.prestations': 'Subcontracted services',
  'operations.breakdown.autres': 'Other charges',
  'nav.employees': 'Employees',
  'employees.title': 'Employees',
  'employees.description': 'The farm staff: who answers for an operation, who works on it, and what they cost.',
  'employees.new': 'New employee',
  'employees.edit': 'Edit employee',
  'employees.code': 'Staff number',
  'employees.fullName': 'Full name',
  'employees.jobTitle': 'Job title',
  'employees.employmentType': 'Staff type',
  'employees.farm': 'Home farm',
  'employees.farm.all': 'All farms',
  'employees.hourlyCost': 'Hourly cost',
  'employees.dailyCost': 'Daily cost',
  'employees.monthlyCost': 'Monthly cost',
  'employees.active': 'In service',
  'employees.active.hint': 'An inactive employee can no longer be designated on an operation; past lines remain.',
  'employees.empty': 'No employee recorded.',
  'employees.saved': 'Employee saved.',
  'employees.required': 'A staff number, a name and a staff type are required.',
  'employees.readOnly': 'Read-only: managing employee records requires the master data permission.',
  'operations.saveDraft': 'Save draft',
  'operations.saveValidate': 'Save and validate',
  'operations.saving': 'Saving…',
  'operations.created': 'Operation created.',
  'operations.validated': 'Operation validated: it is now immutable.',
  'operations.validatedBadge': 'Validated — immutable',
  'operations.result': 'Operation result',
  'operations.number': 'Number',
  'operations.directCost': 'Direct cost',
  'operations.validateDraft': 'Validate operation',
  'operations.consumption': 'Inputs issued from stock',
  'operations.consumptionHint': 'Consuming issues the product from the warehouse AT STOCK PRICE and attaches it to the operation: the price is never re-typed. Validate afterwards.',
  'operations.warehouse': 'Warehouse',
  'operations.product': 'Product',
  'operations.consume': 'Consume',
  'operations.consuming': 'Consuming…',
  'operations.consumed': 'Input attached to the operation.',
  'operations.breakdown': 'Direct cost, line by line',
  'operations.breakdown.intrants': 'Inputs',
  'operations.breakdown.machines': 'Machinery',
  "operations.breakdown.main-d'œuvre": 'Labor',
  'operations.lineLabel': 'Line',
  'operations.unitPrice': 'Unit price',
  'operations.total': 'Total',
  'operations.importTitle': 'Import operations (CSV)',
  'operations.importHint': 'One file row = one operation, validated on import. Download the template: it carries the instructions in its first lines.',

  'references.importTitle': 'Bulk import (CSV)',
  'references.importHint': 'Valid rows are imported, faulty ones are reported with their line number. The template carries the expected columns.',
  'references.importHintVariety': 'For varieties, the crop_code column designates the crop by its exact code — create the crops first.',

  'import.template': 'Download template',
  'import.pending': 'Importing…',
  'import.allDone': '{created} row(s) imported out of {total}.',
  'import.partial': '{created} row(s) imported out of {total}; {failed} rejected — fix them and submit them alone.',
  'import.columnLine': 'Line',
  'import.columnError': 'Error',
  'import.columnDetail': 'Detail',

  'csv.empty': 'The file is empty.',
  'csv.header_missing': 'The first line must carry the column names.',
  'csv.header_invalid': 'File columns differ from the expected template.',
  'csv.no_data_rows': 'The file carries no data under its header.',
  'csv.column_count': 'A row carries more values than the template allows columns.',
  'csv.too_many_rows': 'Too many rows: split the file.',

  'import.crop_unknown': 'Unknown crop: create it first in the crop reference.',
  'import.farm_unknown': 'Unknown farm: check its code.',
  'import.work_type_unknown': 'Unknown work type: check its code.',
  'import.plot_unknown': 'Unknown plot in this farm.',
  'import.plot_duplicate': 'The same plot appears twice in the row.',
  'import.no_open_season': 'No open season on this plot.',
  'import.machine_unknown': 'Unknown machine: check its code.',
  'import.hours_required': 'Positive hours are required when machinery is designated.',
  'import.partner_unknown': 'Unknown contractor: check their code.',
  'import.pay_basis_invalid': 'Invalid pay basis (hourly, daily, monthly, piece_rate).',
  'import.number_invalid': 'Unreadable number.',
  'import.date_invalid': 'Unreadable date (YYYY-MM-DD or DD/MM/YYYY).',
  'import.quantity_required': 'A positive quantity is required.',

  'farm.plot.no_open_season': 'This plot has no open season: create one before charging work to it.',
  'farm.plot.not_found': 'Plot not found.',
  'farm.plot.irrigation_requires_irrigable': 'A rainfed plot has neither an irrigation mode nor a water source: mark it irrigable, or remove them.',
  'reference.not_found': 'The chosen entry does not exist, or no longer exists, in the reference list.',
  'farm.farm.not_found': 'Farm not found.',
  'purchase.requisition.organization_required': 'A purchase requisition belongs to a company.',
  'purchase.requisition.document_number_required': 'The requisition number is required (30 characters at most).',
  'purchase.requisition.already_numbered': 'This requisition already has its number.',
  'purchase.requisition.needed_before_request': 'A need cannot be expected before it was requested.',
  'purchase.requisition.text_too_long': 'A requisition reason fits in 500 characters, a line or decision reason in 300.',
  'purchase.requisition.no_lines': 'A requisition that requests nothing can neither be created nor approved.',
  'purchase.requisition.rejection_reason_required': 'A refusal needs a reason.',
  'purchase.requisition.decided': 'This requisition is already decided: it can no longer be changed or decided again.',
  'purchase.requisition.not_requester': 'Only the person who made the requisition can withdraw it. Others reject it, with a reason.',
  'purchase.requisition.not_found': 'Purchase requisition not found.',
  'purchase.requisition.unknown_status': 'A requisition is submitted, approved, refused or withdrawn.',
  'purchase.requisition.line_not_found': 'The designated requisition line was not found.',
  'purchase.requisition.not_approved': 'An order can only answer an approved requisition.',
  'purchase.requisition.product_mismatch': 'The order line does not carry the product of the requisition it answers.',
  'purchase.requisition.unit_mismatch': 'A line answering a requisition is counted in the product’s unit.',
  'purchase.requisition_line.invalid': 'A requisition line designates a product and a strictly positive quantity.',
  'purchase.requisition_line.duplicate_product': 'This product is already requested: a product is requested once per requisition.',
  'purchase.requisition_line.product_not_found': 'A requested product was not found.',
  'agro.budget.season_closed': 'This season is closed: its budget can no longer be changed.',
  'agro.budget.invalid_yield': 'An expected yield cannot be negative.',
  'agro.budget.invalid_price': 'A forecast price cannot be negative.',
  'agro.budget.unit_required': 'An expected yield or forecast price must come with its unit.',
  'agro.budget.text_too_long': 'A budget note holds 300 characters, a line label 120.',
  'agro.budget.not_found': 'This crop has no budget in this season.',
  'agro.budget_line.invalid_nature': 'A budget line is an input, machinery, labour or another charge.',
  'agro.budget_line.label_required': 'A budget line says what it budgets: its label is required.',
  'agro.budget_line.invalid_quantity': 'The quantity per hectare must be strictly positive.',
  'agro.budget_line.price_required': 'A budget line’s price is required: zero is a price, an empty field is not.',
  'agro.budget_line.invalid_price': 'A price cannot be negative.',
  'agro.budget_line.duplicate': 'This budget already has a line with this label and nature.',
  'agro.budget_line.not_found': 'This line is not a line of this budget.',
  'sales.invoice.not_found': 'Invoice not found.',
  'sales.invoice.due_before_invoice': 'An invoice cannot be due before its date.',
  'sales.invoice.date_in_future': 'An invoice cannot be dated in the future.',
  'sales.invoice.no_deliveries': 'An invoice takes up at least one delivery note.',
  'sales.invoice.delivery_not_invoicable': 'A delivery note is missing, not delivered, of another customer or already invoiced: nothing is invoiced.',
  'sales.invoice.mixed_partners': 'An invoice does not mix customers.',
  'sales.invoice.tax_rate_invalid': 'This tax rate is not in force.',
  'sales.payment.amount_must_be_positive': 'A receipt amount must be strictly positive.',
  'sales.payment.date_in_future': 'A receipt cannot be dated in the future.',
  'sales.payment.text_too_long': 'A receipt reference holds 60 characters, its note or cancellation reason 300.',
  'sales.payment.customer_not_found': 'Customer not found.',
  'sales.payment.method_not_found': 'Payment method not found.',
  'sales.payment.reference_required': 'This payment method requires a reference.',
  'sales.payment.allocation_invalid': 'An allocation must be a strictly positive amount.',
  'sales.payment.allocation_duplicate': 'An invoice can only be allocated once per receipt.',
  'sales.payment.over_allocated': 'The allocations exceed what the receipt is still worth.',
  'sales.payment.invoice_not_payable': 'A designated invoice is not an issued invoice of this customer.',
  'sales.payment.exceeds_remaining': 'An allocation exceeds what the invoice still owes, credit notes deducted.',
  'sales.payment.not_found': 'Receipt not found.',
  'sales.payment.cancelled': 'This receipt is cancelled: it no longer settles anything.',
  'sales.payment.already_cancelled': 'This receipt is already cancelled.',
  'sales.payment.cancellation_reason_required': 'Cancelling a receipt requires a reason.',
  'sales.payment.partner_mismatch': 'The receipt and the invoice are not from the same customer.',
  'sales.payment.allocation_immutable': 'An allocation cannot be changed or removed: cancel the receipt instead.',
  'sales.payment.immutable': 'A receipt cannot be rewritten: it is cancelled.',
  'agro.plot_season.not_found': 'Plot season not found.',
  'agro.plot_season.area_must_be_positive': 'The sown area must be strictly positive.',
  'agro.plot_season.area_exceeds_plot': 'The sown area exceeds the plot area.',
  'agro.plot_season.area_unknown': 'Unknown area for this plot: state it explicitly.',
  'agro.plot_season.already_assigned': 'This plot already carries a crop for this season.',
  'agro.plot_season.invalid_sequence': 'The crop sequence is 1 (main) or 2 (secondary).',
  'agro.plot_season.double_cropping_disabled': 'Double cropping is disabled: a plot carries one crop per season.',
  'agro.plot_season.invalid_period': 'The season end must come after its start.',
  'agro.plot_season.period_outside_season': 'The campaign dates fall outside the period that carries it.',
  'agro.season.not_found': 'Period not found.',
  'agro.plot_season.code_too_long': 'The season code exceeds 30 characters.',
  'agro.plot_season.duplicate_code': 'A season already carries this code.',
  'agro.plot_season.name_too_long': 'The season name exceeds 150 characters.',
  'agro.plot_season.description_too_long': 'The season description exceeds 500 characters.',
  'agro.cropping_plan.no_plot_selected': 'No plot selected: there is nothing to assign.',
  'agro.cropping_plan.too_many_plots': 'Too many plots at once (200 at most): proceed farm by farm.',

  'stock.warehouse_required': 'A warehouse must be designated.',

  'ops.operation.no_target': 'Designate at least one plot: an operation charges something.',
  'ops.operation.no_line': 'Add machinery, labor or a charge: an operation without cost cannot be validated.',
  'ops.operation.already_validated': 'Operation already validated.',
  'ops.operation.immutable': 'Validated operation: it is immutable, correct it through a reversal.',
  'ops.operation.cancelled': 'A cancelled operation cannot be validated.',
  'ops.operation.references_required': 'Choose a farm and a work type.',
  'ops.operation.invalid_area': 'Covered area must be strictly positive.',
  'ops.operation_target.cost_object_required': 'A target designates a cost object.',
  'ops.operation_target.allocation_key_required': 'Each target carries an area or a share.',
  'ops.operation_target.invalid_allocation': 'Area must be positive and share between 0 and 100.',
  'ops.operation_target.duplicate': 'This plot is already covered by the operation.',
  'ops.operation_input.quantity_must_be_positive': 'Consumed quantity must be strictly positive.',
  'ops.operation_input.movement_already_used': 'This stock movement is already attached to a consumption.',
  'ops.operation_input.references_required': 'An input designates a product, its movement and a unit.',
  'ops.operation_machine.invalid': 'A machine, positive hours and an hourly cost are required.',
  'ops.operation_labor.who_required': 'Designate an employee OR a contractor, not both.',
  'ops.operation_labor.invalid': 'Worker count and quantity must be positive.',
  'ops.operation_machine.invalid_meter': 'A meter is read at start and at return, the return above the start, with its type.',
  'ops.operation_machine.hours_from_meter': 'Hours are derived from the hour meter: they are not typed alongside.',
  'ops.operation_machine.distance_from_meter': 'Distance is derived from the odometer: it is not typed alongside.',
  'ops.operation_machine.invalid_distance': 'A distance is strictly positive.',
  'ops.operation_labor.times_together': 'Start time and end time go together.',
  'ops.operation_labor.times_order': 'End time follows start time. A line does not cross midnight: enter two.',
  'ops.operation_labor.hours_from_times': 'Hours are derived from the times: they are not typed alongside.',
  'agro.employee.not_found': 'Employee not found.',
  'agro.employee.code_required': 'The staff number is required.',
  'agro.employee.name_required': 'The name is required.',
  'agro.employee.text_too_long': 'A staff number fits in 30 characters, a name in 150, a job title in 100.',
  'agro.employee.negative_cost': 'A cost cannot be negative.',
  'agro.employee.employment_type_not_found': 'The designated staff type does not exist.',
  'agro.employee.duplicate_code': 'This staff number is already taken.',
  'livestock.animal.organization_required': 'An animal belongs to an organization.',
  'livestock.animal.farm_required': 'An animal lives on a farm.',
  'livestock.animal.tag_required': 'The tag (loop number) is required.',
  'livestock.animal.species_required': 'Species is required.',
  'livestock.animal.breed_required': 'Breed is required.',
  'livestock.animal.sex_invalid': 'Sex is "male" or "female".',
  'livestock.animal.entry_reason_required': 'The entry reason (birth, purchase…) is required.',
  'livestock.animal.entry_date_required': 'The entry date is required.',
  'livestock.animal.text_too_long': 'A text exceeds its maximum length.',
  'livestock.animal.species_not_found': 'Unknown species.',
  'livestock.animal.breed_not_found': 'Unknown breed.',
  'livestock.animal.duplicate_tag': 'This tag is already taken on this farm.',
  'livestock.movement.animal_not_found': 'Animal not found.',
  'livestock.movement.direction_invalid': 'A movement moves in ("in") or out ("out").',
  'livestock.movement.date_required': 'The movement date is required.',
  'livestock.movement.reason_required': 'The movement reason is required.',
  'livestock.movement.animal_exited': 'This animal has left the herd.',
  'livestock.movement.exit_without_entry': 'Cannot move out an animal that never moved in.',
  'livestock.movement.already_inside': 'This animal is already inside.',
  'livestock.registry.title': 'Animal registry',
  'livestock.registry.description': 'Each head identified by its tag: registration, entries and exits.',
  'livestock.registry.new': 'New animal',
  'livestock.registry.tag': 'Tag (loop)',
  'livestock.registry.species': 'Species',
  'livestock.registry.breed': 'Breed',
  'livestock.registry.sex': 'Sex',
  'livestock.registry.sex.male': 'Male',
  'livestock.registry.sex.female': 'Female',
  'livestock.registry.farm': 'Farm',
  'livestock.registry.entryDate': 'Entry date',
  'livestock.registry.entryReason': 'Entry reason',
  'livestock.registry.saved': 'Animal registered.',
  'livestock.registry.required': 'Tag, species, breed and entry reason are required.',
  'livestock.registry.movement.title': 'Entry / exit',
  'livestock.registry.movement.animal': 'Animal',
  'livestock.registry.movement.direction': 'Direction',
  'livestock.registry.movement.in': 'Entry',
  'livestock.registry.movement.out': 'Exit',
  'livestock.registry.movement.date': 'Date',
  'livestock.registry.movement.reason': 'Reason',
  'livestock.registry.movement.saved': 'Movement recorded.',
  'livestock.registry.status': 'Status',
  'livestock.registry.status.active': 'Present',
  'livestock.registry.status.exited': 'Exited',
  'livestock.inventory.title': 'Herd inventory',
  'livestock.inventory.description': 'Animals present, counted by species and category.',
  'livestock.inventory.species': 'Species',
  'livestock.inventory.category': 'Category',
  'livestock.inventory.count': 'Headcount',
  'livestock.farm.all': 'All farms',
  'nav.livestock.registry': 'Registry',
  'nav.livestock.inventory': 'Inventory',
  'references.kind.species': 'Species',
  'references.kind.breed': 'Breeds',
  'references.kind.animal_category': 'Animal categories',
  'livestock.registry.empty': 'No animals on record.',
  'livestock.registry.register': 'Register',
  'livestock.registry.movement.record': 'Record',
  'livestock.registry.movement.required': 'Animal, direction and reason are required.',
  'livestock.registry.movement.price': 'Price (TND)',
  'livestock.inventory.empty': 'Empty herd.',
  'livestock.movement.withdrawal_active': 'This animal is under withdrawal: its exit to slaughter is refused.',
  'livestock.movement_immutable': 'A herd movement cannot be changed or deleted: it is corrected by a reverse movement.',
  'livestock.movement.price_invalid': 'A movement price cannot be negative.',
  'livestock.health.organization_required': 'A health event belongs to an organization.',
  'livestock.health.animal_required': 'A health event concerns an animal.',
  'livestock.health.date_required': 'The intervention date is required.',
  'livestock.health.type_required': 'The intervention kind (treatment, vaccination…) is required.',
  'livestock.health.withdrawal_invalid': 'The withdrawal period cannot be negative.',
  'livestock.health.dose_invalid': 'The dose, when entered, is strictly positive.',
  'livestock.health.text_too_long': 'A text exceeds its maximum length.',
  'livestock.health.animal_not_found': 'Animal not found.',
  'livestock.health.after_exit': 'This animal has left the herd: it cannot be treated after its exit.',
  'livestock.health.title': 'Health tracking',
  'livestock.health.description': 'Treatments, vaccinations, visits — and the withdrawal before slaughter.',
  'livestock.health.new': 'New intervention',
  'livestock.health.animal': 'Animal',
  'livestock.health.type': 'Intervention kind',
  'livestock.health.type.treatment': 'Treatment',
  'livestock.health.type.vaccination': 'Vaccination',
  'livestock.health.type.visit': 'Visit',
  'livestock.health.type.deworming': 'Deworming',
  'livestock.health.date': 'Intervention date',
  'livestock.health.product': 'Product',
  'livestock.health.dose': 'Dose',
  'livestock.health.withdrawal': 'Withdrawal (days)',
  'livestock.health.vet': 'Veterinarian',
  'livestock.health.saved': 'Intervention recorded.',
  'livestock.health.required': 'Animal, date and kind are required.',
  'livestock.health.empty': 'No interventions for this animal.',
  'livestock.health.record': 'Record',
  'livestock.health.cost': 'Cost (TND)',
  'livestock.health.cost_invalid': 'An intervention cost cannot be negative.',
  'livestock.alerts.title': 'Vaccinations due',
  'livestock.alerts.description': 'Animals present with no recent vaccination.',
  'livestock.alerts.empty': 'No vaccination due.',
  'livestock.alerts.animal': 'Animal',
  'livestock.alerts.last': 'Last vaccination',
  'livestock.alerts.never': 'Never',
  'nav.livestock.health': 'Health',
  'nav.livestock.production': 'Production',
  'livestock.production.title': 'Dairy production',
  'livestock.production.description': 'Daily milking, morning and evening — and the daily total.',
  'livestock.production.table': 'Recorded milkings',
  'livestock.production.new': 'New milking',
  'livestock.production.animal': 'Animal',
  'livestock.production.date': 'Milking date',
  'livestock.production.quantity': 'Quantity (l)',
  'livestock.production.quality': 'Quality score (0-100)',
  'livestock.production.session': 'Session',
  'livestock.production.session.morning': 'Morning',
  'livestock.production.session.evening': 'Evening',
  'livestock.production.date_total': 'Date totalled',
  'livestock.production.total': 'Daily total',
  'livestock.production.saved': 'Milking recorded.',
  'livestock.production.after_exit': 'This animal has left the herd: it is no longer milked after its exit.',
  'livestock.production.animal_not_found': 'Animal not found.',
  'livestock.production.animal_required': 'A milking concerns an animal.',
  'livestock.production.date_required': 'The milking date is required.',
  'livestock.production.organization_required': 'A milking belongs to a company.',
  'livestock.production.product_required': 'The product (milk, wool, eggs) is required.',
  'livestock.production.quality_invalid': 'The quality score is between 0 and 100.',
  'livestock.production.quantity_invalid': 'The milked quantity must be strictly positive.',
  'livestock.production.text_too_long': 'A text exceeds its maximum length.',
  'livestock.production.withdrawal_active': 'This animal is under withdrawal: its milk is excluded from collection.',
  'livestock.production.required': 'Animal, date and quantity are required.',
  'livestock.production.empty': 'No milkings for this animal.',
  'livestock.production.record': 'Record',
  'livestock.production.daily_total': 'Daily total',
  'nav.livestock.feeding': 'Feeding',
  'livestock.feeding.title': 'Feeding',
  'livestock.feeding.description': 'What each animal is given: product, quantity, cost.',
  'livestock.feeding.table': 'Recorded feedings',
  'livestock.feeding.new': 'New ration',
  'livestock.feeding.animal': 'Animal',
  'livestock.feeding.date': 'Date',
  'livestock.feeding.product': 'Feed product',
  'livestock.feeding.quantity': 'Quantity (kg)',
  'livestock.feeding.cost_total': 'Total cost (TND)',
  'livestock.feeding.saved': 'Feeding recorded.',
  'livestock.feeding.required': 'Animal, date and quantity are required.',
  'livestock.feeding.empty': 'No feedings for this animal.',
  'livestock.feeding.record': 'Record',
  'livestock.feeding.animal_not_found': 'Animal not found.',
  'livestock.feeding.organization_required': 'A feeding belongs to an organization.',
  'livestock.feeding.animal_required': 'A feeding concerns an animal.',
  'livestock.feeding.date_required': 'The feeding date is required.',
  'livestock.feeding.product_required': 'The feed product is required.',
  'livestock.feeding.quantity_invalid': 'The quantity in kg is strictly positive.',
  'livestock.feeding.after_exit': 'This animal has left the herd: it cannot be fed after its exit.',
  'livestock.feeding.insufficient_stock': 'Insufficient stock for this feeding.',
  'livestock.feeding.no_warehouse': 'This animal’s farm has no default warehouse: the ration cannot be taken from stock.',
  'livestock.feeding.text_too_long': 'The note is limited to 500 characters.',
  'nav.livestock.reproduction': 'Reproduction',
  'livestock.reproduction.title': 'Reproduction and genealogy',
  'livestock.reproduction.description': 'Mating and expected due date — and gestation by species (sheep ≈ 150 days, cattle ≈ 285 days).',
  'livestock.reproduction.table': 'Recorded matings',
  'livestock.reproduction.new': 'New mating',
  'livestock.reproduction.animal': 'Dam',
  'livestock.reproduction.sire': 'Sire (ram, bull)',
  'livestock.reproduction.date': 'Mating date',
  'livestock.reproduction.type': 'Type',
  'livestock.reproduction.type.sai': 'Natural mating',
  'livestock.reproduction.type.natural': 'Natural mating',
  'livestock.reproduction.type.ivt': 'Artificial insemination',
  'livestock.reproduction.expected_due': 'Expected due date',
  'livestock.reproduction.notes': 'Notes',
  'livestock.reproduction.saved': 'Mating recorded.',
  'livestock.reproduction.required': 'Animal, date and sire are required.',
  'livestock.reproduction.empty': 'No matings for this animal.',
  'livestock.reproduction.record': 'Record',
  'livestock.reproduction.animal_not_found': 'Animal not found.',
  'livestock.reproduction.organization_required': 'A reproduction event belongs to an organization.',
  'livestock.reproduction.animal_required': 'A reproduction event concerns a dam.',
  'livestock.reproduction.sire_required': 'The sire (ram, bull) is required.',
  'livestock.reproduction.date_required': 'The mating date is required.',
  'livestock.reproduction.type_required': 'The event type (mating, insemination…) is required.',
  'livestock.reproduction.same_animal': 'The sire and dam cannot be the same animal.',
  'livestock.reproduction.dam_sex_invalid': 'The dam of a mating is a female.',
  'livestock.reproduction.sire_sex_invalid': 'The sire of a mating is a male.',
  'livestock.reproduction.sire_not_found': 'The sire was not found.',
  'livestock.reproduction.text_too_long': 'A text exceeds its maximum length.',
  'livestock.reproduction.after_exit': 'This animal has left the herd: it cannot be bred after exit.',
  'nav.livestock.reports': 'Costs and margins',
  'livestock.reports.title': 'Herd costs and margins',
  'livestock.reports.description': 'Cost per head and cost per litre — two figures, two readings.',
  'livestock.reports.head': 'Head',
  'livestock.reports.total_cost': 'Cumulated cost',
  'livestock.reports.liters': 'Milk produced (l)',
  'livestock.reports.per_head': 'Cost per head',
  'livestock.reports.per_liter': 'Cost per litre',
  'livestock.reports.table': 'Cost per animal',
  'livestock.reports.animal': 'Animal',
  'livestock.reports.purchase': 'Purchase',
  'livestock.reports.feeding': 'Feeding',
  'livestock.reports.health': 'Health',
  'livestock.reports.total': 'Total cost',
  'livestock.reports.sale': 'Sale price',
  'livestock.reports.margin': 'Margin',
  'livestock.reports.empty': 'No animal in the registry.',
  'livestock.reports.forbidden': 'Your role does not allow viewing costs.',
  'ops.operation_planned_input.invalid': 'A planned input designates a product and a strictly positive quantity.',
  'ops.operation_planned_input.duplicate': 'This product is already planned on the operation: a product is planned once.',
  'ops.operation_planned_input.product_not_stocked': 'A planned product is not stock-managed: it cannot be consumed, so it cannot be planned.',
  'ops.operation_charge.invalid_kind': 'A charge is a subcontracted service or another charge.',
  'ops.operation_charge.description_required': 'Say what was paid for: the nature of the service, or the label of the charge.',
  'ops.operation_charge.description_too_long': 'A charge label fits in 200 characters.',
  'ops.operation_charge.partner_required': 'A subcontracted service names its provider.',
  'ops.operation_charge.invalid_amount': 'A charge carries a strictly positive quantity and a price that is not negative.',
  'ops.operation_charge.price_required': 'Enter the price: zero is a price, an empty field is not.',
  'agro.partner.not_found': 'Third party not found.',
  'operations.consumption.no_lines': 'Add at least one product to consume.',
  'operations.operation.not_found': 'Operation not found.',

  'nav.references': 'References',
  'nav.stock': 'Stock',
  'ops.rule.outside_season': 'This date falls outside the season: the operation would charge its cost to the wrong harvest.',
  'ops.rule.inactive_plot': 'This plot is no longer active: it cannot be worked.',
  'references.title': 'Agronomic reference data',
  'references.description': 'The delivered catalog is readable by everyone and writable by no one: rename it through an override, add your own entries alongside.',
  'references.kind': 'Reference',
  'references.kind.crop_family': 'Crop families',
  'references.kind.crop': 'Crops',
  'references.kind.variety': 'Varieties',
  'references.kind.work_category': 'Work categories',
  'references.kind.work_type': 'Work types',
  'references.kind.machine_category': 'Machinery',
  'references.kind.employment_type': 'Labor',
  'references.kind.farm_type': 'Farm types',
  'references.kind.soil_type': 'Soil types',
  'references.kind.irrigation_mode': 'Irrigation modes',
  'references.kind.water_source': 'Water sources',
  'common.notSpecified': 'Not specified',
  'farms.type': 'Farm type',
  'farms.type.choose': 'Choose the type',
  'farms.type.saved': 'Farm type saved.',
  'plots.soilType': 'Soil type',
  'plots.irrigation.title': 'Soil and irrigation',
  'plots.irrigation.irrigable': 'Irrigable plot',
  'plots.irrigation.mode': 'Irrigation mode',
  'plots.irrigation.source': 'Water source',
  'plots.irrigation.chooseMode': 'Choose the irrigation mode',
  'plots.irrigation.chooseSource': 'Choose the water source',
  'plots.irrigation.dryHint': 'A rainfed plot has neither an irrigation mode nor a water source.',
  'plots.irrigation.saved': 'Irrigation saved.',
  'requisitions.title': 'Purchase requisitions',
  'requisitions.description': 'Say what is needed, have it approved, then order it.',
  'requisitions.new': 'New requisition',
  'requisitions.list': 'Requisitions',
  'requisitions.one': 'Requisition',
  'requisitions.empty': 'No purchase requisition.',
  'requisitions.number': 'Number',
  'requisitions.requestDate': 'Requested on',
  'requisitions.neededBy': 'Needed by',
  'requisitions.farm': 'Farm',
  'requisitions.farm.central': 'Central need',
  'requisitions.warehouse': 'Destination warehouse',
  'requisitions.notes': 'Reason for the request',
  'requisitions.product': 'Product',
  'requisitions.quantity': 'Requested quantity',
  'requisitions.lineNotes': 'Note',
  'requisitions.addLine': 'Add a product',
  'requisitions.submit': 'Submit the requisition',
  'requisitions.created': 'Requisition submitted.',
  'requisitions.open': 'Open',
  'requisitions.status.submitted': 'Submitted',
  'requisitions.status.approved': 'Approved',
  'requisitions.status.rejected': 'Refused',
  'requisitions.status.cancelled': 'Withdrawn',
  'requisitions.fulfilment': 'Ordering',
  'requisitions.fulfilment.none': 'Nothing ordered',
  'requisitions.fulfilment.partial': 'Partly ordered',
  'requisitions.fulfilment.full': 'Fully ordered',
  'requisitions.requested': 'Requested',
  'requisitions.ordered': 'Ordered',
  'requisitions.remaining': 'Left to order',
  'requisitions.decisionReason': 'Decision reason',
  'requisitions.rejectReason': 'Reason for refusal',
  'requisitions.rejectReason.hint': 'Required to refuse.',
  'requisitions.approve': 'Approve',
  'requisitions.reject': 'Refuse',
  'requisitions.cancel': 'Withdraw the requisition',
  'requisitions.approved': 'Requisition approved.',
  'requisitions.rejected': 'Requisition refused.',
  'requisitions.cancelled': 'Requisition withdrawn.',
  'requisitions.order.title': 'Order what is left',
  'requisitions.order.supplier': 'Supplier',
  'requisitions.order.quantity': 'Quantity to order',
  'requisitions.order.unitPrice': 'Negotiated price',
  'requisitions.order.submit': 'Create the order',
  'requisitions.order.created': 'Order created.',
  'requisitions.order.complete': 'Everything requested has been ordered.',
  'requisitions.order.supplierRequired': 'Choose the supplier.',
  'requisitions.order.nothing': 'Enter at least one quantity to order.',
  'requisitions.order.priceRequired': 'Enter the price of each ordered line: zero is a price, an empty field is not.',
  'nav.budget': 'Season budget',
  'budget.title': 'Season budget',
  'budget.description': 'What you plan to spend and produce, crop by crop and per hectare — then what actually happened.',
  'budget.season': 'Season',
  'budget.closed': 'This season is closed: its budget can no longer be changed.',
  'budget.forbidden': 'The budget carries amounts: it is reserved for those who can see costs.',
  'budget.noSeason': 'No season. Open one to budget it.',
  'budget.noCrop': 'No crop in this season’s crop plan, and none budgeted.',
  'budget.area': 'Area in the crop plan',
  'budget.plots': 'Plots',
  'budget.notBudgeted': 'No budget for this crop. Adding a line or assumptions budgets it.',
  'budget.notPlanted': 'No plot in the crop plan carries this crop: this budget weighs nothing until it is planned.',
  'budget.assumptions': 'Sales assumptions',
  'budget.expectedYield': 'Expected yield per hectare',
  'budget.yieldUnit': 'Yield unit',
  'budget.forecastPrice': 'Forecast price per unit',
  'budget.saveAssumptions': 'Save assumptions',
  'budget.assumptionsSaved': 'Budget saved.',
  'budget.lines': 'Planned costs per hectare',
  'budget.noLine': 'No cost line.',
  'budget.nature': 'Nature',
  'budget.nature.input': 'Inputs',
  'budget.nature.machine': 'Machinery',
  'budget.nature.labor': 'Labour',
  'budget.nature.other': 'Other charges',
  'budget.label': 'Label',
  'budget.quantityPerHa': 'Quantity per hectare',
  'budget.unit': 'Unit',
  'budget.unitPrice': 'Unit price',
  'budget.costPerHa': 'Cost per hectare',
  'budget.totalPerHa': 'Total per hectare',
  'budget.addLine': 'Add line',
  'budget.lineAdded': 'Line added.',
  'budget.removeLine': 'Remove',
  'budget.lineRemoved': 'Line removed.',
  'budget.remove': 'Remove budget',
  'budget.removed': 'Budget removed.',
  'budget.otherCrop': 'Budget another crop',
  'budget.otherCrop.hint': 'A crop can be budgeted before it is even planned.',
  'budget.otherCrop.submit': 'Budget it',
  'budget.crop': 'Crop',
  'budget.comparison': 'Planned and actual',
  'budget.comparison.empty': 'No budgeted crop: nothing to compare.',
  'budget.post': 'Item',
  'budget.planned': 'Planned',
  'budget.actual': 'Actual',
  'budget.gap': 'Variance',
  'budget.gapPercent': 'Relative variance',
  'budget.post.input': 'Inputs',
  'budget.post.machine': 'Machinery',
  'budget.post.labor': 'Labour',
  'budget.post.other': 'Other charges',
  'budget.post.direct_cost': 'Direct cost',
  'budget.post.yield': 'Production',
  'budget.post.yield_per_ha': 'Yield per hectare',
  'budget.post.revenue': 'Revenue',
  'budget.post.gross_margin': 'Gross margin',
  'budget.total': 'All budgeted crops',
  'budget.unitMismatch': 'A harvest of this crop is weighed in a different unit from the budget’s: actual production is not shown.',
  'budget.unbudgeted': 'Crops without a budget',
  'budget.unbudgeted.hint': 'Their actuals are left out of the total: there is nothing to set against them.',
  'budget.actualDirectCost': 'Actual direct cost',
  'budget.actualRevenue': 'Actual revenue',
  // Lot 8b (migration 0098) : les dettes fournisseurs et leurs règlements.
  // Lot 8c (migration 0099) : l'échéancier.
  'nav.group.treasury': 'Treasury',
  'nav.schedule': 'Schedule',
  'nav.treasuryAccounts': 'Bank and cash',
  'nav.commitments': 'Commitments',
  'nav.reconciliation': 'Bank reconciliation',
  'nav.executive': 'Management',
  'schedule.title': 'Payment schedule',
  'schedule.description': 'What remains to be received and paid, at its date.',
  'schedule.forbidden': 'The schedule is reserved for those who can see amounts.',
  'schedule.asOf': 'Situation as of',
  'schedule.asOf.hint': 'Left empty: today.',
  'schedule.situation': 'Situation of due items',
  'schedule.situation.asOf': 'Ranked as of',
  'schedule.bucket': 'Bucket',
  'schedule.bucket.overdue': 'Overdue',
  'schedule.bucket.0-7': 'Within 7 days',
  'schedule.bucket.8-30': '8 to 30 days',
  'schedule.bucket.31-60': '31 to 60 days',
  'schedule.bucket.61-90': '61 to 90 days',
  'schedule.bucket.90+': 'Beyond 90 days',
  'schedule.bucket.no_due_date': 'No due date',
  'schedule.bucket.total': 'All',
  'schedule.receivable': 'To receive',
  'schedule.payable': 'To pay',
  'schedule.net': 'Net',
  'schedule.treasury': 'Treasury today:',
  'schedule.projected': 'Projected balance',
  'schedule.projected.hint': 'The projected balance is what we would hold at the end of each bracket if everyone paid on their date: today’s treasury, plus what is expected, minus what is owed. What is overdue is counted at once; what has no due date is left out.',
  'schedule.entries': 'Due items',
  'schedule.entries.hint': 'An invoice is listed for what it still owes. An issued cheque or bill stays, at its own due date, until its debit is recorded on the payments screen.',
  'schedule.empty': 'No due item: nothing to receive, nothing to pay.',
  // Lot 8d (migration 0102) : les comptes de banque et de caisse.
  'treasury.title': 'Bank and cash',
  'treasury.description': 'Bank accounts and cash boxes, their balance, and the statement of each.',
  'treasury.forbidden': 'Treasury is reserved for those who can see amounts.',
  'treasury.accounts': 'Accounts',
  'treasury.accounts.empty': 'No account yet: open your bank and your cash box below.',
  'treasury.account': 'Account',
  'treasury.code': 'Code',
  'treasury.name': 'Account name',
  'treasury.kind': 'Kind',
  'treasury.kind.bank': 'Bank',
  'treasury.kind.cash': 'Cash',
  'treasury.bankReference': 'Bank and account number',
  'treasury.bankReference.hint': 'As printed on a statement.',
  'treasury.openingDate': 'Opening date',
  'treasury.openingBalance': 'Opening balance',
  'treasury.openingBalance.hint': 'What the account held that day. An empty account opens at 0, typed in; an overdrawn one, as a negative.',
  'treasury.inflow': 'Inflows',
  'treasury.outflow': 'Outflows',
  'treasury.balance': 'Balance',
  'treasury.amount': 'Amount',
  'treasury.status.open': 'Open',
  'treasury.status.closed': 'Closed',
  'treasury.totals': 'Treasury',
  'treasury.totals.bank': 'In bank',
  'treasury.totals.cash': 'In cash',
  'treasury.totals.total': 'Total treasury',
  'treasury.account.new': 'Open an account',
  'treasury.account.new.hint': 'The opening — balance and date — cannot be corrected: every balance of the account starts from it. A payment dated before the opening is already in that balance: it names the account without moving it. An account entered by mistake can be deleted as long as no payment names it.',
  'treasury.account.create': 'Open the account',
  'treasury.account.created': 'Account opened.',
  'treasury.account.rename': 'Save the name',
  'treasury.account.renamed': 'Account corrected.',
  'treasury.account.close': 'Close',
  'treasury.account.closed': 'Account closed: it keeps its balance and receives nothing more.',
  'treasury.account.reopen': 'Reopen',
  'treasury.account.reopened': 'Account reopened.',
  'treasury.account.delete': 'Delete',
  'treasury.account.deleted': 'Account deleted.',
  'treasury.statement': 'Statement',
  'treasury.statement.open': 'Statement',
  'treasury.statement.date': 'Date',
  'treasury.statement.counterparty': 'Counterparty',
  'treasury.statement.reference': 'Document',
  'treasury.statement.opening': 'Starting balance',
  'treasury.statement.closing': 'Ending balance',
  'treasury.statement.from': 'From',
  'treasury.statement.from.hint': 'Empty: since the account was opened.',
  'treasury.statement.to': 'To',
  'treasury.statement.empty': 'No movement over this period.',
  'treasury.direction': 'Direction',
  'treasury.direction.in': 'In',
  'treasury.direction.out': 'Out',
  'treasury.unassigned': 'Payments to assign',
  'treasury.unassigned.hint': 'Entered before accounts existed, they count in no balance. Once assigned, a payment no longer changes account.',
  'treasury.unassigned.assign': 'Assign',
  'treasury.unassigned.assigned': 'Payment assigned.',
  'treasury.field.bank': 'Bank account',
  'treasury.field.cash': 'Cash box',
  'treasury.field.hint': 'The account this payment moves.',
  'treasury.field.none.bank': 'No open bank account: this payment method requires one.',
  'treasury.field.none.cash': 'No open cash box: cash requires one.',
  'treasury.field.open': 'Open an account',
  'treasury.field.any': 'Bank or cash box',
  'treasury.field.none.any': 'No open account: this payment requires one.',
  // Lot 8d (migration 0104) : les mouvements saisis, et le virement d'un compte à l'autre.
  'treasury.entries': 'Recorded movements',
  'treasury.entries.hint': 'What moves an account without being the payment of an invoice: bank charges, wages, contribution, withdrawal. And a transfer from one account to another — cash deposited at the bank, for instance.',
  'treasury.entry.category': 'Kind',
  'treasury.entry.category.transfer': 'Transfer between accounts',
  'treasury.entry.category.bank_charges': 'Bank charges',
  'treasury.entry.category.interest': 'Interest',
  'treasury.entry.category.owner_contribution': 'Owner contribution',
  'treasury.entry.category.owner_withdrawal': 'Owner withdrawal',
  'treasury.entry.category.salary': 'Wages',
  'treasury.entry.category.rent': 'Rent',
  'treasury.entry.category.insurance': 'Insurance',
  'treasury.entry.category.tax': 'Taxes',
  'treasury.entry.category.other': 'Other',
  'treasury.entry.target': 'Destination account',
  'treasury.entry.target.hint': 'The money leaves the first account and enters this one.',
  'treasury.entry.label': 'Label',
  'treasury.entry.reference': 'Reference',
  'treasury.entry.submit': 'Record the movement',
  'treasury.entry.recorded': 'Movement recorded.',
  'treasury.entry.cancel': 'Cancel',
  'treasury.entry.cancelled': 'Movement cancelled: the account is restored.',
  'treasury.entry.reason': 'Cancellation reason',
  'treasury.entry.reason.hint': 'Required to cancel a movement. A movement is not corrected: it is cancelled.',
  'treasury.entry.status.posted': 'Recorded',
  'treasury.entry.status.cancelled': 'Cancelled',
  // Lot 8d (migration 0105) : les autres engagements — salaires, loyers, assurances, impôts.
  'commitments.title': 'Commitments',
  'commitments.description': 'Wages, rent, insurance, taxes and other charges: what is owed, on its date, and what has been paid.',
  'commitments.forbidden': 'Commitments are reserved for those who can see amounts.',
  'commitments.due': 'To pay',
  'commitments.due.empty': 'No commitment to pay.',
  'commitments.totalDue': 'Remaining to pay:',
  'commitments.dueDate': 'Due date',
  'commitments.category': 'Kind',
  'commitments.category.salary': 'Wages',
  'commitments.category.rent': 'Rent',
  'commitments.category.insurance': 'Insurance',
  'commitments.category.tax': 'Taxes',
  'commitments.category.other': 'Other charge',
  'commitments.label': 'Label',
  'commitments.counterparty': 'Owed to',
  'commitments.counterparty.hint': 'A landlord, an insurer, the tax office. Empty for wages.',
  'commitments.paidAmount': 'Paid',
  'commitments.remaining': 'Remaining',
  'commitments.pay': 'Pay',
  'commitments.payment': 'Amount paid',
  'commitments.paid': 'Payment recorded.',
  'commitments.paidOn': 'Payment day',
  'commitments.cancel': 'Cancel the commitment',
  'commitments.cancelled': 'Commitment cancelled: nothing more is owed.',
  'commitments.reason': 'Cancellation reason',
  'commitments.reason.hint': 'Required to cancel a commitment. What has been paid stays paid.',
  'commitments.new': 'Enter a commitment',
  'commitments.new.hint': 'A payment amount left empty pays all that remains. A series writes as many commitments, each on its date: twelve monthly rents are twelve lines. A commitment is not corrected, it is cancelled.',
  'commitments.firstDueDate': 'Due date — the first, for a series',
  'commitments.count': 'Number of due dates',
  'commitments.count.hint': 'Empty: just one.',
  'commitments.interval': 'Repeats',
  'commitments.interval.monthly': 'Every month',
  'commitments.interval.quarterly': 'Every quarter',
  'commitments.interval.yearly': 'Every year',
  'commitments.create': 'Record the commitment',
  'commitments.created': 'Commitment recorded.',
  'commitments.closed': 'Settled and cancelled',
  'commitments.settlement.settled': 'Settled',
  'commitments.settlement.cancelled': 'Cancelled',
  // Lot 8e (migration 0106) : le rapprochement bancaire — un relevé, et les mouvements qu'on y pointe.
  'reconciliation.title': 'Bank reconciliation',
  'reconciliation.description': 'Enter the bank statement, tick the account movements that appear on it, and read what separates them.',
  'reconciliation.forbidden': 'Bank reconciliation is reserved for those who see amounts.',
  'reconciliation.noBank': 'No bank account: open one to reconcile it.',
  'reconciliation.noBank.link': 'Open an account',
  'reconciliation.account': 'Bank account',
  'reconciliation.statements': 'Statements',
  'reconciliation.statements.empty': 'No statement entered for this account.',
  'reconciliation.statementDate': 'Statement date',
  'reconciliation.reference': 'Reference',
  'reconciliation.reference.hint': 'The statement number, if it has one.',
  'reconciliation.openingBalance': 'Opening balance',
  'reconciliation.closingBalance': 'Closing balance',
  'reconciliation.pointedCount': 'Ticked',
  'reconciliation.gap': 'Gap',
  'reconciliation.state.reconciled': 'Reconciled',
  'reconciliation.state.open': 'To reconcile',
  'reconciliation.chain.broken': 'Does not follow the previous statement',
  'reconciliation.open': 'Open',
  'reconciliation.delete': 'Delete',
  'reconciliation.deleted': 'Statement deleted.',
  'reconciliation.new': 'New statement',
  'reconciliation.new.hint': 'Copy the two balances printed on the statement, even if zero. An overdraft is entered as a negative.',
  'reconciliation.create': 'Enter the statement',
  'reconciliation.created': 'Statement entered.',
  'reconciliation.detail': 'Statement reconciliation',
  'reconciliation.line.date': 'Statement date:',
  'reconciliation.line.opening': 'Opening balance:',
  'reconciliation.line.pointed': 'Ticked movements:',
  'reconciliation.line.pointedBalance': 'Opening balance plus ticked movements:',
  'reconciliation.line.closing': 'Statement closing balance:',
  'reconciliation.line.gap': 'Gap:',
  'reconciliation.reconciled.hint': 'The opening balance plus the ticked movements gives the closing balance: the statement is reconciled.',
  'reconciliation.open.hint': 'Tick the movements that appear on the statement. Whatever appears there and is missing here — charges, overdraft interest, interest — is entered under “Bank and cash”, then ticked.',
  'reconciliation.chain.alert': 'The opening balance does not carry over the previous statement’s closing balance: a statement is missing, or a balance was miscopied. Gap:',
  'reconciliation.correct': 'Correct the statement',
  'reconciliation.correct.hint': 'The date, reference and balances can be corrected without unticking anything. The account cannot.',
  'reconciliation.corrected': 'Statement corrected.',
  'reconciliation.pointed': 'Ticked movements',
  'reconciliation.pointed.empty': 'No movement ticked on this statement.',
  'reconciliation.unpointed': 'Movements to tick',
  'reconciliation.unpointed.hint': 'Every movement of the account that no statement has confirmed yet, whatever its date.',
  'reconciliation.unpointed.empty': 'Every movement of this account is ticked.',
  'reconciliation.source': 'Document',
  'reconciliation.source.customer_payment': 'Receipt',
  'reconciliation.source.supplier_payment': 'Supplier payment',
  'reconciliation.source.lease_payment': 'Lease instalment',
  'reconciliation.source.treasury_entry': 'Entered movement',
  'reconciliation.point': 'Tick',
  'reconciliation.pointedDone': 'Movement ticked.',
  'reconciliation.unpoint': 'Untick',
  'reconciliation.unpointedDone': 'Tick removed.',
  // Lot 9 (migration 0107) : le centre d'alertes.
  'alerts.title': 'Alerts',
  'alerts.description': 'Everything that needs attention, in one place: overdue items, unvalued operations, agronomic warnings, stock, uncontrolled production, lots without origin.',
  'alerts.asOf': 'As of',
  'alerts.total': 'Alerts:',
  'alerts.none': 'Nothing to report.',
  'alerts.family.count': 'Count:',
  'alerts.family.total': 'Total:',
  'alerts.shown': 'Shown:',
  'alerts.shown.of': 'of',
  'alerts.open': 'Open:',
  'alerts.col.dueDate': 'Due date',
  'alerts.col.daysOverdue': 'Days overdue',
  'alerts.col.kind': 'Kind',
  'alerts.col.counterparty': 'Counterparty',
  'alerts.col.reference': 'Document',
  'alerts.col.amount': 'Amount',
  'alerts.col.date': 'Date',
  'alerts.col.workType': 'Work',
  'alerts.col.rule': 'Rule exceeded',
  'alerts.col.subject': 'On',
  'alerts.col.observed': 'Measured',
  'alerts.col.threshold': 'Threshold',
  'alerts.col.product': 'Item',
  'alerts.col.warehouse': 'Warehouse',
  'alerts.col.lot': 'Lot',
  'alerts.col.quantity': 'Quantity',
  'alerts.col.expiryDate': 'Expiry',
  'alerts.col.receivedOn': 'Entered on',
  'alert.payable_overdue': 'Overdue payables',
  'alert.payable_overdue.hint': 'What we owe past its due date: invoices, instruments handed over, lease instalments, commitments.',
  'alert.receivable_overdue': 'Customers late in paying',
  'alert.receivable_overdue.hint': 'What customers owe past its due date.',
  'ops.operation_unvalued': 'Unvalued operations',
  'ops.operation_unvalued.hint': 'Done or validated, with an input, a machine, labour or a charge worth nothing: their cost is incomplete.',
  'ops.warning': 'Agronomic warnings',
  'ops.warning.hint': 'Raised when operations were entered, while their season is open: what was measured, and the threshold exceeded.',
  'stock.lot_uncontrolled': 'Uncontrolled production',
  'stock.lot_uncontrolled.hint': 'Harvests in stock that nobody has controlled yet.',
  'stock.lot_without_origin': 'Lots without origin',
  'stock.lot_without_origin.hint': 'Lots in stock that no receipt, harvest, split, takeover or entry explains.',
  'orchard.window_open': 'Harvest window open',
  'orchard.window_open.hint': 'The species’ regional harvest window is open and nothing has been harvested on the plot yet.',
  'orchard.dar_active': 'Pre-harvest interval running',
  'orchard.dar_active.hint': 'A validated pesticide treatment on the plot: harvest stays blocked until the pre-harvest interval ends.',
  // Lot 9 (migration 0108) : le tableau de bord de direction.
  'executive.title': 'Management dashboard',
  'executive.description': 'The farm’s indicators for a season: areas, costs and margin per hectare, production, stock, receivables, payables, treasury.',
  'executive.forbidden': 'The management dashboard is reserved for those who see amounts.',
  'executive.season': 'Season',
  'executive.season.current': 'Current season',
  'executive.season.none': 'No current season: the season indicators stay silent. Choose one.',
  'executive.asOf': 'As of',
  'executive.readSeason': 'Season read:',
  'executive.areas': 'Areas',
  'executive.farmedArea': 'Farmed area (ha):',
  'executive.cultivatedArea': 'Cultivated area (ha):',
  'executive.perHa': 'Per hectare',
  'executive.perHa.hint': 'Relative to the season’s cultivated area.',
  'executive.costPerHa': 'Average cost:',
  'executive.machineCostPerHa': 'Machine cost:',
  'executive.fuelCostPerHa': 'Fuel cost:',
  'executive.marginPerHa': 'Margin:',
  'executive.production': 'Production',
  'executive.production.empty': 'No harvest in this season.',
  'executive.production.hint': 'The yield is relative to the area of the harvested plots, in the harvest’s unit.',
  'executive.production.product': 'Item',
  'executive.production.quantity': 'Quantity',
  'executive.production.area': 'Harvested area (ha)',
  'executive.production.yield': 'Yield per hectare',
  'executive.fuel': 'Fuel',
  'executive.fuelLiters': 'Fuel used (litres):',
  'executive.stock': 'Stock',
  'executive.stockInput': 'Input stock:',
  'executive.stockProduct': 'Product stock:',
  'executive.money': 'Partners and treasury',
  'executive.receivables': 'Customer receivables:',
  'executive.payables': 'Supplier payables:',
  'executive.treasury': 'Treasury:',
  'executive.silent': 'A dash: the indicator has nothing to be computed from — no season, no cultivated area, no fuel recorded, no open account. It is not zero.',
  'schedule.dueDate': 'Due date',
  'schedule.noDueDate': 'No due date',
  'schedule.overdueDays': 'Days overdue',
  'schedule.direction': 'Direction',
  'schedule.direction.in': 'To receive',
  'schedule.direction.out': 'To pay',
  'schedule.kind': 'Kind',
  'schedule.kind.customer_invoice': 'Customer invoice',
  'schedule.kind.supplier_invoice': 'Supplier invoice',
  'schedule.kind.lease_installment': 'Lease instalment',
  'schedule.kind.supplier_instrument': 'Issued cheque or bill',
  'schedule.kind.commitment_salary': 'Wages',
  'schedule.kind.commitment_rent': 'Rent',
  'schedule.kind.commitment_insurance': 'Insurance',
  'schedule.kind.commitment_tax': 'Taxes',
  'schedule.kind.commitment_other': 'Other charge',
  'schedule.counterparty': 'Counterparty',
  'schedule.reference': 'Document',
  'schedule.amount': 'Amount',
  'nav.payables': 'Payables and payments',
  'payables.title': 'Payables and payments',
  'payables.description': 'What is owed to suppliers, since when — and what gets paid.',
  'payables.forbidden': 'Payables and payments are reserved for those who keep the documents.',
  'payables.aging': 'What is owed to suppliers',
  'payables.empty': 'No approved invoice awaits payment, and no advance awaits allocation.',
  'payables.supplier': 'Supplier',
  'payables.outstanding': 'Outstanding',
  'payables.unallocated': 'Advances to allocate',
  'payables.overpaid': 'Overpaid',
  'payables.net': 'Net',
  'payables.total': 'All suppliers',
  'payables.open': 'Open',
  'payables.openOther': 'Open another supplier',
  'payables.openOther.hint': 'To pay a deposit to a supplier that has no approved invoice yet.',
  'payables.invoices': 'Unsettled approved invoices',
  'payables.invoices.empty': 'This supplier has no unsettled approved invoice.',
  'payables.invoice': 'Invoice',
  'payables.invoiceDate': 'Invoice date',
  'payables.dueDate': 'Due date',
  'payables.noDueDate': 'No due date',
  'payables.setDueDate': 'Set due date',
  'payables.dueDate.saved': 'Due date saved.',
  'payables.invoiced': 'Invoiced',
  'payables.credited': 'Credit notes',
  'payables.paid': 'Paid',
  'payables.remaining': 'Remaining',
  'payables.allocate': 'Allocate',
  'payables.advances': 'Advances to allocate',
  'payables.advances.hint': 'Enter the amounts to allocate in the invoice table, then allocate the advance.',
  'payables.advance.allocate': 'Allocate this advance',
  'payables.advance.allocated': 'Advance allocated.',
  'payables.payment': 'Payment',
  'payables.payment.new': 'New payment',
  'payables.payment.hint': 'The amounts entered in the “Allocate” column say which invoices this payment settles. What is not allocated remains an advance — a deposit, for instance.',
  'payables.payment.submit': 'Record payment',
  'payables.payment.recorded': 'Payment recorded.',
  'payables.payment.cancel': 'Cancel',
  'payables.payment.cancelled': 'Payment cancelled.',
  'payables.payment.clear': 'Debited',
  'payables.payment.cleared': 'Debit recorded: the payment leaves the schedule.',
  'payables.clearedOn': 'Debit day',
  'payables.clearedOn.hint': 'To record that the bank debited a cheque or a bill. Left empty: today.',
  'payables.paymentDate': 'Payment date',
  'payables.method': 'Payment method',
  'payables.method.cash': 'Cash',
  'payables.method.cheque': 'Cheque',
  'payables.method.transfer': 'Bank transfer',
  'payables.method.bill_of_exchange': 'Bill of exchange',
  'payables.method.card': 'Card',
  'payables.method.compensation': 'Offset',
  'payables.reference': 'Reference',
  'payables.instrumentDueDate': 'Cheque or bill due date',
  'payables.instrumentDueDate.hint': 'The day the money will leave: not the payment date.',
  'payables.amount': 'Amount',
  'payables.allocated': 'Allocated',
  'payables.notes': 'Note',
  'payables.payments': 'Payments',
  'payables.payments.empty': 'No payment.',
  'payables.cancelReason': 'Cancellation reason',
  'payables.cancelReason.hint': 'Required to cancel: returned cheque, entry error…',
  'payables.status.issued': 'Issued',
  'payables.status.presented': 'Presented',
  'payables.status.cleared': 'Cleared',
  'payables.status.bounced': 'Bounced',
  'payables.status.cancelled': 'Cancelled',
  'nav.receivables': 'Receivables and receipts',
  'receivables.title': 'Receivables and receipts',
  'receivables.description': 'What customers owe, since when — and what you collect.',
  'receivables.forbidden': 'Receivables and receipts are reserved for those who manage documents.',
  'receivables.aging': 'What customers owe',
  'receivables.empty': 'No customer owes anything, and no advance is waiting to be allocated.',
  'receivables.customer': 'Customer',
  'receivables.outstanding': 'Outstanding',
  'receivables.bucket.not_due': 'Not yet due',
  'receivables.bucket.1-30': '1 to 30 days overdue',
  'receivables.bucket.31-60': '31 to 60 days overdue',
  'receivables.bucket.61-90': '61 to 90 days overdue',
  'receivables.bucket.90+': 'Over 90 days overdue',
  'receivables.bucket.no_due_date': 'No due date',
  'receivables.unallocated': 'Advances to allocate',
  'receivables.overpaid': 'Overpaid',
  'receivables.net': 'Net',
  'receivables.total': 'All customers',
  'receivables.open': 'Open',
  'receivables.openOther': 'Open another customer',
  'receivables.openOther.hint': 'To record an advance from a customer that has no issued invoice yet.',
  'receivables.invoices': 'Unsettled invoices',
  'receivables.invoices.empty': 'This customer has no unsettled invoice.',
  'receivables.invoice': 'Invoice',
  'receivables.invoiceDate': 'Invoice date',
  'receivables.dueDate': 'Due date',
  'receivables.noDueDate': 'No due date',
  'receivables.setDueDate': 'Set due date',
  'receivables.dueDate.saved': 'Due date saved.',
  'receivables.invoiced': 'Invoiced',
  'receivables.credited': 'Credit notes',
  'receivables.paid': 'Received',
  'receivables.remaining': 'Outstanding',
  'receivables.allocate': 'Allocate',
  'receivables.advances': 'Advances to allocate',
  'receivables.advances.hint': 'Enter the amounts to allocate in the invoices table, then allocate the advance.',
  'receivables.advance.allocate': 'Allocate this advance',
  'receivables.advance.allocated': 'Advance allocated.',
  'receivables.payment': 'Receipt',
  'receivables.payment.new': 'New receipt',
  'receivables.payment.hint': 'The amounts entered in the “Allocate” column say which invoices this receipt settles. Whatever is not allocated remains an advance.',
  'receivables.payment.submit': 'Record receipt',
  'receivables.payment.recorded': 'Receipt recorded.',
  'receivables.payment.cancel': 'Cancel',
  'receivables.payment.cancelled': 'Receipt cancelled.',
  'receivables.paymentDate': 'Receipt date',
  'receivables.method': 'Payment method',
  'receivables.reference': 'Reference',
  'receivables.reference.required': 'This payment method requires a reference.',
  'receivables.amount': 'Amount',
  'receivables.allocated': 'Allocated',
  'receivables.notes': 'Note',
  'receivables.payments': 'Receipts',
  'receivables.payments.empty': 'No receipt.',
  'receivables.cancelReason': 'Cancellation reason',
  'receivables.cancelReason.hint': 'Required to cancel: bounced cheque, entry error…',
  'receivables.status.posted': 'Recorded',
  'receivables.status.cancelled': 'Cancelled',
  'references.origin': 'Origin',
  'references.origin.catalog': 'Catalog',
  'references.origin.own': 'Own',
  'references.status.active': 'Active',
  'references.status.inactive': 'Inactive',
  'references.status.hidden': 'Hidden',
  'references.renamed': 'Renamed',
  'references.search': 'Search in this reference',
  'references.showInactive': 'Show inactive and hidden entries',
  'references.empty': 'No entry in this reference.',
  'references.fiche': 'Details',
  'references.baseName': 'Delivered label',
  'references.effectiveName': 'Displayed name',
  'references.arabicLabel': 'Arabic label',
  'references.englishLabel': 'English label',
  'references.crop': 'Crop',
  'references.maturityDays': 'Maturity (days)',
  'references.certified': 'Certified seed',
  'references.referencesOriginField': 'Provenance',
  'references.activate': 'Activate',
  'references.deactivate': 'Deactivate',
  'references.activated': 'Entry activated.',
  'references.deactivated': 'Entry deactivated.',
  'references.overrideTitle': 'Catalog override',
  'references.overrideHint': 'The delivered line stays identical for every client: your label is displayed instead of it, without replacing it.',
  'references.overrideSave': 'Save override',
  'references.overrideSaved': 'Override saved.',
  'references.newEntry': 'Add an entry',
  'references.create': 'Create entry',
  'references.creating': 'Creating…',
  'references.created': 'Entry added to your reference data.',

  'stock.balance.insufficient_quantity': 'Insufficient stock: the issue exceeds what the warehouse holds.',
  'harvest.batch.net_exceeds_gross': 'Net cannot exceed gross: was the entry reversed?',
  'purchase.receipt.over_delivery': 'Over-delivery beyond the subscription tolerance.',
  'purchase.receipt.order_required': 'Receipt without order forbidden (V16): create a purchase order first.',
  'purchase.invoice.requires_receipt': 'Invoice without receipt forbidden: match a receipt first.',
  'purchase.invoice_match.no_counterpart': 'Invoice line without order nor receipt: nothing to match.',
  'purchase.invoice_match.variance_reason_required': 'Variance detected: a reason is required to accept it.',
  // Codes frappés par le lot 8b, côté gestionnaires comme côté déclencheurs.
  'purchase.invoice_match.rejection_reason_required': 'Rejecting an invoice requires a reason.',
  'purchase.invoice_match.no_lines': 'This invoice has no line to match.',
  'purchase.invoice_match.quantity_must_be_positive': 'A line invoices a zero or negative quantity.',
  'purchase.invoice_match.not_found': 'No matching for this invoice.',
  'purchase.supplier_invoice.not_found': 'Supplier invoice not found.',
  'purchase.supplier_invoice.not_an_invoice': 'A credit note is not matched and has no due date.',
  'purchase.supplier_invoice.already_decided': 'This invoice is already decided: it cannot be decided again.',
  'purchase.supplier_invoice.due_before_invoice': 'An invoice is not due before its date.',
  'purchase.supplier_invoice.text_too_long': 'An invoice decision reason holds 300 characters.',
  'purchase.supplier_invoice.immutable': 'This invoice is no longer a draft: it cannot be rewritten.',
  'purchase.supplier_invoice.match_undecided': 'This invoice has no matching decided in its favour: it cannot be approved.',
  'purchase.supplier_invoice.no_lines': 'An invoice without lines invoices nothing.',
  'purchase.supplier_invoice.quantity_must_be_positive': 'Each invoice line must carry a strictly positive quantity.',
  'purchase.supplier_invoice.negative_amount': 'Neither a price nor a tax can be negative: what reduces an invoice is a credit note.',
  'purchase.supplier_invoice.duplicate_product': 'A product appears only once on an invoice: group its quantities.',
  'purchase.supplier_invoice.date_in_future': 'An invoice cannot be dated in the future.',
  'purchase.supplier_invoice.reference_too_long': 'The supplier invoice number holds 50 characters.',
  'purchase.supplier_invoice.order_not_found': 'Order not found.',
  'purchase.supplier_invoice.order_of_another_supplier': 'This order was placed with another supplier.',
  'purchase.supplier_invoice.nothing_to_invoice': 'An invoiced product has nothing received on this order that is not already invoiced.',
  'purchase.payment.amount_must_be_positive': 'A payment amount must be strictly positive.',
  'purchase.payment.unknown_method': 'A payment is made in cash, by cheque, transfer, bill of exchange, card or offset.',
  'purchase.payment.due_date_required': 'A cheque or a bill of exchange comes with its due date.',
  'purchase.payment.date_in_future': 'A payment cannot be dated in the future.',
  'purchase.payment.text_too_long': 'A payment reference holds 50 characters, its note or cancellation reason 300.',
  'purchase.payment.supplier_not_found': 'Supplier not found.',
  'purchase.payment.allocation_invalid': 'An allocation must be a strictly positive amount.',
  'purchase.payment.allocation_duplicate': 'An invoice can only be allocated once per payment.',
  'purchase.payment.over_allocated': 'The allocations exceed what the payment is still worth.',
  'purchase.payment.invoice_not_payable': 'A designated invoice is not an approved invoice of this supplier.',
  'purchase.payment.exceeds_remaining': 'An allocation exceeds what the invoice still owes, credit notes deducted.',
  'purchase.payment.not_found': 'Payment not found.',
  'purchase.payment.cancelled': 'This payment is cancelled: it no longer settles anything.',
  'purchase.payment.already_cancelled': 'This payment is already cancelled.',
  'purchase.payment.cancellation_reason_required': 'Cancelling a payment requires a reason.',
  'purchase.payment.partner_mismatch': 'The payment and the invoice are not from the same supplier.',
  'purchase.payment.allocation_immutable': 'An allocation cannot be changed or removed: cancel the payment instead.',
  'purchase.payment.immutable': 'A payment cannot be rewritten: it is cancelled.',
  'purchase.payment.already_cleared': 'This payment is already debited.',
  'purchase.payment.not_an_instrument': 'Only a cheque or a bill of exchange can be declared debited.',
  'purchase.payment.cleared_before_payment': 'A cheque is not debited before it is issued.',
  // Codes frappés par le lot 8d, côté gestionnaires comme côté déclencheurs.
  'finance.treasury.account_required': 'A payment that moves money names its bank account or cash box.',
  'finance.treasury.account_not_applicable': 'This payment method moves no account; it names none.',
  'finance.treasury.account_not_found': 'Treasury account not found.',
  'finance.treasury.account_kind_mismatch': 'Cash goes through a cash box; a cheque, a transfer, a bill of exchange or a card, through a bank.',
  'finance.treasury.account_inactive': 'This account is closed: it neither receives nor pays any more.',
  'finance.treasury.account_immutable': 'The kind and opening of an account cannot be rewritten, nor the account of a payment.',
  'finance.treasury.account_in_use': 'This account has been used: it is closed, not deleted.',
  'finance.treasury.code_required': 'A treasury account has a code.',
  'finance.treasury.name_required': 'A treasury account has a name.',
  'finance.treasury.unknown_kind': 'A treasury account is a bank or a cash box.',
  'finance.treasury.opening_balance_required': 'The opening balance must be entered, even if zero: every balance of the account starts from it.',
  'finance.treasury.cash_cannot_open_negative': 'A cash box does not open with a negative balance.',
  'finance.treasury.opening_in_future': 'An account does not open at a future date.',
  'finance.treasury.code_taken': 'This code already names a treasury account.',
  'finance.treasury.text_too_long': 'An account code fits in 30 characters, its name in 120, its bank reference in 60.',
  'finance.treasury.period_invalid': 'A period does not end before it starts.',
  'finance.treasury.nothing_to_assign': 'This payment is not to be assigned: unknown, cancelled, or already on an account.',
  'finance.treasury.unknown_source': 'Only a receipt, a payment or an instalment payment can be assigned to an account.',
  'finance.treasury.unknown_category': 'The kind of a treasury movement comes from the list.',
  'finance.treasury.unknown_direction': 'A treasury movement goes in or out.',
  'finance.treasury.entry_amount_must_be_positive': 'The amount of a treasury movement is strictly positive.',
  'finance.treasury.entry_in_future': 'A treasury movement cannot be dated in the future.',
  'finance.treasury.label_required': 'A treasury movement has a label.',
  'finance.treasury.entry_text_too_long': 'A movement label fits in 200 characters, its reference in 60, its cancellation reason in 300.',
  'finance.treasury.transfer_target_required': 'A transfer names the account the money enters.',
  'finance.treasury.transfer_same_account': 'An account cannot transfer to itself.',
  'finance.treasury.cancellation_reason_required': 'Cancelling a treasury movement requires a reason.',
  'finance.treasury.entry_not_found': 'Treasury movement not found.',
  'finance.treasury.entry_already_cancelled': 'This treasury movement is already cancelled.',
  'finance.treasury.entry_immutable': 'A treasury movement is neither rewritten nor deleted: it is cancelled, and that cannot be undone.',
  'finance.commitment.unknown_category': 'A commitment is wages, rent, insurance, a tax or another charge.',
  'finance.commitment.label_required': 'A commitment has a label.',
  'finance.commitment.text_too_long': 'A commitment label and its counterparty fit in 200 characters, its cancellation reason in 300.',
  'finance.commitment.amount_must_be_positive': 'The amount of a commitment is strictly positive.',
  'finance.commitment.due_date_required': 'A commitment has a due date.',
  'finance.commitment.invalid_count': 'A series has 1 to 60 due dates.',
  'finance.commitment.unknown_interval': 'A series repeats every month, every quarter or every year.',
  'finance.commitment.not_found': 'Commitment not found.',
  'finance.commitment.not_payable': 'Nothing more is owed on this commitment: it is settled, or cancelled.',
  'finance.commitment.payment_must_be_positive': 'The payment of a commitment is strictly positive.',
  'finance.commitment.exceeds_remaining': 'This payment exceeds what the commitment still owes.',
  'finance.commitment.payment_mismatch': 'The payment of a commitment leaves an account, and is of its kind.',
  'finance.commitment.cancellation_reason_required': 'Cancelling a commitment requires a reason.',
  'finance.commitment.already_cancelled': 'This commitment is already cancelled.',
  'finance.commitment.immutable': 'A commitment is neither rewritten nor deleted: it is cancelled, and that cannot be undone.',
  'finance.reconciliation.account_not_bank': 'This account is a cash box: a cash box is counted, not reconciled.',
  'finance.reconciliation.statement_date_required': 'A bank statement has a date: the day it stops.',
  'finance.reconciliation.statement_in_future': 'A bank statement cannot be dated in the future.',
  'finance.reconciliation.balance_required': 'The opening and closing balances must be entered, even if zero: the gap depends on them.',
  'finance.reconciliation.reference_too_long': 'A statement reference holds 60 characters.',
  'finance.reconciliation.statement_exists': 'This account already has a statement at this date.',
  'finance.reconciliation.statement_not_found': 'Bank statement not found.',
  'finance.reconciliation.statement_has_marks': 'Movements are ticked on this statement: untick them before deleting it.',
  'finance.reconciliation.statement_immutable': 'A statement does not change account: delete it and enter it again.',
  'finance.reconciliation.movement_not_found': 'This document is not a movement of this statement’s account.',
  'finance.reconciliation.already_pointed': 'This movement is already ticked on a statement.',
  'finance.reconciliation.not_pointed': 'This movement is not ticked on this statement.',
  'finance.reconciliation.movement_pointed': 'This document is ticked on a bank statement: untick it before cancelling it.',
  'finance.reconciliation.mark_immutable': 'A tick is not rewritten: remove it and make it again.',
  'purchase.order.not_approved': 'Order not approved: no receipt allowed.',
  'core.capability_denied': 'Access denied: your role does not allow this operation.',
  'operations.operation.not_planned': 'Operation already validated: reverse it instead of consuming.',
  'users.create.admin_only': 'Only an administrator creates users.',

  'farms.detail.title': 'Farm details',
  'farms.detail.back': 'Back to list',
  'farms.create.title': 'New farm',
  'farms.create.submit': 'Create',
  'farms.create.creating': 'Creating…',
  'farms.firstPlot.title': 'First plot',
  'farms.firstPlot.hint':
    'A farm is only active with at least one plot. Left empty, it is saved as a draft and only appears when inactive farms are shown.',
  'farms.firstPlot.code': 'Plot code',
  'farms.firstPlot.name': 'Plot name',
  'farms.firstPlot.area': 'Cadastral area (ha)',
  'farms.firstPlot.irrigable': 'Irrigable plot',
  'farms.firstPlot.incomplete': 'To create the first plot, its code and area are required.',
  'farms.description': 'Description',
  'farms.owner': 'Land owner',
  'farms.address': 'Address',
  'farms.draft': 'Draft',
  'farms.created': 'Farm created.',
  'farms.location': 'Location',
  'farms.latitude': 'Latitude',
  'farms.longitude': 'Longitude',
  'farms.map.open': 'Open in OpenStreetMap',
  'farms.fields.codeHint': 'Unique code, upper-cased.',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.cancel': 'Cancel',
  'common.details': 'Details',
  'common.actions': 'Actions',

  'plots.detail.title': 'Plot details',
  'plots.detail.back': 'Back to list',
  'plots.create.title': 'New plot',
  'plots.create.submit': 'Create plot',
  'plots.create.creating': 'Creating…',
  'plots.created': 'Plot created.',
  'plots.fields.farm': 'Farm',
  'plots.fields.soilDepth': 'Soil depth (cm)',
  'plots.fields.slope': 'Average slope (%)',
  'plots.fields.parent': 'Parent plot',
  'plots.yields.title': 'Season yields',
  'plots.yields.brut': 'Gross yield',
  'plots.yields.net': 'Net yield',
  'plots.yields.normalized': 'Normalized yield (14%)',
  'plots.yields.perHa': 'Yield per ha',
  'plots.yields.comparison': 'Comparison',
  'plots.yields.farmAvg': 'Farm average',
  'plots.yields.seasonAvg': 'Season average',
  'plots.yields.deviation': 'Deviation',
  'plots.location.title': 'Location',
  'plots.map.view': 'View plot on map',
  'plots.mapLabel': 'Plot location map',
  'plots.mapHint': 'Click the map to place the plot; the latitude and longitude fields stay editable for precise field surveys.',
  'plots.pickOnMap': 'Pick on the map',
  'plots.latitude': 'Latitude',
  'plots.longitude': 'Longitude',

  'campaigns.title': 'Campaigns',
  'campaigns.description': 'Each campaign carries a code, a name, a normalized group and its own dates, and always belongs to a plot.',
  'campaigns.list': 'Campaigns list',
  'campaigns.code': 'Code',
  'campaigns.name': 'Name',
  'campaigns.period': 'Period',
  'campaigns.plot': 'Plot',
  'campaigns.group': 'Group',
  'campaigns.invalidPeriod': 'The campaign end date must come after its start date.',
  'campaigns.outsidePeriod': 'The campaign dates must fall within those of the period:',
  'campaigns.status': 'Status',
  'campaigns.status.planned': 'Planned',
  'campaigns.status.sown': 'Sown',
  'campaigns.status.growing': 'Growing',
  'campaigns.status.harvested': 'Harvested',
  'campaigns.status.closed': 'Closed',
  'campaigns.status.abandoned': 'Abandoned',
  'campaigns.current': 'Current',
  'campaigns.closed': 'Closed',
  'campaigns.empty': 'No campaign.',
  'campaigns.create.title': 'New campaign',
  'campaigns.create.submit': 'Create campaign',
  'campaigns.created': 'Campaign created.',
  'campaigns.plan.title': 'Cropping plan',
  'campaigns.plan.empty': 'No plot-season for this filter.',
  'campaigns.plan.assign': 'Assign crop',
  'campaigns.plan.crop': 'Crop',
  'campaigns.plan.sownArea': 'Sown area (ha)',
  'campaigns.plan.assigned': 'Cropping saved.',

  'campaignGroups.title': 'Campaign groups',
  'campaignGroups.description': 'The normalized name that keeps "Tomato", "tomatoes" and "TOMATO" from scattering into three different groups.',
  'campaignGroups.empty': 'No campaign group.',

  'normCategories.title': 'Norm categories',
  'normCategories.description': 'The norm classification — quality, phytosanitary, agriculture, environment, safety, production — shared across the whole company.',
  'normCategories.empty': 'No norm category.',

  'norms.title': 'Norms',
  'norms.description': 'A norm is an evaluable comparison — thresholds, operators, optional unit — never a sentence: the displayed expression is composed by the database, never typed.',
  'norms.empty': 'No norm.',
  'norms.name': 'Name',
  'norms.category': 'Category',
  'norms.parameter': 'Observed parameter',
  'norms.kind': 'Form',
  'norms.minValue': 'Minimum threshold',
  'norms.maxValue': 'Maximum threshold',
  'norms.exactValue': 'Exact value',
  'norms.tolerance': 'Tolerance',
  'norms.inclusive': 'Inclusive bounds',
  'norms.expression': 'Expression',
  'norms.expressionHint': 'Composed automatically from the thresholds: it is never typed.',
  'norms.create.title': 'New norm',
  'norms.create.submit': 'Create norm',
  'norms.created': 'Norm created.',

  'normKinds.minimum': 'Minimum',
  'normKinds.maximum': 'Maximum',
  'normKinds.range': 'Range',
  'normKinds.exact': 'Exact value',
  'normKinds.qualitative': 'Qualitative',

  'verdicts.compliant': 'Compliant',
  'verdicts.non_compliant': 'Non-compliant',
  'verdicts.not_measured': 'Not measured',
  'verdicts.not_applicable': 'Not applicable',

  'campaignNorms.title': 'Applied norms',
  'campaignNorms.empty': 'No norm applied to this campaign.',
  'campaignNorms.norm': 'Norm',
  'campaignNorms.observedValue': 'Observed value',
  'campaignNorms.appliedOn': 'Applied on',
  'campaignNorms.comment': 'Comment',
  'campaignNorms.verdict': 'Verdict',
  'campaignNorms.apply.title': 'Apply a norm',
  'campaignNorms.apply.submit': 'Apply',
  'campaignNorms.applied': 'Norm applied.',
  'campaignNorms.remove': 'Remove',
  'campaignNorms.removeTitle': 'Remove this norm',
  'campaignNorms.removeMessage': 'The norm will no longer apply to this campaign. The value already recorded will no longer appear.',
  'campaignNorms.removed': 'Norm removed.',

  'purchases.title': 'Purchase orders',
  'purchases.description': 'Order → receipt without leaving the screen: remaining updates with each line.',
  'purchases.list.empty': 'No purchase order.',
  'purchases.search': 'Search',
  'purchases.new': 'New order',
  'purchases.code': 'Number',
  'purchases.partner': 'Supplier',
  'purchases.status': 'Status',
  'purchases.status.draft': 'Draft',
  'purchases.status.approved': 'Approved',
  'purchases.status.received': 'Received',
  'purchases.status.cancelled': 'Cancelled',
  'purchases.total': 'Total excl. tax',
  'purchases.detail.title': 'Order details',
  'purchases.detail.back': 'Back',
  'purchases.lines': 'Lines',
  'purchases.lines.product': 'Product',
  'purchases.lines.quantity': 'Quantity',
  'purchases.lines.unitPrice': 'Unit price',
  'purchases.lines.received': 'Received',
  'purchases.lines.remaining': 'Remaining',
  'purchases.lines.total': 'Line total',
  'purchases.approve': 'Approve order',
  'purchases.approving': 'Approving…',
  'purchases.approved': 'Order approved.',
  'purchases.cancel': 'Cancel order',
  'purchases.cancelReason': 'Cancellation reason',
  'purchases.cancelled': 'Order cancelled.',
  'purchases.receive.title': 'Receipt',
  'purchases.receive.warehouse': 'Receiving warehouse',
  'purchases.receive.quantityArrived': 'Quantity arrived',
  'purchases.receive.rejected': 'Rejected quantity',
  'purchases.receive.submit': 'Save receipt',
  'purchases.receive.receiving': 'Receiving…',
  'purchases.received': 'Receipt saved.',
  'purchases.update': 'Save header',
  'purchases.updated': 'Order updated.',
  'purchases.create.title': 'New order',
  'purchases.create.submit': 'Create order',
  'purchases.created': 'Order created.',

  'dashboard.title': 'Dashboard',
  'dashboard.description': 'Campaign indicators, costs by item, margins and comparison between plots.',
  'dashboard.filters': 'Filters',
  'dashboard.season': 'Season',
  'dashboard.farm': 'Farm',
  'dashboard.level': 'Level',
  'dashboard.level.plot': 'Plot',
  'dashboard.level.farm': 'Farm',
  'dashboard.level.crop': 'Crop',
  'dashboard.level.season': 'Season',
  'dashboard.loading': 'Loading…',
  'dashboard.kpis.totalArea': 'Total area (ha)',
  'dashboard.kpis.directCost': 'Direct cost',
  'dashboard.kpis.netCost': 'Net cost',
  'dashboard.kpis.revenue': 'Revenue',
  'dashboard.kpis.margin': 'Margin',
  'dashboard.kpis.marginRate': 'Margin rate',
  'dashboard.kpis.avgCostPerHa': 'Avg cost / ha',
  'dashboard.kpis.avgMarginPerHa': 'Margin / ha',
  'dashboard.kpis.yieldTotal': 'Total yield (q)',
  'dashboard.costByPost': 'Costs by item',
  'dashboard.costByPost.input': 'Inputs',
  'dashboard.costByPost.machine': 'Machinery',
  'dashboard.costByPost.labor': 'Labor',
  'dashboard.costByPost.other': 'Other',
  'dashboard.comparison': 'Comparison between plots',
  'dashboard.comparison.empty': 'No data to compare.',
  'dashboard.table.label': 'Label',
  'dashboard.table.area': 'Area',
  'dashboard.table.cost': 'Net cost',
  'dashboard.table.margin': 'Margin',
  'dashboard.table.yield': 'Yield',
  'dashboard.refresh': 'Refresh',

  'exports.title': 'Exports',
  'exports.description': 'Full reversible export (§16.5): UTF-8 CSV + manifest, reloadable on a fresh DB.',
  'exports.download': 'Download ZIP export',
  'exports.downloading': 'Generating…',
  'exports.downloaded': 'Export downloaded.',
  'exports.manifest': 'Manifest preview',


  // --- Plan d'assolement (§7.3) ---
  'assolement.title': 'Cropping plan',
  'assolement.description': 'Assign a crop to many plots in one go.',
  'assolement.season': 'Season',
  'assolement.farm': 'Farm',
  'assolement.allFarms': 'All farms',
  'assolement.crop': 'Crop',
  'assolement.variety': 'Variety',
  'assolement.unassigned': 'Plots still free this season',
  'assolement.empty': 'No free plot: everything is already assigned this season.',
  'assolement.selectAll': 'Select all',
  'assolement.clearAll': 'Clear selection',
  'assolement.selected': 'selected',
  'assolement.area': 'Sown area (ha)',
  'assolement.areaHint': 'Left empty: each plot is sown on its own area.',
  'assolement.assign': 'Assign crop',
  'assolement.assigning': 'Assigning…',
  'assolement.chooseSeason': 'Choose a season to see the free plots.',
  'assolement.chooseCrop': 'Choose a crop before assigning.',
  'assolement.result': 'Result',
  'assolement.result.succeeded': 'assigned',
  'assolement.result.failed': 'refused',
  'assolement.result.area': 'hectares assigned',
  'assolement.irrigable': 'Irrigable',

  // --- Reprise des données (L12-04) ---
  'reprise.title': 'Data takeover',
  'reprise.description': 'Collect, clean, load, check — the client gets their data back.',
  'reprise.kind': 'Kind',
  'reprise.kind.plot': 'Plots',
  'reprise.kind.product': 'Products',
  'reprise.kind.partner': 'Partners',
  'reprise.file': 'CSV file',
  'reprise.template': 'Expected columns',
  'reprise.analyse': '1. Analyse (no writing)',
  'reprise.analysing': 'Analysing…',
  'reprise.load': '2. Load the accepted rows',
  'reprise.loading': 'Loading…',
  'reprise.rowCount': 'Rows read',
  'reprise.accepted': 'Rows accepted',
  'reprise.loaded': 'Rows loaded',
  'reprise.rejected': 'Rows refused',
  'reprise.duplicates': 'Duplicates',
  'reprise.orphans': 'Orphans',
  'reprise.issues': 'What was refused',
  'reprise.line': 'Line',
  'reprise.history': 'Previous takeovers',
  'reprise.empty': 'No takeover recorded.',
  'reprise.noFile': 'Choose a file before analysing.',
  'reprise.status.analysed': 'Analysed',
  'reprise.status.loaded': 'Loaded',
  'reprise.status.failed': 'Failed',

  // --- Utilisateurs et portées (§4.3) ---
  'users.title': 'Users',
  'users.description': 'Accounts, roles and scopes. Only an administrator creates them.',
  'users.create': 'Create a user',
  'users.email': 'Email address',
  'users.fullName': 'Full name',
  'users.role': 'Role',
  'users.password': 'Password',
  'users.active': 'Active',
  'users.inactive': 'Disabled',
  'users.list': 'List',
  'users.empty': 'No user.',
  'users.created': 'User created.',
  'users.updated': 'Changes saved.',
  'users.detail': 'User record',
  'users.lastSignIn': 'Last sign-in',
  'users.never': 'Never',
  'users.locale': 'Language',
  'users.scopes': 'Scopes',
  'users.scopes.farms': 'Farms',
  'users.scopes.activities': 'Activities',
  'users.scopes.all': 'No scope set: access to everything the company owns.',
  'users.scopes.restricted': 'Access restricted to the ticked farms.',
  'users.scopes.save': 'Save scopes',
  'users.scopes.saved': 'Scopes saved.',
  'users.capabilities': 'What this role allows',
  'users.role.admin': 'Administrator',
  'users.role.manager': 'Farm manager',
  'users.role.accountant': 'Accountant',
  'users.role.operator': 'Operator',
  'users.role.viewer': 'Viewer',

  // --- Profil ---
  'profile.title': 'My profile',
  'profile.description': 'Your details, your role and your rights.',
  'profile.organization': 'Company',
  'profile.capabilities': 'Your rights',
  'profile.capabilities.hint': 'Enforced by the server: a forbidden route answers 403, never truncated amounts.',
  'profile.granted': 'granted',
  'profile.denied': 'denied',
  'profile.password.title': 'Change my password',
  'profile.password.current': 'Current password',
  'profile.password.new': 'New password',
  'profile.password.confirm': 'Confirm the new password',
  'profile.password.rule': 'At least ten characters.',
  'profile.password.submit': 'Change password',
  'profile.password.pending': 'Changing…',
  'profile.password.mismatch': 'The two entries do not match.',
  'profile.password.other_sessions': 'Your other sessions can no longer be extended and will have to sign in again; a device already open may stay open until its current session expires. This one stays open.',
  'profile.password.changed': 'Password changed. Your other sessions can no longer be extended; a device already open may stay open until its current session expires.',
  'capability.view_costs': 'See costs, prices and margins',
  'capability.record_work': 'Record operations, harvests and stock movements',
  'capability.manage_documents': 'Enter and approve purchase and sales documents',
  'capability.manage_master_data': 'Manage reference data, farms and plots',
  'capability.administer': 'Manage users, roles and the subscription',

  // --- Avoirs fournisseur (L4-11) ---
  'nav.invoices': 'Sales invoices',
  'invoices.description': 'Sales invoices and credit notes. A credit note corrects with negative lines; it never rewrites the original.',
  // Migration 0101 : émettre une facture client depuis ce qui est livré et pas encore facturé.
  'invoicing.title': 'Issue an invoice',
  'invoicing.hint': 'An invoice takes up delivered delivery notes that no invoice has taken up. Tick those of this customer it covers; tax, totals and number are set on issue.',
  'invoicing.customer': 'Customer',
  'invoicing.nothing': 'Nothing delivered to this customer that is not already invoiced.',
  'invoicing.pick': 'Invoice',
  'invoicing.deliveryDate': 'Delivery date',
  'invoicing.lines': 'Delivered products',
  'invoicing.total': 'Left to invoice, excl. tax',
  'invoicing.date': 'Invoice date',
  'invoicing.dueDate': 'Agreed due date',
  'invoicing.dueDate.hint': 'Optional: without it, the invoice has “no due date”.',
  'invoicing.taxRate': 'Tax rate',
  'invoicing.taxRate.none': 'The deliveries’ own',
  'invoicing.notes': 'Note',
  'invoicing.submit': 'Issue invoice',
  'invoicing.created': 'Invoice issued:',
  'invoices.empty': 'No sales invoice.',
  'invoices.lineId': 'Invoiced line',
  'invoices.creditHint': 'The credit note names the original line and its reason: that is what makes reconciliation possible six months later.',
  'credit.title': 'Supplier credit note',
  'credit.reason': 'Reason',
  'credit.reason.return': 'Goods returned',
  'credit.reason.damaged': 'Goods damaged',
  'credit.reason.billing_error': 'Billing error',
  'credit.reason.rebate': 'Rebate granted afterwards',
  'credit.warehouse': 'Warehouse the goods leave from',
  'credit.warehouseHint': 'Required for a return or damaged goods: only those leave the stock.',
  'credit.lines': 'Credit note lines',
  'credit.create': 'Issue the credit note',
  'credit.created': 'Credit note issued.',
  'credit.movesStock': 'The goods left the stock.',
  'credit.valueOnly': 'No stock movement: only the value changes.',
  'invoices.kind': 'Kind',
  'invoices.kind.invoice': 'Invoice',
  'invoices.kind.credit_note': 'Credit note',
  'invoices.paid': 'Paid',
  'matching.title': 'Three-way matching',
  'matching.product': 'Product',
  'matching.ordered': 'Ordered',
  'matching.received': 'Received',
  'matching.invoiced': 'Invoiced',
  'matching.variance': 'Variance',
  'matching.empty': 'No matching for this invoice.',
  // Lot 8b : la décision qui fait d'une facture fournisseur une dette, ou un litige.
  'matching.status.pending': 'Awaiting decision',
  'matching.status.matched': 'Matched',
  'matching.status.variance_accepted': 'Variance accepted',
  'matching.status.rejected': 'Rejected',
  'matching.quantityVariance': 'Quantity variance',
  'matching.valueVariance': 'Price variance',
  'matching.reason': 'Reason',
  'invoices.status.draft': 'To decide',
  'invoices.status.approved': 'Approved',
  'invoices.status.disputed': 'Disputed',
  'invoices.payablesHint': 'What is owed and what gets paid live on the screen:',
  'invoices.decision.title': 'Decide this invoice',
  'invoices.decision.clean': 'Everything matches the receipt: the invoice can be approved.',
  'invoices.decision.variance': 'A variance was found against the receipt: approving it requires a written reason.',
  'invoices.decision.blocked': 'This invoice cannot be approved, only rejected:',
  'invoices.decision.dueDate': 'Invoice due date',
  'invoices.decision.dueDate.hint': 'Optional: it can be set or moved later.',
  'invoices.decision.varianceReason': 'Reason for accepting the variance',
  'invoices.decision.approve': 'Approve invoice',
  'invoices.decision.approved': 'Invoice approved: it is now a debt.',
  'invoices.decision.rejectReason': 'Rejection reason',
  'invoices.decision.rejectReason.hint': 'Required to reject: the invoice will be disputed.',
  'invoices.decision.reject': 'Reject invoice',
  'invoices.decision.rejected': 'Invoice rejected: it is disputed.',
  // Migration 0100 : saisir une facture fournisseur depuis ce qui est reçu et pas encore facturé.
  'invoices.entry.title': 'Enter a supplier invoice',
  'invoices.entry.hint': 'An invoice is entered from what is received and not yet invoiced. The proposed quantities and prices are those of the receipt: correct them from the supplier’s invoice — any variance will be found at approval.',
  'invoices.entry.supplier': 'Supplier',
  'invoices.entry.order': 'Received order',
  'invoices.entry.noOrder': 'Nothing received from this supplier that is not already invoiced.',
  'invoices.entry.product': 'Product',
  'invoices.entry.received': 'Received, not yet invoiced',
  'invoices.entry.receiptPrice': 'Receipt price',
  'invoices.entry.quantity': 'Invoiced quantity',
  'invoices.entry.unitPrice': 'Invoiced price',
  'invoices.entry.lineHint': 'Clear the quantity of a product this invoice does not carry.',
  'invoices.entry.reference': 'Supplier invoice number',
  'invoices.entry.date': 'Invoice date',
  'invoices.entry.dueDate': 'Agreed due date',
  'invoices.entry.dueDate.hint': 'Optional: it can also be given at approval.',
  'invoices.entry.tax': 'Tax amount',
  'invoices.entry.submit': 'Record invoice',
  'invoices.entry.created': 'Invoice recorded, awaiting decision:',

  // --- Articles ---
  'products.title': 'Products',
  'products.description': 'Product record, valuation method and price history.',
  'products.search': 'Search a product',
  'products.code': 'Code',
  'products.name': 'Designation',
  'products.type': 'Family',
  'products.unit': 'Unit',
  'products.valuation': 'Valuation method',
  'products.valuation.weighted_average': 'Weighted average cost',
  'products.valuation.fifo': 'FIFO by lot',
  'products.valuation.lifo': 'LIFO — last in, first out',
  'products.valuation.standard': 'Standard cost',
  'products.valuation.last_purchase': 'Last purchase price',
  'products.valuationHint': 'Set at creation (V20: weighted average by default).',
  'products.standardCost': 'Standard cost',
  'products.stockManaged': 'Stock managed',
  'products.lotTracked': 'Lot tracked',
  'products.trackingFromFamily': 'Stock tracking comes from the chosen family.',
  'products.outOfStockFamily': 'Non-stock family: neither valuation nor lot tracking.',
  'products.create': 'Create a product',
  'products.created': 'Product created.',
  'products.empty': 'No product.',
  'products.detail': 'Product record',
  'products.prices': 'Price history',
  'products.prices.partner': 'Partner',
  'products.prices.price': 'Unit price',
  'products.prices.from': 'From',
  'products.prices.to': 'Until',
  'products.prices.add': 'Add a price',
  'products.prices.empty': 'No price recorded.',

  // --- Récolte ---
  'harvests.title': 'Harvest',
  'harvests.description': 'Harvested batches, gross and net weight, and unsold valuation.',
  'harvests.plotSeason': 'Plot-season',
  'harvests.product': 'Harvested product',
  'harvests.gross': 'Gross weight',
  'harvests.net': 'Net weight',
  'harvests.moisture': 'Moisture (%)',
  'harvests.date': 'Harvest date',
  'harvests.normalized': 'Normalised weight',
  'harvests.create': 'Record the batch',
  'harvests.created': 'Batch recorded.',
  'harvests.list': 'Harvested batches',
  'harvests.empty': 'No harvest batch.',
  // Lot Q1b (migration 0086) : la récolte entre en stock.
  'harvests.stock': 'Stock',
  'harvests.stock.warehouse': 'Stock-entry warehouse',
  'harvests.stock.warehouseHint': 'Optional: without a warehouse the harvest is weighed and waits for stock entry. Also used by the “Enter in stock” button of the list.',
  'harvests.stock.later': 'Later',
  'harvests.stock.enter': 'Enter in stock',
  'harvests.stock.exempt': 'Predates stock',
  'harvests.stock.unvalued': 'Not valued',
  'harvests.stock.warehouseRequired': 'Choose a stock-entry warehouse first.',
  'harvests.stock.done': 'Harvest entered in stock: its lot awaits quality control.',
  'harvests.stock.doneUnvalued': 'Harvest entered in stock by quantity only: the product has no standard cost.',
  'harvest.batch.not_found': 'Harvest not found.',
  'harvest.batch.already_stocked': 'This harvest is already in stock.',
  'harvest.batch.stock_exempt': 'This harvest predates harvest stock entry: it is delivered without stock.',
  'harvest.batch.product_not_stock_managed': 'This product is not stock-managed: its harvest has no stock to enter.',
  'harvest.batch.unit_mismatch': 'The unit cannot be converted to the product’s unit.',
  'harvest.batch.stock_fields_are_final': 'This harvest is already in stock: its warehouse, lot and entry value cannot be rewritten.',
  'harvest.batch.stock_exempt_is_final': 'A harvest’s stock exemption can be neither set nor removed.',
  'harvest.batch.not_readable': 'The harvest was entered in stock but cannot be read back.',
  'sales.delivery_note.harvest_not_in_stock': 'This harvest is not in stock yet: enter it in stock before delivering it.',
  'sales.delivery_note.harvest_not_released': 'This harvest’s lot is not released: a quality decision is required before any delivery.',
  'stock.balance.not_found': 'No stock of this product in this warehouse.',
  'stock.movement.inactive_warehouse': 'A deactivated warehouse cannot receive goods.',
  'stock.warehouse.not_found': 'Warehouse not found.',
  'harvests.valuation': 'Unsold valuation (V3)',
  'harvests.valuation.unsold': 'Unsold quantity',
  'harvests.valuation.avgPrice': 'Average sale price',
  'harvests.valuation.stockValue': 'Stock value',
  'harvests.valuation.load': 'Compute',
  'harvest.valuation.no_sale_price': 'No sale this season: the average price does not exist yet, and the stock cannot be valued without it.',
  'harvest.valuation.no_harvest': 'No harvest recorded: there is no stock to value.',

  // --- Subventions (V7) ---
  'subsidies.title': 'Subsidies',
  'subsidies.description': 'Grants received, and their share-out in proportion to eligible areas.',
  'subsidies.code': 'Code',
  'subsidies.name': 'Label',
  'subsidies.amount': 'Granted amount',
  'subsidies.type': 'Kind',
  'subsidies.status': 'Status',
  'subsidies.allocated': 'Already shared out',
  'subsidies.remaining': 'Left to share out',
  'subsidies.create': 'Record a subsidy',
  'subsidies.created': 'Subsidy recorded.',
  'subsidies.allocate': 'Share out',
  'subsidies.allocate.basis': 'Basis',
  'subsidies.allocate.area': 'In proportion to eligible areas',
  'subsidies.allocate.equal': 'Equal shares',
  'subsidies.allocate.run': 'Run the share-out',
  'subsidies.allocate.done': 'Share-out saved.',
  'subsidies.allocate.hint': 'Running it again replaces the previous share-out; it does not add to it.',
  'subsidies.allocations': 'Shares',
  'subsidies.allocations.plot': 'Plot',
  'subsidies.allocations.area': 'Eligible area (ha)',
  'subsidies.allocations.amount': 'Share',
  'subsidies.allocations.empty': 'No share-out recorded.',
  'subsidies.empty': 'No subsidy.',
  'finance.subsidy.zero_basis': 'All eligible areas are nil: choose "equal shares" if that is what is meant.',
  'finance.subsidy.no_cost_object': 'No eligible plot-season for this subsidy.',

  // --- Champs personnalisés ---
  'customFields.title': 'Custom fields',
  'customFields.description': 'Add your own fields to records, with no developer.',
  'customFields.entity': 'Record',
  'customFields.key': 'Key',
  'customFields.name': 'Label',
  'customFields.type': 'Type',
  'customFields.required': 'Required',
  'customFields.options': 'Possible choices (comma separated)',
  'customFields.create': 'Define the field',
  'customFields.created': 'Field defined.',
  'customFields.empty': 'No custom field on this record.',
  'customFields.values': 'Custom fields',
  'customFields.save': 'Save',
  'customFields.saved': 'Values saved.',
  'customFields.export': 'Export as CSV',
  'customFields.type.string': 'Text',
  'customFields.type.integer': 'Whole number',
  'customFields.type.decimal': 'Decimal number',
  'customFields.type.boolean': 'Yes / no',
  'customFields.type.date': 'Date',
  'customFields.type.list': 'Choice list',
  'meta.custom_field.required': 'This field is required.',
  'meta.custom_field.not_in_options': 'This value is not among the offered choices.',
  'meta.custom_field.invalid_value': 'The value does not match the expected type.',
  'meta.custom_field.above_maximum': 'The value exceeds the allowed maximum.',
  'meta.custom_field.below_minimum': 'The value is below the allowed minimum.',

  // --- Cahier de plaine et alertes (§10.3) ---
  'fieldbook.title': 'Field book',
  'fieldbook.date': 'Date',
  'fieldbook.work': 'Work',
  'fieldbook.products': 'Inputs',
  'fieldbook.area': 'Area (ha)',
  'fieldbook.warnings': 'Alerts',
  'fieldbook.empty': 'No operation recorded on this plot-season.',
  'fieldbook.noWarning': 'No alert',
  'ops.warning.dose_exceeded': 'Dose above the one on the product record.',
  'ops.warning.pre_harvest_interval': 'Pre-harvest interval: the expected harvest falls too early.',
  'ops.warning.nitrogen_cumulative': 'Cumulated nitrogen above the season ceiling.',
  'ops.warning.cost_anomaly': 'Cost well above comparable operations.',

  // --- Rapports (§15.1) ---
  'reports.title': 'Reports',
  'reports.description': 'Five Excel workbooks, with the same figures as the screens.',
  'reports.download': 'Download',
  'reports.language': 'Report language',
  'reports.hint': 'Headers are translated and numbers stay numbers: a column sums as it is.',
  'report.couts-detailles': 'Detailed costs',
  'report.stocks': 'Stocks',
  'report.achats': 'Purchases',
  'report.ventes': 'Sales',
  'report.marges': 'Margins',

  'nav.stockJournal': 'Stock journal',
  'nav.stockBalances': 'Stock balances',
  'nav.seasons': 'Season lifecycle',

  'common.search': 'Search',
  'common.searchPlaceholder': 'Type to filter…',
  'common.showInactive': 'Show inactive',
  'common.confirm': 'Confirm',
  'common.edit': 'Edit',
  'common.deactivate': 'Deactivate',
  'common.activate': 'Activate',
  'common.inactive': 'Inactive',

  'auth.too_many_attempts': 'Too many attempts from this device. Try again in a minute.',
  'dash.cost.inputs': 'Inputs',
  'dash.cost.machinery': 'Machinery',
  'dash.cost.labor': 'Labour',
  'dash.cost.other': 'Other',
  'dash.costBreakdown': 'Cost breakdown',
  'dash.realData': 'real data',
  'dash.byNature': 'By nature',
  'dash.byItem': 'By item',
  'dash.noCharges': 'No cost recorded',
  'dash.alerts': 'Alerts to handle',
  'dash.alertCount': 'alert(s)',
  'dash.noAlerts': 'No alert',
  'dash.recentOps': 'Latest operations',
  'dash.netMarginPerHa': 'Net margin / ha',
  'dash.totalYield': 'Total yield',
  'dash.noAggregated': 'No aggregated data for these filters. Record operations and harvests to see the figures.',
  'common.noData': 'No data',
  'common.noDataToShow': 'Nothing to show',
  'common.comingSoon': 'Coming soon',
  'common.comingSoonHint': 'This section will be available soon.',
  'common.total': 'Total',
  'common.date': 'Date',
  'common.work': 'Work',
  'common.number': 'Number',
  'common.cost': 'Cost',
  'common.status': 'Status',
  'ops.checkAlerts': 'Check alerts §10.3',
  'ops.alertHint': 'The alert informs without blocking the entry, and stays visible in the field book',
  'plots.titleFull': 'Plots and cropping plans',
  'plots.countLabel': 'plots',
  'plots.subtitle': 'The plot is permanent; the cropping plan — plot × season — is the cost object.',
  'subscription.title': 'Current subscription',
  'subscription.description': 'Plan, quotas and subscribed modules',
  'subscription.modules': 'Modules',
  'subscription.area': 'Area',
  'subscription.support': 'Past a limit, creating is refused. To change plan or add an add-on, the company administrator sends a request from this page.',

  'common.inTotal': 'in total',
  'common.area': 'Area',
  'common.crop': 'Crop',
  'common.variety': 'Variety',
  'common.yield': 'Yield',
  'common.costPerHa': 'Cost/ha',
  'common.marginPerHa': 'Margin/ha',
  'common.action': 'Action',
  'common.label': 'Label',
  'common.description': 'Description',
  'common.start': 'Start',
  'common.end': 'End',
  'common.filter': 'Filter',
  'common.open': 'Open',
  'common.toCheck': 'To check',
  'common.inProgress': 'In progress',
  'common.closed': 'Closed',
  'common.toReview': 'to review',
  'farms.countLabel': 'farms',
  'farms.subtitle': 'Plot and activity tracking, farm by farm.',
  'farms.new': 'New farm',
  'farms.list': 'Farm list',
  'farms.delegation': 'Delegation',
  'farms.workshop': 'Activity',
  'farms.phone': 'Phone',
  'farms.sheet': 'Record',
  'farms.codeHint': 'Unique code, upper case.',
  'farms.areaBreakdown': 'Area breakdown',
  'farms.hectaresPerFarm': 'hectares per farm',
  'plots.allFarms': 'All farms',
  'plots.allCrops': 'All crops',
  'plots.allStatuses': 'All statuses',
  'plots.previousCrop': 'Previous crop',
  'plots.cropBreakdown': 'Breakdown by crop',
  'plots.consistency': 'Consistency checks',
  'plots.rule1': 'The sown areas of a plot cannot add up to more than its cadastral area.',
  'plots.rule2': 'The previous crop is filled in automatically from the prior season.',
  'plots.rule3': 'An inactive plot is no longer worked — alert RA-02.',
  'season.countLabel': 'seasons',
  'season.costObjects': 'Cost objects',
  'season.checks': 'Pre-closing checks',
  'season.selectHint': 'Pick a season in the table to see its checks.',
  'season.check.operations': 'Approved operations',
  'season.check.harvests': 'Linked harvests',
  'season.check.costs': 'Allocated costs',
  'season.check.stock': 'Counted stock',
  'campaigns.doubleCrop': 'Double cropping (V5)',
  'campaigns.doubleCrop.on': 'enabled — sequences 1 and 2 allowed',
  'campaigns.doubleCrop.off': 'disabled — one crop per season (sequence 1)',

  'partners.counter': 'Counter',
  'partners.code': 'Code',
  'partners.name': 'Name',
  'partners.legalName': 'Legal name',
  'partners.taxId': 'Tax number',
  'partners.secondaryTaxId': 'Secondary tax number',
  'partners.category': 'Legal form',
  'partners.category.company': 'Company',
  'partners.category.individual': 'Individual',
  'partners.category.other': 'Other',
  'partners.firstName': 'First name',
  'partners.lastName': 'Last name',
  'partners.address': 'Address',
  'partners.city': 'City',
  'partners.governorate': 'Governorate',
  'partners.postalCode': 'Postal code',
  'partners.country': 'Country',
  'partners.phone': 'Phone',
  'partners.phoneSecondary': 'Secondary phone',
  'partners.email': 'Email',
  'partners.website': 'Website',
  'partners.contact': 'Main contact',
  'partners.bankName': 'Bank',
  'partners.bankAccount': 'Bank account',
  'partners.iban': 'IBAN',
  'partners.rib': 'Bank identity',
  'partners.paymentTermsDays': 'Payment term (days)',
  'partners.creditLimit': 'Credit ceiling',
  'partners.notes': 'Notes',
  'partners.empty': 'No matching partner.',
  'partners.searchHint': 'Counter, tax number, code, legal name, phone or email.',
  'partners.counterHint': 'Generated on creation, ten characters. It never changes.',
  'referentiels.codeSuggere': 'Suggested from the last code. Editable.',
  'partners.deactivateTitle': 'Deactivate this partner',
  'partners.deactivateMessage': '{nom} will no longer appear in entry lists. Past documents stay intact.',
  'customers.title': 'Customers',
  'customers.description': 'Partners you sell to. The counter starts with 411.',
  'customers.empty': 'No matching customer.',
  'partners.saved': 'Partner saved.',
  'agro.partner.code_required': 'The partner code is required (30 characters maximum).',
  'agro.partner.name_required': 'The partner name is required (200 characters maximum).',
  'agro.partner.legal_name_required': 'The legal name is required (200 characters maximum).',
  'agro.partner.tax_id_required': 'The tax number is required, except for an individual.',
  'agro.partner.duplicate_code': 'Another partner already uses this code.',
  'agro.partner.duplicate_tax_id': 'Another partner already uses this tax number.',
  'agro.partner.negative_payment_terms': 'The payment term cannot be negative.',
  'agro.partner.negative_credit_limit': 'The credit ceiling cannot be negative.',

  'behaviors.none': 'Not stocked',
  'behaviors.quantity': 'Quantity only',
  'behaviors.lot': 'Lot by lot',
  'behaviors.weighted_average': 'Weighted average cost',
  'behaviors.fifo': 'First in, first out',
  'behaviors.lifo': 'Last in, first out',
  'behaviors.last_purchase': 'Last purchase price',
  'behaviors.standard': 'Standard cost',
  'behaviors.hint': 'What the method actually computes. The list is closed.',
  'units.title': 'Units of measure',
  'units.description': 'The units an article is counted, bought or sold in.',
  'units.empty': 'No matching unit.',
  'units.symbol': 'Symbol',
  'units.dimension': 'Dimension',
  'units.factorToBase': 'Factor to base unit',
  'units.isBase': 'Base unit',
  'dimensions.mass': 'Mass',
  'dimensions.volume': 'Volume',
  'dimensions.area': 'Area',
  'dimensions.length': 'Length',
  'dimensions.count': 'Count',
  'dimensions.time': 'Time',
  'families.title': 'Product families',
  'families.description': 'The grouping that carries an article’s default stock tracking mode and tax rates.',
  'families.empty': 'No matching family.',
  'families.counter': 'Counter',
  'families.nature': 'Kind',
  'families.nature.input': 'Input',
  'families.nature.harvest': 'Harvest',
  'families.nature.service': 'Service',
  'families.nature.asset': 'Asset',
  'families.trackingMode': 'Tracking mode',
  'families.purchaseTax': 'Purchase VAT',
  'families.salesTax': 'Sales VAT',
  'trackingModes.title': 'Stock tracking modes',
  'trackingModes.description': 'What stock actually knows how to do with an article: not stocked, by quantity, or lot by lot.',
  'trackingModes.empty': 'No matching tracking mode.',
  'trackingModes.behavior': 'Behavior',
  'valuationMethods.title': 'Valuation methods',
  'valuationMethods.description': 'The calculation used to value stock: weighted average, FIFO, LIFO, last purchase price or standard cost.',
  'valuationMethods.empty': 'No matching method.',
  'valuationMethods.behavior': 'Behavior',
  'references.deactivateTitle': 'Deactivate this entry',
  'references.deactivateMessage': '{nom} will no longer appear in entry lists. What already uses it stays unchanged.',
  'nav.families': 'Families',
  'nav.trackingModes': 'Stock tracking',
  'nav.valuationMethods': 'Valuation',
  'platform.title': 'Platform panel',
  'platform.nav.clients': 'Clients',
  'platform.forbidden': 'This panel is reserved for platform administrators.',
  'platform.legal_name_required': 'The client legal name is required.',
  'platform.modules_required': 'Pick at least one activity: a client with no open activity can do nothing.',
  'platform.module_unknown': 'That activity is not in the catalogue.',
  'platform.plan_unknown': 'That plan does not exist, or is no longer offered.',
  'platform.email_taken': 'That email already belongs to an account. It is unique across the whole product, all clients together.',
  'platform.client_not_found': 'That client no longer exists, or cannot be administered.',
  'platform.clients.title': 'Clients',
  'platform.clients.description': 'The organisations using the product, their plan, and what they do with it.',
  'platform.clients.list': 'Client list',
  'platform.clients.legalName': 'Legal name',
  'platform.clients.governorate': 'Governorate',
  'platform.clients.plan': 'Plan',
  'platform.clients.farms': 'Farms',
  'platform.clients.users': 'Users',
  'platform.clients.status': 'Status',
  'platform.clients.createdAt': 'Created on',
  'platform.clients.noPlan': 'No subscription',
  'platform.clients.empty': 'No client yet.',
  'platform.clients.new': 'New client',
  'platform.clients.new.description': 'Organisation, first administrator and subscription are created together: all four exist, or none.',
  'platform.clients.group.company': 'The organisation',
  'platform.clients.group.admin': 'Its first administrator',
  'platform.clients.group.plan': 'What it buys',
  'platform.clients.email': 'Organisation email',
  'platform.clients.adminName': 'Administrator name',
  'platform.clients.adminEmail': 'Administrator email',
  'platform.clients.adminPassword': 'Initial password',
  'platform.clients.passwordWarning': 'This password is handed over outside the software, by voice. No screen can change it today, and no reset email is sent: write it down before you confirm.',
  'platform.clients.passwordHint': 'Ten characters at least.',
  'platform.clients.modules': 'Open activities',
  'platform.clients.modulesHint': 'At least one. Another activity can be opened later.',
  'platform.clients.created': 'Client created. Its administrator can sign in right away.',
  'platform.clients.activated': 'Client activated: its users can sign in again.',
  'platform.clients.deactivated': 'Client deactivated: nobody can sign in there any more.',
  'platform.clients.activate.title': 'Reactivate this client?',
  'platform.clients.activate.message': 'Its users will be able to sign in again. No data was lost while it was closed.',
  'platform.clients.deactivate.title': 'Deactivate this client?',
  'platform.clients.deactivate.message': 'Nobody will be able to sign in for this client, neither its administrator nor its workers, for as long as it stays deactivated. No data is deleted.',
  'platform.plan.essentiel': 'Essential',
  'platform.plan.exploitation': 'Farm',
  'platform.plan.domaine': 'Estate',
  'platform.plan.conseil': 'Advisory',
  'platform.plan.sur_mesure': 'Custom',
  'platform.subscription.trial': 'Trial',
  'platform.subscription.active': 'Active',
  'platform.subscription.past_due': 'Past due',
  'platform.subscription.suspended': 'Suspended',
  'platform.subscription.cancelled': 'Cancelled',
  'platform.subscription.expired': 'Expired',
  'platform.client.back': 'Back to clients',
  'platform.client.levers.title': 'Two levers, and they do not do the same thing',
  'platform.client.levers.organization': 'Deactivating the company shuts the door: nobody can open a session at this client any more, neither its administrator nor its workers. No data is deleted.',
  'platform.client.levers.subscription': 'Suspending the subscription closes no session: the client signs in, reads all of its data, and simply cannot create anything any more — no farm, no plot. This is the unpaid-invoice lever: it cuts adding, not access.',
  'platform.client.company': 'The company',
  'platform.client.orgType': 'Kind',
  'platform.client.taxId': 'Tax identifier',
  'platform.client.city': 'City',
  'platform.client.phone': 'Phone',
  'platform.client.subscription': 'The subscription',
  'platform.client.startedOn': 'Start',
  'platform.client.periodEnd': 'Due date',
  'platform.client.endedOn': 'End',
  'platform.client.price': 'Price',
  'platform.client.autoRenew': 'Automatic renewal',
  'platform.client.cancellationReason': 'Cancellation reason',
  'platform.client.noSubscription': 'This client has no subscription. No ceiling applies, and it cannot create anything until it has an active one.',
  'platform.client.noExpiry': 'Subscriptions move forward on their own: at the end of the trial or period, payment is due; after the grace period the client becomes read-only. Reactivating the subscription records the payment.',
  'platform.client.suspend': 'Suspend',
  'platform.client.resume': 'Reactivate',
  'platform.client.terminate': 'Cancel',
  'platform.client.changePlan': 'Change plan',
  'platform.client.extend': 'Extend',
  'platform.client.months': 'Number of months',
  'platform.client.reason': 'Reason',
  'platform.client.reasonRequired': 'Required: the database demands it, and it is what we will have to explain to the client who calls back.',
  'platform.client.suspend.title': 'Suspend this subscription?',
  'platform.client.suspend.message': 'The client will keep signing in and reading all of its data. It will no longer be able to create a farm or a plot. Nobody is signed out: to shut the door, deactivate the company.',
  'platform.client.resume.title': 'Reactivate this subscription?',
  'platform.client.resume.message': 'The client will again be able to create farms and plots, within the ceilings of its plan.',
  'platform.client.terminate.title': 'Cancel this subscription?',
  'platform.client.terminate.message': 'The end date is set to today and the client creates nothing more. It keeps its data and goes on reading it as long as the company stays active.',
  'platform.client.changePlan.title': "Change this client's plan?",
  'platform.client.extend.title': 'Push back the due date?',
  'platform.client.extend.message': 'The due date moves from its current value, not from today: extending a running subscription never shortens it.',
  'platform.client.extended': 'Due date pushed back.',
  'platform.client.suspended': 'Subscription suspended: the client still reads, it no longer creates.',
  'platform.client.resumed': 'Subscription reactivated.',
  'platform.client.terminated': 'Subscription cancelled.',
  'platform.client.planChanged': 'Plan changed.',
  'platform.client.grant': 'Give a subscription',
  'platform.client.grant.title': 'Give this client a subscription?',
  'platform.client.grant.message': "The subscription starts today, active, for one period of the chosen plan, and renews automatically. Price, billing period and currency are the plan's.",
  'platform.client.granted': 'Subscription given.',
  'platform.client.modules': 'The activities',
  'platform.client.modules.description': 'What the client is entitled to work with. A closed activity disappears from its menu.',
  'platform.client.module.guards': 'Closing this activity really does refuse its screens.',
  'platform.client.module.cosmetic': 'Closing this activity changes nothing but the menu today: it guards no screen, because it does not exist in the product yet.',
  'platform.client.module.open': 'Open',
  'platform.client.module.close': 'Close',
  'platform.client.module.openedAt': 'Opened on',
  'platform.client.module.closedAt': 'Closed on',
  'platform.client.module.open.title': 'Open this activity?',
  'platform.client.module.open.message': 'The client will see this activity in its menu and will be able to use its screens, if any exist.',
  'platform.client.module.close.title': 'Close this activity?',
  'platform.client.module.opened': 'Activity opened.',
  'platform.client.module.closed': 'Activity closed.',
  'platform.client.module.unchanged': 'This activity was already in that state: nothing changed.',
  'platform.client.usage': 'Usage against the ceilings',
  'platform.client.usage.description': 'Counted live on farms, plots and accounts — never read from a hand-maintained counter, which would drift.',
  'platform.client.usage.limit': 'Ceiling',
  'platform.client.usage.used': 'Used',
  'platform.client.usage.unlimited': 'No limit',
  'platform.client.usage.noPlan': 'Without a subscription, there is no ceiling to show.',
  'platform.client.usage.usersNotEnforced': 'This ceiling is NOT enforced: creating a user goes through no guard. A client limited to three users can create three hundred. The figure is indicative, it is not a limit.',
  'platform.client.limit.farms': 'Farms',
  'platform.client.limit.plots': 'Plots',
  'platform.client.limit.area': 'Area',
  'platform.client.limit.users': 'Users',
  'platform.client.downgrade.title': 'What this plan takes away from this client',
  'platform.client.downgrade.intro': 'It keeps all of its data and goes on reading it. What it will no longer be able to do is create new data beyond these ceilings.',
  'platform.client.downgrade.has': 'it has',
  'platform.client.downgrade.allows': 'the target plan allows',
  'platform.client.downgrade.none': 'This plan takes nothing away from this client: its usage fits within every ceiling.',
  'platform.client.downgrade.notEnforced': 'no real effect: this ceiling is not enforced',
  'platform.months_invalid': 'Extending requires a strictly positive number of months.',
  'platform.no_subscription': 'This client has no subscription to change.',
  'platform.subscription_exists': 'This client already has a subscription: reactivate it or change its plan.',
  'platform.status_unknown': 'A subscription is set by hand to active, suspended or cancelled, and nothing else.',
  'platform.reason_required': 'The reason is required: it is what we will have to explain to the client.',
  'platform.last_module': 'This is the last open activity of this client: closing it would leave it unable to do anything. Cancel the subscription if that is the intent.',
  'notice.region': 'Notices about your subscription',
  'notice.renewal_reminder': 'Your subscription renews on',
  'notice.trial_ending': 'Your trial ends on',
  'notice.past_due': 'Payment due: without payment, your account becomes read-only on',
  'notice.suspended': 'Your subscription is suspended: you can view your data but not create any. Since',
  'notice.expired': 'Your subscription expired on',
  'notice.cancelled': 'Your subscription was cancelled on',
  'notice.inDays': 'in',
  'notice.see': 'View subscription',
  'notice.dismiss': 'Dismiss',
  'platform.plan_unchanged': 'The client is already on this plan.',
  'platform.no_period_end': 'This subscription has no end date: change plans immediately.',
  'platform.grace_invalid': 'The grace period lasts from 0 to 90 days.',
  'platform.reminders_invalid': 'At most ten reminders, each 1 to 365 days before the end date.',
  'subscription.notice_not_found': 'This notice cannot be found or was already read.',
  'platform.client.lifecycle': 'Lifecycle',
  'platform.client.lifecycle.none': 'No subscription: nothing to schedule.',
  'platform.client.lifecycle.pastDue': 'Payment due since',
  'platform.client.lifecycle.readOnlyOn': 'Read-only from',
  'platform.client.lifecycle.reactivateHint': 'Reactivate the subscription as soon as payment is received.',
  'platform.client.lifecycle.trialUntil': 'Trial until',
  'platform.client.lifecycle.nextEnd': 'Next end date',
  'platform.client.lifecycle.willRenew': 'automatic renewal, then payment due.',
  'platform.client.lifecycle.willEnd': 'the subscription will stop on that date.',
  'platform.client.lifecycle.scheduled': 'Plan change at end of period',
  'platform.client.lifecycle.switchTo': 'Switching to',
  'platform.client.lifecycle.on': 'on',
  'platform.client.lifecycle.cancelChange': 'Cancel this change',
  'platform.client.lifecycle.targetPlan': 'Target plan',
  'platform.client.lifecycle.choosePlan': 'Choose a plan…',
  'platform.client.lifecycle.scheduleChange': 'Schedule at end of period',
  'platform.client.lifecycle.changeScheduled': 'Change scheduled.',
  'platform.client.lifecycle.changeCancelled': 'Scheduled change cancelled.',
  'platform.client.lifecycle.cancellation': 'Cancellation at end of period',
  'platform.client.lifecycle.cancelScheduled': 'Cancellation scheduled at end of period. Reason:',
  'platform.client.lifecycle.withdrawCancellation': 'Withdraw cancellation',
  'platform.client.lifecycle.cancelAtEnd': 'Cancel at end of period',
  'platform.client.lifecycle.cancellationScheduled': 'Cancellation scheduled.',
  'platform.client.lifecycle.cancellationWithdrawn': 'Cancellation withdrawn.',
  'platform.client.lifecycle.history': 'History',
  'platform.client.lifecycle.noHistory': 'No events recorded.',
  'platform.client.lifecycle.when': 'When',
  'platform.client.lifecycle.what': 'What',
  'platform.client.lifecycle.who': 'By',
  'platform.client.lifecycle.byJob': 'Automatic',
  'platform.client.lifecycle.byPlatform': 'Panel',
  'platform.client.lifecycle.event.created': 'Subscription created',
  'platform.client.lifecycle.event.status_changed': 'Status changed',
  'platform.client.lifecycle.event.plan_changed': 'Plan changed',
  'platform.client.lifecycle.event.period_changed': 'End date moved',
  'platform.client.lifecycle.event.plan_change_scheduled': 'Plan change scheduled',
  'platform.client.lifecycle.event.plan_change_cancelled': 'Scheduled change cancelled',
  'platform.client.lifecycle.event.cancellation_scheduled': 'Cancellation scheduled',
  'platform.client.lifecycle.event.cancellation_withdrawn': 'Cancellation withdrawn',
  'platform.policy.title': 'End dates, grace and reminders',
  'platform.policy.description': 'Every six hours the lifecycle job moves subscriptions forward: trial end and end date → payment due, then read-only after the grace period; reminders before the end date. No email is sent: notices appear in the client\'s app.',
  'platform.policy.graceDays': 'Grace days',
  'platform.policy.graceHint': 'Days of payment due before read-only (0 to 90).',
  'platform.policy.reminderDays': 'Reminders (days before end date)',
  'platform.policy.reminderHint': 'Comma-separated, for example 30, 7, 1.',
  'platform.policy.saved': 'Policy saved.',
  'platform.policy.runNow': 'Run now',
  'platform.policy.ran': 'Job run. Acts:',
  'feature.stock': 'Stock and warehouses',
  'feature.purchasing': 'Purchasing',
  'feature.sales': 'Sales and invoicing',
  'feature.assets': 'Fixed assets',
  'feature.subsidies': 'Subsidies',
  'feature.audit': 'Audit log',
  'feature.mobile_sync': 'Offline mobile app',
  'saas.feature_not_included': 'This feature is not included in your plan. Contact your administrator to change plans.',
  'platform.plan_code_invalid': 'The code starts with a letter and contains only lowercase letters, digits and underscores (2 to 30 characters).',
  'platform.plan_code_taken': 'A plan already uses this code, even if archived. Choose another one.',
  'platform.plan_name_required': 'A plan without a name cannot be sold: give it a name.',
  'platform.billing_period_invalid': 'The billing period is monthly or yearly.',
  'platform.plan_price_invalid': 'A price is required and cannot be negative.',
  'platform.plan_quota_invalid': 'A limit is either empty (unlimited) or strictly positive: zero would allow nothing.',
  'platform.plan_trial_invalid': 'The trial lasts from 0 to 365 days.',
  'platform.plan_module_count_invalid': 'The number of included activities cannot exceed the number of activities offered.',
  'platform.feature_unknown': 'This feature is not in the catalogue.',
  'platform.plan_not_found': 'This plan cannot be found.',
  'platform.plan_stale': 'The plan was changed in the meantime by someone else. Reload the page before saving.',
  'platform.expiry_invalid': 'An exception cannot expire before today.',
  'platform.nav.plans': 'Plans',
  'platform.plans.title': 'Plans',
  'platform.plans.description': 'What is sold: prices, limits, activities and features. Changing a plan changes neither the price nor the rights of an existing subscriber.',
  'platform.plans.list': 'Plan catalogue',
  'platform.plans.empty': 'No plans.',
  'platform.plans.new': 'New plan',
  'platform.plans.name': 'Plan',
  'platform.plans.price': 'Price',
  'platform.plans.perMonth': 'month',
  'platform.plans.perYear': 'year',
  'platform.plans.content': 'Content',
  'platform.plans.subscribers': 'Subscribers',
  'platform.plans.status': 'Status',
  'platform.plans.state.public': 'Public',
  'platform.plans.state.hidden': 'Hidden',
  'platform.plans.state.archived': 'Archived',
  'platform.plans.archive': 'Archive',
  'platform.plans.restore': 'Restore',
  'platform.plans.archive.title': 'Archive this plan?',
  'platform.plans.archive.message': 'It will no longer be offered when creating a client or changing plans. Its current subscribers keep their plan, price and features.',
  'platform.plans.archive.subscribers': 'Current subscribers:',
  'platform.plans.restore.title': 'Restore this plan?',
  'platform.plans.restore.message': 'It will again be offered when creating a client and when changing plans.',
  'platform.plans.archived': 'Plan archived.',
  'platform.plans.restored': 'Plan restored.',
  'platform.plans.created': 'Plan created.',
  'platform.plans.saved': 'Plan saved.',
  'platform.plans.editor.description': 'A plan\'s price, limits, activities and features.',
  'platform.plans.editor.grandfathered': 'Existing subscribers keep their price: a change here only applies to new subscriptions. Current subscribers:',
  'platform.plans.group.identity': 'Identity',
  'platform.plans.group.price': 'Price and trial',
  'platform.plans.group.limits': 'Limits',
  'platform.plans.group.content': 'Content',
  'platform.plans.group.visibility': 'Visibility',
  'platform.plans.code': 'Code',
  'platform.plans.codeHint': 'Lowercase letters, digits and underscores. It cannot be changed later.',
  'platform.plans.codeFrozen': 'The code is frozen after creation.',
  'platform.plans.nameFr': 'Name (French)',
  'platform.plans.nameAr': 'Name (Arabic)',
  'platform.plans.nameEn': 'Name (English)',
  'platform.plans.descriptionField': 'Description',
  'platform.plans.period': 'Billing period',
  'platform.plans.yearly': 'Yearly',
  'platform.plans.monthly': 'Monthly',
  'platform.plans.priceField': 'Price per period (TND)',
  'platform.plans.extraFarmPrice': 'Price per extra farm',
  'platform.plans.trialDays': 'Trial days',
  'platform.plans.emptyMeansUnlimited': 'Empty = unlimited. Zero would allow nothing.',
  'platform.plans.maxFarms': 'Farms',
  'platform.plans.maxUsers': 'Users',
  'platform.plans.maxPlots': 'Plots',
  'platform.plans.maxArea': 'Area (ha)',
  'platform.plans.includedModules': 'Included activities',
  'platform.plans.includedAll': 'All those ticked',
  'platform.plans.features': 'Features',
  'platform.plans.featuresHint': 'Each one really closes screens and routes for a client who does not have it.',
  'platform.plans.public': 'Public',
  'platform.plans.publicHint': 'Unticked, the plan can still be sold from this panel without appearing in the catalogue.',
  'platform.plans.order': 'Display order',
  'platform.client.features': 'Features',
  'platform.client.features.explain': 'What the plan gives, the exceptions granted to or withdrawn from this client, and what it has today. An exception overrides the plan until it expires.',
  'platform.client.features.empty': 'No features in the catalogue.',
  'platform.client.features.feature': 'Feature',
  'platform.client.features.plan': 'Plan',
  'platform.client.features.included': 'Included',
  'platform.client.features.notIncluded': 'Not included',
  'platform.client.features.override': 'Exception',
  'platform.client.features.granted': 'Granted',
  'platform.client.features.withdrawn': 'Withdrawn',
  'platform.client.features.inactive': 'Not in effect',
  'platform.client.features.until': 'until',
  'platform.client.features.today': 'Today',
  'platform.client.features.on': 'On',
  'platform.client.features.off': 'Off',
  'platform.client.features.grant': 'Grant',
  'platform.client.features.withdraw': 'Withdraw',
  'platform.client.features.clear': 'Back to plan',
  'platform.client.features.grantExplain': 'Grant this client, outside its plan:',
  'platform.client.features.withdrawExplain': 'Withdraw from this client even though its plan includes it. Its screens and routes will be refused:',
  'platform.client.features.expiresOn': 'Expires on',
  'platform.client.features.expiresHint': 'Empty = no expiry. The last day counts.',
  'platform.client.features.saved': 'Exception saved.',
  'platform.client.features.cleared': 'Exception removed: the plan decides again.',

  'nav.assets': 'Assets',
  'assets.title': 'Fixed assets',
  'assets.description': 'Asset registry, transfers and disposals.',
  'assets.empty': 'No assets.',
  'assets.code': 'Code',
  'assets.designation': 'Designation',
  'assets.entryValue': 'Entry value',
  'assets.status': 'Status',
  'assets.status.draft': 'Draft',
  'assets.status.in_progress': 'Under construction',
  'assets.status.in_service': 'In service',
  'assets.status.inactive': 'Inactive',
  'assets.status.disposed': 'Disposed',
  'assets.create': 'New asset',
  'assets.created': 'Asset created.',
  'assets.transfer': 'Transfer',
  'assets.transfer.done': 'Transfer recorded.',
  'assets.transfer.to': 'Destination',
  'assets.dispose': 'Dispose',
  'assets.dispose.buyer': 'Buyer',
  'assets.dispose.capitalGain': 'Disposal result',
  'assets.dispose.date': 'Disposal date',
  'assets.dispose.method': 'Disposal method',
  'assets.dispose.method.destruction': 'Destruction',
  'assets.dispose.method.reform': 'Write-off',
  'assets.dispose.method.sale': 'Sale',
  'assets.dispose.method.theft': 'Theft',
  'assets.dispose.nbv': 'Net book value',
  'assets.dispose.noPrice': 'No disposal price',
  'assets.dispose.notValued': 'Disposal not valued: the asset had no depreciation plan.',
  'assets.dispose.price': 'Disposal price',
  'assets.dispose.reason': 'Disposal reason',
  'assets.inProgress': 'Construction expenses',
  'assets.inProgress.add': 'Add the expense',
  'assets.inProgress.amount': 'Amount',
  'assets.inProgress.date': 'Date',
  'assets.inProgress.done': 'Expense recorded.',
  'assets.inProgress.label': 'Label',
  'assets.inProgress.none': 'No expense recorded yet.',
  'assets.inProgress.supplier': 'Supplier',
  'assets.inProgress.total': 'Total, which will become the entry value',
  'assets.lifecycle.putInProgress': 'Declare under construction',
  'assets.dispose.done': 'Disposal recorded.',
  'assets.categories': 'Categories',
  'assets.dashboard.assets': 'Assets',
  'assets.dashboard.nbv': 'Total NBV',
  'assets.dashboard.hours': 'Productive hours',
  'assets.dashboard.alerts': 'Alerts',
  'assets.dashboard.title': 'Assets dashboard',
  'assets.dashboard.fleet': 'Fleet',
  'assets.dashboard.inService': 'In service',
  'assets.dashboard.gross': 'Gross value',
  'assets.dashboard.accumulated': 'Accumulated depreciation',
  'assets.dashboard.depreciation.none': 'No asset in the fleet has a depreciation plan: the accumulated depreciation and net book value cannot be computed.',
  'assets.dashboard.work': 'Running',
  'assets.dashboard.maintenance': 'Maintenance',
  'assets.dashboard.fuel': 'Fuel',
  'assets.dashboard.missing': 'Missing stocktake',
  'assets.dashboard.noAlerts': 'No alerts.',
  'assets.meters': 'Meters',
  'assets.meters.kind': 'Meter kind',
  'assets.meters.hours': 'Hours',
  'assets.meters.km': 'Kilometers',
  'assets.meters.value': 'Reading value',
  'assets.meters.record': 'Record',
  'assets.meters.done': 'Reading recorded.',
  'assets.meters.empty': 'No readings.',
  'assets.usages': 'Usages',
  'assets.usages.empty': 'No linked hours.',
  'assets.usages.operation': 'Operation no.',
  'assets.usages.line': 'Machine line no.',
  'assets.usages.attach': 'Link',
  'assets.usages.done': 'Hours linked to the asset.',
  'assets.depreciation': 'Depreciation',
  'assets.depreciation.year': 'Year',
  'assets.depreciation.item': 'Item',
  'assets.depreciation.charge': 'Charge',
  'assets.depreciation.cumulative': 'Cumulative',
  'assets.depreciation.nbv': 'NBV',
  'assets.depreciation.recompute': 'Regenerate the table',
  'assets.depreciation.recomputed': 'Table regenerated from the current base.',
  'assets.depreciation.duration': 'Duration (years)',
  'assets.depreciation.startHint': 'The plan starts on the commissioning date, or failing that the acquisition date:',
  'assets.depreciation.startMissing': 'neither one is filled in.',
  'assets.acquisition': 'Acquisition and commissioning',
  'assets.acquisitionDate': 'Acquisition date',
  'assets.commissioningDate': 'Commissioning date',
  'assets.acquisition.hint': 'The commissioning date opens depreciation: it sets its first year.',
  'assets.acquisition.save': 'Save the dates',
  'assets.acquisition.done': 'Dates saved.',
  'assets.depreciation.create': 'Create plan',
  'assets.depreciation.done': 'Schedule generated.',
  'assets.components': 'Components',
  'assets.components.empty': 'No components.',
  'assets.components.add': 'Add',
  'assets.components.done': 'Component added: regenerate the table.',
  'assets.revaluation.reason': 'Revaluation reason',
  'assets.revaluation.apply': 'Revalue',
  'assets.revaluation.done': 'Value rewritten and table regenerated.',
  'assets.hourly': 'Hourly cost',
  'assets.hourly.unavailable': 'No productive hours yet: cost refuses to answer.',
  'assets.hourly.fuel': 'Fuel',
  'assets.fuel.theoretical': 'Theoretical use (L/h)',
  'assets.fuel.set': 'Save',
  'assets.fuel.done': 'Theoretical use saved.',
  'assets.maintenance': 'Maintenance',
  'assets.maintenance.empty': 'No interventions.',
  'assets.maintenance.title': 'Intervention title',
  'assets.maintenance.cost': 'Parts cost',
  'assets.maintenance.kind': 'Kind',
  'assets.maintenance.corrective': 'Corrective',
  'assets.maintenance.preventive': 'Preventive',
  'assets.maintenance.meter': 'Meter reading',
  'assets.maintenance.nextMeter': 'Next due (meter)',
  'assets.maintenance.add': 'Add',
  'assets.maintenance.done': 'Intervention recorded.',
  'assets.tyres': 'Tyres',
  'assets.tyres.empty': 'No tracked tyres.',
  'assets.tyres.removed': 'removed',
  'assets.tyres.add': 'Fit a tyre',
  'assets.tyres.done': 'Tyre recorded.',
  'assets.tyres.position': 'Position',
  'assets.insurance': 'Insurance',
  'assets.insurance.empty': 'No policies.',
  'assets.insurance.add': 'Add a policy',
  'assets.insurance.done': 'Policy recorded.',
  'assets.insurance.insurer': 'Insurer',
  'assets.insurance.policy': 'Policy no.',
  'assets.insurance.start': 'Start',
  'assets.insurance.end': 'End',
  'assets.lease': 'Lease',
  'assets.lease.empty': 'No contract.',
  'assets.lease.add': 'Link a contract',
  'assets.lease.done': 'Contract recorded.',
  'assets.lease.lessor': 'Lessor',
  'assets.lease.ref': 'Contract ref',
  'assets.lease.start': 'Contract start',
  'assets.lease.end': 'Contract end',
  'assets.lease.assetValue': 'Asset value',
  'assets.lease.downPayment': 'Down payment',
  'assets.lease.count': 'Planned number of instalments',
  'assets.lease.amount': 'Rent per instalment',
  'assets.lease.installments.entered': 'instalments entered, out of',
  'assets.lease.installments.planned': 'planned in the contract.',
  'assets.lease.installments.empty': 'No instalment entered: neither the treasury schedule nor the hourly cost sees this contract.',
  'assets.lease.installment.new': 'Add an instalment',
  'assets.lease.installment.dueDate': 'Due date',
  'assets.lease.installment.principal': 'Principal',
  'assets.lease.installment.interest': 'Interest',
  'assets.lease.installment.vat': 'Tax',
  'assets.lease.installment.paid.amount': 'Paid',
  'assets.lease.installment.balance': 'Balance due',
  'assets.lease.installment.add': 'Add instalment',
  'assets.lease.installment.added': 'Instalment added.',
  'assets.lease.installment.payment': 'Amount paid',
  'assets.lease.installment.paidOn': 'Payment day',
  'assets.lease.installment.pay': 'Pay',
  'assets.lease.installment.paid': 'Payment recorded.',
  'assets.lease.installment.settled': 'Settled',
  // Lot 8d (migration 0103) : un paiement d'échéance est une pièce, qui s'annule.
  'assets.lease.payments': 'Payments',
  'assets.lease.payment.date': 'Paid on',
  'assets.lease.payment.installment': 'Instalment due',
  'assets.lease.payment.status.posted': 'Recorded',
  'assets.lease.payment.status.cancelled': 'Cancelled',
  'assets.lease.payment.cancel': 'Cancel',
  'assets.lease.payment.cancelled': 'Payment cancelled: the instalment is due again.',
  'assets.lease.payment.reason': 'Cancellation reason',
  'assets.lease.payment.reason.hint': 'Required to cancel a payment. A payment is not corrected: it is cancelled.',
  'assets.fixedCosts': 'Yearly fixed costs',
  'assets.fixedCosts.label': 'Label',
  'assets.fixedCosts.amount': 'Amount',
  'assets.fixedCosts.add': 'Add',
  'assets.fixedCosts.done': 'Fixed cost recorded.',
  'assets.documents': 'Digital file',
  'assets.documents.empty': 'No filed documents.',
  'assets.documents.type': 'Kind',
  'assets.documents.name': 'File name',
  'assets.documents.add': 'File',
  'assets.documents.done': 'Document filed (metadata).',
  'assets.stocktake': 'Stocktake',
  'assets.stocktake.empty': 'No records.',
  'assets.stocktake.record': 'Record',
  'assets.stocktake.done': 'Record saved.',
  'assets.lifecycle': 'Lifecycle',
  'assets.lifecycle.commission': 'Commission',
  'assets.lifecycle.done': 'Status updated.',
  'operations.fixedAsset': 'Tracked asset',
  'nav.assetsDashboard': 'Assets board',
  'asset.fixed_asset.duplicate_code': 'An asset already uses this code.',
  'asset.fixed_asset.not_found': 'The requested asset was not found.',
  'asset.fixed_asset.commissioning_before_acquisition': 'Commissioning cannot precede acquisition.',
  'asset.transfer_on_disposed': 'The asset is disposed and cannot be transferred.',
  'asset.disposal.already_disposed': 'This asset is already disposed.',
  'asset.disposal.reason_required': 'The disposal reason is required.',
  'asset.category.duplicate_code': 'A category already uses this code.',
  'asset.meter.decrease_refused': 'The meter already reads higher: this is a rollback.',
  'asset.meter_on_disposed': 'A disposed asset takes no more readings.',
  'asset.depreciation.plan_exists': 'This asset already has a plan.',
  'asset.depreciation.start_required': 'The plan needs a start date.',
  'asset.hourly_cost.unavailable': 'No productive hours: cost refuses to answer.',
  'asset.maintenance.negative_cost': 'Costs cannot be negative.',
  'asset.lease.already_exists': 'This asset already has a lease.',
  'asset.usage.asset_required': 'Linking designates a tracked asset.',
  'asset.component.duplicate_code': 'A component already uses this code.',
  'asset.document.type_required': 'The document kind is required.',
  'asset.fuel.anomaly': 'Abnormal consumption to check.',
  'asset.maintenance.due_meter': 'Maintenance due by meter.',
  'assets.referenceRate': 'Reference rate',
  'assets.referenceRate.none': 'No reference rate: a line with no rate entered will apply zero.',
  'assets.referenceRate.observed': 'Observed hourly cost',
  'assets.referenceRate.drift': 'The gap between this rate and the observed cost is judged on the assets board.',
  'assets.referenceRate.sourceLabel': 'Rate source',
  'assets.referenceRate.source.budget': 'Budgeted',
  'assets.referenceRate.source.fiscalYear': 'Taken from a fiscal year',
  'assets.referenceRate.year': 'Source fiscal year',
  'assets.referenceRate.takenFromYear': 'The rate is recomputed from the chosen year: nothing to enter.',
  'assets.referenceRate.rate': 'Rate (TND/h)',
  'assets.referenceRate.set': 'Set the rate',
  'assets.referenceRate.done': 'Reference rate saved.',
  'assets.hours': 'Hours for the year',
  'assets.hours.empty': 'The hours for this year cannot be read.',
  'assets.hours.noCapacity': 'Annual capacity not declared.',
  'assets.hours.theoretical': 'Annual capacity',
  'assets.hours.unavailable': 'Unavailable',
  'assets.hours.available': 'Available',
  'assets.hours.productive': 'Productive',
  'assets.hours.utilization': 'Utilisation rate',
  'assets.hours.availability': 'Availability rate',
  'assets.downtimes': 'Downtime register',
  'assets.downtimes.empty': 'No downtime for this year.',
  'assets.downtimes.add': 'Record a downtime',
  'assets.downtimes.done': 'Downtime recorded.',
  'assets.downtimes.from': 'From',
  'assets.downtimes.to': 'To',
  'assets.downtimes.hours': 'Hours',
  'assets.downtimes.reason': 'Reason',
  'assets.downtimes.notes': 'Note',
  'assets.downtimes.deduced': 'Inferred period',
  'assets.downtimes.deducedHint': 'Period inferred from the maintenance log: the end is computed at twenty-four hours per calendar day, it was never entered.',
  'assets.downtimes.reason.breakdown': 'Breakdown',
  'assets.downtimes.reason.maintenance': 'Maintenance',
  'assets.downtimes.reason.weather': 'Weather',
  'assets.downtimes.reason.idle': 'Idle',
  'assets.hourlyCost.tyres': 'Tyres',
  'assets.hourlyCost.tyresIncomplete': 'At least one worn tyre has no price: the tyre share is understated.',
  'assets.hourlyCost.maintenancePerHour': 'Maintenance per hour',
  'assets.hourlyCost.maintenancePerHectare': 'Maintenance per hectare',
  'assets.hourlyCost.noArea': 'No worked area: the cost per hectare cannot be computed.',
  'assets.dashboard.fleetHourlyCost': 'Fleet hourly cost',
  'assets.dashboard.fleetHourlyCost.none': 'No asset in the fleet has a computable hourly cost.',
  'assets.dashboard.alertsCapped': 'Each alert family is capped at twenty rows, and nothing says how many were left out.',
  'operations.referenceRate.hint': 'Reference rate of the asset, applied if this field stays empty:',
  'operations.referenceRate.none': 'This asset has no reference rate: an empty field will apply zero.',
  'asset.category.code_required': 'The category code is required (30 characters at most).',
  'asset.category.invalid_depreciation_years': 'The default depreciation period must be between 1 and 100 years.',
  'asset.category.name_required': 'The category name is required (100 characters at most).',
  'asset.category.organization_required': 'The asset category must belong to an organization.',
  'asset.category.parent_not_found': 'The parent category was not found.',
  'asset.category.parent_required': 'The parent category is invalid.',
  'asset.component.asset_required': 'The component must designate an asset.',
  'asset.component.code_required': 'The component code is required (30 characters at most).',
  'asset.component.designation_required': 'The component designation is required (150 characters at most).',
  'asset.component.invalid_duration': 'The component life must be between 1 and 100 years.',
  'asset.component.negative_value': 'The component value cannot be negative.',
  'asset.component.organization_required': 'The component must belong to an organization.',
  'asset.depreciation.asset_required': 'The plan must designate an asset.',
  'asset.depreciation.empty_schedule': 'The schedule needs at least one item to depreciate.',
  'asset.depreciation.invalid_duration': 'The duration must be between 1 and 100 years.',
  'asset.depreciation.invalid_month': 'The depreciation month must be between 1 and 12.',
  'asset.depreciation.method_unsupported': 'Only straight-line depreciation is computed for now.',
  'asset.depreciation.month_required': 'A monthly plan only carries monthly lines: the month is required.',
  'asset.depreciation.month_unexpected': 'An annual plan only carries annual lines: the month is unexpected.',
  'asset.depreciation.negative_base': 'The depreciable base cannot be negative.',
  'asset.depreciation.organization_required': 'The plan must belong to an organization.',
  'asset.depreciation.period_unsupported': 'Depreciation is annual or monthly, nothing else.',
  'asset.depreciation.plan_not_found': 'This asset has no depreciation plan.',
  'asset.disposal.asset_required': 'The disposal must designate an asset.',
  'asset.disposal.before_commissioning': 'The disposal date cannot precede the asset commissioning date.',
  'asset.disposal.future_date': 'A disposal cannot be recorded with a future date.',
  'asset.disposal.method_required': 'The disposal method is required: sale, destruction, theft or reform.',
  'asset.disposal.negative_net_book_value': 'The net book value cannot be negative.',
  'asset.disposal.negative_price': 'The disposal price cannot be negative.',
  'asset.disposal.organization_required': 'The disposal must belong to an organization.',
  'asset.document.asset_required': 'The document must designate an asset.',
  'asset.document.file_required': 'The file name is required (200 characters at most).',
  'asset.document.organization_required': 'The document must belong to an organization.',
  'asset.document.path_required': 'The file path is required.',
  'asset.downtime.asset_required': 'The downtime must designate an asset.',
  'asset.downtime.hours_exceed_period': 'These downtime hours exceed what the period can cover.',
  'asset.downtime.invalid_hours': 'A downtime lasts a strictly positive number of hours.',
  'asset.downtime.invalid_period': 'A downtime ends after it begins.',
  'asset.downtime.invalid_reason': 'The reason is a breakdown, maintenance, weather or idleness.',
  'asset.downtime.organization_required': 'The downtime must belong to an organization.',
  'asset.downtime.overlap': 'A downtime already covers all or part of this period.',
  'asset.fixed_asset.code_required': 'The asset code is required (30 characters at most).',
  'asset.fixed_asset.designation_required': 'The asset designation is required (150 characters at most).',
  'asset.fixed_asset.disposed_locked': 'A disposed asset can no longer be changed.',
  'asset.fixed_asset.entry_value_blocks_in_progress': 'Bring the entry value back to zero before putting the asset back under construction: it will be recomputed from its expenses at commissioning.',
  'asset.fixed_asset.invalid_entry_value': 'The entry value must be positive and stay plausible.',
  'asset.fixed_asset.invalid_responsibility_center': 'The responsibility centre is 100 characters at most.',
  'asset.fixed_asset.invalid_theoretical_hours': 'The annual capacity must be strictly positive, or absent.',
  'asset.fixed_asset.invalid_year': 'The manufacture year must be between 1900 and 2100.',
  'asset.fixed_asset.negative_amount': 'Acquisition amounts cannot be negative.',
  'asset.fixed_asset.negative_theoretical_fuel': 'The theoretical consumption cannot be negative.',
  'asset.fixed_asset.organization_required': 'The asset must belong to an organization.',
  'asset.fixed_cost.asset_required': 'The fixed cost must designate an asset.',
  'asset.fixed_cost.invalid_year': 'The year must be between 1900 and 2100.',
  'asset.fixed_cost.label_required': 'The label is required (100 characters at most).',
  'asset.fixed_cost.negative_amount': 'The amount cannot be negative.',
  'asset.fixed_cost.organization_required': 'The fixed cost must belong to an organization.',
  'asset.hourly_cost.drift': 'The applied rate drifts from the observed cost: it is the rate that needs revising.',
  'asset.hourly_cost.unrecorded': 'The machine ran and no cost was recorded for the year: it is the charges that need entering.',
  'asset.hourly_cost.invalid_year': 'The year must fall between 1900 and 2100.',
  'asset.hourly_cost.negative_input': 'The hourly cost components cannot be negative.',
  'asset.hours.negative': 'None of these hours can be negative.',
  'asset.in_progress.amount_below_millime': 'An expense below one millime cannot be represented.',
  'asset.in_progress.asset_required': 'The expense must designate an asset.',
  'asset.in_progress.commissioning_date_required': 'Commissioning an asset under construction requires its date.',
  'asset.in_progress.entry_value_not_editable':
    'The entry value of an asset under construction is the sum of its expenses; it cannot be entered.',
  'asset.in_progress.expense_after_commissioning': 'An expense cannot be later than the commissioning date.',
  'asset.in_progress.invalid_amount': 'The expense amount must be strictly positive.',
  'asset.in_progress.label_required': 'The expense label is required (150 characters at most).',
  'asset.in_progress.no_expense': 'An asset under construction with no expense cannot be commissioned.',
  'asset.in_progress.not_in_progress': 'The asset is not an asset under construction.',
  'asset.in_progress.organization_required': 'The expense must belong to an organization.',
  'asset.in_progress.supplier_not_found': 'The expense supplier cannot be found.',
  'asset.insurance.asset_required': 'The insurance policy must designate an asset.',
  'asset.insurance.incoherent_dates': 'The policy end must follow its start.',
  'asset.insurance.insurer_required': 'The insurer is required (150 characters at most).',
  'asset.insurance.negative_premium': 'The premium cannot be negative.',
  'asset.insurance.organization_required': 'The insurance policy must belong to an organization.',
  'asset.lease.asset_required': 'The lease must designate an asset.',
  'asset.lease.incoherent_dates': 'The contract end must follow its start.',
  'asset.lease.installment_not_found': 'The instalment was not found.',
  'asset.lease.empty_installment': 'An instalment carries at least one amount.',
  'asset.lease.installment_outside_contract': 'The instalment falls outside the contract period.',
  'asset.lease.invalid_count': 'The number of instalments must be between 1 and 600.',
  'asset.lease.invalid_payment': 'The payment must be positive and not exceed the outstanding balance.',
  'asset.lease.payment_in_future': 'An instalment payment cannot be dated in the future.',
  'asset.lease.payment_immutable': 'An instalment payment is neither rewritten nor deleted: it is cancelled, and that cannot be undone.',
  'asset.lease.paid_amount_is_derived': 'What is paid of an instalment is the sum of its payments; it cannot be written by hand.',
  'asset.lease.payment_not_found': 'Instalment payment not found.',
  'asset.lease.payment_already_cancelled': 'This instalment payment is already cancelled.',
  'asset.lease.cancellation_reason_required': 'Cancelling an instalment payment requires a reason.',
  'asset.lease.text_too_long': 'The note of an instalment payment, or its cancellation reason, fits in 300 characters.',
  'asset.lease.invalid_terms': 'The financial terms are inconsistent.',
  'asset.lease.lease_required': 'The instalment must designate a contract.',
  'asset.lease.lessor_required': 'The lessor is required.',
  'asset.lease.negative_installment': 'Principal and interest cannot be negative.',
  'asset.lease.not_found': 'The lease contract was not found.',
  'asset.lease.organization_required': 'The lease must belong to an organization.',
  'asset.maintenance.asset_required': 'The maintenance order must designate an asset.',
  'asset.maintenance.kind_required': 'The kind is required: preventive or corrective.',
  'asset.maintenance.negative_meter': 'Meters and downtime hours cannot be negative.',
  'asset.maintenance.organization_required': 'The maintenance order must belong to an organization.',
  'asset.maintenance.title_required': 'The title is required (150 characters at most).',
  'asset.meter.asset_required': 'The reading must designate an asset.',
  'asset.meter.future_date': 'The reading date cannot be in the future.',
  'asset.meter.kind_required': 'The meter kind is required: hours or km.',
  'asset.meter.negative_value': 'The meter value cannot be negative.',
  'asset.meter.organization_required': 'The reading must belong to an organization.',
  'asset.reference_rate.excessive': 'The reference rate exceeds the plausible ceiling.',
  'asset.reference_rate.invalid_source': 'The reference rate source must be budget or fiscal_year.',
  'asset.reference_rate.invalid_year': 'The source year of the rate must be between 1900 and 2100.',
  'asset.reference_rate.negative': 'The reference rate cannot be negative.',
  'asset.reference_rate.rate_required': 'The reference rate is required once a source is set.',
  'asset.reference_rate.year_required': 'A rate taken from a fiscal year must name that year.',
  'asset.revaluation.asset_required': 'The revaluation must designate an asset.',
  'asset.revaluation.negative_value': 'Revalued amounts cannot be negative.',
  'asset.revaluation.organization_required': 'The revaluation must belong to an organization.',
  'asset.revaluation.reason_required': 'The revaluation reason is required (300 characters at most).',
  'asset.stocktake.asset_required': 'The stocktake must designate an asset.',
  'asset.stocktake.condition_required': 'The observed condition is required: good, worn, damaged or out_of_service.',
  'asset.stocktake.organization_required': 'The stocktake must belong to an organization.',
  'asset.transfer.asset_required': 'The transfer must designate an asset.',
  'asset.transfer.destination_required': 'The transfer destination is required (200 characters at most).',
  'asset.transfer.future_date': 'The transfer date cannot be in the future.',
  'asset.transfer.organization_required': 'The transfer must belong to an organization.',
  'asset.tyre.asset_required': 'The tyre must designate an asset.',
  'asset.tyre.incoherent_dates': 'Removal cannot precede mounting.',
  'asset.tyre.incoherent_meter': 'The removal meter cannot precede the mounting meter.',
  'asset.tyre.invalid_life': 'The reference life must be strictly positive, or absent.',
  'asset.tyre.invalid_meter_kind': 'The meter kind is hours or km.',
  'asset.tyre.label_too_long': 'The tyre brand and reference are 100 characters at most.',
  'asset.tyre.negative_meter': 'The mounting meter cannot be negative.',
  'asset.tyre.negative_price': 'The tyre price cannot be negative.',
  'asset.tyre.not_found': 'The tyre was not found.',
  'asset.tyre.organization_required': 'The tyre must belong to an organization.',
  'asset.tyre.position_required': 'The tyre position is required (30 characters at most).',
}

export const MESSAGES: Record<Language, Messages> = { fr, ar, en }

export const LANGUAGE_LABELS: Record<Language, string> = {
  fr: 'Français',
  ar: 'العربية',
  en: 'English',
}

/**
 * Étiquette courte, pour la barre supérieure où le nom complet ne tient pas.
 * Elle reste indexée par `Language` : ajouter une langue sans son étiquette ne compile pas.
 */
export const LANGUAGE_SHORT: Record<Language, string> = {
  fr: 'FR',
  ar: 'ع',
  en: 'EN',
}
